/* ============================================
   QUOTE DASHBOARD — Email Modal + Phase 2 Reply Composer
   Asset: dashboard-email-modal.js

   Extracted from dashboard-main.js on 2026-05-21 as Phase A.1
   of the dashboard refactor. Loaded via <script defer> in
   sections/rep-quote-dashboard.liquid BEFORE main.js so the
   RQD.openEmailModal binding exists by the time any caller in
   main.js can reach it.

   Public API:
     window.RQD.openEmailModal(entry, q, deps)
       Opens the email modal for `entry` (a noteEntry with emailMeta).
       If the entry's conversationId has siblings on this deal, renders
       the full thread with the clicked email highlighted + auto-scrolled
       into view. Reply / Reply All buttons swap the modal into a Quill-
       backed compose pane that posts to /outlook-reply on send.

   Caller-injected deps (read once per call from the third arg):
     esc(str)              — HTML-escape utility (main.js:~18673)
     formatNoteDate(ts)    — timestamp formatter (main.js:~18800)
     fetchQuotes(opts)     — dashboard refresh (called quiet after Phase 2 send)

   No closure state lives in this file. Each call is self-contained —
   the previous open modal (if any) is removed at the top. Quill is
   CDN-loaded lazily on the first Reply click; the link/script tags
   persist across modal opens so subsequent replies are instant.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  /* ============================================
     Shared chip-field subsystem — Phase B.5a (Ryan 2026-05-22)
     ============================================
     Used by both compose paths (reply + new email) to wire Outlook-
     style recipient chip behavior on To/Cc/Bcc fields. Before this
     extraction the two modals had ~150 LOC of duplicated handlers
     (renderChips / addChipsFromText / commitInputAsChip / removeChip /
     wireChipField); every chip UX change had to touch both copies.
     Now: both modals call _installChipFields(opts) and the shared
     code owns the DOM wiring.

     opts:
       overlay:   root DOM element to query for chip fields
       kinds:     array like ['to', 'cc', 'bcc'] — kinds to wire
       esc:       HTML-escape function (caller passes its own)
       getChips:  (kind) → array of chip strings
       setChips:  (kind, array) → void
       onChange:  () → void — called after any chip mutation (use for
                  draft save / state sync)

     Field markup contract (must exist in caller's modal HTML):
       <div data-kind="{kind}" class="rqd-email-modal__chip-field">
         <input class="rqd-email-modal__chip-input" ...>
       </div>

     Returns:
       { renderAll, commitAll, addFromText, removeChip }
     ============================================ */
  var _CHIP_EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* ============================================
     Shared compose helpers — Phase B.5b (Ryan 2026-05-22)
     ============================================
     Both compose paths (reply + new email) need Quill + the same
     pt-based font-size registration + the rep's signature preview.
     These three helpers are module-scope so each modal calls them
     once, no duplication.
     ============================================ */

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Area 2: "attach an existing deal file".
     Shared by the reply composer (_enterCompose) and the New-Email composer (openComposeModal).
     Lists the deal's retrievable documents and, on pick, fetches + base64-encodes them into the
     same {name,contentType,base64,sizeBytes} shape the device file-input produces. Quote PDF +
     Floor-plan PDF are client-retrievable today (Invoice + Linked-design PDFs need a signed-URL
     worker endpoint — follow-up — so they're intentionally omitted here).
     ============================================ */
  var _WORKER_BASE = 'https://rfq-worker.ryan-29b.workers.dev';
  function _getRepToken() {
    return (window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getAccessToken)
      ? window.RepPortal.Auth.getAccessToken() : Promise.resolve('');
  }
  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — theme the floating menus.
     ============================================
     ⛔ The portal's colour tokens are defined on `.rep-quote-portal-wrap` (and swapped by
     `.rep-quote-dark` ON THAT SAME ELEMENT), NOT on :root. The modals inherit them because
     they mount INSIDE the wrap (portalWrap.appendChild(overlay)). The attach menu and the
     "@" picker are hoisted to <body> so they can escape the modal's overflow — which drops
     them out of that variable scope, so every var(--rq-…) silently fell back to its LIGHT
     literal. That is why the attach menu was a white card in front of a dark modal.

     Fix is to copy the resolved values onto the hoisted element itself rather than move it:
     re-parenting into the wrap would put the menu back inside the clipping/stacking context
     it was hoisted out of. Read fresh on every open, so the dark toggle needs no listener. */
  /* ⛔ HTML-escape, and it lives at MODULE scope on purpose (Ryan 2026-08-19).
     It used to exist TWICE, byte-identical, as a local inside _openDealAttachMenu and again
     inside _openTemplateMenu. Both menus build rows by string concatenation, so both needed
     it, and the second copy was written rather than the first one hoisted.

     That is not a style complaint, it cost a live bug: _openSnippetMenu was written against
     the same pattern as its two neighbours, called _e(), and threw ReferenceError on the
     first click. Nothing rendered and nothing logged where a rep would see it, so the ⚡
     button read as completely dead - it took its hover colour and did nothing else. Ryan:
     *"I click it, and nothing happens at all. it just turns blue."*

     One definition now. A helper that two functions need belongs above both of them. */
  function _e(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function _mailFilterTag(em) {
    /* CUSTOM_MAILFILTERTAG_BY_CLAUDE (Ryan 2026-10-01, RW-1451-A) — the email filter's warning as a
       small grey tag. The worker cuts the filter's yellow banner out of the body when it files the
       email and keeps what it said on emailMeta.mailFilter ({ by, title, tags }). */
    var mf = em && em.mailFilter;
    if (!mf) return '';
    var tags = Array.isArray(mf.tags) && mf.tags.length ? mf.tags : [mf.title || 'Flagged'];
    var tip = (mf.title || 'Flagged') + ' (' + (mf.by || 'mail filter') + '): ' + tags.join(', ');
    return '<span class="rqd-mailfilter-tag" title="' + _e(tip) + '">&#9888; Mail filter: ' + _e(tags.join(', ')) + '</span>';
  }

  /* ⛔ ONE DEFINITION FOR EVERY FLOATING MENU ROW (Ryan 2026-08-19).
     The attach menu, the 📝 template menu and the ⚡ snippet menu are three separate popovers
     that must read as one control. They were three separate copies of this string, and the
     snippet one had drifted: `font:inherit` instead of `font-size:13px`. ⚠️ `inherit` is not a
     safe default in this theme - the root is 62.5%, so an inherited size lands wherever the
     nearest ancestor happens to be rather than at the 13px the other two menus pin. Ryan:
     *"Make the font size/UI for the pop-down menu for the snippets match that of the
     templates."* It also carried border-radius 8px against their 6px.

     Same lesson as `_e` above, one file and one day apart: a value three functions need belongs
     above all three. The base is the ATTACH menu's (align-items:center); the two-line menus
     append `align-items:flex-start;` and win on last-wins, so all three compute exactly what
     they computed before this was hoisted. */
  var _MENU_ITEM_CSS = 'display:flex;align-items:center;gap:8px;width:100%;padding:8px 10px;background:none;border:0;border-radius:6px;cursor:pointer;font-size:13px;color:var(--rq-text,#000F1C);text-align:left;';
  var _MENU_ITEM_CSS_STACKED = _MENU_ITEM_CSS + 'align-items:flex-start;';
  var _MENU_META_CSS = 'display:block;font-size:11px;color:var(--rq-text-secondary,#475467);';
  /* The popover box itself, shared by the template and snippet menus so they are the same
     card at the same width. The attach menu keeps its own: it is a wider, sectioned list. */
  var _MENU_BOX_CSS = 'position:absolute;z-index:100050;min-width:260px;max-width:360px;max-height:min(60vh,420px);overflow-y:auto;background:var(--rq-bg,#ffffff);border:1px solid var(--rq-border,#D0D5DD);border-radius:10px;box-shadow:var(--rq-shadow,0 10px 30px rgba(0,0,0,.20));padding:6px;';

  var _PORTAL_TOKENS = ['--rq-bg', '--rq-bg-surface', '--rq-bg-section', '--rq-bg-hover',
    '--rq-text', '--rq-text-secondary', '--rq-text-muted', '--rq-border', '--rq-border-light',
    '--rq-blue', '--rq-success', '--rq-error', '--rq-shadow'];
  function _applyPortalTheme(el) {
    try {
      if (!el) return;
      var wrap = document.querySelector('.rep-quote-portal-wrap');
      if (!wrap) return;
      var cs = getComputedStyle(wrap);
      _PORTAL_TOKENS.forEach(function (t) {
        var v = cs.getPropertyValue(t);
        if (v && v.trim()) el.style.setProperty(t, v.trim());
      });
    } catch (_) { /* fall back to the inline light literals */ }
  }
  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — where "the deals I know about" comes from.
     ============================================
     ⛔ `window.RPDash.allQuotes` IS NOT AVAILABLE ON EVERY PAGE THAT OPENS THIS MODAL.
     It is published by dashboard-main.js, which the Deal Dashboard loads and the QUOTE
     WORKSTATION does not (`sections/rep-quote-workstation.liquid` pulls in the composer
     without the dashboard). So on the Workstation's Send-Quote composer it is simply
     undefined, and every feature reading it rendered empty with nothing to say why:
     "Other quotes on this chain" AND "Previously sent to this customer" were both dead
     there. Ryan hit it as *"I need to click the attachment button on version -B and see
     version -A there… I'm not seeing that right now."*

     📌 **The same modal on two pages had two different amounts of knowledge, and only one
     of them was ever exercised.** A shared component cannot assume the host page's globals.

     So the deal list is now a UNION: whatever the dashboard has loaded (free, already in
     memory, carries this session's optimistic patches), plus `GET /quote-chain?ref=` — a
     KV-only listing of the ref's revisions and what has already been emailed on each. On
     the Dashboard the fetch adds nothing it did not already have and simply confirms it;
     on the Workstation it is the only source there is.
     ============================================ */
  var _chainCache = {};        // base ref -> Promise<[dealRecord]>
  var _chainRecords = {};      // base ref -> [dealRecord], once resolved (sync reads)

  function _baseOf(ref) { return String(ref || '').replace(/-[A-Z]$/i, ''); }

  /* Shape the endpoint's rows like the dashboard's quote objects, so every consumer below
     reads ONE shape and cannot tell which source a deal came from. */
  function _loadChainDeals(ref) {
    var base = _baseOf(ref);
    if (!base) return Promise.resolve([]);
    if (_chainCache[base]) return _chainCache[base];
    /* On the Dashboard this fetch would tell us nothing new. `/rep-quotes` paginates BY
       CHAIN — every revision of a chain is kept on the same page as its siblings — so if
       the composer's own ref is in the loaded list, the whole chain is already in memory.
       Skip the round trip and answer from what is on the page. */
    var loaded = (window.RPDash && Array.isArray(window.RPDash.allQuotes)) ? window.RPDash.allQuotes : [];
    if (loaded.some(function (q) { return q && q.reference === ref; })) {
      _chainRecords[base] = [];
      _chainCache[base] = Promise.resolve([]);
      return _chainCache[base];
    }
    var api = (window.RepPortal && window.RepPortal.API) ? window.RepPortal.API : null;
    if (!api) return Promise.resolve([]);
    _chainCache[base] = api.get('/quote-chain?ref=' + encodeURIComponent(ref))
      .then(function (d) {
        var rows = (d && Array.isArray(d.revisions)) ? d.revisions : [];
        var recs = rows.map(function (r) {
          return {
            reference: r.reference,
            customerEmail: r.customerEmail || '',
            customer: r.customerName || '',
            total: (r.total != null) ? r.total : '',
            /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-24) — the linked weight design, in
               the board row's own shape (store / authored / missing / chainShareId), so the
               first-quote template's "To see the weights you designed" sentence renders on the
               Workstation too. ⛔ It never did: this row carried no `linkedFiles`, the template
               read an absent array as "no design", and RW-1440-A went out without its link. */
            linkedFiles: Array.isArray(r.linkedFiles) ? r.linkedFiles : [],
            /* Rebuilt into the noteEntry/emailMeta shape `_sentAttachmentHistory` walks,
               rather than giving that function a second code path to maintain. The call
               entry is synthesised the same way: the server has already applied the
               "did they actually speak" filter, so this stands in as the answer it gave. */
            /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — the customer's own last email, and
               our last reply to them, stood up as entries.

               ⛔ WITHOUT THESE THE WORKSTATION COMPOSER CANNOT SEE INBOUND MAIL AT ALL. The only
               emails this feed ever carried were OUTBOUND sends that happened to have an
               attachment (below), so the first-quote template's "Thanks very much for your email."
               branch was unreachable on this surface — it was asking a feed that structurally
               contains no inbound. Ryan hit it on RW-1296-A: the customer wrote at 7:22am, the
               email opened with nothing at all, because the newest thing the composer could see
               was a call from sixteen days earlier.

               ⚠️ `from`/`to` are filled in so the ENTRY passes the same participant test a real
               one does. The template must not need to know which surface built its feed — that is
               how the two drift. The server has already applied the customer filter (a vendor
               freight request is not a reply), so these stand in as the answer it gave, exactly
               like the call entry beside them. */
            noteEntries: (r.lastCallTs ? [{ ts: r.lastCallTs, types: ['call'], callOutcome: 'connected' }] : [])
              .concat(r.lastCustomerInboundTs ? [{
                ts: r.lastCustomerInboundTs,
                types: ['email'],
                emailMeta: { direction: 'inbound', from: r.customerEmail || '', to: [], cc: [] },
              }] : [])
              .concat(r.lastCustomerOutboundTs ? [{
                ts: r.lastCustomerOutboundTs,
                types: ['email'],
                emailMeta: { direction: 'outbound', from: '', to: [r.customerEmail || ''], cc: [] },
              }] : [])
              .concat((Array.isArray(r.sentAttachments) ? r.sentAttachments : []).map(function (a) {
              return {
                ts: a.ts,
                originRef: a.originRef || '',
                emailMeta: {
                  direction: 'outbound', messageId: a.messageId, subject: a.subject || '',
                  attachments: [{ id: a.attachmentId, name: a.name, size: a.size, contentType: a.contentType }],
                },
              };
            })),
          };
        });
        _chainRecords[base] = recs;
        return recs;
      })
      .catch(function () {
        // A dead lookup must not cache as "this chain has one revision".
        _chainCache[base] = null;
        return [];
      });
    return _chainCache[base];
  }

  /* Every deal this composer can see, right now, without waiting on anything. */
  function _knownDeals(ref) {
    var d = (window.RPDash || {});
    var loaded = Array.isArray(d.allQuotes) ? d.allQuotes : [];
    var extra = _chainRecords[_baseOf(ref)] || [];
    if (!extra.length) return loaded;
    var seen = {};
    loaded.forEach(function (q) { if (q && q.reference) seen[q.reference] = 1; });
    // The dashboard's copy wins on a tie: it is richer and carries session patches.
    return loaded.concat(extra.filter(function (q) { return q && q.reference && !seen[q.reference]; }));
  }

  function _dealQByRef(ref) {
    return _knownDeals(ref).find(function (x) { return x && x.reference === ref; }) || null;
  }
  /* Fetch a worker-served file with the rep's JWT and shape it like a device file-input
     pick. fallbackType (Ryan 2026-08-18) lets non-PDF callers — the "previously sent"
     re-attach path, which replays arbitrary attachments — keep their stored MIME type
     when the blob comes back typeless. */
  function _fetchDealPdfAttachment(url, token, fallbackName, fallbackType, quoteRef) {
    return fetch(url, { headers: token ? { 'Authorization': 'Bearer ' + token } : {} })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        var cd = r.headers.get('Content-Disposition') || '';
        var m = /filename\*?=\s*"?([^";]+)"?/i.exec(cd);
        var name = (m && m[1]) ? m[1].trim() : fallbackName;
        return r.blob().then(function (b) { return { blob: b, name: name, meta: _quoteMetaFromHeaders(r, quoteRef) }; });
      })
      .then(function (res) {
        return new Promise(function (resolve, reject) {
          var fr = new FileReader();
          fr.onload = function () {
            var s = String(fr.result || ''); var i = s.indexOf(',');
            resolve({ name: res.name, contentType: res.blob.type || fallbackType || 'application/pdf',
              base64: i >= 0 ? s.slice(i + 1) : s, sizeBytes: res.blob.size, quoteMeta: res.meta });
          };
          fr.onerror = reject; fr.readAsDataURL(res.blob);
        });
      });
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — published so the QUOTE WORKSTATION's
     Send-Quote pre-attach uses this fetcher instead of its own.

     ⛔ IT HAD ITS OWN, AND THAT IS THE WHOLE BUG. workstation-save.js predates snippets: it
     fetched /pdf, read Content-Disposition for the filename, and resolved
     {name, contentType, base64, sizeBytes} — never touching the X-BSF-* headers. So a quote
     auto-attached by Send Quote carried NO quoteMeta, and the ordering-instructions snippet
     answered "attach the quote first" to a rep looking at the quote it had just attached for
     them. Two fetchers for one job, and only one of them learned about the headers.

     One definition now: any header this path learns to read, every caller inherits. */
  window.RQD = window.RQD || {};
  /* CUSTOM_SENTITEMS_BY_CLAUDE (Ryan 2026-08-28) — one sentence about which rail an email left on,
     shared by every sendOverride that has a real mailbox behind it (freight/dims, PO, invoice).
     Returns '' on the happy path: a Graph send needs no explanation, and a composer that
     announced its own plumbing on every success would just be noise. */
  window.RQD.transportNote = function (resp) {
    if (!resp || resp.transport !== 'resend') return '';
    var why = resp.fallbackReason ? (' — ' + resp.fallbackReason) : '';
    return 'Sent, but it will NOT appear in Outlook Sent Items' + why + '.';
  };
  window.RQD.fetchDealPdfAttachment = function (url, token, fallbackName, fallbackType, quoteRef) {
    return _fetchDealPdfAttachment(url, token, fallbackName, fallbackType, quoteRef);
  };

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19) — what the attached PDF actually says.
     ============================================
     Ryan, on the ordering-instructions snippet: *"the page of which the 'ORDERING
     INSTRUCTIONS' section appears on a quote differs depending on the PDF and how long the
     first few pages are, so this needs to be dynamic"* — and *"if we send them quote versions
     -A in one email and -B in another, the payment link will need to change according to
     what's attached."*

     ⛔ BOTH FACTS TRAVEL WITH THE BYTES. /pdf answers them on the response headers of the very
     render being attached (see handlers/customer-public.js). Looking them up separately would
     mean describing a DIFFERENT document than the one on the email: the page number moves with
     the line-item count, and the checkout link is per revision.

     ⚠️ A missing header is the normal case, not an error - the "previously sent" re-attach
     replays bytes out of Outlook and never touches /pdf, and a floor plan has no ordering page
     at all. Absent meta means the snippet leaves that proposal out and TELLS the rep, rather
     than printing a page number nobody checked.
     ============================================ */
  function _quoteMetaFromHeaders(res, quoteRef) {
    if (!quoteRef) return null;
    var page = parseInt(res.headers.get('X-BSF-Ordering-Page') || '', 10);
    if (!isFinite(page) || page <= 0) return null;   // an old worker, or a CORS expose that did not land
    var total = parseFloat(res.headers.get('X-BSF-Quote-Total') || '');
    /* How many gym-layout plan pages this render appended. 0 on an older worker, which is the
       right answer for a template that only speaks up when it knows there IS one. */
    var layoutPages = parseInt(res.headers.get('X-BSF-Layout-Pages') || '', 10);
    return {
      ref: quoteRef,
      orderingPage: page,
      /* 'none' means the PDF printed NO card link (fees still pending). The snippet must not
         offer a link the customer cannot find on the page it just sent them to. */
      paymentKind: String(res.headers.get('X-BSF-Payment-Kind') || 'none'),
      paymentLink: String(res.headers.get('X-BSF-Payment-Link') || ''),
      total: isFinite(total) && total > 0 ? total : 0,
      layoutPages: isFinite(layoutPages) && layoutPages > 0 ? layoutPages : 0,
      /* CUSTOM_LAYOUTOVERAGE_BY_CLAUDE (Ryan 2026-09-20) — whether those plan pages are the
         OVERAGE render. The worker sends '1' only when the o1 variant was the one actually
         appended; a ticked Overage box whose variant had not rendered yet appends the plain
         layout and sends '0'. An older worker sends nothing, which reads false — the right
         answer for a sentence that should only speak when it knows there is red on the page. */
      layoutOverage: String(res.headers.get('X-BSF-Layout-Overage') || '') === '1',
      /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE (Ryan 2026-09-24) — the drawing's fingerprint (a digest of
         the plan image bytes). Sent back with the email as `quoteLayout` so the ledger can say
         which floor plan the customer already holds; the "2nd+ Quote" template compares this
         render's key against it. Empty on an older worker or a render with no plan pages. */
      layoutKey: String(res.headers.get('X-BSF-Layout-Key') || '').replace(/[^0-9a-f]/g, ''),
      /* CUSTOM_ONLINESIGN_BY_CLAUDE (Ryan 2026-10-26) — the sign-online link THIS render printed in its APPROVAL box (an
         offline deal only). Empty on an older worker or a quote that carries none, and the sentence then stays out. */
      signLink: /^https:\/\/www\.blueskyfitsupply\.com\/pages\/sign\?q=[A-Z0-9]{8}$/.test(String(res.headers.get('X-BSF-Sign-Link') || ''))
        ? String(res.headers.get('X-BSF-Sign-Link')) : '',
    };
  }
  /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE (Ryan 2026-09-24) — what the send tells the ledger about the
     floor plan on the attached quote. ⛔ ONLY WHEN A QUOTE PDF WITH A RENDER STAMP IS ON THE
     EMAIL: a "previously sent" re-attach carries no headers, and posting `pages: 0` for it would
     record "the customer got a quote with no plan" about a document that may well have had one.
     Undefined is the honest answer there, and the worker records nothing.
     The composer's own deal wins when several quotes are attached; otherwise the first. */
  function _quoteLayoutPayload(attachments, ref) {
    var T = window.RQDTpl;
    if (!T || typeof T.attachedQuoteMeta !== 'function') return undefined;
    var metas = T.attachedQuoteMeta(attachments || []);
    if (!metas.length) return undefined;
    var m = null;
    metas.some(function (x) { if (String(x.ref).toUpperCase() === String(ref || '').toUpperCase()) { m = x; return true; } return false; });
    m = m || metas[0];
    return {
      ref: String(m.ref || ''),
      pages: m.layoutPages || 0,
      overage: m.layoutOverage === true,
      key: String(m.layoutKey || ''),
    };
  }
  /* CUSTOM_QUOTESENTCLOCK_BY_CLAUDE (Ryan 2026-10-19) - every quote PDF on the email, so the worker records each quote as
     SENT when this email really goes (the 30-day freight hold runs from that send). The fetch that attached it passed
     `attach=1`, so attaching alone never counts. */
  function _attachedQuoteRefs(attachments) {
    var T = window.RQDTpl;
    if (!T || typeof T.attachedQuoteMeta !== 'function') return [];
    var out = [];
    T.attachedQuoteMeta(attachments || []).forEach(function (m) { var r = String((m && m.ref) || '').toUpperCase(); if (r && out.indexOf(r) < 0) out.push(r); });
    return out;
  }
  /* Returns the deal's client-retrievable files as [{kind,label,icon,fetch(token)->Promise<att>}]. */
  function _dealFileSources(ref) {
    var q = _dealQByRef(ref);
    var out = [];
    if (ref) {
      /* CUSTOM_QUOTEPDFNAME_BY_CLAUDE (Ryan 2026-10-10, RW-1379-C) — the row names the file that will
         attach ("RW-1379-C - Laurie Smith - 2026-10-08.pdf", from GET /quote-pdf-names, the rule /pdf
         names it with) over the total and the customer, the same two lines the chain rows carry.
         "Quote PDF" only until the name lands, or when the lookup could not name it. */
      out.push({ kind: 'quote', label: _pdfNameCache[String(ref).toUpperCase()] || 'Quote PDF', icon: '📄',
        meta: q ? [_chainMoney(q.total), q.customerCompany || q.customer || ''].filter(Boolean).join(' · ') : '',
        fetch: function (token) {
        var email = (q && q.customerEmail) ? q.customerEmail : '';
        var url = _WORKER_BASE + '/pdf?ref=' + encodeURIComponent(ref)
          + (email ? '&email=' + encodeURIComponent(email) : '')
          + '&token=' + encodeURIComponent(token || '') + '&attach=1';
        return _fetchDealPdfAttachment(url, token, ref + '.pdf', null, ref);
      } });
    }
    if (q && q.floorPlanPdfUrl) {
      var fp = String(q.floorPlanPdfUrl);
      var fpUrl = /^https?:/i.test(fp) ? fp : (_WORKER_BASE + (fp.charAt(0) === '/' ? '' : '/') + fp);
      out.push({ kind: 'floorplan', label: 'Floor plan PDF', icon: '🗺️', fetch: function (token) {
        return _fetchDealPdfAttachment(fpUrl, token, (ref || 'floor-plan') + '-floor-plan.pdf');
      } });
    }
    return out;
  }
  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — every quote on the chain, sent or not.
     ============================================
     Ryan: *"if you create quote versions -A and -B right off the bat, I'd like to be able to
     attach them both to the email on the first email send."*

     "Previously sent" cannot serve this — it can only replay bytes that already went out, and
     on a first send nothing has. So the sibling revisions get their own section, and each row
     GENERATES that revision's quote PDF exactly the way the deal's own "Quote PDF" row does:
     /pdf?ref=<that revision>. The current state of a revision the customer has never seen IS
     the right document.

     ⛔ This does NOT replace "Previously sent", and must not be folded into it. The two answer
     different questions: this one says "here is revision B as it stands now", that one says
     "here are the exact bytes you sent them last Tuesday". A revision that has been sent AND
     revised since appears in both, deliberately, and the section headings are what tell them
     apart.

     Siblings = same base ref (RW-1330-A / -B are one chain), from the deals the dashboard has
     loaded — /rep-quotes serves 200 a page, but a chain lands on one page with its own deal,
     so the case Ryan described never misses. Ordered by revision letter: A, B, C.
     ============================================ */
  /* CUSTOM_QUOTEPDFNAME_BY_CLAUDE (Ryan 2026-10-10) — ref -> the file name its quote PDF gets.
     Filled by GET /quote-pdf-names when the attach menu opens; a ref the worker could not name
     stays absent and its row keeps the plain label. */
  /* CUSTOM_CHAINSORT_BY_CLAUDE (Ryan 2026-10-10) — quote PDFs of ONE chain always read A to Z on the email, in
     whatever order they were attached ("-B, -C, -A" shows "-A, -B, -C"). They are sorted inside the slots they
     already hold, so a house document or a photo stays where the rep put it; quotes from different deals keep
     the order they were added. The ref is the render's own (quoteMeta.ref), else the one in the file name. */
  var _ATT_REF_RX = /\b([A-Z]{2,4}-\d{3,6}-[A-Z])\b/;
  function _attQuoteRef(a) {
    if (!a) return '';
    var r = a.quoteMeta && a.quoteMeta.ref ? String(a.quoteMeta.ref).toUpperCase() : '';
    if (r) return r;
    var m = _ATT_REF_RX.exec(String(a.name || '').toUpperCase());
    return m ? m[1] : '';
  }
  function _sortChainQuotes(list) {
    if (!Array.isArray(list) || list.length < 2) return list;
    var slots = [], refs = [];
    list.forEach(function (a, i) { var r = _attQuoteRef(a); if (r) { slots.push(i); refs.push(r); } });
    if (slots.length < 2) return list;
    var base = refs[0].replace(/-[A-Z]$/, '');
    if (!refs.every(function (r) { return r.replace(/-[A-Z]$/, '') === base; })) return list;
    var quotes = slots.map(function (i) { return list[i]; })
      .sort(function (x, y) { return _attQuoteRef(x).localeCompare(_attQuoteRef(y)); });
    var out = list.slice();
    slots.forEach(function (slot, k) { out[slot] = quotes[k]; });
    return out;
  }
  window.RQD = window.RQD || {};
  window.RQD._sortChainQuotes = _sortChainQuotes;

  /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE (Ryan 2026-10-10) — a draft keeps its ATTACHMENTS too. The text half of a draft
     has lived in localStorage (rqd-compose-draft:v2:{ref}) since 2026-05; quote PDFs are megabytes and do not
     fit there, so they go to IndexedDB under the same key. Close the browser, reopen the deal, Send Quote: the
     email you started is back with its files. Best-effort throughout: no IndexedDB = text-only drafts, as before. */
  var _idbP = null;
  function _idbDb() {
    if (_idbP) return _idbP;
    _idbP = new Promise(function (resolve) {
      try {
        var req = window.indexedDB.open('rqd-compose', 1);
        req.onupgradeneeded = function () { try { req.result.createObjectStore('drafts'); } catch (_) {} };
        req.onsuccess = function () { resolve(req.result); };
        req.onerror = function () { resolve(null); };
      } catch (_) { resolve(null); }
    });
    return _idbP;
  }
  function _idbDraftPut(key, atts) {
    var keep = (atts || []).filter(function (a) { return a && a.base64 && !a.locked && !a.displayOnly; })
      .map(function (a) { return { name: a.name, contentType: a.contentType, base64: a.base64, sizeBytes: a.sizeBytes, quoteMeta: a.quoteMeta || null }; });
    return _idbDb().then(function (db) {
      if (!db) return;
      try { db.transaction('drafts', 'readwrite').objectStore('drafts').put({ attachments: keep, savedAt: Date.now() }, key); } catch (_) {}
    });
  }
  function _idbDraftGet(key) {
    return _idbDb().then(function (db) {
      if (!db) return null;
      return new Promise(function (resolve) {
        try {
          var r = db.transaction('drafts', 'readonly').objectStore('drafts').get(key);
          r.onsuccess = function () { resolve(r.result || null); };
          r.onerror = function () { resolve(null); };
        } catch (_) { resolve(null); }
      });
    });
  }
  function _idbDraftDel(key) {
    return _idbDb().then(function (db) {
      if (!db) return;
      try { db.transaction('drafts', 'readwrite').objectStore('drafts').delete(key); } catch (_) {}
    });
  }

  /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE (Ryan 2026-10-10) — A MINIMIZED EMAIL SURVIVES A REFRESH. Minimizing a customer email
     (the Workstation's Send Quote, the Deal Dashboard's Email pill: the callers pass `persistMinimized`) writes
     `rqd-compose-min:v1:{REF}` = the composer's opening context (functions, the PDF and anything private dropped);
     reopening that quote or that deal shows the tab again, and clicking it reopens the composer on it, where the
     saved draft text (localStorage) and files (IndexedDB) restore as they always do. Close (X) means "put it away":
     the marker goes, the draft stays, and Send Quote / Email reopen it. A button carrying `data-compose-draft-ref`
     wears a "Draft" mark while a saved draft exists (CSS ::after, so the button's own text is never touched). */
  var _MIN_PREFIX = 'rqd-compose-min:v1:';
  var _liveMin = null;        // { ref, restore } while a composer on THIS page is minimized
  var _restoredTab = null;    // { ref, el, open } a tab brought back from storage
  function _minKey(ref) { return _MIN_PREFIX + String(ref || '').trim().toUpperCase(); }
  function _minRead(ref) {
    if (!ref) return null;
    try { var v = localStorage.getItem(_minKey(ref)); return v ? JSON.parse(v) : null; } catch (_) { return null; }
  }
  function _minWrite(ref, rec) {
    try { localStorage.setItem(_minKey(ref), JSON.stringify(rec)); return; } catch (_) {}
    /* A long thread preview is the only big field; without it the email still reopens, minus the preview. */
    try { var c = Object.assign({}, rec.ctx); delete c.replyThread; localStorage.setItem(_minKey(ref), JSON.stringify(Object.assign({}, rec, { ctx: c }))); } catch (_) {}
  }
  function _minClear(ref) { try { localStorage.removeItem(_minKey(ref)); } catch (_) {} }
  function _snapshotCtx(ctx) {
    var out = {};
    Object.keys(ctx || {}).forEach(function (k) {
      if (k === 'preAttachments' || k.charAt(0) === '_') return;
      var v = ctx[k];
      if (v === undefined || typeof v === 'function') return;
      if (k === 'threadChoices' && Array.isArray(v)) v = v.map(function (c) { var o = Object.assign({}, c); delete o.emails; return o; });
      try { JSON.stringify(v); out[k] = v; } catch (_) {}
    });
    return out;
  }
  /* CUSTOM_DDMOBILE_BY_CLAUDE (Ryan 2026-10-29): "if there's no text in the window, it should never save that as a draft
     .. even if you write a long email, change your mind, delete all the text, then it still shows you've saved a draft".
     A draft exists only when the BODY holds words (or a picture). Emptying the body removes the draft; a prefilled To
     chip or subject on an empty body is not a draft; and the "Draft" badge asks the same question of what is stored.
     The Workstation's composer is this file too, so it follows. */
  function _bodyHasText(html) {
    if (!html) return false;
    if (/<img\b/i.test(html)) return true;
    var t = String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;|&#xa0;|\u00a0|\u200b|\ufeff/gi, '').replace(/\s+/g, '');
    return t.length > 0;
  }
  function _storedDraftHasText(raw) {
    if (!raw) return false;
    try { var d = JSON.parse(raw); if (d && typeof d === 'object') return _bodyHasText(d.body); } catch (_) { /* a reply draft is plain html */ }
    return _bodyHasText(raw);
  }
  function _hasDraftFor(ref) {
    if (!ref) return false;
    var stored = null;
    try { stored = localStorage.getItem('rqd-compose-draft:v2:' + ref); } catch (_) { stored = null; }
    /* A minimized composer with an empty body is not a draft either: only stored words count. */
    return _storedDraftHasText(stored);
  }
  function _ensureWinBtnCss() {
    if (document.getElementById('rqd-compose-winbtn-css')) return;
    var st = document.createElement('style');
    st.id = 'rqd-compose-winbtn-css';
    /* Hover highlight on the window buttons (they carry inline styles, so the hover needs !important). */
    st.textContent = '#rqd-compose-minimize,#rqd-compose-x,.rqd-compose-mintab__x{transition:background-color .12s,color .12s;}'
      + '#rqd-compose-minimize:hover,#rqd-compose-minimize:focus-visible,.rqd-compose-mintab__x:hover{background:rgba(255,255,255,.12)!important;color:var(--rq-text,#e6edf6)!important;}'
      + '#rqd-compose-x:hover,#rqd-compose-x:focus-visible{background:#c42b1c!important;color:#fff!important;}'
      + '.rqd-compose-mintab{transition:border-color .12s,box-shadow .12s;}'
      + '.rqd-compose-mintab:hover{border-color:var(--rq-blue,#006BCC)!important;}'
      + '[data-has-draft="1"]::after{content:"Draft";display:inline-block;margin-left:7px;padding:1px 6px;border-radius:999px;'
      +   'background:rgba(0,107,204,.28);color:#a9d2ff;font:700 10px/1.5 Montserrat,sans-serif;letter-spacing:.02em;vertical-align:middle;}';
    document.head.appendChild(st);
  }
  function _minTabLeft() {
    /* Just right of the portal's left menu rail (the tab covered its Settings button); the corner on a phone. */
    var rail = document.querySelector('.rep-portal-drawer');
    if (rail) {
      var r = rail.getBoundingClientRect();
      if (r.width > 0 && r.right > 0 && r.right < 140) return Math.round(r.right + 16);
    }
    return 16;
  }
  function _reduceMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; }
  }
  function _flyFromRect(box, t) {
    var b = box.getBoundingClientRect();
    if (!b.width || !t || !t.width) return null;
    var dx = (t.left + t.width / 2) - (b.left + b.width / 2);
    var dy = (t.top + t.height / 2) - (b.top + b.height / 2);
    var sc = Math.max(0.06, Math.min(t.width / b.width, t.height / b.height * 3));
    return 'translate(' + dx + 'px,' + dy + 'px) scale(' + sc + ')';
  }
  function _rectOf(el) { var r = el.getBoundingClientRect(); return { left: r.left, top: r.top, width: r.width, height: r.height }; }
  function _popIn(el) {
    if (el && el.animate && !_reduceMotion()) el.animate([{ transform: 'scale(.85)', opacity: 0 }, { transform: 'scale(1.04)', opacity: 1, offset: 0.7 }, { transform: 'scale(1)', opacity: 1 }],
      { duration: 220, easing: 'ease-out' });
  }
  /* The tab itself: one look for a live minimized email and one brought back from storage. */
  function _buildMinTab(subject, ref, onOpen, onClose) {
    _ensureWinBtnCss();
    var tab = document.createElement('div');
    tab.className = 'rqd-compose-mintab';
    tab.setAttribute('role', 'button');
    tab.tabIndex = 0;
    tab.title = 'Open the email';
    tab.style.cssText = 'position:fixed;left:' + _minTabLeft() + 'px;bottom:16px;z-index:100040;display:flex;align-items:center;gap:10px;max-width:340px;padding:9px 10px 9px 14px;border:1px solid var(--rq-border,#1e2a42);border-radius:10px;background:var(--rq-bg-surface,#0f1726);color:var(--rq-text,#e6edf6);box-shadow:0 10px 28px rgba(0,0,0,.35);font:600 13px Montserrat,sans-serif;cursor:pointer;';
    tab.innerHTML = '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">&#9993; ' + _e(subject || 'New email') + (ref ? ' <span style="font-weight:500;color:var(--rq-text-secondary,#9aa7b8);">&middot; ' + _e(ref) + '</span>' : '') + '</span>'
      + '<button type="button" class="rqd-compose-mintab__x" title="Close (your draft is kept)" aria-label="Close" style="flex:0 0 auto;width:22px;height:22px;border:0;border-radius:5px;background:transparent;color:var(--rq-text-secondary,#9aa7b8);font:600 15px/1 Montserrat,sans-serif;cursor:pointer;">&times;</button>';
    (document.querySelector('.rep-quote-portal-wrap') || document.body).appendChild(tab);
    tab.addEventListener('click', function (e) {
      if (e.target.closest('.rqd-compose-mintab__x')) { e.stopPropagation(); onClose(); return; }
      onOpen();
    });
    tab.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } });
    return tab;
  }
  function _refreshDraftMarkers() {
    var els = document.querySelectorAll('[data-compose-draft-ref]');
    for (var i = 0; i < els.length; i++) {
      var want = _hasDraftFor(els[i].getAttribute('data-compose-draft-ref')) ? '1' : '';
      if ((els[i].getAttribute('data-has-draft') || '') !== want) {
        if (want) { _ensureWinBtnCss(); els[i].setAttribute('data-has-draft', '1'); } else els[i].removeAttribute('data-has-draft');
      }
    }
  }
  function _dropRestoredTab() { if (_restoredTab) { try { _restoredTab.el.remove(); } catch (_) {} _restoredTab = null; } }
  window.RQD.hasComposeDraft = _hasDraftFor;
  window.RQD.refreshComposeDraftMarkers = _refreshDraftMarkers;
  /* Bring back the tab of an email minimized on this deal before a refresh. `hooks` are the page's own callbacks
     (the Workstation's "mark the quote sent"), which cannot be stored. */
  window.RQD.showMinimizedDraft = function (ref, hooks) {
    ref = String(ref || '').trim();
    if (_restoredTab && _restoredTab.ref !== ref) _dropRestoredTab();
    if (!ref || _liveMin || _restoredTab || document.getElementById('rqd-email-modal')) return false;
    var rec = _minRead(ref);
    if (!rec || !rec.ctx) return false;
    var el;
    function open() {
      var rect = _rectOf(el);
      _dropRestoredTab();
      _minClear(ref);
      window.RQD.openComposeModal(Object.assign({}, rec.ctx, hooks || {}, { persistMinimized: true, _growFrom: rect }));
    }
    el = _buildMinTab(rec.subject, ref, open, function () { _dropRestoredTab(); _minClear(ref); _refreshDraftMarkers(); });
    _restoredTab = { ref: ref, el: el, open: open };
    _popIn(el);
    return true;
  };
  /* Send Quote / Email on a deal whose email is already minimized opens THAT email, never a second one. */
  window.RQD.restoreMinimizedCompose = function (ref) {
    ref = String(ref || '').trim();
    if (!ref) return false;
    if (_liveMin && _liveMin.ref === ref) { _liveMin.restore(); return true; }
    if (_restoredTab && _restoredTab.ref === ref) { _restoredTab.open(); return true; }
    return false;
  };
  /* The Deal Dashboard: the Email pill marks itself, and an open deal with a minimized email shows its tab. */
  (function _watchDraftHosts() {
    var t = null;
    function run() {
      t = null;
      var btns = document.querySelectorAll('.rqd-notes-log__new-email-btn[data-ref]');
      for (var i = 0; i < btns.length; i++) {
        if (!btns[i].getAttribute('data-compose-draft-ref')) btns[i].setAttribute('data-compose-draft-ref', btns[i].getAttribute('data-ref'));
      }
      _refreshDraftMarkers();
      if (_liveMin || _restoredTab) return;
      for (var j = 0; j < btns.length; j++) {
        var r = btns[j].getAttribute('data-ref');
        if (r && _minRead(r)) { window.RQD.showMinimizedDraft(r); break; }
      }
    }
    function kick() { if (!t) t = setTimeout(run, 300); }
    function start() {
      var host = document.querySelector('.rep-quote-portal-wrap') || document.body;
      try { new MutationObserver(kick).observe(host, { childList: true, subtree: true }); } catch (_) {}
      kick();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  })();
  var _pdfNameCache = {};
  function _loadPdfNames(refs) {
    var want = (refs || []).map(function (r) { return String(r || '').toUpperCase(); })
      .filter(function (r, i, a) { return r && a.indexOf(r) === i && !_pdfNameCache[r]; });
    if (!want.length) return Promise.resolve(false);
    return _getRepToken().then(function (tok) {
      return fetch(_WORKER_BASE + '/quote-pdf-names?refs=' + encodeURIComponent(want.join(',')), {
        headers: tok ? { 'Authorization': 'Bearer ' + tok } : {},
      });
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var names = (j && j.names) || {};
        var got = false;
        Object.keys(names).forEach(function (k) { if (names[k]) { _pdfNameCache[k] = names[k]; got = true; } });
        return got;
      }).catch(function () { return false; });
  }
  function _chainMoney(n) {
    var v = parseFloat(n);
    if (!isFinite(v) || v <= 0) return '';
    try {
      return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    } catch (_) { return '$' + Math.round(v).toLocaleString(); }
  }
  function _chainQuoteSources(ref) {
    var sibs = [];
    try {
      var cur = String(ref || '').trim();
      var base = _baseRef(cur);
      if (!base) return [];
      var quotes = _knownDeals(cur);
      quotes.forEach(function (dq) {
        if (!dq || !dq.reference || dq.reference === cur) return;
        if (_baseRef(dq.reference) !== base) return;
        sibs.push(dq);
      });
      sibs.sort(function (a, b) { return String(a.reference).localeCompare(String(b.reference)); });
    } catch (_) { return []; }
    return sibs.map(function (dq) {
      var r = dq.reference;
      /* The revision letter alone doesn't say which one is which — the total does, and it is
         the thing a rep is picking between when both go out at once. */
      var meta = [_chainMoney(dq.total), dq.customerCompany || dq.customer || ''].filter(Boolean).join(' · ');
      return {
        kind: 'chain', ref: r, label: _pdfNameCache[String(r).toUpperCase()] || (r + ' quote PDF'), name: r + ' quote PDF', meta: meta,
        fetch: function (token) {
          var email = dq.customerEmail || '';
          var url = _WORKER_BASE + '/pdf?ref=' + encodeURIComponent(r)
            + (email ? '&email=' + encodeURIComponent(email) : '')
            + '&token=' + encodeURIComponent(token || '') + '&attach=1';
          return _fetchDealPdfAttachment(url, token, r + '.pdf', null, r);
        },
      };
    });
  }
  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — "Previously sent" re-attach.
     ============================================
     A follow-up email almost always needs the SAME file the first email carried — the
     quote PDF the rep sent a week ago, the spec sheet, the floor plan. Re-generating the
     quote PDF ("From this deal") is not the same thing: the quote may have been revised
     since, so the customer would get a document that doesn't match the one they're being
     asked about. This lists the actual files already emailed to this customer, newest
     first, and re-attaches the exact bytes from the rep's Outlook mailbox in one click.

     Source of truth = the deal feed. outlook-sync writes every synced message's attachment
     metadata (Graph id + name + contentType + size) onto the noteEntry's emailMeta, and
     /outlook-attachment streams those bytes back under the same ownership gate the read
     modal's download chips use. So this needs NO new worker endpoint — it re-reads data
     the dashboard already holds and re-fetches through a path that is already authorized.

     Scope of "this customer": the current deal's whole email history, PLUS any outbound
     email on ANY of the rep's other deals that went to one of the composer's current To
     addresses (or that deal's customerEmail). That way a second deal for the same buyer
     still surfaces the quote sent on the first one.
     ============================================ */
  /* CUSTOM_BIGATTACH_BY_CLAUDE (Ryan 2026-10-10) — 10 MB a file, 15 MB an email (20 MB of base64). The server
     sends big emails draft-first and big files in chunks (rfq-worker lib/graph-attach.js). */
  var _MAX_ATTACH_BYTES_M = 10 * 1024 * 1024;   // mirrors the composers' per-file cap
  var _MAX_ATTACH_TOTAL_B64 = 20 * 1024 * 1024;

  /* ============================================
     CUSTOM_HOUSEDOCS_BY_CLAUDE (Ryan 2026-09-13) — THE HOUSE DOCUMENTS SHELF, IN THE COMPOSER
     ============================================
     Paperwork the company sends over and over — the W-9, a tax exemption certificate, a COI —
     lives on a shelf an admin fills (Settings → Admin: House Documents). Abby has been able to
     attach one by name since 2026-09-11; a rep could not, so the W-9 was still being dug out of
     whichever laptop happened to have it.

     ⛔ THE ROLE IS THE POINTER, NOT THE YEAR. A W-9 is replaced every January, so anything
     pointing at `2026-w-9` breaks on the day Ryan does the swap — which is the one requirement he
     stated. `w-9` is the canonical slug going forward and re-uploading under it REPLACES the
     document, exactly as the shelf already works. The ladder below is ordered, first match wins,
     canonical first, so the moment he uploads under `w-9` the legacy row stops being consulted.
     ⛔ A LADDER, NOT A PATTERN: a regex on "contains w9" would one day pick up
     `w-9-instructions-for-vendors`. Drop `2026-w-9` from the list once the shelf is migrated. */
  /* CUSTOM_HOUSEDOCS_BY_CLAUDE (Ryan 2026-09-16, AIOPS 0a47981f866f) — ACH / WIRING INSTRUCTIONS,
     the second role on the shelf, and the whole reason the roles were a MAP rather than one slug.
     Everything above applies unchanged: the role is the pointer, an admin types the slug when
     uploading, and re-uploading under `ach-wiring` REPLACES the document the day the bank details
     change. The aliases are the spellings an admin might reasonably type, canonical first — a
     short ladder, never a pattern, so a future `ach-instructions-for-vendors` cannot capture it.

     ⚠️ ONE ROLE, ONE DOCUMENT, AND THE MENU IS BUILT FROM THE ROLES: adding this line is what puts
     the PDF in the rep's "Frequently required" attach menu. Nothing else needed teaching. */
  var _HOUSE_DOC_ROLES = {
    w9: ['w-9', 'w9', '2026-w-9'],
    ach: ['ach-wiring', 'ach', 'ach-wiring-instructions', 'wiring-instructions', 'ach-instructions'],
  };

  var _houseDocsP = null;
  var _houseShelf = { state: 'unknown', items: [], error: '' };

  /**
   * The catalogue, memoised per page load. Resolves to {state, items, error}.
   * ⛔ THREE STATES THAT MUST NOT LOOK ALIKE. An empty shelf is a real thing with no action
   * available; a FAILED read rendered as empty reads as "the W-9 vanished", which is exactly what
   * a non-admin rep used to get before the gate was widened on 2026-09-13.
   * ⚠️ A FAILURE IS NOT MEMOISED, so re-opening the attach menu retries.
   */
  function _houseDocShelf() {
    if (_houseDocsP) return _houseDocsP;
    var api = window.RepPortal && window.RepPortal.API;
    if (!api) return Promise.resolve({ state: 'error', items: [], error: 'The portal API is not loaded.' });
    _houseShelf = { state: 'loading', items: [], error: '' };
    _houseDocsP = api.get('/house-docs').then(function (d) {
      var items = (d && Array.isArray(d.items)) ? d.items : [];
      _houseShelf = { state: 'ok', items: items, error: '' };
      return _houseShelf;
    }).catch(function (e) {
      _houseDocsP = null;
      _houseShelf = { state: 'error', items: [], error: (e && e.message) || 'Could not load the house documents.' };
      return _houseShelf;
    });
    return _houseDocsP;
  }

  /** A sync peek. NEVER fetches, so a snippet's `available()` may call it. */
  function _houseShelfNow() { return _houseShelf; }

  /**
   * The documents a REP may attach from the composer — one per role, current version only.
   *
   * ⛔ NOT THE WHOLE SHELF (Ryan 2026-09-13). The shelf also holds the multistate resale
   * certificate and the Colorado and Massachusetts ones, and those are there for ABBY to pull in
   * when a state asks: *"they don't need to be in these emails as an addable attachment."* A menu
   * that lists every document invites a rep to attach a Colorado affidavit to a Massachusetts
   * customer, which is a worse failure than the convenience is worth.
   *
   * ⚠️ THE FILTER IS "DOES A ROLE POINT AT IT", not a list of slugs to hide. A hide-list has to be
   * updated every time an admin puts something new on the shelf, and the one time it is forgotten
   * the document appears in front of every rep. This way the default is OFF and a document reaches
   * the menu only when the composer has actually been taught about it — which is the same moment
   * it gets a snippet.
   *
   * ⚠️ And it resolves through the ladder, so a shelf carrying both `w-9` and the legacy
   * `2026-w-9` shows the canonical one ONCE rather than two rows a rep has to choose between.
   */
  function _houseDocsForComposer() {
    var out = [];
    var seen = {};
    Object.keys(_HOUSE_DOC_ROLES).forEach(function (role) {
      var doc = _houseDocForRole(role);
      if (doc && !seen[doc.slug]) { seen[doc.slug] = 1; out.push(doc); }
    });
    return out;
  }

  /** The document currently filling a role, or null. */
  function _houseDocForRole(role) {
    var ladder = _HOUSE_DOC_ROLES[role] || [];
    var items = (_houseShelf && _houseShelf.items) || [];
    for (var i = 0; i < ladder.length; i++) {
      for (var j = 0; j < items.length; j++) {
        if (items[j] && items[j].slug === ladder[i]) return items[j];
      }
    }
    return null;
  }

  /**
   * ⚠️ "ALREADY ATTACHED" IS ANSWERED BY THE PILL, NOT BY WHERE IT CAME FROM. Matched on the
   * docSlug we stamp AND on the file name, so a W-9 the rep dragged off their own desktop or
   * re-attached from "Previously sent" also counts. The rep is looking at the attachment; how it
   * got there is not the question they are asking.
   */
  function _houseDocAttached(attachments, doc) {
    if (!doc) return false;
    return (Array.isArray(attachments) ? attachments : []).some(function (a) {
      if (!a) return false;
      if (a.docSlug && a.docSlug === doc.slug) return true;
      return !!(a.name && doc.name && String(a.name).toLowerCase() === String(doc.name).toLowerCase());
    });
  }

  /* One fetcher, the deal-PDF one, because it already does fetch → blob → base64 and reads the
     name off Content-Disposition. A second decoder is how one path stops learning what the other
     learns — the lesson the Workstation's own PDF fetcher taught. */
  function _fetchHouseDocAttachment(doc, tok) {
    var url = _WORKER_BASE + '/house-doc?slug=' + encodeURIComponent(doc.slug);
    return _fetchDealPdfAttachment(url, tok, doc.name, doc.contentType, null).then(function (att) {
      /* ⚠️ CANNOT FIRE TODAY — the shelf's own upload cap is pinned to this number. If it ever
         does, the two caps have diverged, and that is the thing worth being told about rather
         than discovering as a Graph rejection after the rep pressed Send. */
      if (att.sizeBytes > _MAX_ATTACH_BYTES_M) {
        throw new Error(_attBytesLabel(att.sizeBytes) + ' — over the 10 MB limit');
      }
      att.docSlug = doc.slug;
      att.docUpdatedAt = doc.updatedAt || '';
      return att;
    });
  }
  var _SENT_HISTORY_LIMIT = 25;

  /* Outlook returns cid-referenced inline images and email-signature images as ordinary
     file attachments. outlook-sync already drops them at capture time; this repeats the
     heuristic for entries captured before that filter existed. Same rules as the read
     modal's chip filter. */
  function _isInlineAttachmentName(n) {
    if (!n) return true;
    if (/^image\d+\.(png|jpe?g|gif|bmp)$/i.test(n)) return true;
    if (/^~WRD/i.test(n)) return true;
    if (/^oledata\.mso$/i.test(n)) return true;
    if (/signature/i.test(n)) return true;
    return false;
  }
  function _attBytesLabel(n) {
    if (!n) return '';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB';
    return (n / (1024 * 1024)).toFixed(1) + ' MB';
  }
  function _attShortDate(ts) {
    try {
      var d = new Date(ts);
      if (isNaN(d.getTime())) return '';
      var o = { month: 'short', day: 'numeric' };
      if (d.getFullYear() !== new Date().getFullYear()) o.year = 'numeric';
      return d.toLocaleDateString(undefined, o);
    } catch (_) { return ''; }
  }
  function _emailWentTo(em, addrs) {
    var lists = [em.to, em.cc];
    for (var i = 0; i < lists.length; i++) {
      var l = lists[i];
      if (!Array.isArray(l)) continue;
      for (var j = 0; j < l.length; j++) {
        if (l[j] && addrs[String(l[j]).toLowerCase().trim()]) return true;
      }
    }
    return false;
  }
  /* Pull one previously-sent attachment's bytes back out of the owner's mailbox and shape
     it like a device file-input pick ({name,contentType,base64,sizeBytes}). */
  function _fetchSentAttachment(src, token) {
    var url = _WORKER_BASE + '/outlook-attachment'
      + '?ref=' + encodeURIComponent(src.ref)
      + '&messageId=' + encodeURIComponent(src.messageId)
      + '&attachmentId=' + encodeURIComponent(src.attachmentId);
    return _fetchDealPdfAttachment(url, token, src.name, src.contentType)
      .then(function (att) {
        att.name = att.name || src.name;
        if (att.sizeBytes > _MAX_ATTACH_BYTES_M) {
          throw new Error(_attBytesLabel(att.sizeBytes) + ' — over the 10 MB limit');
        }
        return att;
      });
  }
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19) — every attachment ONE prior send carried.
     ⛔ Deliberately not `_sentAttachmentHistory().filter(byMessageId)`: that list dedupes
     name+size across the whole thread and truncates at _SENT_HISTORY_LIMIT, so filtering it
     answers "some of what that email carried" and calls it all of it. A template that says
     "I have re-attached the quote" must not be able to attach three of four files.
     Ryan: "automatically reattach any and all attachments sent in email 1 in case they
     didn't get them." */
  function _attachmentsOfMessage(ref, messageId) {
    var out = [];
    if (!messageId) return out;
    try {
      _knownDeals(ref).forEach(function (dq) {
        if (!dq || !Array.isArray(dq.noteEntries)) return;
        dq.noteEntries.forEach(function (n) {
          var em = n && n.emailMeta;
          if (!em || em.messageId !== messageId) return;
          if (!Array.isArray(em.attachments)) return;
          var srcRef = n.originRef || dq.reference;
          em.attachments.forEach(function (a) {
            /* Inline images are the sender's own signature logo and pasted screenshots.
               Re-attaching those would hand the customer our letterhead as a file. */
            if (!a || !a.id || !a.name || _isInlineAttachmentName(a.name)) return;
            if (out.some(function (o) { return o.attachmentId === a.id; })) return;
            out.push({
              name: a.name, sizeBytes: a.size || 0, contentType: a.contentType || '',
              ts: n.ts, ref: srcRef, messageId: em.messageId, attachmentId: a.id,
            });
          });
        });
      });
    } catch (_) { return out; }
    return out;
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — one definition of "this customer's
     deals", shared by the re-attach menu and the "@" picker's ranking.

     Both features answer the same question — which deals belong to the person being
     emailed — and two copies of that answer would be free to drift. A deal counts when it
     IS the composer's deal, when it shares the base ref (the revision chain: RW-1162-A and
     RW-1162-B are one conversation even if a revision lost its customerEmail), or when its
     customerEmail matches an address in the composer's To/Cc.

     Bounded by what the dashboard loaded: /rep-quotes serves 200 deals a page, so reach is
     the current page. The composer's own deal is always in it. */
  function _customerAddrSet(opts) {
    var addrs = {};
    (Array.isArray(opts && opts.toEmails) ? opts.toEmails : []).forEach(function (a) {
      if (a) addrs[String(a).toLowerCase().trim()] = 1;
    });
    var ref = String((opts && opts.ref) || '').trim();
    var q = ref ? _dealQByRef(ref) : null;
    if (q && q.customerEmail) addrs[String(q.customerEmail).toLowerCase().trim()] = 1;
    return addrs;
  }
  function _baseRef(ref) { return String(ref || '').replace(/-[A-Z]$/, ''); }
  function _isCustomerDeal(dq, ref, base, addrs) {
    if (!dq || !dq.reference) return false;
    if (ref && dq.reference === ref) return true;
    if (base && _baseRef(dq.reference) === base) return true;
    return !!(dq.customerEmail && addrs[String(dq.customerEmail).toLowerCase().trim()]);
  }
  /* Refs of this customer's deals, the composer's own deal first so it survives the cap. */
  function _customerDealRefs(opts, limit) {
    var out = [];
    try {
      var ref = String((opts && opts.ref) || '').trim();
      var base = _baseRef(ref);
      var addrs = _customerAddrSet(opts);
      var quotes = _knownDeals(ref);
      var seen = {};
      if (ref) { out.push(ref); seen[ref] = 1; }
      quotes.forEach(function (dq) {
        if (!dq || !dq.reference || seen[dq.reference]) return;
        if (!_isCustomerDeal(dq, ref, base, addrs)) return;
        seen[dq.reference] = 1;
        out.push(dq.reference);
      });
    } catch (_) { return out; }
    return out.slice(0, limit || 12);
  }
  /* Returns [{name,sizeBytes,contentType,ts,ref,messageId,attachmentId,subject}] — every
     distinct file already emailed to this customer, newest first. Deduped by name+size so
     a quote re-sent three times shows once, dated by the most recent send. */
  function _sentAttachmentHistory(opts) {
    var found = [];
    try {
      var ref = String((opts && opts.ref) || '').trim();
      var base = _baseRef(ref);
      var addrs = _customerAddrSet(opts);
      var quotes = _knownDeals(ref);
      quotes.forEach(function (dq) {
        if (!dq || !Array.isArray(dq.noteEntries)) return;
        var sameCustomer = _isCustomerDeal(dq, ref, base, addrs);
        dq.noteEntries.forEach(function (n) {
          var em = n && n.emailMeta;
          if (!em || em.direction !== 'outbound') return;
          if (!Array.isArray(em.attachments) || !em.attachments.length) return;
          if (!em.messageId) return;
          if (!sameCustomer && !_emailWentTo(em, addrs)) return;
          /* Entries cascaded from another revision live on a different record — the
             ownership gate resolves against the record the entry LIVES on, so use
             originRef when present (same rule as the read modal's download chips). */
          var srcRef = n.originRef || dq.reference;
          if (!srcRef) return;
          em.attachments.forEach(function (a) {
            if (!a || !a.id || !a.name || _isInlineAttachmentName(a.name)) return;
            found.push({
              name: a.name,
              sizeBytes: a.size || 0,
              contentType: a.contentType || '',
              ts: n.ts,
              ref: srcRef,
              messageId: em.messageId,
              attachmentId: a.id,
              subject: em.subject || '',
            });
          });
        });
      });
    } catch (_) { return []; }
    // Sort BEFORE deduping so the surviving copy of a repeatedly-sent file is the newest one.
    found.sort(function (a, b) { return new Date(b.ts) - new Date(a.ts); });
    var seen = {};
    var out = [];
    found.forEach(function (f) {
      var key = String(f.name).toLowerCase() + '|' + f.sizeBytes;
      if (seen[key]) return;
      seen[key] = 1;
      out.push(f);
    });
    return out.slice(0, _SENT_HISTORY_LIMIT);
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* Floating menu anchored under the attach button: "From files…", the deal's live files,
     and (Ryan 2026-08-18) the files already sent to this customer.
     opts: { anchorBtn, ref, toEmails, onDevice(), onAdd(att) -> false when refused, onError(msg) }.
     The menu STAYS OPEN after a successful pick (the row flips to "✓ attached") so a
     follow-up can re-attach several files without reopening it once per file. */
  function _openDealAttachMenu(opts) {
    var existing = document.getElementById('rqd-attach-menu');
    if (existing) { if (existing._closeMenu) existing._closeMenu(); else existing.remove(); return; }   // second click closes it
    /* Reassigned by _buildBody() so the delegated click handler below always indexes the
       rows currently on screen. The chain lookup can land AFTER the menu opens. */
    var sources = [], chainQuotes = [], sentFiles = [], houseDocs = [];
    var itemCss = _MENU_ITEM_CSS;
    var headCss = 'padding:6px 10px 2px;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--rq-text-secondary,#475467);';
    var metaCss = _MENU_META_CSS;
    /* Two-line row: glyph + name over a muted meta line. Shared by the chain-revision rows
       and the previously-sent rows so they stay visually one family. */
    /* CUSTOM_HOUSEDOCS_BY_CLAUDE (Ryan 2026-09-13) — `off` renders the row GREYED AND INERT.
       Labelling a row "already attached" while leaving it clickable is a control that looks
       disabled and is not: the click handler refused the second attach, but the rep had already
       been invited to try it. A house document has no honest reason to be attached twice. */
    function _row(act, i, glyph, label, meta, title, off) {
      return '<button type="button" class="rqd-attach-menu-item" data-act="' + act + '" data-idx="' + i + '"'
        + (off ? ' disabled aria-disabled="true"' : '')
        + ' style="' + itemCss + 'align-items:flex-start;' + (off ? 'opacity:.45;cursor:not-allowed;' : '') + '"'
        + (title ? ' title="' + _e(title) + '"' : '') + '>'
        + '<span style="line-height:1.5;">' + glyph + '</span>'
        + '<span style="flex:1;min-width:0;">'
        +   '<span class="rqd-attach-menu-label" style="display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + _e(label) + '</span>'
        +   (meta ? '<span style="' + metaCss + '">' + _e(meta) + '</span>' : '')
        + '</span>'
        + '</button>';
    }
    /* `pending` = the chain lookup is still in flight and nothing local answered it yet.
       Saying so beats an empty menu that looks like a definitive "there are no others". */
    function _bodyHtml(pending) {
    var html = '<button type="button" class="rqd-attach-menu-item" data-act="device" style="' + itemCss + '">📁 <span class="rqd-attach-menu-label">From files…</span></button>';
    /* CUSTOM_HOUSEDOCS_BY_CLAUDE (Ryan 2026-09-13) — directly under "From files…", above the deal.
       Ryan's own placement, and it is right: unlike every other section this one is not derived
       from the deal — it is always there, always the same, and it is what the rep came for when
       they came for it. Deal-proximity ordering resumes below it.
       ⛔ THREE STATES, THREE LOOKS. Loading says so; a genuinely empty shelf renders NO SECTION at
       all (a heading over nothing reads as broken); a FAILED read renders a disabled row that says
       it failed, because rendering that as empty is how "the W-9 vanished" gets reported. */
    var shelf = _houseShelfNow();
    if (shelf.state === 'loading') {
      html += '<div style="' + headCss + '">Frequently required</div>'
        + '<div style="padding:8px 10px;font-size:12px;color:var(--rq-text-secondary,#475467);">Loading house documents…</div>';
    } else if (shelf.state === 'error') {
      html += '<div style="' + headCss + '">Frequently required</div>'
        + '<button type="button" class="rqd-attach-menu-item" disabled aria-disabled="true" title="' + _e(shelf.error) + '"'
        + ' style="' + itemCss + 'opacity:.55;cursor:not-allowed;">⚠️ <span class="rqd-attach-menu-label">Could not load (reopen to retry)</span></button>';
    } else if (houseDocs.length) {
      html += '<div style="' + headCss + '">Frequently required</div>';
      houseDocs.forEach(function (d, i) {
        var already = _houseDocAttached(opts.attachments && opts.attachments(), d);
        /* ⚠️ THE NOTE IS THE HALF THAT MATTERS. It is where "covers IL among others, NOT every
           state" lives, and this menu is the last moment before that document reaches a customer.
           The row is one line and ellipsized, so the FULL note goes in the tooltip. */
        var meta = [_attBytesLabel(d.bytes), d.note].filter(Boolean).join(' · ');
        html += _row('housedoc', i, '📋', d.name + (already ? ' — ✓ already attached' : ''), meta,
          already ? 'Already attached to this email.' : [d.note, 'shelf name: ' + d.slug].filter(Boolean).join('\n'),
          already);
      });
    }
    if (sources.length) {
      html += '<div style="' + headCss + '">From this deal</div>';
      sources.forEach(function (s, i) {
        html += _row('deal', i, s.icon, s.label, s.meta || '', s.kind === 'quote' ? 'Generate this quote as it stands now' : '');
      });
    }
    if (chainQuotes.length) {
      /* CUSTOM_CHAINSORT_BY_CLAUDE (Ryan 2026-10-10) — "More from this chain" + Add all: every quote of the chain
         (this deal's own included) that is not on the email yet, each once. */
      html += '<div style="' + headCss + 'display:flex;align-items:center;justify-content:space-between;gap:8px;">'
        + '<span>More from this chain</span>'
        + '<button type="button" class="rqd-attach-addall" style="flex:0 0 auto;padding:2px 9px;border:1px solid var(--rq-blue,#006BCC);border-radius:999px;background:var(--rq-blue,#006BCC);color:#fff;font:600 11px Montserrat,sans-serif;letter-spacing:0;text-transform:none;cursor:pointer;">Add all</button>'
        + '</div>';
      chainQuotes.forEach(function (c, i) {
        html += _row('chain', i, '📄', c.label, c.meta, 'Generate ' + c.label + ' as it stands now');
      });
    }
    if (sentFiles.length) {
      html += '<div style="' + headCss + '">Previously sent to this customer</div>';
      sentFiles.forEach(function (h, i) {
        var meta = [_attShortDate(h.ts), _attBytesLabel(h.sizeBytes), h.ref].filter(Boolean).join(' · ');
        html += _row('hist', i, '📎', h.name, meta, h.subject || h.name);
      });
    }
    if (pending && !chainQuotes.length && !sentFiles.length) {
      html += '<div style="padding:8px 10px;font-size:12px;color:var(--rq-text-secondary,#475467);">Looking for other revisions…</div>';
    }
    return html;
    }

    /* Recompute the three source lists from whatever is known right now, and paint. */
    function _buildBody(menuEl, pending) {
      sources = _dealFileSources(opts.ref);
      chainQuotes = _chainQuoteSources(opts.ref);
      sentFiles = _sentAttachmentHistory(opts);
      houseDocs = _houseDocsForComposer();   // CUSTOM_HOUSEDOCS_BY_CLAUDE — one row per ROLE, not the whole shelf
      menuEl.innerHTML = _bodyHtml(pending);
      Array.prototype.forEach.call(menuEl.querySelectorAll('.rqd-attach-menu-item'), function (el) {
        /* --rq-bg-hover, not --rq-hover: the latter is not a token this theme defines, so the
           hover was pinned to the light fallback no matter which mode the portal was in. */
        el.addEventListener('mouseenter', function () { if (!el.disabled) el.style.background = 'var(--rq-bg-hover,#F5F8FC)'; });
        el.addEventListener('mouseleave', function () { if (!el.disabled) el.style.background = 'none'; });
      });
    }

    var menu = document.createElement('div');
    menu.id = 'rqd-attach-menu';
    /* Opaque --rq-bg, not the translucent --rq-input-bg: the menu floats over the page, not
       over the modal's own panel, so a see-through card would read the wrong colour. */
    menu.style.cssText = 'position:absolute;z-index:100050;min-width:240px;max-width:340px;max-height:min(60vh,420px);overflow-y:auto;background:var(--rq-bg,#ffffff);border:1px solid var(--rq-border,#D0D5DD);border-radius:10px;box-shadow:var(--rq-shadow,0 10px 30px rgba(0,0,0,.20));padding:6px;';
    _applyPortalTheme(menu);   // hoisted to <body> → carry the portal's (possibly dark) tokens with it
    _buildBody(menu, true);
    document.body.appendChild(menu);
    /* ⛔ The Workstation has no dashboard deal list, so on that page this fetch is the ONLY
       thing that can answer "what else is on this chain". Repaint when it lands; if the rep
       has already closed the menu or picked something, leave it alone. */
    _loadChainDeals(opts.ref).then(function () {
      if (!menu.isConnected) return;
      if (menu.querySelector('.rqd-attach-menu-item[disabled]')) return;   // a pick is in flight
      _buildBody(menu, false);
      /* CUSTOM_QUOTEPDFNAME_BY_CLAUDE — the real file names, once the chain is known. */
      var _nameRefs = [opts.ref].concat(chainQuotes.map(function (c) { return c.ref; }));
      return _loadPdfNames(_nameRefs).then(function (got) {
        if (!got || !menu.isConnected) return;
        if (menu.querySelector('.rqd-attach-menu-item[disabled]:not([aria-disabled])')) return;
        _buildBody(menu, false);
      });
    }).catch(function () {
      if (menu.isConnected) _buildBody(menu, false);
    });
    /* CUSTOM_HOUSEDOCS_BY_CLAUDE (Ryan 2026-09-13) — the shelf, read when the MENU OPENS rather
       than at page load: a rep who never attaches anything should not spend the call. Repaints
       the same way the chain lookup does, and by the same rule leaves a pick in flight alone. */
    _houseDocShelf().then(function () {
      if (!menu.isConnected) return;
      if (menu.querySelector('.rqd-attach-menu-item[disabled]:not([aria-disabled])')) return;
      _buildBody(menu, false);
    });
    _menuPlaceAt(menu, opts.anchorBtn.getBoundingClientRect());   // CUSTOM_DDMOBILE_BY_CLAUDE - fits the screen
    function _close() { menu.remove(); document.removeEventListener('click', _onDoc, true); document.removeEventListener('keydown', _onKey, true); }
    menu._closeMenu = _close;
    function _onDoc(ev) { if (!menu.contains(ev.target) && !opts.anchorBtn.contains(ev.target) && ev.target !== opts.anchorBtn) _close(); }
    /* CUSTOM_CHAINSORT_BY_CLAUDE — Add all: this deal's quote and every other revision, skipping any already on
       the email (by ref), one after another (each is a fresh PDF render); stops at the attachment limit. */
    function _addWholeChain(btn) {
      if (btn.disabled) return;
      var onEmail = {};
      ((opts.attachments && opts.attachments()) || []).forEach(function (a) { var r = _attQuoteRef(a); if (r) onEmail[r] = 1; });
      var todo = [];
      sources.forEach(function (s) { if (s.kind === 'quote' && opts.ref && !onEmail[String(opts.ref).toUpperCase()]) todo.push({ src: s, ref: String(opts.ref).toUpperCase() }); });
      chainQuotes.forEach(function (c) { var r = String(c.ref || '').toUpperCase(); if (r && !onEmail[r]) todo.push({ src: c, ref: r }); });
      var seen = {};
      todo = todo.filter(function (t) { if (seen[t.ref]) return false; seen[t.ref] = 1; return true; });
      if (!todo.length) { btn.textContent = '✓ All on the email'; btn.disabled = true; return; }
      btn.disabled = true;
      var done = 0;
      _getRepToken().then(function (tok) {
        var chain = Promise.resolve(true);
        todo.forEach(function (t) {
          chain = chain.then(function (ok) {
            if (!ok) return false;
            btn.textContent = 'Adding ' + (done + 1) + ' of ' + todo.length + '...';
            return t.src.fetch(tok).then(function (att) {
              if (opts.onAdd(att) === false) return false;
              done++;
              return true;
            });
          });
        });
        return chain;
      }).then(function (ok) {
        btn.textContent = ok ? '✓ All added' : ('Added ' + done + ' (limit reached)');
      }).catch(function (e) {
        btn.disabled = false;
        btn.textContent = 'Add all';
        if (opts.onError) opts.onError('Could not add every quote (' + ((e && e.message) || e) + ')');
      });
    }
    function _onKey(ev) { if (ev.key === 'Escape') _close(); }
    setTimeout(function () { document.addEventListener('click', _onDoc, true); document.addEventListener('keydown', _onKey, true); }, 0);
    menu.addEventListener('click', function (ev) {
      var addAll = ev.target.closest('.rqd-attach-addall');
      if (addAll) { ev.stopPropagation(); _addWholeChain(addAll); return; }
      var item = ev.target.closest('.rqd-attach-menu-item'); if (!item || item.disabled) return;
      if (item.dataset.act === 'device') { _close(); opts.onDevice(); return; }
      var act = item.dataset.act;
      var isHist = act === 'hist';
      var isDoc = act === 'housedoc';
      if (!isHist && !isDoc && act !== 'deal' && act !== 'chain') return;
      var _i = parseInt(item.dataset.idx, 10);
      var src = isHist ? sentFiles[_i] : (isDoc ? houseDocs[_i] : (act === 'chain' ? chainQuotes[_i] : sources[_i]));
      if (!src) return;
      /* ⛔ ONE COPY OF A HOUSE DOCUMENT, EVER. The menu stays open after a pick, and unlike a quote
         revision there is no honest reason to attach the same certificate twice — a rep who
         double-clicks and mails two identical W-9s is a visible embarrassment. */
      if (isDoc && _houseDocAttached(opts.attachments && opts.attachments(), src)) {
        var l0 = item.querySelector('.rqd-attach-menu-label');
        if (l0 && l0.textContent.indexOf('already attached') === -1) l0.textContent += ' — ✓ already attached';
        item.disabled = true; item.style.opacity = '.55';
        return;
      }
      var lbl = item.querySelector('.rqd-attach-menu-label');
      var origLabel = lbl ? lbl.textContent : '';
      item.disabled = true; item.style.opacity = '.6';
      if (lbl) lbl.textContent = origLabel + ' — attaching…';
      _getRepToken()
        .then(function (tok) {
          if (isDoc) return _fetchHouseDocAttachment(src, tok);
          return isHist ? _fetchSentAttachment(src, tok) : src.fetch(tok);
        })
        .then(function (att) {
          /* onAdd returns false when the composer refused it (5-file cap) — say so ON the
             row, since the open menu covers the composer's status line. */
          var accepted = opts.onAdd(att) !== false;
          /* ⛔ A HOUSE DOCUMENT STAYS DISABLED ONCE IT IS ON THE EMAIL (Ryan 2026-09-13). Every
             other source here re-enables, because attaching a second quote revision is a real
             thing to want; a second copy of the same W-9 never is, and this menu stays open after
             a pick — so re-enabling would hand the rep the same button back. */
          var stayOff = isDoc && accepted;
          item.style.opacity = stayOff ? '.45' : '1';
          item.disabled = stayOff;
          if (stayOff) item.style.cursor = 'not-allowed';
          if (lbl) lbl.textContent = origLabel + (accepted ? (stayOff ? ' — ✓ already attached' : ' — ✓ attached') : ' — limit reached');
          item.style.color = accepted ? 'var(--rq-success,#16a34a)' : 'var(--rq-error,#dc2626)';
        })
        .catch(function (e) {
          item.disabled = false; item.style.opacity = '1';
          if (lbl) lbl.textContent = origLabel;
          _close();
          if (opts.onError) opts.onError('Could not attach ' + (isHist ? src.name : src.label) + ' (' + ((e && e.message) || e) + ')');
        });
    });
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — email templates.
     ============================================
     The 📝 button beside the paperclip on the Quill toolbar. The templates themselves
     (copy + the deal reads behind it) live in the email-tpl-* assets; this end
     owns the button, the picker menu, and putting the result into Quill.

     A template arrives as paragraphs of RUNS, not HTML, because one sentence carries a
     product hyperlink and that link has to ride as Quill's inline `link` format on those
     characters — the same constraint the "@" picker is built around. Pasting HTML into
     quill.root.innerHTML would work until the rep backspaced into the anchor.

     ⛔ The rich-body composers (Mark-as-Fulfilled, delay notices) do NOT get this button.
     They are a contenteditable holding a server-rendered document, not a Quill editor,
     and a template that replaced that body would throw the rendered tables away.
     ============================================ */
  var _TEMPLATE_BTN_SVG = '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<path d="M4 4h16v16H4z"/><path d="M8 9h8"/><path d="M8 13h8"/><path d="M8 17h5"/></svg>';
  function _templateBtnHtml(id) {
    return '<button type="button" id="' + id + '" class="rqd-email-modal__tb-attach rqd-email-modal__tb-template" title="Insert an email template">'
      + _TEMPLATE_BTN_SVG + '</button>';
  }

  /* Replace the body with the template. Every insert states its formats explicitly
     (`link: false` on unlinked runs) rather than relying on what Quill happens to be
     carrying at the caret — otherwise the sentence after a linked product name gets
     swallowed into the anchor, the same trap the mention picker's trailing space
     exists to avoid. 'silent' throughout: this is not the rep typing, and a
     text-change from us must not reopen the "@" picker.

     ⛔ `bold: false` IS STATED FOR THE SAME REASON `link: false` IS (Ryan 2026-09-13, when a
     run-level `b` arrived for the sample-confirm address). A bold run followed by a plain one
     that named no format would bleed — the rest of the paragraph, and the blank line under
     it, would come out bold. Every insert below therefore answers BOTH questions, including
     the paragraph gaps, which carry no text a reader sees but do carry formats. */
  function _insertTemplateBody(quill, paragraphs) {
    quill.setText('', 'silent');
    var blocks = (paragraphs || []).map(function (p) {
      /* A block is either a bare array of runs (the original shape, still most of them)
         or { runs, list } for a bulleted line. Normalised here so nothing downstream has
         to ask which one it is. */
      return Array.isArray(p)
        ? { runs: p, list: '', tight: false }
        : { runs: (p && p.runs) || [], list: (p && p.list) ? String(p.list) : '', tight: !!(p && p.tight) };
    });
    var at = 0;
    var listLines = [];
    blocks.forEach(function (b, pi) {
      var start = at;
      b.runs.forEach(function (r) {
        var txt = String((r && r.t) || '');
        if (!txt) return;
        quill.insertText(at, txt, { link: (r && r.link) ? r.link : false, bold: !!(r && r.b) }, 'silent');
        at += txt.length;
      });
      if (b.list && at > start) listLines.push({ start: start, list: b.list });
      /* Blank line between paragraphs; nothing after the last, since a Quill document
         already ends with its own newline. ⛔ A SINGLE newline before a bullet, though:
         Quill hangs a list format off the newline that TERMINATES each line, so a blank
         line between two bullets is a third, empty, unformatted line sitting inside the
         list and breaking it into two. */
      if (pi < blocks.length - 1) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) - `tight` joins a block to the one
           above it with a SINGLE newline, exactly as a bullet already does. It exists for an
           address block: "please confirm you want these shipped to:" followed by the lines
           of the address, which are ONE thing and not three paragraphs. Without it every line
           gets a blank line under it, which is the spacing Ryan has rejected more than once
           ("monster spacing on lines instead of clean tight single line breaks"). The flag
           rides on the NEXT block because the gap decided here is the one before it. */
        var gap = (blocks[pi + 1].list || blocks[pi + 1].tight) ? '\n' : '\n\n';
        quill.insertText(at, gap, { link: false, bold: false }, 'silent');
        at += gap.length;
      }
    });
    /* Formats applied AFTER every character is in. Doing it inline would mean each
       formatLine ran against indices that the next insert then moved. */
    listLines.forEach(function (l) {
      try { quill.formatLine(l.start, 1, 'list', l.list, 'silent'); } catch (_) { /* a bullet that will not draw is not worth failing the insert over */ }
    });
    /* Caret placement is cosmetic, and it must not be able to void the insert. Quill's
       selection work reaches into the live DOM selection, which can throw (an editor that
       is not laid out yet, a browser that refuses focus). Letting that escape would reject
       the caller's promise AFTER the body had already landed: the rep sees the template on
       screen, an error under it, and the re-render bookkeeping never records the insert, so
       attaching a second quote silently stops updating the copy. */
    try {
      quill.setSelection(quill.getLength() - 1, 0, 'silent');
      quill.focus();
    } catch (_) { /* the body is in; where the caret sits is not worth failing over */ }
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — paragraph runs -> EMAIL HTML
     ============================================
     The other renderer of the same input. `_insertTemplateBody` above puts paragraphs into
     Quill for a human to edit; this one turns them into the bytes that will be SENT, for an
     automated sequence rung that no human will ever open.

     ⛔ IT CANNOT GO THROUGH QUILL, AND THAT IS THE WHOLE REASON IT EXISTS. Quill re-renders
     anything pasted into its own document model and keeps only the formats it knows: bold and
     colour survive, INLINE MARGINS DO NOT. PM³³ is the record of what that costs — the first
     sample request that ever reached an inbox came out with "monster spacing on lines instead of
     clean tight single line breaks", because a bare <p> carries a 1em margin top and bottom in
     Outlook. The fix that day was normalizeQuillHtml at the door of `abbySendCore`; ⚠️ these
     rungs do NOT go through abbySendCore (they send via rep-thread-send.js, from the rep's own
     mailbox), so they would inherit none of it. Correct HTML has to be emitted here.

     ⛔ THE GAP BETWEEN PARAGRAPHS IS A REAL EMPTY LINE, NOT A MARGIN. Ryan, on the first ladder
     rung that reached a customer: *"I want the gaps to be clean individual line breaks, not just
     extensions of the text line above them."* Put a caret in a margin and there is nothing there.

     ⚠️ DELIBERATELY THE SAME OUTPUT AS the worker's paragraphsToHtml (email/samples-followup.js).
     Two ladders' emails land in the same customer's inbox, and one of them looking different
     would read as two different companies writing. `tight` is the one addition — the modal's
     block shape has it and the worker's does not yet. */
  /* "Thu 27 Aug" — enough for a rep to picture when, short enough to sit inside a status line.
     ⚠️ Deliberately not a time: the send rides the 6am-6pm MT window and naming an hour the
     drain can move by fifteen minutes would be precision we cannot keep. */
  /* ============================================
     CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-20) — WHEN WILL THIS EMAIL ACTUALLY ARRIVE
     ============================================
     Ryan: *"if the 'send within business hours' box is checked, which in a given circumstance is
     going to result in the email going out tomorrow instead, does the tool know to change stuff
     like 'i left a voicemail today' to 'i left a voicemail yesterday'?"*

     ⛔ IT DID NOT, AND THAT IS A SENTENCE THE CUSTOMER READS. A held email is composed at 9pm and
     delivered at 6am; every phrase in it was resolved against the moment the rep typed. "I just
     left you a brief voicemail" about a call nine hours earlier, "earlier today" about yesterday.

     ⛔ THE ANSWER IS TO WRITE THE EMAIL FOR WHEN IT ARRIVES, not to re-date it afterwards. The copy
     lives client-side and Quill strips any marker span we could wrap a phrase in (PM³³), so
     patching a frozen string at send time is not available. Moving the CLOCK is — and it also
     makes the draft on screen honest, which the rep can check before sending.

     ⚠️ MIRRORS email/notification.js nextShippingSendTime, in Mountain, 6am-6pm, seven days. Two
     implementations of one window is a duplication worth naming: this one only decides what to
     PRINT, and the worker's is the one that decides when to SEND. If they ever disagree the
     preview is wrong by an hour and nothing is mis-sent — but keep them in step. */
  var _MT_FMT = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  function _mtParts(d) {
    var g = {};
    _MT_FMT.formatToParts(d).forEach(function (p) { g[p.type] = p.value; });
    return { y: +g.year, m: +g.month, d: +g.day, hh: (+g.hour) % 24, mi: +g.minute };
  }
  /* The next instant the window is open: now if it already is, else the next 6:00am MT. */
  function _nextSendWindowMs(fromMs) {
    var now = fromMs || Date.now();
    var p = _mtParts(new Date(now));
    if (p.hh >= 6 && p.hh < 18) return now;
    /* Walk forward in whole hours until Denver reads 06:00 — cheaper and more honest than a
       hard-coded UTC offset that DST invalidates twice a year. Bounded so a bad clock cannot
       spin: 36 hours is more than any gap between now and the next opening. */
    var t = now;
    for (var i = 0; i < 36 * 60; i++) {
      t += 60000;
      var q = _mtParts(new Date(t));
      if (q.hh === 6 && q.mi === 0) return t;
    }
    return now;
  }

  /* CUSTOM_PAIDCOMPOSE_BY_CLAUDE (Ryan 2026-08-31) — exported so a CALLER can render its email
     against the time it will actually arrive. The paid-invoice composer in dashboard-main.js
     fetches SERVER-rendered prose ("The final payment came in earlier today"), so it has to ask
     the worker for that copy with the same clock this modal will hold the send to. A third
     implementation of the 6am–6pm MT window is precisely the drift the ⚠️ two paragraphs up warns
     about. Read-only: the modal still owns the decision, this just answers "when, if held?". */
  window.RQD.nextSendWindowMs = _nextSendWindowMs;

  /* ⛔ "TOMORROW MORNING", NOT A MINUTE, AND THAT IS THE HONEST ANSWER (Ryan 2026-08-20).
     Ryan: *"if the times get randomized, should it not say '6:00 AM' one time, but then say
     something a little different next time I check it?"* Right on both counts — held emails used
     to go at exactly 6:00am, and they are jittered into 07:05-09:45 now.

     ⚠️ BUT THE MINUTE IS THE WORKER'S TO PICK, seeded off a queue key that does not exist until
     the send lands. So BEFORE sending, the composer names the part it actually knows; AFTER
     sending, the status prints the exact time the worker reports back. Naming a minute here would
     be a promise made by the half of the system that is not keeping it.

     ⚠️ AND IT MUST NOT RE-ROLL ON EVERY LOOK. A time that changes each time the rep opens the
     composer is not a schedule, it is noise — the jitter varies BETWEEN emails and is fixed for
     any one of them. */
  function _heldWhenLabel(ms) {
    var day = function (d) {
      try {
        return new Intl.DateTimeFormat('en-US', { timeZone: 'America/Denver', day: '2-digit' }).format(d);
      } catch (_) { return String(d.getDate()); }
    };
    return day(new Date(ms)) === day(new Date()) ? 'this morning' : 'tomorrow morning';
  }

  /* "Thu 6:00 AM" — the hold needs the TIME, which _seqFmtDay deliberately drops. A rep told
     "sends Thursday" would not know whether that beats the call they were about to make. */
  function _seqFmtWhen(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return 'the next business-hours window';
    try {
      return d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    } catch (_) { return String(iso).slice(0, 16).replace('T', ' '); }
  }

  /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — the moment a follow-up 1 delayed by `days`
     business days will arrive, for WRITING the email for then ("I called you on Monday", "the quote
     sent last week"). ⚠️ MIRRORS lib/delayed-start.js businessDayAhead in the worker: Mon-Fri in
     Mountain time, no holiday calendar. The worker picks the exact minute; this only has to land on
     the same DAY, which is all the time phrases read. Keep the two in step. */
  var _MT_WEEKDAY = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Denver', weekday: 'short' });
  function _businessDaysAheadMs(days, fromMs) {
    var t = fromMs || Date.now();
    var counted = 0;
    for (var guard = 0; counted < days && guard < 14; guard++) {
      t += 86400000;
      var wd = _MT_WEEKDAY.format(new Date(t));
      if (wd !== 'Sat' && wd !== 'Sun') counted++;
    }
    return t;
  }
  function _mtWeekdayShort(ms) {
    try { return _MT_WEEKDAY.format(new Date(ms)); } catch (_) { return ''; }
  }

  function _seqFmtDay(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return 'its scheduled date';
    try {
      return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    } catch (_) { return String(iso).slice(0, 10); }
  }

  var _EMAIL_P_OPEN = '<p style="margin:0;">';
  var _EMAIL_BLANK_LINE = '<p style="margin:0;"><br></p>';

  function _paragraphsToEmailHtml(paragraphs) {
    var out = [];
    var openList = '';
    function closeList() {
      if (openList) { out.push(openList === 'ordered' ? '</ol>' : '</ul>'); openList = ''; }
    }
    (paragraphs || []).forEach(function (p) {
      var block = Array.isArray(p)
        ? { runs: p, list: '', tight: false }
        : { runs: (p && p.runs) || [], list: (p && p.list) || '', tight: !!(p && p.tight) };
      /* ⛔ BOLD IS INNERMOST, INSIDE THE ANCHOR. `<a><strong>…</strong></a>` survives every
         mail client's sanitiser; `<strong><a>…</a></strong>` is the version some of them
         unwrap, and an unwrapped anchor is a dead link rather than a plain-looking one.
         (Ryan 2026-09-13, with the sample-confirm address.) */
      var inner = block.runs.map(function (r) {
        var txt = _e((r && r.t) || '');
        if (!txt) return '';
        if (r && r.b) txt = '<strong>' + txt + '</strong>';
        return (r && r.link) ? '<a href="' + _e(r.link) + '">' + txt + '</a>' : txt;
      }).join('');
      if (!inner) return;

      if (block.list) {
        var want = block.list === 'ordered' ? 'ordered' : 'bullet';
        if (openList !== want) {
          closeList();
          if (out.length) out.push(_EMAIL_BLANK_LINE);
          out.push(want === 'ordered'
            ? '<ol style="margin:0;padding-left:22px;">'
            : '<ul style="margin:0;padding-left:22px;">');
          openList = want;
        }
        /* Bullets sit tight against each other — a blank line between list items is a
           different look, and no rung has asked for one. */
        out.push('<li style="margin:0;">' + inner + '</li>');
        return;
      }
      closeList();
      /* ⚠️ A separator only ever goes BETWEEN blocks, never before the first — and `tight`
         joins a block to the one above with no gap at all, exactly as _insertTemplateBody
         does for an address block. */
      if (out.length && !block.tight) out.push(_EMAIL_BLANK_LINE);
      out.push(_EMAIL_P_OPEN + inner + '</p>');
    });
    closeList();
    return out.join('\n');
  }

  /* Re-render bookkeeping. Ryan: "as soon as I attach more than 1, then change it to
     quotes" — so a template that is still EXACTLY as we inserted it is regenerated when
     the attachment list changes. The instant the rep types, the text stops matching and
     we never touch their words again. Comparing the full text (not a flag) is what makes
     that safe: there is no state to get out of sync with what is on screen. */
  function _newTemplateBinding() { return { id: '', text: null, tokens: null }; }

  /* ⛔ DID THE TEMPLATE SURVIVE TO THE SEND? (Ryan 2026-08-19)
     Some templates may only go out once per customer, and the ledger that enforces that is
     written from the send. Ryan's rule: "if a template is added then changed/added to then
     that's fine. If it's added then fully deleted then we can assume the template wasn't
     sent." So the question is not "is this byte-identical" (the pristine test above already
     answers that, and reps edit constantly) but "is what we inserted still in here?"

     Measured on DISTINCTIVE words - four letters or more, deduped - because those are what
     an edit keeps and a rewrite loses. A rep who tightens a sentence or adds a paragraph
     keeps nearly all of them; a rep who selects all, deletes, and writes their own email
     keeps almost none. Half is the line between those two populations, and it is far from
     either. */
  function _tplTokens(text) {
    var seen = {}, out = [];
    String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(' ').forEach(function (w) {
      if (w.length < 4 || seen[w]) return;
      seen[w] = 1; out.push(w);
    });
    return out;
  }
  function _templateSurvived(binding, quill) {
    if (!binding || !binding.id || !Array.isArray(binding.tokens) || !binding.tokens.length || !quill) return false;
    var now = {};
    _tplTokens(quill.getText()).forEach(function (w) { now[w] = 1; });
    var kept = binding.tokens.filter(function (w) { return now[w]; }).length;
    return (kept / binding.tokens.length) >= 0.5;
  }
  /* ⛔ The "has the rep edited this?" test must compare MEANING, not bytes.
     An exact string compare freezes the template permanently on ANY character-level drift,
     including drift we do not control: Quill normalises whitespace and non-breaking spaces
     as the editor is focused, clicked into, or re-laid-out, and a browser autocorrect or an
     extension can retouch a character without the rep typing a word. One such touch and the
     copy silently stops following the attachment list, with no symptom except that it stops.
     Collapsing whitespace survives all of that while still catching a real edit, because a
     real edit changes WORDS. */
  function _tplNorm(t) { return String(t == null ? '' : t).replace(/[\s ]+/g, ' ').trim(); }
  /* `fx` carries the side effects a template may ask for: a subject, files re-attached from
     an earlier send, and threading this send onto that earlier message.

     ⛔ fx IS PASSED ONLY FROM AN EXPLICIT PICK, never from _retemplateIfPristine. Attaching a
     file mutates the attachment list, which re-renders the chips, which re-templates a
     pristine body - so a re-render that could attach would attach again, and again. The same
     asymmetry protects the subject line: a rep who retyped it must not have it overwritten
     because they later added a PDF. */
  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-29) — a template that needs the deal's quote PDF
     gets it attached BEFORE it builds.

     ⛔ BEFORE, NOT AFTER, AND THAT IS THE WHOLE POINT. The first-quote template reads the live
     attachment list to write its payment sentence — the pay link and the ordering-instructions
     page number come off the PDF's own X-BSF-* response headers, not off the deal record. Attach
     after the build and the body is already written WITHOUT the "click here to pay" line, which
     is exactly what Ryan hit: he replied on a thread, picked the template, and got neither the
     PDF nor the link. `ctx.attachments` is the composer's LIVE array rather than a copy, so
     pushing onto it here is visible to the build that follows.

     ⛔ AND IT HANGS OFF THE TEMPLATE, NOT THE ✉ BUTTON. Rendering /pdf without preview=1 stamps
     pdfDownloaded + lastPdfSentAt + quotedAt, arms the +5d follow-up and transitions the lead out
     of NEW. The pill is a general "email this customer" button, so fetching on every click would
     mark a plain follow-up note as a quote the customer received — and since lastPdfSentAt drives
     freightSynced/installSynced, it would silently re-close the stale-quote gate. Asking for the
     quote template IS the signal that the rep wants the quote.

     ⚠️ Never fatal. A failed fetch warns and leaves the rep the manual attach menu, exactly as the
     QW's own Send Quote does. */
  function _quotePdfAlreadyOn(atts, ref) {
    var want = String(ref || '').toUpperCase();
    return (atts || []).some(function (a) {
      return a && a.quoteMeta && String(a.quoteMeta.ref || '').toUpperCase() === want;
    });
  }
  function _ensureQuotePdfAttached(id, ctx, fx, warnings) {
    var def = ((window.RQDTemplates && window.RQDTemplates.list) || [])
      .filter(function (t) { return t.id === id; })[0];
    var ref = ctx && ctx.ref;
    if (!def || !def.needsQuotePdf || !ref || !fx || !fx.attach) return Promise.resolve();
    if (_quotePdfAlreadyOn(ctx.attachments, ref)) return Promise.resolve();
    var q = _dealQByRef(ref);
    return _getRepToken().then(function (tok) {
      var url = _WORKER_BASE + '/pdf?ref=' + encodeURIComponent(ref)
        + ((q && q.customerEmail) ? '&email=' + encodeURIComponent(q.customerEmail) : '')
        + '&token=' + encodeURIComponent(tok || '') + '&attach=1';
      return _fetchDealPdfAttachment(url, tok, ref + '.pdf', null, ref);
    }).then(function (att) {
      if (!att) throw new Error('no attachment returned');
      if (fx.attach(att) === false) {
        warnings.push('The quote PDF could not be attached automatically — the composer is at its attachment limit. Add it from the attach menu.');
        return;
      }
      /* ⛔ AND PUT IT ON THE CTX, NOT ONLY ON THE COMPOSER. `fx.attach` REPLACES the composer's
         array (`attachments = attachments.concat([att])`) rather than mutating it, so the array
         this ctx captured a moment ago is now a stale reference — the PDF would show as a pill on
         screen while the template built against a list that did not contain it, and the payment
         sentence would still be missing. Exactly the symptom this change exists to remove, only
         harder to spot because the attachment is visibly there. */
      ctx.attachments = (ctx.attachments || []).concat([att]);
    }).catch(function () {
      warnings.push('The quote PDF could not be attached automatically, so the payment link is not in this email. Attach it from the attach menu (From this deal) and re-pick the template.');
    });
  }

  function _applyTemplate(binding, quill, id, ctx, onWarn, fx) {
    if (!window.RQDTemplates) return Promise.reject(new Error('templates not loaded'));
    var preWarnings = [];
    return _ensureQuotePdfAttached(id, ctx, fx, preWarnings)
      .then(function () { return window.RQDTemplates.build(id, ctx); })
      .then(function (out) {
      out = out || {};
      out.warnings = preWarnings.concat(out.warnings || []);
      _insertTemplateBody(quill, out.paragraphs);
      binding.id = id;
      binding.text = _tplNorm(quill.getText());
      binding.tokens = _tplTokens(quill.getText());
      var warnings = (out.warnings || []).slice();

      if (fx) {
        if (out.subject && fx.setSubject) fx.setSubject(String(out.subject));
        /* Threading is recorded before the attach resolves: it is a property of the send,
           and a failed attach must not quietly turn a reply into a new thread. */
        if (out.replyToMessage && out.replyToMessage.messageId && fx.setReplyTo) {
          fx.setReplyTo(out.replyToMessage);
        }
        if (out.attachFromMessage && out.attachFromMessage.messageId && fx.attach) {
          var files = _attachmentsOfMessage(out.attachFromMessage.ref || (ctx && ctx.ref),
                                            out.attachFromMessage.messageId);
          if (!files.length) {
            warnings.push('Could not find the files from that email to re-attach, so nothing was added. Attach them by hand.');
          } else {
            return _getRepToken()
              .then(function (tok) {
                /* Sequential, not Promise.all: the composer caps attachments and refuses the
                   overflow, and a refusal has to be reported against the file that hit it. */
                var refused = [];
                return files.reduce(function (chain, f) {
                  return chain.then(function () {
                    return _fetchSentAttachment(f, tok)
                      .then(function (att) { if (fx.attach(att) === false) refused.push(f.name); })
                      .catch(function (e) { refused.push(f.name + ' (' + ((e && e.message) || e) + ')'); });
                  });
                }, Promise.resolve()).then(function () { return refused; });
              })
              .then(function (refused) {
                if (refused.length) {
                  warnings.push('Could not re-attach ' + refused.join(', ') + '. Add ' +
                    (refused.length === 1 ? 'it' : 'them') + ' by hand.');
                }
                if (onWarn && warnings.length) onWarn(warnings.join(' '));
                return out;
              });
          }
        }
      }
      if (onWarn && warnings.length) onWarn(warnings.join(' '));
      return out;
    });
  }
  function _retemplateIfPristine(binding, quill, ctxFn) {
    try {
      if (!binding || !binding.id || binding.text == null || !quill) return;
      if (_tplNorm(quill.getText()) !== binding.text) return;   // the rep has edited it — hands off
      _applyTemplate(binding, quill, binding.id, ctxFn() || {}).catch(function () {});
    } catch (_) { /* never let a re-render break the attach list */ }
  }

  /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — the start delay moved, so the email is re-written
     for the day it now arrives ("I called and left you a voicemail on Monday", "the quote sent last
     week"). Shared by both composers. ⛔ ONLY IF THE BODY IS STILL OURS - the ⏱ clock's rule: a rep's
     own words are never rewritten, and when they have edited, the status line SAYS so rather than
     leaving a "just now" that will read wrong on Wednesday. */
  function _onStartChange(n, whenMs, binding, quill, ctxFn, statusFn) {
    var pristine = !!(binding && binding.id && quill && binding.text != null
      && _tplNorm(quill.getText()) === binding.text);
    _retemplateIfPristine(binding, quill, ctxFn);
    if (typeof statusFn !== 'function' || !binding || !binding.id) return;
    if (!n) { statusFn(''); return; }
    var day = _mtWeekdayShort(whenMs);
    statusFn(pristine
      ? '📅 Follow-up 1 sends ' + day + '. The wording has been written for that day.'
      : '📅 Follow-up 1 sends ' + day + '. You have edited the body, so check that anything about '
        + 'timing ("today", "just now") still reads correctly for then.', false);
  }

  /* Picker menu, same chrome (and same <body> hoist + theme copy) as the attach menu. */
  function _openTemplateMenu(opts) {
    var existing = document.getElementById('rqd-template-menu');
    if (existing) { existing.remove(); return; }
    /* Opening the picker is a fresh intent, so drop the per-ref memo: a rep who changed
       freight to FREE between two inserts must not be served the reading from before the
       change. (Ryan hit exactly that: "I set the freight to FREE and it said that freight
       was still being calculated.") */
    if (window.RQDTemplates && typeof window.RQDTemplates.invalidate === 'function') {
      window.RQDTemplates.invalidate();
    }
    var itemCss = _MENU_ITEM_CSS_STACKED;
    var metaCss = _MENU_META_CSS;
    var menu = document.createElement('div');
    menu.id = 'rqd-template-menu';
    menu.style.cssText = _MENU_BOX_CSS;
    _applyPortalTheme(menu);
    /* Availability is a read (the send ledger), so the rows arrive a beat after the menu.
       ⛔ The menu still opens INSTANTLY with a placeholder rather than waiting: a picker
       that appears only after a network round-trip reads as a dead button, and the rep
       clicks it again. Same reason the attach menu paints before its chain resolves. */
    menu.innerHTML = '<div style="padding:8px 10px;font-size:12px;color:var(--rq-text-secondary,#475467);">Loading templates…</div>';
    document.body.appendChild(menu);

    /* ⛔ GREY, NOT GONE. A template a rep cannot use today still tells them something -
       "First quote" going live is how they learn the deal is quoted, and "Already sent to
       this customer" answers the question they opened the menu to ask. Hiding the row would
       leave them hunting for a template that had silently disappeared. */
    function _renderRows(list) {
      if (!menu.isConnected) return;
      /* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE (AIOPS 88a57d3920c4) — `listFor` has already dropped the
         rows an admin hid; the FALLBACK below hands us `RQDTemplates.list`, which keeps them
         flagged because it is a lookup projection. Filtering here rather than there is what lets
         the sequence card still resolve a hidden rung's label instead of printing its raw id. */
      list = (list || []).filter(function (t) { return !t.hidden; });
      if (!list.length) {
        menu.innerHTML = '<div style="padding:8px 10px;font-size:12px;color:var(--rq-text-secondary,#475467);">No templates available.</div>';
        return;
      }
      var html = '';
      list.forEach(function (t) {
        var off = !!t.disabled;
        /* ⛔ THE HINT IS A TOOLTIP, NOT A SECOND LINE (Ryan 2026-08-20): "on these email
           templates, we don't need the sub-text. you can have a tooltip on hover if you like,
           but no sub-text." Ten templates x two lines is a menu you have to READ; ten labels
           is a menu you can scan. ⚠️ The DISABLED REASON stays on the page, deliberately - a
           greyed row with no visible explanation is the one case where the second line is
           doing real work, and it is the answer to the question that made them open the menu. */
        html += '<button type="button" class="rqd-template-menu-item' + (off ? ' rqd-template-menu-item--off' : '')
          + '" data-id="' + _e(t.id) + '"' + (t.hint ? ' title="' + _e(t.hint) + '"' : '')
          + (off ? ' disabled aria-disabled="true"' : '')
          + ' style="' + itemCss + (off ? 'cursor:default;opacity:.55;' : '') + '">'
          + '<span style="line-height:1.5;' + (off ? 'filter:grayscale(1);' : '') + '">' + (t.icon || '\U0001f4dd') + '</span>'
          + '<span style="flex:1;min-width:0;">'
          +   '<span class="rqd-template-menu-label" style="display:block;">' + _e(t.label) + '</span>'
          +   (off ? '<span style="' + metaCss + 'font-style:italic;">' + _e(t.disabledReason) + '</span>' : '')
          + '</span>'
          + '</button>';
      });
      menu.innerHTML = html;
      Array.prototype.forEach.call(menu.querySelectorAll('.rqd-template-menu-item'), function (el) {
        el.addEventListener('mouseenter', function () { if (!el.disabled) el.style.background = 'var(--rq-bg-hover,#F5F8FC)'; });
        el.addEventListener('mouseleave', function () { if (!el.disabled) el.style.background = 'none'; });
      });
      _menuPlaceAt(menu, opts.anchorBtn.getBoundingClientRect());   // CUSTOM_DDMOBILE_BY_CLAUDE - fits the screen
    }

    var _ctx = (typeof opts.ctx === 'function') ? (opts.ctx() || {}) : {};
    var _listP = (window.RQDTemplates && typeof window.RQDTemplates.listFor === 'function')
      ? window.RQDTemplates.listFor(_ctx)
      /* No listFor means an old templates asset is cached alongside a new modal. Fall back
         to the ungated list rather than an empty menu: a rep with no gating is worse off
         than one with it, and far better off than one with no templates at all. */
      : Promise.resolve((window.RQDTemplates && window.RQDTemplates.list) || []);
    _listP.then(_renderRows).catch(function () {
      _renderRows((window.RQDTemplates && window.RQDTemplates.list) || []);
    });
    _menuPlaceAt(menu, opts.anchorBtn.getBoundingClientRect());   // CUSTOM_DDMOBILE_BY_CLAUDE - fits the screen
    function _close() { menu.remove(); document.removeEventListener('click', _onDoc, true); document.removeEventListener('keydown', _onKey, true); }
    function _onDoc(ev) { if (!menu.contains(ev.target) && !opts.anchorBtn.contains(ev.target) && ev.target !== opts.anchorBtn) _close(); }
    function _onKey(ev) { if (ev.key === 'Escape') _close(); }
    setTimeout(function () { document.addEventListener('click', _onDoc, true); document.addEventListener('keydown', _onKey, true); }, 0);
    menu.addEventListener('click', function (ev) {
      var item = ev.target.closest('.rqd-template-menu-item');
      if (!item || item.disabled) return;
      var lbl = item.querySelector('.rqd-template-menu-label');
      /* A template REPLACES the body, so anything the rep already wrote would be lost.
         Two-step on the row itself rather than a confirm() — the menu is already open
         and pointed at the thing being replaced. */
      if (opts.isDirty() && item.dataset.armed !== '1') {
        item.dataset.armed = '1';
        if (lbl) lbl.textContent = 'Replace what you have written?';
        item.style.color = 'var(--rq-error,#dc2626)';
        return;
      }
      item.disabled = true; item.style.opacity = '.6';
      if (lbl) lbl.textContent = 'Building…';
      opts.onPick(item.dataset.id)
        .then(function () {
          _close();
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — fired only on a SUCCESSFUL insert.
             A caller with state riding on WHICH template is in the body (the automated-sequence
             control) re-reads it here rather than guessing from the click, so a build that threw
             never leaves the control armed for a template that is not there. */
          if (opts.onPicked) { try { opts.onPicked(item.dataset.id); } catch (_) {} }
        })
        .catch(function (e) {
          _close();
          if (opts.onError) opts.onError('Could not build that template (' + ((e && e.message) || e) + ')');
        });
    });
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — "@" product mentions.
     ============================================
     Type "@" in the body, type a few letters of a product name, pick from the short
     list with ↑/↓ + Enter (or click), and the product title drops in hyperlinked to
     its page on blueskyfitsupply.com.

     THE LINK IS A TEXT FORMAT, NOT AN EMBED — this is the whole design constraint.
     A mention "chip" (an embed blot) would be atomic: one backspace would delete the
     entire thing. Ryan needs to insert "Product 1 - Gray" and then backspace the
     " - Gray" off, leaving "Product 1" STILL linked. Quill's built-in `link` is an
     inline format spanning a run of ordinary characters, so deleting characters off
     either end just shortens the run and the survivors keep their href. Typing INTO
     the middle extends the link, which is also what you want. Do not "improve" this
     into a mention blot.

     The trailing space is inserted with link:false so the caret lands outside the
     link — otherwise Quill carries the link format forward and the next sentence the
     rep types silently becomes part of the anchor.

     Matching is client-side against /product-mention-index (title + handle + vendor +
     storefront URL, ~60 KB), fetched once per page on the first "@" and shared by
     both composers. Products Shopify reports as unpublished never reach the client,
     so a rep cannot mail a customer a 404.
     ============================================ */
  var _MENTION_MAX_ROWS = 8;
  var _MENTION_MAX_QUERY = 45;
  var _mentionIndex = null;
  var _mentionIndexPromise = null;

  /* Shared with email-tpl-core.js (a template links the product it names,
     and must obey the same "never link an unpublished product" rule the picker does).
     One fetch, one definition — a second copy would be free to drift. */
  window.RQD.loadMentionIndex = function () { return _loadMentionIndex(); };

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — how a product NAME reads in an email.
     ============================================
     Ryan: *"any product name inserted into an email which contains an ' - ' in it (inserted
     via the template or by typing '@') automatically strip the ' - ' and anything that comes
     after it."* Catalog titles carry a variant tail ("Rubber Gym Flooring Roll - Gray") that
     is house bookkeeping, not how a customer refers to the thing.

     ⚠️ The separator is a hyphen WITH SPACES AROUND IT. "Heavy-Duty", "10-20 lb" and
     "3-Tier" must survive untouched, which is why this is not a plain /-/ split. En and em
     dashes are treated the same when spaced, because Shopify titles use all three
     interchangeably and the rep cannot see which one a title happens to carry.

     📌 The LINK is unaffected — the href still points at the exact product. Only the words
     shorten. And this shortens the text at INSERT time; the picker list still shows full
     titles, because "- Gray" and "- Black" may be the only thing telling two rows apart.

     Lives here, not in the templates file, because both insertion paths are in this file's
     hands and one definition is the whole point. */
  function _productDisplayName(title) {
    var s = String(title == null ? '' : title);
    var m = s.match(/\s[-–—]\s/);
    return (m ? s.slice(0, m.index) : s).trim() || s.trim();
  }
  window.RQD.productDisplayName = function (t) { return _productDisplayName(t); };

  /* CUSTOM_HOUSEDOCS_BY_CLAUDE (Ryan 2026-09-13) — published so email-tpl-snippets.js can ask about
     the shelf without owning the fetch. ⚠️ `houseShelf` and `houseDocForRole` are SYNC peeks and
     never fetch, which is what lets a snippet's `available()` call them; the read itself happens
     when the attach menu opens, and `loadHouseDocs` is the door for anything that needs to force it. */
  window.RQD.houseShelf = function () { return _houseShelfNow(); };
  window.RQD.houseDocForRole = function (role) { return _houseDocForRole(role); };
  window.RQD.houseDocAttached = function (atts, doc) { return _houseDocAttached(atts, doc); };
  window.RQD.loadHouseDocs = function () { return _houseDocShelf(); };
  /* The composer's deal list, union of the dashboard's (when the page has one) and the
     chain lookup. email-tpl-core.js resolves the customer + the last call
     through this, so a template built on the Workstation sees what the menu sees. */
  window.RQD.knownDeals = function (ref) { return _knownDeals(ref); };
  window.RQD.loadChainDeals = function (ref) { return _loadChainDeals(ref); };

  function _loadMentionIndex() {
    if (_mentionIndex) return Promise.resolve(_mentionIndex);
    if (_mentionIndexPromise) return _mentionIndexPromise;
    var api = (window.RepPortal && window.RepPortal.API) ? window.RepPortal.API : null;
    if (!api) return Promise.resolve([]);
    _mentionIndexPromise = api.get('/product-mention-index')
      .then(function (d) {
        var list = (d && Array.isArray(d.products)) ? d.products : [];
        list.forEach(function (p) {
          p._t = String(p.t || '').toLowerCase();
          p._idx = (p._t + ' ' + String(p.v || '')).toLowerCase();
        });
        _mentionIndex = list;
        return list;
      })
      .catch(function () {
        // Let a later "@" retry — a dead index must not be cached as "no products".
        _mentionIndexPromise = null;
        return [];
      });
    return _mentionIndexPromise;
  }

  /* Products already quoted to this customer, for ranking. One request per composer
     deal-set, cached for the page: the picker asks on the first "@" and every keystroke
     after that is local. A failure resolves EMPTY rather than rejecting — the boost is an
     enhancement, and losing it should degrade to the plain list, not break the picker. */
  var _dealBoostCache = {};
  function _loadDealBoost(opts) {
    var refs = _customerDealRefs(opts, 12);
    if (!refs.length) return Promise.resolve(null);
    var key = refs.join(',');
    if (_dealBoostCache[key]) return _dealBoostCache[key];
    var api = (window.RepPortal && window.RepPortal.API) ? window.RepPortal.API : null;
    if (!api) return Promise.resolve(null);
    _dealBoostCache[key] = api.get('/deal-products?refs=' + encodeURIComponent(key))
      .then(function (d) {
        var h = {}, t = {};
        ((d && d.handles) || []).forEach(function (x) { if (x) h[String(x)] = 1; });
        ((d && d.titles) || []).forEach(function (x) { if (x) t[String(x).toLowerCase()] = 1; });
        return { h: h, t: t };
      })
      .catch(function () { delete _dealBoostCache[key]; return null; });
    return _dealBoostCache[key];
  }
  /* Handle is the exact identity; the lowercase title is the fallback for manual
     Workstation lines that never carried a catalog handle. */
  function _isOnDeal(p, boost) {
    if (!boost || !p) return false;
    if (p.h && boost.h[p.h]) return true;
    return !!(p._t && boost.t[p._t]);
  }

  /* Rank: title-prefix beats title-substring beats scattered-token match (which is
     what lets "bump gray" find "Bumper Plate - Gray"). Ties break alphabetically so
     the list is stable while the rep keeps typing. */
  function _matchProducts(list, query, boost) {
    var q = String(query || '').toLowerCase().trim();
    if (!q) return [];
    var tokens = q.split(/\s+/).filter(Boolean);
    var hits = [];
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      var ok = true;
      for (var j = 0; j < tokens.length; j++) {
        if (p._idx.indexOf(tokens[j]) === -1) { ok = false; break; }
      }
      if (!ok) continue;
      var rank = p._t.indexOf(q) === 0 ? 0 : (p._t.indexOf(q) !== -1 ? 1 : 2);
      hits.push({ p: p, rank: rank, on: _isOnDeal(p, boost) ? 0 : 1 });
      if (hits.length > 400) break;   // bounded scan — ranking still picks the best 8
    }
    hits.sort(function (a, b) {
      // Already on this customer's quote outranks a better text match on anything else:
      // when a rep names a product mid-follow-up it is nearly always one they quoted.
      if (a.on !== b.on) return a.on - b.on;
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.p._t < b.p._t ? -1 : (a.p._t > b.p._t ? 1 : 0);
    });
    return hits.slice(0, _MENTION_MAX_ROWS).map(function (h) { return h.p; });
  }

  function _mentionHighlight(title, query) {
    var esc = function (s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); };
    var t = String(title || '');
    var q = String(query || '').trim();
    var at = q ? t.toLowerCase().indexOf(q.toLowerCase()) : -1;
    if (at === -1) return esc(t);
    return esc(t.slice(0, at)) + '<strong>' + esc(t.slice(at, at + q.length)) + '</strong>' + esc(t.slice(at + q.length));
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19) — the caret picker, shared.
     ============================================
     "@" (a product) and "#" (a snippet) are ONE mechanism wearing two corpora: type a
     trigger character, type a few letters, pick from a short list with ↑/↓ + Enter or a
     click, and something lands where the trigger was. Everything about that except the
     rows and the insert is identical, so it lives here once.

     Extracted when "#" arrived rather than cloned, on Ryan's standing rule for this system:
     *"reusable components/snippets/insertions/logic is key ... set us up for long term
     success."* Two copies of a positioning routine drift; the second one is where the
     flip-above-the-caret fix does not get made.

     ⚠️ The "@" behaviour is a MOVE, not a rewrite. Same scan rule, same capture-phase key
     handling, same re-scan after an async load, same close-on-no-match.

     WHAT A CALLER OWNS
       trigger      the character that opens it
       fetchRows    (query) -> Promise<rows>. Rows are opaque here.
       rowHtml      (row, index, isActive, query) -> the HTML for one row
       choose       (row, start, len) -> the insert. `start` is the index of the trigger
                    character and `len` covers the trigger plus everything typed after it,
                    so the caller deletes exactly what the rep typed and replaces it.
     ============================================ */
  function _installCaretPicker(quill, opts) {
    if (!quill || !opts || !opts.flag) return;
    if (quill[opts.flag]) return;                 // idempotent per Quill instance
    quill[opts.flag] = true;

    var maxQuery = opts.maxQuery || 45;
    var panel = null;
    var rows = [];
    var active = 0;
    var atIndex = -1;     // document index of the trigger character
    var query = '';

    function close() {
      if (!panel) return;
      document.removeEventListener('mousedown', onDocDown, true);
      window.removeEventListener('scroll', position, true);
      window.removeEventListener('resize', close);
      if (panel.parentNode) panel.parentNode.removeChild(panel);
      panel = null; rows = []; active = 0; atIndex = -1; query = '';
      if (opts.onClose) opts.onClose();
    }
    function onDocDown(ev) {
      if (panel && !panel.contains(ev.target) && !quill.root.contains(ev.target)) close();
    }

    function ensurePanel() {
      if (panel) return panel;
      panel = document.createElement('div');
      panel.className = 'rqd-mention-panel';
      panel.style.cssText = 'position:fixed;z-index:100060;min-width:260px;max-width:420px;max-height:280px;overflow-y:auto;'
        + 'background:var(--rq-bg,#ffffff);border:1px solid var(--rq-border,#D0D5DD);border-radius:10px;'
        + 'box-shadow:var(--rq-shadow,0 10px 30px rgba(0,0,0,.20));padding:6px;font-size:13px;color:var(--rq-text,#000F1C);';
      _applyPortalTheme(panel);   // same <body> hoist, same lost variable scope as the attach menu
      /* Keep focus in the editor: a mousedown that blurs Quill would drop the
         selection, and the insert needs the caret still sitting after the query. */
      panel.addEventListener('mousedown', function (ev) { ev.preventDefault(); });
      panel.addEventListener('click', function (ev) {
        var row = ev.target.closest ? ev.target.closest('[data-mrow]') : null;
        if (!row) return;
        pick(parseInt(row.getAttribute('data-mrow'), 10));
      });
      document.body.appendChild(panel);
      document.addEventListener('mousedown', onDocDown, true);
      window.addEventListener('scroll', position, true);
      window.addEventListener('resize', close);
      return panel;
    }

    function position() {
      if (!panel) return;
      var b = null;
      try { b = quill.getBounds(atIndex, 0); } catch (_) { b = null; }
      if (!b) { close(); return; }
      var host = quill.container.getBoundingClientRect();
      var w = panel.offsetWidth || 260;
      var h = panel.offsetHeight || 120;
      // Quill's Bounds is container-relative; `bottom` is present in Quill 2 but
      // derive it anyway so a shape change can't silently mis-place the panel.
      var bBottom = (b.bottom != null) ? b.bottom : (b.top + (b.height || 16));
      var left = Math.max(8, Math.min(host.left + b.left, window.innerWidth - w - 8));
      var below = host.top + bBottom + 4;
      // Flip above the caret when the list would run off the bottom of the viewport.
      var top = (below + h > window.innerHeight - 8) ? Math.max(8, host.top + b.top - h - 4) : below;
      panel.style.left = Math.round(left) + 'px';
      panel.style.top = Math.round(top) + 'px';
    }

    function render() {
      var el = ensurePanel();
      var hintCss = 'padding:8px 10px;font-size:12px;color:var(--rq-text-secondary,#5a6b7d);';
      if (!rows.length) {
        el.innerHTML = '<div style="' + hintCss + '">' + (opts.emptyHint || '') + '</div>';
        position();
        return;
      }
      el.innerHTML = rows.map(function (r, i) {
        return opts.rowHtml(r, i, i === active, query);
      }).join('');
      position();
      var sel = el.querySelector('[data-mrow="' + active + '"]');
      if (sel && sel.scrollIntoView) { try { sel.scrollIntoView({ block: 'nearest' }); } catch (_) {} }
    }

    function move(delta) {
      if (!rows.length) return;
      active = (active + delta + rows.length) % rows.length;
      render();
    }

    function pick(i) {
      var row = rows[i];
      if (!row || atIndex < 0) return;
      var start = atIndex;
      var len = query.length + 1;   // the trigger plus what was typed after it
      close();
      opts.choose(row, start, len);
    }

    /* Find an open trigger behind the caret: same line, preceded by start-of-line or
       whitespace (so "ryan@blueskyfitsupply.com" never opens the product picker), and no
       more than maxQuery characters of query behind it. */
    function scan() {
      var range = quill.getSelection();
      if (!range || range.length > 0) return null;
      var back = Math.min(range.index, maxQuery + 1);
      if (back <= 0) return null;
      var text = quill.getText(range.index - back, back);
      var at = text.lastIndexOf(opts.trigger);
      if (at === -1) return null;
      var q = text.slice(at + 1);
      if (/[\n\r]/.test(q)) return null;
      var before = at === 0 ? (range.index - back === 0 ? '' : quill.getText(range.index - back - 1, 1)) : text.charAt(at - 1);
      if (before && !/[\s(\[>]/.test(before)) return null;
      return { index: range.index - back + at, query: q };
    }

    function refresh() {
      var hit = scan();
      if (!hit) { close(); return; }
      atIndex = hit.index;
      query = hit.query;
      Promise.resolve(opts.fetchRows(query)).then(function (list) {
        // The caret may have moved on while the rows were in flight — re-scan.
        var now = scan();
        if (!now || now.index !== atIndex) { close(); return; }
        query = now.query;
        rows = list || [];
        /* A query that matches nothing closes the picker rather than parking a dead
           list over the text — deleting a character re-scans and reopens it. */
        if (query && !rows.length) { close(); return; }
        active = 0;
        render();
      }).catch(function () { close(); });
    }

    quill.on('text-change', function (delta, oldDelta, source) {
      if (source !== 'user') { if (panel) close(); return; }
      refresh();
    });
    quill.on('selection-change', function (range) {
      if (!panel) return;
      if (!range) { close(); return; }   // blurred
      refresh();
    });

    /* Capture phase on the editor root so these keys are consumed BEFORE Quill's own
       keyboard module (which binds on the same element, bubble phase) turns Enter
       into a newline or lets the arrows move the caret out of the query. */
    quill.root.addEventListener('keydown', function (ev) {
      if (!panel) return;
      var k = ev.key;
      if (k === 'ArrowDown') { move(1); }
      else if (k === 'ArrowUp') { move(-1); }
      else if (k === 'Enter' || k === 'Tab') { if (!rows.length) { close(); return; } pick(active); }
      else if (k === 'Escape') { close(); }
      else return;
      ev.preventDefault();
      ev.stopPropagation();
    }, true);
  }

  /* Install the "@" picker on one Quill instance. Idempotent per instance.
     getCtx() is called when the picker opens and returns { ref, toEmails } — read live,
     not captured, so editing the To field re-scopes which products get promoted. */
  function _installProductMentions(quill, getCtx) {
    var boost = null;     // products already quoted to this customer (ranking only)
    _installCaretPicker(quill, {
      flag: '__bsfMentionsInstalled',
      trigger: '@',
      maxQuery: _MENTION_MAX_QUERY,
      emptyHint: 'Type a product name…',
      onClose: function () { boost = null; },
      fetchRows: function (query) {
        var mctx = (typeof getCtx === 'function') ? (getCtx() || {}) : {};
        return Promise.all([_loadMentionIndex(), _loadDealBoost(mctx)]).then(function (res) {
          boost = res[1];
          return _matchProducts(res[0], query, boost);
        });
      },
      rowHtml: function (p, i, isActive, query) {
        return '<div data-mrow="' + i + '" style="display:flex;flex-direction:column;gap:1px;padding:7px 10px;border-radius:6px;cursor:pointer;'
          + (isActive ? 'background:var(--rq-bg-hover,#F5F8FC);' : '') + '">'
          + '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + _mentionHighlight(p.t, query) + '</span>'
          + (function () {
              var onDeal = _isOnDeal(p, boost);
              if (!onDeal && !p.v) return '';
              return '<span style="font-size:11px;color:var(--rq-text-secondary,#5a6b7d);">'
                + (onDeal ? '<span style="color:var(--rq-success,#1a7f4b);font-weight:700;">On this customer’s quote</span>' + (p.v ? ' · ' : '') : '')
                + (p.v ? _mentionHighlight(p.v, '') : '')
                + '</span>';
            })()
          + '</div>';
      },
      choose: function (p, start, len) {
        /* Ryan 2026-08-18 — the variant tail is stripped on the way in, so picking
           "Rubber Gym Flooring Roll - Gray" types "Rubber Gym Flooring Roll" and links it.
           The PICKER still lists full titles: "- Gray" and "- Black" may be the only thing
           telling two rows apart, so shortening the list would be shortening the choice. */
        var title = _productDisplayName(p.t);
        quill.deleteText(start, len, 'user');
        /* The link rides as an inline FORMAT on the title's characters — see the header
           note. Deleting characters off either end leaves the survivors linked, which is
           what lets the rep trim the name further by hand. */
        quill.insertText(start, title, { link: p.u }, 'user');
        /* Unlinked trailing space so the caret ends up OUTSIDE the anchor. Without
           it Quill keeps applying `link` to everything typed next.
           ⛔ No `quill.format('link', false)` after this (Ryan 2026-09-24). The caret
           inherits the space's formats, which are already unlinked — verified in Chromium:
           text typed after a pick stays plain either way. What the call DID do was park
           Quill's `<span class="ql-cursor">` marker in the DOM, which the draft autosave
           then copied and the restore turned into undeletable ghost text. See
           _stripQuillCursor. */
        quill.insertText(start + title.length, ' ', 'link', false, 'user');
        quill.setSelection(start + title.length + 1, 0, 'silent');
        quill.focus();
      },
    });
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19) — "#" snippets.
     ============================================
     Ryan: *"I want to add some email snippets - like 1-2 sentences that can be inserted
     wherever in an email. Sort of like how HubSpot has these."*

     Type "#" in the body, type a few letters, pick with ↑/↓ + Enter, and one or two
     sentences land AT THE CARET. The same rows are also behind the ⚡ button on the toolbar,
     because a keyboard trigger nobody was told about is a feature nobody uses.

     ⛔ AT THE CARET, NOT INSTEAD OF THE BODY. This is the whole difference from 📝 templates,
     and it is why there is no "replace what you have written?" step here, no gating, and no
     send ledger: a snippet is a sentence, not an email, and a rep may insert five of them.

     ⚠️ A SNIPPET INSERT DIRTIES A TEMPLATE BODY, deliberately. _retemplateIfPristine compares
     the full text, so the moment a snippet lands the composer stops regenerating the template
     when the attachment list changes. That is correct: the rep has edited this email, and
     silently overwriting their snippet on the next attach would be the worse surprise. The
     "did the template survive to the send" token test is unaffected - adding words keeps them.
     ============================================ */

  /* Runs → the document, at the selection. Every insert states its formats explicitly
     (`link: false` on unlinked runs) rather than inheriting whatever the caret is carrying,
     the same trap the mention picker's trailing space exists to avoid: dropped after a
     linked product name, an inherited link would swallow the whole snippet into the anchor.

     'user' source throughout, unlike a template insert: this is a normal edit. Ctrl+Z steps
     back over it, and the template's pristine check correctly sees the body change. */
  function _insertRunsAtCursor(quill, paragraphs) {
    var blocks = (paragraphs || []).map(function (p) {
      return Array.isArray(p) ? { runs: p, list: '' }
        : { runs: (p && p.runs) || [], list: (p && p.list) ? String(p.list) : '' };
    });
    if (!blocks.length) return;

    var range = quill.getSelection(true);
    var at = range ? range.index : Math.max(0, quill.getLength() - 1);
    if (range && range.length) { quill.deleteText(at, range.length, 'user'); }

    /* ⛔ SPACING IS THE CALLER'S PROBLEM, NOT THE SNIPPET'S. A snippet is written as a
       standalone sentence, so it cannot know whether it is landing at the start of an empty
       line or immediately after "Thanks!". Pad only where the neighbouring character is real
       text: without this, dropping one at the end of a sentence produces "...quote.The
       ordering instructions", which is the first thing a rep would have to fix by hand every
       single time. */
    var before = at > 0 ? quill.getText(at - 1, 1) : '';
    var after = quill.getText(at, 1);
    var lead = (before && !/\s/.test(before)) ? ' ' : '';
    var trail = (after && !/[\s]/.test(after)) ? ' ' : '';

    var start = at;
    if (lead) { quill.insertText(at, lead, { link: false, bold: false }, 'user'); at += lead.length; }

    var listLines = [];
    blocks.forEach(function (b, bi) {
      var lineStart = at;
      b.runs.forEach(function (r) {
        var txt = String((r && r.t) || '');
        if (!txt) return;
        quill.insertText(at, txt, { link: (r && r.link) ? r.link : false, bold: !!(r && r.b) }, 'user');
        at += txt.length;
      });
      if (b.list && at > lineStart) listLines.push({ start: lineStart, list: b.list });
      /* Same rule as a template body: a SINGLE newline before a bullet, because Quill hangs
         the list format off the newline that terminates each line and a blank line between
         two bullets breaks the list into two. */
      if (bi < blocks.length - 1) {
        var gap = blocks[bi + 1].list ? '\n' : '\n\n';
        quill.insertText(at, gap, { link: false, bold: false }, 'user');
        at += gap.length;
      }
    });
    if (trail) { quill.insertText(at, trail, { link: false, bold: false }, 'user'); at += trail.length; }

    /* Formats applied AFTER every character is in — doing it inline would run formatLine
       against indices the next insert then moved. */
    listLines.forEach(function (l) {
      try { quill.formatLine(l.start, 1, 'list', l.list, 'silent'); } catch (_) { /* a bullet that will not draw is not worth failing the insert over */ }
    });

    /* Caret after the insert, and NOT carrying the link format: a snippet that ends on a
       hyperlink ("clicking here") would otherwise turn everything typed next into part of
       the anchor. Cosmetic, so it must not be able to void an insert that already landed. */
    try {
      quill.setSelection(at, 0, 'silent');
      quill.format('link', false, 'user');
      quill.focus();
    } catch (_) { /* the text is in; where the caret sits is not worth failing over */ }
    return { start: start, end: at };
  }

  function _snippetRows(ctx) {
    /* ⚠️ `listFor(ctx)`, not `list` — `available()` needs the deal to answer "is there a version to
       link?", and the bare getter has no way to hand it one. Both callers already build a ctx for
       `build()`; they pass the same one here so the row and the sentence agree about the deal.

       CUSTOM_HUMANTPLCATALOG_BY_CLAUDE (AIOPS 88a57d3920c4) — ⛔ THIS RETURNS A PROMISE NOW.
       `listFor` awaits the stored arrangement, because a snippet an admin WROTE arrives over the
       wire and a menu painted before it lands would simply be missing it. The `list` fallback
       stays synchronous — it is the "an old templates asset is cached alongside a new modal"
       path — so it is wrapped rather than returned bare. */
    var visible = function (rows) { return (rows || []).filter(function (s) { return !s.hidden; }); };
    if (window.RQDSnippets && typeof window.RQDSnippets.listFor === 'function') {
      return Promise.resolve(window.RQDSnippets.listFor(ctx || {}))
        .then(visible)
        .catch(function () { return visible((window.RQDSnippets && window.RQDSnippets.list) || []); });
    }
    return Promise.resolve(visible((window.RQDSnippets && window.RQDSnippets.list) || []));
  }

  /* Build one and drop it in. Warnings are rep-facing only and never reach the customer:
     "no first name on this deal, so that part reads generically" is exactly the thing a rep
     needs to see BEFORE they send, and exactly the thing that must not be in the email. */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — the caret survives the second picker.
     ⛔ CAPTURED BEFORE THE PICKER OPENS, because focusing its search input BLURS Quill and the
     range is gone by the time anything is chosen. Restored on pick AND on cancel. */
  function _savedRange(quill) {
    try { return quill.getSelection(); } catch (_) { return null; }
  }
  function _restoreCaret(quill, saved) {
    if (!quill || !quill.root || !quill.root.isConnected) return;
    try {
      if (saved) quill.setSelection(saved.index, saved.length || 0, 'silent');
      quill.focus();
    } catch (_) { /* cosmetic; the insert does not depend on it */ }
  }
  /* Where to hang the picker when there is no button — the "#" path opens it at the caret. */
  function _caretRect(quill) {
    try {
      var sel = quill.getSelection();
      var b = quill.getBounds(sel ? sel.index : 0);
      var c = quill.container.getBoundingClientRect();
      return { left: c.left + b.left, bottom: c.top + b.bottom, top: c.top + b.top, right: c.left + b.right };
    } catch (_) {
      return { left: 40, bottom: 120, top: 100, right: 200 };
    }
  }
  /* ⚠️ A COPY, never a mutation of the caller's ctx — the same object is read again by the next
     snippet the rep inserts, and a leftover `option` would silently re-link the previous manual. */
  function _withOption(ctx, option) {
    var c = {};
    Object.keys(ctx || {}).forEach(function (k) { c[k] = ctx[k]; });
    c.option = option;
    return c;
  }

  /**
   * ⚠️ `fx` IS THE SAME OBJECT `_applyTemplate` TAKES, and both composers already build one. It is
   * passed, never rebuilt: that closure is what enforces MAX_ATTACH_COUNT *and* calls
   * `_renderAttachList`, so a second copy is how a file ends up attached with no pill on screen.
   */
  function _applySnippet(quill, id, ctx, onWarn, onError, fx, option, anchorRect) {
    if (!window.RQDSnippets) return Promise.reject(new Error('snippets not loaded'));
    var saved = _savedRange(quill);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-11) — ⛔ CLEAR THE PREVIOUS VERDICT BEFORE RE-JUDGING.
       Ryan attached the quote, pressed the snippet again, and reported that "the red msg wouldn't
       go away" — because nothing ever cleared it. The status line was written by the failure and
       only the ⚡ menu's own `onPick` blanked it; the "#" keyboard path went straight to a rebuild,
       so a second failure rewrote the identical string and a SUCCESS left the old error standing
       under a snippet that had just inserted fine. Either way the rep cannot tell a stale verdict
       from a fresh one.
       ⛔ CLEARED HERE, NOT IN EACH PICKER: this is the one function both insert paths and both
       composers (reply and free-form) go through, so a third caller cannot forget.
       ⚠️ Order matters — `onError('')` leaves the error styling on, `onWarn('')` takes it off, so
       the warn call goes last and the line ends genuinely neutral. */
    try { if (onError) onError(''); } catch (_e) {}
    try { if (onWarn) onWarn(''); } catch (_e) {}

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — A SNIPPET WITH A SECOND STEP.
       ⛔ THE BRANCH LIVES HERE FOR THE REASON THE COMMENT ABOVE GIVES: this is the one funnel both
       insert paths and both composers go through, so the four existing `onPick` handlers need no
       edit at all and a fifth caller cannot forget.
       ⚠️ `hasOptions` is SYNC, so the ⚡ menu never sits on "Inserting…" while a fetch decides; and
       the promise resolves as soon as the picker is OPEN, so that menu closes instead of stacking
       a second overlay behind this one. */
    if (!option && window.RQDSnippets.hasOptions && window.RQDSnippets.hasOptions(id, ctx || {})) { // CUSTOM_SHIPMENTSPEC_BY_CLAUDE - ctx: a picker only sometimes (optionsWhen)
      var row = (window.RQDSnippets.list || []).filter(function (r) { return r && r.id === id; })[0];
      _openOptionPicker({
        title: (row && row.label) || 'Choose one',
        searchPlaceholder: (window.RQDSnippets.optionsPlaceholder && window.RQDSnippets.optionsPlaceholder(id)) || 'Search manuals…', // CUSTOM_SHIPMENTSPEC_BY_CLAUDE
        anchorRect: anchorRect || _caretRect(quill),
        fallbackUrl: (window.RQDTpl && window.RQDTpl.ecoreDocsUrl) || '',
        isGone: function () { return !quill || !quill.root || !quill.root.isConnected; },
        fetchRows: function (fresh) {
          if (fresh && window.RQDTpl && window.RQDTpl.refreshEcoreDocs) window.RQDTpl.refreshEcoreDocs();
          return window.RQDSnippets.options(id, ctx || {})
            .catch(function (e) { return { error: (e && e.message) || 'Could not load the list.' }; });
        },
        footnote: function () { return ''; },
        /* ⛔ CANCEL INSERTS NOTHING AND RESTORES THE CARET. The body is byte-identical to before
           the rep opened it, whatever state the picker was in. */
        onCancel: function () { _restoreCaret(quill, saved); },
        onPick: function (opt) {
          _restoreCaret(quill, saved);
          _applySnippet(quill, id, ctx, onWarn, onError, fx, opt);
        },
      });
      return Promise.resolve({ deferred: true });
    }

    var c = option ? _withOption(ctx, option) : (ctx || {});
    return window.RQDSnippets.build(id, c).then(function (out) {
      /* CUSTOM_ONLINESIGN_BY_CLAUDE (Ryan 2026-10-27) — `copyText` = a snippet that puts something on the clipboard and
         inserts NOTHING ("🔗 Copy payment link"). The caret is left where it was, so the rep pastes it plainly or selects
         words and adds it as a link. A browser that refuses the write gets the link in a prompt to copy by hand. */
      if (out && out.copyText) {
        var said = function () { if (onWarn) onWarn([out.notice || 'Copied.'].concat(out.warnings || []).join(' ')); return out; };
        var byHand = function () { window.prompt('Copy the payment link:', out.copyText); return out; };
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(out.copyText).then(said, byHand);
        } catch (_) { /* by hand below */ }
        return byHand();
      }
      _insertRunsAtCursor(quill, (out && out.paragraphs) || []);
      var warns = (out && out.warnings) ? out.warnings.slice() : [];
      /* CUSTOM_HOUSEDOCS_BY_CLAUDE (Ryan 2026-09-13) — `attachHouseDocs: ['w-9']` on the build's
         return means "the modal attaches this document". It is the `attachFromMessage` rule with a
         slug instead of a messageId: a snippet NAMES what it wants and the modal fetches, so the
         kernel stays free of the DOM and every cap stays in the one place that owns the array.
         ⛔ SEQUENTIAL, NOT Promise.all — the composer refuses the overflow at five files and the
         refusal has to be reported against the file that hit it (the `_applyTemplate` rule).
         ⛔ AND THE SENTENCE STAYS IN EVEN IF THE ATTACH FAILS. It is already in the body; ripping
         it back out leaves a silently-empty insert, which is the failure the stale-verdict fix of
         2026-09-11 was about. The warning names the manual route instead. */
      var slugs = (out && Array.isArray(out.attachHouseDocs)) ? out.attachHouseDocs : [];
      if (!slugs.length || !fx || !fx.attach) {
        if (slugs.length && (!fx || !fx.attach)) {
          warns.push('This snippet attaches a document, but this composer cannot attach files. Add it from the attach menu under Frequently required.');
        }
        if (onWarn && warns.length) onWarn(warns.join(' '));
        return out;
      }
      return _getRepToken().then(function (tok) {
        return slugs.reduce(function (chain, slug) {
          return chain.then(function () {
            var doc = ((_houseShelfNow().items) || []).filter(function (d) { return d && d.slug === slug; })[0];
            if (!doc) { warns.push('The document "' + slug + '" is not on the house documents shelf, so nothing was attached.'); return; }
            if (_houseDocAttached(fx.attachments ? fx.attachments() : [], doc)) return;   // already on the email
            return _fetchHouseDocAttachment(doc, tok).then(function (att) {
              if (fx.attach(att) === false) warns.push(doc.name + ' could not be attached (the email is at its file limit).');
            }).catch(function (e) {
              warns.push(doc.name + ' could not be attached automatically (' + ((e && e.message) || 'read failed')
                + '). Attach it from the attach menu under Frequently required.');
            });
          });
        }, Promise.resolve());
      }).catch(function () {
        warns.push('The document could not be attached automatically. Attach it from the attach menu under Frequently required.');
      }).then(function () {
        if (onWarn && warns.length) onWarn(warns.join(' '));
        return out;
      });
    }).catch(function (e) {
      if (onError) onError((e && e.message) || String(e));
      throw e;
    });
  }

  function _installSnippetPicker(quill, getCtx, onWarn, onError, getFx) {
    _installCaretPicker(quill, {
      flag: '__bsfSnippetsInstalled',
      trigger: '#',
      maxQuery: 40,
      emptyHint: 'Type to find a snippet…',
      /* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE (AIOPS 88a57d3920c4) — the rep-authored set ARRIVED, on
         exactly this seam: `search` is a fetch now, and the `Promise.resolve` that was here for a
         future that had not happened yet is the reason nothing else had to change here.
         ⚠️ A rejected search must leave the picker responsive, so it falls back to the plain
         (unranked, unfiltered by query) rows rather than to an empty list. */
      fetchRows: function (query) {
        var ctx = (typeof getCtx === 'function') ? (getCtx() || {}) : {};
        if (window.RQDSnippets && typeof window.RQDSnippets.search === 'function') {
          return Promise.resolve(window.RQDSnippets.search(query, ctx))
            .catch(function () { return _snippetRows(ctx); });
        }
        return _snippetRows(ctx);
      },
      /* Single-line, same as the ⚡ menu: a snippet's label is the whole description, and a
         hint in one surface but not the other is exactly the inconsistency the shared row
         style above exists to prevent. */
      rowHtml: function (s, i, isActive, query) {
        return '<div data-mrow="' + i + '" style="display:flex;gap:8px;align-items:center;padding:7px 10px;border-radius:6px;cursor:pointer;font-size:13px;'
          + (isActive ? 'background:var(--rq-bg-hover,#F5F8FC);' : '') + '">'
          + (s.icon ? '<span style="line-height:1.45;">' + s.icon + '</span>' : '')
          + '<span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + _mentionHighlight(s.label, query) + '</span>'
          + '</div>';
      },
      choose: function (s, start, len) {
        /* The "#query" comes out FIRST, so a snippet that refuses (see the ordering
           instructions one, which needs the quote attached) leaves the body clean rather
           than parking "#order" in the middle of a sentence. */
        quill.deleteText(start, len, 'user');
        quill.setSelection(start, 0, 'silent');
        var ctx = (typeof getCtx === 'function') ? (getCtx() || {}) : {};
        /* ⛔ THE `fx` GOES DOWN THIS PATH TOO. "#w9" at the caret is the door a fast rep uses, and
           a snippet that attaches through the ⚡ menu but not through the keyboard would be a
           feature that depends on which button somebody happened to press. */
        var fx = (typeof getFx === 'function') ? getFx() : null;
        _applySnippet(quill, s.id, ctx, onWarn, onError, fx).catch(function () { /* reported by onError */ });
      },
    });
  }

  /* The ⚡ button beside 📝 and the paperclip. Same corpus as "#", for the rep who has not
     learned the trigger yet — and for the one who wants to read the list. */
  var _SNIPPET_BTN_SVG = '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<path d="M13 2 4 14h6l-1 8 9-12h-6z"/></svg>';
  function _snippetBtnHtml(id) {
    return '<button type="button" id="' + id + '" class="rqd-email-modal__tb-attach rqd-email-modal__tb-snippet" title="Insert a snippet (or type # in the body)">'
      + _SNIPPET_BTN_SVG + '</button>';
  }

  /* ⛔ NOT _openTemplateMenu WITH A FLAG. That menu's two defining behaviours are wrong here:
     it greys rows that are unavailable (a snippet is never unavailable), and it arms a
     "Replace what you have written?" confirmation (a snippet replaces nothing). Sharing it
     would mean threading two booleans through every branch of it to switch both off. */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — placement and dismissal, shared.
     Extracted from `_openSnippetMenu` when the two-step snippet picker below needed exactly the
     same behaviour off a CARET rather than a button. A helper two functions need belongs above
     both of them; a second copy of "keep the box on screen" is a second thing to get wrong. */
  /* CUSTOM_DDMOBILE_BY_CLAUDE (Ryan 2026-10-29): "the menu can be too long that it doesn't fully fit on your
     screen.. and when you try to scroll down ... it slides everything behind the menu down while it stays
     locked in place". The box was capped at min(60vh, 420px) wherever it opened, so on a phone its bottom sat
     off-screen and a swipe past its end scrolled the composer behind it. Now its height is the room actually
     left on screen (flipping above the button when there is more room there), and overscroll stays inside
     the menu. Template, Attach and both Snippet pickers all place through here. */
  /* CUSTOM_DDMOBILE_BY_CLAUDE (Ryan 2026-10-29): "the keyboard appears.. despite there being nowhere to type yet". The
     composer put the caret in a field 50 ms after it opened, and on a phone the keyboard follows the caret - even when
     that field sat inside the folded "Email details" section (.rqd-acc__section without --open; it folds by max-height,
     so the field still counts as on screen). On a phone, an automatic focus waits for a field you can see; tapping
     into it works as always. Desktop is unchanged. */
  function _mayAutoFocus(el) {
    if (!el) return false;
    var phone = false;
    try { phone = window.matchMedia('(max-width: 769px) and (pointer: coarse)').matches; } catch (_) { phone = false; }
    if (!phone) return true;
    var sec = el.closest && el.closest('.rqd-acc__section');
    if (sec && !sec.classList.contains('rqd-acc__section--open')) return false;
    return !!(el.getClientRects && el.getClientRects().length);
  }

  function _menuPlaceAt(menu, rect) {
    var mw = menu.offsetWidth || 280;
    var maxLeft = (window.scrollX || 0) + document.documentElement.clientWidth - mw - 8;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var below = vh - rect.bottom - 14, above = rect.top - 14;
    var natural = menu.scrollHeight || 0;
    var cap = Math.min(420, Math.round(vh * 0.6));
    var up = below < Math.min(natural, 220) && above > below;
    var maxH = Math.max(120, Math.min(cap, up ? above : below));
    menu.style.maxHeight = maxH + 'px';
    menu.style.overflowY = 'auto';
    menu.style.overscrollBehavior = 'contain';
    menu.style.webkitOverflowScrolling = 'touch';
    var top = up ? rect.top - Math.min(natural || maxH, maxH) - 6 : rect.bottom + 6;
    menu.style.top = Math.round(top + (window.scrollY || 0)) + 'px';
    menu.style.left = Math.round(Math.max(8, Math.min(rect.left + (window.scrollX || 0), maxLeft))) + 'px';
  }

  /* ⛔ INSTALLED AT OPEN TIME, NOT AT FILL TIME. That ordering is the whole reason the house rule
     about a blocking overlay is satisfied: Escape has to work while the fetch is still in flight,
     which is precisely when a rep discovers they opened the wrong thing. */
  function _menuDismiss(menu, isOutside, onClose) {
    function close() {
      menu.remove();
      document.removeEventListener('click', onDoc, true);
      document.removeEventListener('keydown', onKey, true);
      if (onClose) onClose();
    }
    function onDoc(ev) { if (isOutside(ev)) close(); }
    function onKey(ev) { if (ev.key === 'Escape') { ev.stopPropagation(); close(); } }
    setTimeout(function () {
      document.addEventListener('click', onDoc, true);
      document.addEventListener('keydown', onKey, true);
    }, 0);
    return close;
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — THE SECOND STEP
     ============================================
     A snippet that declares `options(ctx)` cannot be built from the deal alone — "link them to the
     installation instructions" has to know WHICH manual, and on a deal with no ECORE line there is
     nothing to infer it from. Picking the row opens this: a searchable, keyboard-driven list.

     ⛔ `_installCaretPicker` COULD NOT BE REUSED, and forcing it would have been a bug. Its whole
     lifecycle is anchored to a trigger character living in the Quill document — `scan()` reads the
     text behind the caret, `refresh()` re-runs on every text-change, `pick()` deletes
     `query.length + 1` characters. At stage two there is no trigger: the "#" path deliberately
     deletes it BEFORE applying, so the body stays clean. Reusing it would mean re-inserting a fake
     "#" into the customer's email and deleting it again.

     ⛔ AND THE EXIT IS UNCONDITIONAL IN EVERY STATE — loading, loaded, empty, errored. Escape, the
     ✕ and an outside click all restore the caret and leave the body byte-identical; there is no
     `if` around the dismissal. The house rule is "never enter a screen whose exit is conditional",
     and the state it is really about is the one where the fetch never comes back. */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — the picker's scrollbar, matching the DEAL FEED.
     ⚠️ A SCROLLBAR CANNOT BE STYLED INLINE: `::-webkit-scrollbar` is a pseudo-element, so it needs
     a real stylesheet rule. Injected once from here rather than added to dashboard.css because
     this modal is deliberately self-contained — it also runs on the Quote Workstation, which does
     not load the dashboard's stylesheet.
     The values are lifted from `.rqd-notes-log__entries` in dashboard.css (thin, var(--rq-border)
     thumb on a transparent track, brightening on hover) so the two read as one system. */
  function _ensureOptionPickerCss() {
    if (document.getElementById('rqd-optpick-css')) return;
    var st = document.createElement('style');
    st.id = 'rqd-optpick-css';
    st.textContent =
      '#rqd-option-picker .rqd-optpick-list{scrollbar-width:thin;scrollbar-color:var(--rq-border,#D0D5DD) transparent;}'
      + '#rqd-option-picker .rqd-optpick-list::-webkit-scrollbar{width:8px;}'
      + '#rqd-option-picker .rqd-optpick-list::-webkit-scrollbar-track{background:transparent;}'
      + '#rqd-option-picker .rqd-optpick-list::-webkit-scrollbar-thumb{background:var(--rq-border,#D0D5DD);border-radius:4px;}'
      + '#rqd-option-picker .rqd-optpick-list::-webkit-scrollbar-thumb:hover{background:var(--rq-text-secondary,#475467);}';
    document.head.appendChild(st);
  }

  function _openOptionPicker(opts) {
    var old = document.getElementById('rqd-option-picker');
    if (old) old.remove();

    _ensureOptionPickerCss();

    var box = document.createElement('div');
    box.id = 'rqd-option-picker';
    /* ⛔ ONE SCROLLER, AND IT IS THE LIST (Ryan 2026-09-13: "two right-side scroll bars, both
       thick and obnoxious"). `_MENU_BOX_CSS` carries its own `max-height` + `overflow-y:auto`,
       which is right for a plain menu and wrong here: this box has a fixed header and footer that
       must not scroll away, so the inner list already scrolls and the outer bar was a second one
       for the same content. Overridden AFTER the shared string so the later value wins. */
    box.style.cssText = _MENU_BOX_CSS + ';z-index:100060;width:380px;max-width:92vw;'
      + 'max-height:none;overflow:hidden;display:flex;flex-direction:column;';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-label', opts.title || 'Choose one');
    _applyPortalTheme(box);

    var rows = [];          // the flat list currently rendered (rows only, no headings)
    var active = -1;
    var settled = false;

    var head = '<div style="display:flex;align-items:center;gap:8px;padding:10px 12px 6px;">'
      + '<span style="flex:1;min-width:0;font-size:13px;font-weight:700;">' + _e(opts.title || 'Choose one') + '</span>'
      + '<button type="button" class="rqd-optpick-x" aria-label="Close" style="border:none;background:none;cursor:pointer;font-size:16px;line-height:1;color:var(--rq-text-secondary,#475467);">&times;</button>'
      + '</div>'
      + '<div style="padding:0 12px 8px;"><input type="search" class="rqd-optpick-q" placeholder="' + _e(opts.searchPlaceholder || 'Search…')
      + '" aria-controls="rqd-optpick-list" style="width:100%;box-sizing:border-box;padding:7px 10px;font-size:13px;border:1px solid var(--rq-border,#D0D5DD);border-radius:6px;background:var(--rq-bg,#fff);color:inherit;"></div>'
      + '<div class="rqd-optpick-list" id="rqd-optpick-list" role="listbox" style="max-height:min(46vh,320px);overflow-y:auto;flex:1 1 auto;min-height:0;"></div>'
      + '<div style="padding:6px 12px 8px;font-size:11px;color:var(--rq-text-secondary,#475467);border-top:1px solid var(--rq-border,#D0D5DD);">'
      + 'Esc to cancel · <button type="button" class="rqd-optpick-refresh" style="border:none;background:none;padding:0;font:inherit;color:var(--rq-link,#006BCC);cursor:pointer;text-decoration:underline;">Refresh list</button></div>';
    box.innerHTML = head;
    document.body.appendChild(box);
    _menuPlaceAt(box, opts.anchorRect);

    var listEl = box.querySelector('.rqd-optpick-list');
    var inputEl = box.querySelector('.rqd-optpick-q');

    var close = _menuDismiss(box, function (ev) { return !box.contains(ev.target); }, function () {
      if (!settled) { settled = true; if (opts.onCancel) opts.onCancel(); }
    });
    box.querySelector('.rqd-optpick-x').addEventListener('click', function () { close(); });

    function msg(text) { listEl.innerHTML = '<div style="padding:10px 12px;font-size:12px;color:var(--rq-text-secondary,#475467);">' + _e(text) + '</div>'; }

    /* ⛔ LOADING, EMPTY AND FAILED MUST LOOK DIFFERENT. An empty list rendered for a failed read is
       "the manuals vanished"; a failure rendered as empty sends a rep looking for a document that
       is on the page in front of them. */
    msg('Loading the document list…');

    function render(all, query) {
      var toks = String(query || '').trim().toLowerCase().split(/\s+/)
        .map(function (t) { return { raw: t, sq: t.replace(/[^a-z0-9]+/g, '') }; })
        .filter(function (t) { return t.sq; });
      /* The resources page's own matcher: every token must hit, raw-substring OR squashed. So
         `dbtile`, `egrip` and `class1` behave here exactly as they do on the page the customer
         lands on — one rule, learned once. */
      var shown = all.filter(function (r) {
        if (!toks.length) return true;
        var raw = ((r.name || '') + ' ' + (r.title || '') + ' ' + (r.note || '')).toLowerCase();
        var sq = raw.replace(/[^a-z0-9]+/g, '');
        return toks.every(function (t) { return raw.indexOf(t.raw) !== -1 || sq.indexOf(t.sq) !== -1; });
      });
      rows = shown; active = shown.length ? 0 : -1;
      if (!shown.length) { msg(toks.length ? 'Nothing matches that.' : 'There are no tech manuals on the resources page right now.'); return; }

      var html = ''; var group = null;
      shown.forEach(function (r, i) {
        if (r.group && r.group !== group) {
          group = r.group;
          html += '<div style="padding:8px 12px 2px;font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--rq-text-secondary,#475467);">' + _e(group) + '</div>';
        }
        html += '<button type="button" class="rqd-optpick-row" role="option" aria-selected="' + (i === active ? 'true' : 'false') + '" data-i="' + i + '"'
          + ' style="' + _MENU_ITEM_CSS_STACKED + ';flex-direction:column;align-items:flex-start;gap:1px;">'
          + '<span style="font-size:13px;">' + _e(r.name) + '</span>'
          + (r.note ? '<span style="font-size:11px;color:var(--rq-text-secondary,#475467);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;">' + _e(r.note) + '</span>' : '')
          + '</button>';
      });
      if (opts.footnote) {
        html += '<div style="padding:6px 12px 2px;font-size:11px;color:var(--rq-text-secondary,#475467);">' + _e(opts.footnote()) + '</div>';
      }
      listEl.innerHTML = html;
      paintActive();
      _menuPlaceAt(box, opts.anchorRect);
    }

    function paintActive() {
      Array.prototype.forEach.call(listEl.querySelectorAll('.rqd-optpick-row'), function (el) {
        var on = Number(el.dataset.i) === active;
        el.style.background = on ? 'var(--rq-bg-hover,#F5F8FC)' : 'none';
        el.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on && el.scrollIntoView) { try { el.scrollIntoView({ block: 'nearest' }); } catch (_) { /* cosmetic */ } }
      });
    }

    function choose(i) {
      var r = rows[i];
      if (!r || settled) return;
      /* ⛔ THE MODAL CAN BE TORN DOWN WITH THE PICKER OPEN. Inserting into a detached editor throws
         somewhere nobody is listening, so check before acting rather than after. */
      if (opts.isGone && opts.isGone()) { close(); return; }
      settled = true;
      close();                 // takes the box AND both document listeners with it
      opts.onPick(r);
    }

    listEl.addEventListener('click', function (ev) {
      var el = ev.target.closest('.rqd-optpick-row');
      if (el) choose(Number(el.dataset.i));
    });
    listEl.addEventListener('mousemove', function (ev) {
      var el = ev.target.closest('.rqd-optpick-row');
      if (el && Number(el.dataset.i) !== active) { active = Number(el.dataset.i); paintActive(); }
    });

    var loaded = null;
    inputEl.addEventListener('input', function () { if (loaded) render(loaded, inputEl.value); });
    inputEl.addEventListener('keydown', function (ev) {
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
        if (!rows.length) return;
        ev.preventDefault();
        active = (active + (ev.key === 'ArrowDown' ? 1 : rows.length - 1)) % rows.length;
        paintActive();
      } else if (ev.key === 'Enter') {
        ev.preventDefault();
        if (active >= 0) choose(active);
      }
      /* Escape is handled by the document-level dismiss, so it works before the rows arrive. */
    });

    function load(fresh) {
      msg(fresh ? 'Refreshing the document list…' : 'Loading the document list…');
      loaded = null; rows = []; active = -1;
      opts.fetchRows(fresh).then(function (res) {
        if (!box.isConnected) return;
        if (res && res.error) {
          listEl.innerHTML = '<div style="padding:10px 12px;font-size:12px;color:var(--rq-danger,#B42318);">' + _e(res.error) + '</div>'
            + '<div style="padding:0 12px 10px;font-size:12px;">'
            + '<button type="button" class="rqd-optpick-retry" style="border:none;background:none;padding:0;font:inherit;color:var(--rq-link,#006BCC);cursor:pointer;text-decoration:underline;">Try again</button>'
            + ' · <a href="' + _e(opts.fallbackUrl || '#') + '" target="_blank" rel="noopener">Open the resources page</a></div>';
          var retry = listEl.querySelector('.rqd-optpick-retry');
          if (retry) retry.addEventListener('click', function () { load(true); });
          _menuPlaceAt(box, opts.anchorRect);
          return;
        }
        loaded = (res && res.rows) || [];
        render(loaded, inputEl.value);
      }).catch(function () {
        if (!box.isConnected) return;
        msg('Could not load the document list.');
      });
    }

    box.querySelector('.rqd-optpick-refresh').addEventListener('click', function () { load(true); });
    load(false);
    try { inputEl.focus(); } catch (_) { /* where the caret sits is not worth failing over */ }
  }

  function _openSnippetMenu(opts) {
    var old = document.getElementById('rqd-snippet-menu');
    if (old) { old.remove(); return; }                 // clicking the button again closes it
    var itemCss = _MENU_ITEM_CSS_STACKED;
    var menu = document.createElement('div');
    menu.id = 'rqd-snippet-menu';
    menu.style.cssText = _MENU_BOX_CSS;
    _applyPortalTheme(menu);

    /* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE (AIOPS 88a57d3920c4, Ryan 2026-09-03) — PAINTED FIRST,
       FILLED WHEN THE ROWS ARRIVE. `_snippetRows` awaits the stored arrangement now, because a
       snippet an admin WROTE arrives over the wire and a menu built before it lands is a menu
       missing it. An empty box that fills a moment later reads as broken, so it says what it is
       doing — the same treatment the template menu beside it has always had.
       ⚠️ Every path re-runs `_place()`: a menu positioned against the width of the "Loading" line
       sits wrong the moment real rows widen it. */
    menu.innerHTML = '<div style="padding:8px 10px;font-size:12px;color:var(--rq-text-secondary,#475467);">Loading snippets&hellip;</div>';
    document.body.appendChild(menu);
    _place();
    /* Never rejects on its own, but a registry swapped underneath us could — and a keystroke
       handler that throws stops the picker responding instead of explaining. */
    _snippetRows(opts && opts.ctx).catch(function () { return []; }).then(_fill);

    function _place() { _menuPlaceAt(menu, opts.anchorBtn.getBoundingClientRect()); }

    function _fill(list) {
      if (!menu.isConnected) return;      // closed again before the rows landed
      if (!list.length) {
        menu.innerHTML = '<div style="padding:8px 10px;font-size:12px;color:var(--rq-text-secondary,#475467);">No snippets available.</div>';
      } else {
        menu.innerHTML = list.map(function (s) {
          /* ⛔ NO SUB-TEXT. Ryan: *"the snippets don't need sub-text... just the main text header
             is fine."* A template row explains itself because it writes a whole email and can be
             unavailable; a snippet is one sentence, and its label already says which one. Which
             makes every snippet row single-line, here and in the "#" picker both. */
          /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — a snippet that would refuse is greyed
             and inert, with the reason on hover. Still ONE LINE: the reason is a `title`, not
             sub-text, because Ryan's "no sub-text on snippets" rule does not bend for this. */
          var off = !!s.disabled;
          return '<button type="button" class="rqd-snippet-menu-item" data-id="' + _e(s.id) + '"'
            + (off ? ' disabled aria-disabled="true" title="' + _e(s.disabledReason || '') + '"' : '')
            + ' style="' + itemCss + (off ? ';opacity:0.45;cursor:not-allowed;' : '') + '">'
            + (s.icon ? '<span style="line-height:1.5;">' + s.icon + '</span>' : '')
            + '<span class="rqd-snippet-menu-label" style="flex:1;min-width:0;">' + _e(s.label) + '</span>'
            + '</button>';
        }).join('')
        /* Teach the shortcut at the moment they used the slow path. */
        + '<div style="padding:6px 10px 4px;font-size:11px;color:var(--rq-text-secondary,#475467);border-top:1px solid var(--rq-border,#D0D5DD);margin-top:4px;">'
        + 'Tip: type <strong>#</strong> in the body to insert one without leaving the keyboard.</div>';
      }
      Array.prototype.forEach.call(menu.querySelectorAll('.rqd-snippet-menu-item'), function (el) {
        el.addEventListener('mouseenter', function () { el.style.background = 'var(--rq-bg-hover,#F5F8FC)'; });
        el.addEventListener('mouseleave', function () { el.style.background = 'none'; });
      });
      _place();
    }

    function _close() { menu.remove(); document.removeEventListener('click', _onDoc, true); document.removeEventListener('keydown', _onKey, true); }
    function _onDoc(ev) { if (!menu.contains(ev.target) && !opts.anchorBtn.contains(ev.target) && ev.target !== opts.anchorBtn) _close(); }
    function _onKey(ev) { if (ev.key === 'Escape') _close(); }
    setTimeout(function () { document.addEventListener('click', _onDoc, true); document.addEventListener('keydown', _onKey, true); }, 0);

    menu.addEventListener('click', function (ev) {
      var item = ev.target.closest('.rqd-snippet-menu-item');
      if (!item) return;
      var lbl = item.querySelector('.rqd-snippet-menu-label');
      item.disabled = true; item.style.opacity = '.6';
      if (lbl) lbl.textContent = 'Inserting…';
      opts.onPick(item.dataset.id)
        .then(function () { _close(); })
        .catch(function () { _close(); });   // the reason is already on the status line
    });
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — "--" becomes an em dash.
     ============================================
     Typing two hyphens replaces them with a real em dash, the way Word and Outlook
     autocorrect do, so a rep gets proper punctuation without hunting for a special
     character. Body editor only — the subject line is a plain input and is left alone.

     Deliberately narrow, because an over-eager autocorrect is worse than none:
     - Only fires when the delta's last op is a SINGLE typed "-". A paste that happens
       to end in "--" is not a keystroke, and mangling pasted text (a command line, a
       part number, a URL) would be the bad kind of surprise.
     - Only when the caret sits immediately after the two hyphens. Deleting a character
       and happening to leave "--" behind the caret is not a request to convert.
     - Never inside `code` / `code-block`, where a double hyphen is usually a flag.
     - "---" is stable: the first two become an em dash, the third is just a hyphen
       after it, and "—-" no longer matches, so it cannot cascade.

     Applied as ONE updateContents delta rather than delete-then-insert so the history
     module sees a single entry and Ctrl+Z steps back over the whole substitution. The
     formats at the hyphens are carried onto the dash, so a bold or linked "--" stays
     bold or linked. Re-entrancy is impossible on the guard alone (the follow-up
     text-change inserts "—", not "-"), but the flag makes that explicit rather than
     load-bearing on a coincidence.
     ============================================ */
  function _installDashAutocorrect(quill) {
    if (!quill || quill.__bsfDashAutocorrect) return;
    quill.__bsfDashAutocorrect = true;
    var Delta = (window.Quill && window.Quill.import) ? window.Quill.import('delta') : null;
    var busy = false;
    quill.on('text-change', function (delta, oldDelta, source) {
      if (busy || source !== 'user' || !delta || !Array.isArray(delta.ops)) return;
      var last = delta.ops[delta.ops.length - 1];
      if (!last || last.insert !== '-') return;
      var range = quill.getSelection();
      if (!range || range.length > 0 || range.index < 2) return;
      if (quill.getText(range.index - 2, 2) !== '--') return;
      var fmt = quill.getFormat(range.index - 2, 2) || {};
      if (fmt.code || fmt['code-block']) return;
      var at = range.index - 2;
      busy = true;
      try {
        if (Delta) {
          quill.updateContents(new Delta().retain(at).delete(2).insert('—', fmt), 'user');
        } else {
          quill.deleteText(at, 2, 'user');
          quill.insertText(at, '—', fmt, 'user');
        }
        quill.setSelection(at + 1, 0, 'silent');
      } finally {
        busy = false;
      }
    });
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* CUSTOM_AICOMPOSER_BY_CLAUDE (Ryan 2026-10-18) — sentence capitals + one-obvious-fix spelling,
     Word/Outlook style. The logic lives in composer-autocorrect.js (admins only, checked there);
     this is the ONE mount point both body editors call, so the new-email modal and the reply pane
     can never drift apart. Product titles from the "@" index are handed over so a brand or
     product name is never respelled. A page that did not load the script simply has no
     autocorrect — never an error. */
  function _installSpellAutocorrect(quill) {
    if (!window.BSFAutocorrect || typeof window.BSFAutocorrect.install !== 'function') return;
    try {
      window.BSFAutocorrect.install(quill, {
        protectWords: function () {
          return _loadMentionIndex().then(function (list) {
            return (list || []).map(function (p) { return p && p.t; });
          });
        },
      });
    } catch (_) { /* an autocorrect fault must never break the composer */ }
  }
  /* "Draft it" (phase 3): the ONE mount point both plain composers call. The button, the strip and the
     worker call live in composer-ai.js, which decides whether this person may see it. ⛔ The callers
     below never call this for a sendOverride, rich-body or prefilled composer (PO, freight, invoices,
     shipping and delay notices keep their approved wording). */
  function _installDraftIt(quill, toolbar, opts) {
    if (!window.BSFComposerAI || typeof window.BSFComposerAI.mount !== 'function' || !toolbar) return;
    try { window.BSFComposerAI.mount(quill, toolbar, opts || {}); } catch (_) { /* never break the composer */ }
  }
  /* CUSTOM_COMPOSERPHASE6_BY_CLAUDE (Ryan 2026-10-18) — the templates and snippets this composer offers right now (the same
     rows the 📝 and ⚡ menus show, hidden and greyed-out ones left out), so Draft it can point to approved wording instead
     of writing its own. Ids and labels only. */
  function _draftItWording(ctx) {
    var R = window.RQDTemplates;
    var tplP = (R && typeof R.listFor === 'function') ? Promise.resolve(R.listFor(ctx || {})) : Promise.resolve((R && R.list) || []);
    var pick = function (rows) {
      return (rows || []).filter(function (t) { return t && t.id && !t.hidden && !t.disabled; })
        .map(function (t) { return { id: String(t.id), label: String(t.label || t.id), hint: String(t.hint || '') }; });
    };
    return Promise.all([tplP.catch(function () { return []; }), _snippetRows(ctx || {}).catch(function () { return []; })])
      .then(function (r) { return { templates: pick(r[0]), snippets: pick(r[1]) }; });
  }
  /* The draft this email started as, for the send payload: the worker keeps it beside what was sent. */
  function _aiDraftFor(quill) {
    try { return (window.BSFComposerAI && quill) ? window.BSFComposerAI.draftFor(quill) : null; } catch (_) { return null; }
  }
  /* CUSTOM_COMPOSERCHECK_BY_CLAUDE (Ryan 2026-10-18, phase 4) — the check before Send, the ONE mount both plain send paths
     call (the new-email window's doSend and the reply pane's _doSend). The work lives in composer-ai.js. True = send now;
     false = the check took over and calls `o.send` itself. A missing script or any fault answers true: the email sends as
     it did before. ⛔ The callers never ask for a sendOverride, rich-body or prefilled composer. */
  function _checkBeforeSend(quill, o) {
    try {
      if (!window.BSFComposerAI || typeof window.BSFComposerAI.checkBeforeSend !== 'function') return true;
      return window.BSFComposerAI.checkBeforeSend(quill, o) !== false;
    } catch (_) { return true; }
  }
  function _checkPassed(quill, o) {
    try { return !!(window.BSFComposerAI && window.BSFComposerAI.checkPassed && window.BSFComposerAI.checkPassed(quill, o)); } catch (_) { return false; }
  }
  /* END CUSTOM_AICOMPOSER_BY_CLAUDE */

  var QUILL_CDN_JS = 'https://cdn.jsdelivr.net/npm/quill@2.0.2/dist/quill.js';
  var QUILL_CDN_CSS = 'https://cdn.jsdelivr.net/npm/quill@2.0.2/dist/quill.snow.css';
  function _loadQuill() {
    if (window.Quill) return Promise.resolve();
    return new Promise(function(resolve, reject) {
      if (!document.querySelector('link[data-quill-snow]')) {
        var l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = QUILL_CDN_CSS;
        l.setAttribute('data-quill-snow', '1');
        document.head.appendChild(l);
      }
      if (window.Quill) return resolve();
      var s = document.createElement('script');
      s.src = QUILL_CDN_JS;
      s.onload = function() { resolve(); };
      s.onerror = function() { reject(new Error('Failed to load Quill from CDN')); };
      document.head.appendChild(s);
    });
  }

  /* Register pt-based font sizes via the STYLE attributor (writes
     `style="font-size:11pt"` inline — email clients strip class-based
     sizes but honor inline). Dual register so the toolbar picker uses
     our whitelist instead of Quill's default small/normal/large/huge
     labels. Idempotent. Call AFTER Quill is loaded. */
  var _QUILL_FONT_SIZES = ['9pt', '10pt', '11pt', '12pt', '14pt', '16pt', '18pt', '24pt', '36pt'];
  function _registerQuillSizes() {
    if (!window.Quill) return;
    var SizeStyle = window.Quill.import('attributors/style/size');
    SizeStyle.whitelist = _QUILL_FONT_SIZES;
    window.Quill.register(SizeStyle, true);
    window.Quill.register({ 'formats/size': SizeStyle }, true);
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-09) — Paste as plain text + links.
     Pasted content (esp. from websites/docs) carries inline color/background/font
     that renders near-invisible in the compose box AND ships into the customer
     email. This clipboard matcher strips EVERY format from pasted nodes except a
     hyperlink's href, so a paste lands exactly as if typed — links keep their href
     (and render blue + underlined like a normal link). Line breaks are preserved
     (op.insert, incl '\n', is kept); block formatting collapses to plain lines.
     Call once per Quill instance, right after construction. */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-17) — when TRUE, the plain-paste matcher passes
     content through UNCHANGED. Set briefly around dangerouslyPasteHTML of a trusted, server-
     built prefillBody so its formatting (bold, color) survives. The strip is meant for real
     user pastes (Ctrl+V from Word/web), NOT our own templated email HTML — which was getting
     stripped of the <strong>/color it ships with (e.g. the freight-balance email's bold
     labels, bold-red balance, bold CTA all landed plain). */
  var _allowRichPrefill = false;

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-09) — link insertion via a native
     window.prompt instead of Quill's built-in tooltip. Quill's .ql-tooltip is
     absolutely positioned INSIDE .ql-container, which the modal CSS clips
     (overflow:hidden on the container + overflow-y:auto on the compose/body
     ancestors), so on the QW Send-Quote + DD composers the "paste a link" bubble
     got cut off on the left and under the signature. A native prompt can't be
     clipped. Wired to BOTH the toolbar link button (via toolbar handlers) and
     Ctrl/Cmd+K (keyboard binding) on both Quill instances. Mirrors the rich-body
     editor, which already uses window.prompt for the same reason. Returns false
     from the keyboard binding to swallow the shortcut. */
  function _promptQuillLink(quill) {
    if (!quill) return;
    var range = quill.getSelection(true);
    if (!range || range.length === 0) { window.alert('Select the text you want to link first, then press Ctrl+K.'); return; }
    var existing = quill.getFormat(range).link;
    var url = window.prompt('Link URL:', existing || 'https://');
    if (url === null) return;                 // cancelled — leave selection untouched
    url = String(url).trim();
    if (url === '' || url === 'https://') { quill.format('link', false); return; } // empty → remove any link
    quill.format('link', url);
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-10-27) — the bubble you get by CLICKING a link in the body ("Visit URL: ... Edit |
     Remove") is still Quill's own .ql-tooltip, and it lives INSIDE .ql-container, so the composer's overflow clipped it and
     it slid under the window's edges (the 2026-07-09 prompt fix above only covered INSERTING a link). It now floats: moved
     into its own .ql-container.ql-snow box on <body> (Quill's tooltip CSS is scoped to .ql-snow), placed with fixed coordinates under the
     clicked link (above it when there is no room below), kept inside the screen, hidden the moment anything scrolls, and
     removed when the composer closes. Both composers (Reply and Compose, DD and the QW's Send Quote) call it. */
  function _floatQuillTooltip(quill) {
    try {
      var tip = quill && quill.theme && quill.theme.tooltip;
      if (!tip || !tip.root || !quill.container) return;
      var box = document.createElement('div');
      box.className = 'ql-container ql-snow rqd-ql-float'; // ql-container: Quill's 13px font; the inline style overrides its size / position
      box.style.cssText = 'position:fixed;left:0;top:0;width:0;height:0;z-index:2147483000;';
      box.appendChild(tip.root);
      document.body.appendChild(box);
      tip.root.style.position = 'fixed';
      tip.position = function (reference) {
        var c = quill.container.getBoundingClientRect();
        var r = tip.root;
        var w = r.offsetWidth, h = r.offsetHeight;
        var vw = document.documentElement.clientWidth, vh = document.documentElement.clientHeight;
        var left = c.left + reference.left + reference.width / 2 - w / 2;
        var top = c.top + reference.bottom;               // the .ql-tooltip CSS adds its own 10px drop
        var flip = top + 10 + h > vh - 8;
        if (flip) top = c.top + reference.top - h;        // .ql-flip lifts it 10px instead
        r.classList.toggle('ql-flip', flip);
        r.style.marginTop = '0px';                        // Quill shifts this on the editor's own scroll; fixed needs none
        r.style.left = Math.max(8, Math.min(left, vw - w - 8)) + 'px';
        r.style.top = Math.max(8, top) + 'px';
        return 0;
      };
      var onScroll = function () {
        if (!tip.root.classList.contains('ql-hidden') && !tip.root.classList.contains('ql-editing')) tip.hide();
      };
      document.addEventListener('scroll', onScroll, true);
      var gone = setInterval(function () {
        if (quill.container.isConnected) return;
        clearInterval(gone);
        document.removeEventListener('scroll', onScroll, true);
        if (box.parentNode) box.parentNode.removeChild(box);
      }, 2000);
    } catch (e) { /* the bubble stays where Quill put it */ }
  }

  function _applyPlainPaste(quill) {
    if (!quill || !quill.clipboard || !window.Quill) return;
    var Delta = window.Quill.import('delta');
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, function (node, delta) {
      if (_allowRichPrefill) return delta; // trusted prefill — keep bold/color/etc.
      var ops = (delta.ops || []).map(function (op) {
        var link = op.attributes && op.attributes.link;
        return link ? { insert: op.insert, attributes: { link: link } } : { insert: op.insert };
      });
      return new Delta(ops);
    });
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-24) — the editor's HTML never carries Quill's
     caret marker.
     ============================================
     Ryan, on RW-1449-A: *"I'm left with the literal text '@Bounce@ asdf asdf' that it won't
     let me delete no matter what."*

     Quill parks `<span class="ql-cursor">\uFEFF</span>` in the DOM whenever a format is set at
     a collapsed caret (the "@" picker did it after every pick until today; the snippet insert
     still does, on purpose, for a snippet that ends on a link). The span is editor bookkeeping
     with a length of ZERO in the document model, and it sits in `root.innerHTML` until the
     next caret move — a blur does not clear it. The 1.5 s draft autosave copied it verbatim,
     and restoring that draft by assigning innerHTML made Quill build a Cursor blot from it
     WITHOUT the selection it needs: every keystroke into it threw ("reading 'composing'"),
     the characters landed in the DOM and never in the model, so Backspace ate the real text
     beside them while the ghost stayed put. Reproduced keystroke-for-keystroke in Chromium
     against the shipped code.

     So: ONE accessor for "what is in the editor", used by the draft save, the draft restore
     and every send, and it unwraps the marker — the span goes, its \uFEFF goes, and any text
     a rep managed to type INTO one comes out as ordinary, deletable characters. Unwrap rather
     than drop, because in the poisoned case that text is exactly what the rep sees on screen,
     and a send must carry what the screen shows. Parsed with DOMParser so nothing in a stored
     draft can load or run while it is being cleaned. */
  function _stripQuillCursor(html) {
    var s = String(html == null ? '' : html);
    if (s.indexOf('ql-cursor') === -1 && s.indexOf('\uFEFF') === -1) return s;
    var body;
    try { body = new DOMParser().parseFromString(s, 'text/html').body; } catch (_) { return s.replace(/\uFEFF/g, ''); }
    var marks = body.querySelectorAll('.ql-cursor');
    for (var i = 0; i < marks.length; i++) {
      var m = marks[i];
      while (m.firstChild) m.parentNode.insertBefore(m.firstChild, m);
      m.parentNode.removeChild(m);
    }
    return body.innerHTML.replace(/\uFEFF/g, '');
  }
  function _editorHtml(quill) { return quill ? _stripQuillCursor(quill.root.innerHTML) : ''; }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-09) — "Send Test". Delivers the
     fully-composed email (same /outlook-send pipeline as a real send) to the rep's
     OWN inbox so they can eyeball formatting before sending to the customer. The
     worker resolves the recipient server-side, prefixes [TEST], and skips the
     cadence/status change + deal-feed threading. Shared by both panes (reply +
     new compose). Modal stays open; only validates subject + body. */
  function _sendTestEmail(opts) {
    var quill = opts && opts.quill;
    if (!quill) return;
    var status = opts.statusEl;
    var ERR = 'rqd-email-modal__compose-status--err';
    var OK = 'rqd-email-modal__compose-status--ok';
    function showErr(m) { if (status) { status.classList.remove(OK); status.classList.add(ERR); status.textContent = m; } }
    if (typeof opts.commitChips === 'function') { try { opts.commitChips(); } catch (_) {} }
    var subject = String((opts.subjectEl || {}).value || '').trim();
    var textLen = (quill.getText() || '').trim().length;
    if (!subject) { showErr('Add a subject before sending a test.'); return; }
    if (textLen === 0) { showErr('Body cannot be empty.'); return; }
    var att = opts.attachments || [];
    if (att.reduce(function (s, a) { return s + (a && a.base64 && !a.locked ? a.base64.length : 0); }, 0) > _MAX_ATTACH_TOTAL_B64) {
      showErr('Attachments too large combined (15 MB max).'); return;
    }
    var btn = opts.testBtn;
    var origLabel = btn ? btn.textContent : 'Send Test';
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    if (status) { status.classList.remove(ERR, OK); status.textContent = ''; }
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-09, Phase 3) — pluggable test send.
       When the caller supplies sendOverride (freight-balance), route the test through
       it (isTest:true → its own [TEST]/testRecipient handling) instead of /outlook-send. */
    if (typeof opts.sendOverride === 'function') {
      Promise.resolve(opts.sendOverride({ to: [], cc: [], bcc: [], subject: subject, bodyHtml: _editorHtml(quill), attachments: att, isTest: true, highImportance: !!opts.highImportance, fromMailbox: opts.fromMailbox || 'REP', fromSignatureHtml: opts.fromSignatureHtml || '' }))
        .then(function () {
          if (btn) { btn.disabled = false; btn.textContent = origLabel; }
          if (status) { status.classList.remove(ERR); status.classList.add(OK); status.textContent = '✓ Test sent to your inbox — check the formatting, then Send for real.'; }
        })
        .catch(function (err) {
          if (btn) { btn.disabled = false; btn.textContent = origLabel; }
          showErr('Test failed: ' + (err && err.message ? err.message : 'unknown'));
        });
      return;
    }
    var payload = {
      ref: opts.ref || undefined,
      test: true,
      to: [], cc: [], bcc: [],            // worker overrides → rep's own inbox
      subject: subject,
      body: _editorHtml(quill),
      fromMailbox: opts.fromMailbox || 'REP',   // test from the selected identity (Abby/orders@ or rep)
      /* CUSTOM_TESTTHREADALWAYS_BY_CLAUDE (Ryan 2026-10-10) - the email the live send replies on; the worker quotes it under the
         TEST (which itself still goes fresh to your own inbox). Empty on a new email. */
      replyToMessageId: opts.replyToMessageId || undefined,
      attachments: att.map(function (a) { return { name: a.name, contentType: a.contentType, base64: a.base64 }; }),
    };
    var workerBase = 'https://rfq-worker.ryan-29b.workers.dev';
    (window.RepPortal && window.RepPortal.Auth ? window.RepPortal.Auth.getAccessToken() : Promise.resolve(''))
      .then(function (token) {
        return fetch(workerBase + '/outlook-send', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + (token || ''), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error((j && j.error) || ('HTTP ' + r.status)); }, function () { throw new Error('HTTP ' + r.status); });
        return r.json();
      })
      .then(function () {
        if (btn) { btn.disabled = false; btn.textContent = origLabel; }
        if (status) { status.classList.remove(ERR); status.classList.add(OK); status.textContent = '✓ Test sent to your inbox — check the formatting, then Send for real.'; }
      })
      .catch(function (err) {
        if (btn) { btn.disabled = false; btn.textContent = origLabel; }
        showErr('Test failed: ' + (err && err.message ? err.message : 'unknown'));
      });
  }

  /* Fetch the rep's rendered signature HTML from /rep-settings and
     inject into the preview div. Best-effort — failure hides the
     wrap element rather than blocking the modal. */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ THE PREVIEW FOLLOWS THE SENDER. Ryan, on the
     samples composer: *"I checked the box for 'Show signature' and it showed the email signature for
     Ryan, despite the 'From' email at the top of the composer being set to Abby Rivers."* A composer
     locked to ONE sender skips `_installFromSelector` (which swaps signatures per selection) and
     lands here, and this loader only ever fetched the REP's signature. `mailbox` is the locked
     sender; 'ABBY' previews Abby's signature from the same /rep-settings payload.
     📌 PREVIEW ONLY — nothing was ever SENT wrong. This element sits outside the editor, so it is
     not part of the body the composer posts, and the sample request sends `includesSignature:false`
     so the worker appends Abby's own signature server-side (composeAbbyHtml). The sent email has
     always carried Abby's; the on-screen preview was the thing lying. */
  function _loadSignaturePreviewInto(previewEl, wrapEl, mailbox) {
    if (!previewEl || !(window.RepPortal && window.RepPortal.API)) {
      if (wrapEl) wrapEl.style.display = 'none';
      return;
    }
    window.RepPortal.API.get('/rep-settings').then(function(d) {
      var html = d ? (String(mailbox || '').toUpperCase() === 'ABBY' ? d.abbySignatureHtml : d.signatureHtml) : '';
      if (html) {
        previewEl.innerHTML = html;
      } else if (wrapEl) {
        wrapEl.style.display = 'none';
      }
    }).catch(function() {
      if (wrapEl) wrapEl.style.display = 'none';
    });
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — onboarded-teammate emails for the
     Bcc autocomplete. Pulls every onboarded rep's @blueskyfitsupply.com address from
     /rep-list-basic (accessible to any authenticated rep; excludes self + agency users).
     Cached module-wide so it's fetched once per page, not per modal open. Resolves to a
     sorted, lowercased array; failure degrades to [] (self-only autocomplete still works). */
  var _repEmailsCache = null;
  var _repEmailsPromise = null;
  function _loadRepEmails() {
    if (_repEmailsCache) return Promise.resolve(_repEmailsCache);
    if (_repEmailsPromise) return _repEmailsPromise;
    if (!(window.RepPortal && window.RepPortal.API)) return Promise.resolve([]);
    _repEmailsPromise = window.RepPortal.API.get('/rep-list-basic').then(function(d) {
      var out = [];
      if (d && d.success && Array.isArray(d.reps)) {
        d.reps.forEach(function(r) {
          var e = String((r && r.email) || '').trim().toLowerCase();
          if (e && /@blueskyfitsupply\.com$/.test(e) && out.indexOf(e) === -1) out.push(e);
        });
      }
      out.sort();
      _repEmailsCache = out;
      return out;
    }).catch(function() { _repEmailsCache = []; return []; });
    return _repEmailsPromise;
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — THE AUTOMATED SEQUENCE CONTROL
     ============================================
     Tick it, and the rungs below the one in the body are RENDERED NOW and posted with the
     send. The worker stores them and mails them on a cadence, each replying into this thread
     from this mailbox.

     ⛔ WHY PRE-RENDER RATHER THAN LET THE CRON BUILD THEM. The copy lives here, client-side,
     in assets/email-tpl-*.js. Porting it into the worker so a cron could rebuild it is exactly
     what happened to the post-delivery samples ladder, and it is why email-tpl-samples.js was
     deleted on 2026-08-19. One home for the prose, or two that drift.

     ⚠️ THE CADENCE STRING BELOW IS DESCRIPTIVE ONLY. email/sequence-jobs.js FLOWS is the
     authority on both the rung list and the intervals, and re-derives them from the template
     that actually sent. If these two ever disagree the customer still gets the right schedule
     and the rep read a wrong label — which is the right way round for a duplication that
     cannot be avoided (the template IDS are theme knowledge; the CLOCK is worker knowledge).

     ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 3b7daa183669, Ryan 2026-09-14) — AND IT IS MODULE SCOPE
     NOW, BECAUSE THERE ARE TWO COMPOSERS
     ============================================
     Ryan: *"reps who reply from the real email (the natural path) silently lose the auto
     ladder."* Clicking an inbound email in the Deal Feed and pressing Reply opens
     `_enterCompose`, a separate composer from `openComposeModal` — same template picker, same
     snippets, same Follow-up bell, and it never had this row. Inserting Follow-up 1 there armed
     nothing, with nothing on screen to say so.

     ⛔ THE FIX IS ONE CONTROL MOUNTED TWICE, NOT A SECOND COPY OF IT. A copy is how the two
     composers got here: every rule below (arming follows the BODY, the default is set only on a
     template CHANGE, the preview is dropped only when the flow id moves, a failed build STOPS
     the send) would have to be re-derived correctly in the other file, and the first one anybody
     got wrong would be silent — an armed ladder that never sends, or an untick that undoes
     itself. So the state, the sentences and the send path live here once.

     ⚠️ IT IS PARAMETERISED ON THE ID PREFIX AND NOTHING ELSE. The two composers already name
     their fields in parallel — `rqd-compose-seq-arm` / `rqd-reply-seq-arm`,
     `rqd-compose-followup-days` / `rqd-reply-followup-days`, `rqd-compose-send` /
     `rqd-reply-send`, `rqd-compose-status` / `rqd-reply-status` — so `P + 'seq-arm'` reaches the
     right box in both. A control that took a dozen element handles instead would drift the
     moment either composer renamed one.

     ⚠️ THE WORKER NEEDED NOTHING. Both composers POST to /outlook-reply, and that route has read
     `body.sequence` through armSequenceFromSend since 2026-08-20. This was only ever a missing
     control on one of two doors. */
  var _SEQ_FLOWS = {
    'follow-up-1': {
      flow: 'quote-followup',
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 3c9bef569140, Ryan 2026-09-14) — ⛔ THIS ONE STARTS
         TICKED NOW, AND IT REVERSES THE CALL DIRECTLY BELOW. On 2026-08-26 Ryan asked for
         samples only, and the note under `samples-confirm-1` recorded why: a quote chase is the
         rep's own judgement about a live negotiation. What changed is the evidence, not the
         argument — Ryan: *"Owners often forget to tick the box; the ladder never starts."* A
         ladder that never armed is not the rep exercising judgement, it is nobody deciding
         anything, and an opt-OUT still leaves the judgement with them on every single send.
         ⚠️ THE BOX IS STILL THE DECISION. `autoArm` moves the default, not the control: the rep
         unticks and sends one email, exactly as before. See `sync()`, which sets `checked` ONLY
         when the template changes, so an untick cannot be undone under them. */
      autoArm: true,
      rungs: ['follow-up-2', 'follow-up-3', 'follow-up-4'],
      cadence: '+7, +7, +10 days',
      /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — this flow may START LATER: follow-up 1
         itself goes N business days out, so a voicemail today and this email in two days are two
         separate touches. The quote ladder only; the samples one starts from a sample request and
         has no call-first step to leave room for. */
      startDelay: true,
    },
    'samples-confirm-1': {
      flow: 'samples-confirm',
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — ⛔ THIS ONE STARTS TICKED.
         Ryan: *"I thought it was automatic to send a deal through the automation when it's an
         admin and they request samples via the DD... I'd prefer a choice to cancel or opt out,
         otherwise no choice = automation begins."* The rest of the samples chain already works
         that way — Abby asks the vendor, captures the tracking, mails it to the customer, and
         the post-delivery ladder arms itself off the FedEx scan. This nudge was the one link
         that waited on somebody remembering a checkbox, and on RW-1335-A nobody did.
         ⚠️ QUOTE FOLLOW-UPS DELIBERATELY DID NOT GET THIS, UNTIL THEY DID. This note used to end
         "Adding `autoArm` to 'follow-up-1' is a product decision, not a tidy-up" — and it was
         right: Ryan made that product decision on 2026-09-14, and the reasoning is on the flow
         above. The sentence is kept rather than deleted because the rule it states still holds
         for the NEXT flow somebody wants to arm by default. */
      autoArm: true,
      rungs: ['samples-confirm-2', 'samples-confirm-3'],
      /* ⛔ THREE RUNGS, NOT FOUR, AND IT IS A DECISION. All three sample emails ask the same
         thing (confirm the address), and a fourth chase for a FREE box contradicts rung 3's
         "If your timing or needs have changed, no problem at all." */
      cadence: '+7, +7 days',
    },
  };

  /* The row, for a composer that is still assembling its markup as a string. Returns '' when
     the composer may not offer a sequence at all, so the caller concatenates unconditionally.
     ⚠️ RENDERED DISABLED AND WITHOUT A CADENCE. Both come from sync(), which runs whenever the
     inserted template changes — the cadence belongs to the SEQUENCE, so the control cannot name
     one until it knows which template is in the body. */
  function _seqControlHtml(prefix, allowed) {
    if (!allowed) return '';
    return ''
      + '<label class="rqd-email-modal__seq-opt" id="' + prefix + 'seq-row" style="display:flex;align-items:center;gap:6px;margin-top:8px;font-size:13px;opacity:.55;">'
      +   '<input type="checkbox" id="' + prefix + 'seq-arm" disabled>'
      +   '<span id="' + prefix + 'seq-label">⚡ Automated sequence</span>'
      +   '<button type="button" id="' + prefix + 'seq-preview" class="rqd-email-modal__seq-preview" hidden>Preview</button>'
      + '</label>'
      /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — when follow-up 1 itself goes. Its own row,
         NOT inside the <label> above: a click on a control inside a label can toggle the label's
         checkbox in some browsers, and this row must never untick the sequence. Hidden until a
         flow that allows a delayed start is in the body; see sync().
         ⚠️ Shown and hidden through style.display, never the `hidden` attribute: an inline
         `display:flex` beats `hidden`, and the row would never go away. */
      + '<div class="rqd-email-modal__seq-start" id="' + prefix + 'seq-start-row" style="display:none;align-items:center;gap:6px;margin-top:6px;font-size:13px;">'
      /* Ryan 2026-09-28: the 📅 lines up under the ⚡. An invisible copy of the row-above's checkbox
         takes exactly its width in every browser, where a hand-picked padding would drift. */
      +   '<input type="checkbox" tabindex="-1" aria-hidden="true" disabled style="visibility:hidden;">'
      +   '<span>📅 Send Follow-up 1:</span>'
      +   '<select id="' + prefix + 'seq-start" style="font-size:13px;padding:2px 6px;border-radius:6px;background:var(--rq-input-bg,#ffffff);color:var(--rq-text,#000F1C);border:1px solid var(--rq-border,#2a3a4d);">'
      +     '<option value="0">Now</option>'
      +     '<option value="1">Next business day</option>'
      +     '<option value="2">In 2 business days</option>'
      +     '<option value="3">In 3 business days</option>'
      +   '</select>'
      + '</div>'
      + '<div class="rqd-email-modal__seq-why" id="' + prefix + 'seq-why"></div>'
      + '<div class="rqd-email-modal__seq-preview-pane" id="' + prefix + 'seq-pane" hidden></div>';
  }

  /**
   * Wire the sequence control for one composer.
   *
   * @param opts.overlay    the composer's root element
   * @param opts.prefix     'rqd-compose-' | 'rqd-reply-' — every id and the Send button hang off it
   * @param opts.allowed    may this composer arm at all (admin, real deal, rep's own mailbox)
   * @param opts.getTplId   () => the template id currently in the body, or ''
   * @param opts.getTplCtx  () => the ctx to build the later rungs against
   */
  function _installSeqControl(opts) {
    var P = opts.prefix;
    var allowed = !!opts.allowed;
    var _rungs = null;          // the built rungs, or null until Preview / Send builds them
    var _builtFor = '';         // which flow `_rungs` was built for — see sync()
    /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — the start delay the body was last written
       for, so sync() can tell the composer to re-write it only when the delay actually changes. */
    var _lastStart = 0;
    var START_DEFAULT = 2;      // Ryan: "default to +2 business days"

    /* How many business days follow-up 1 is being held back, or 0 for "now".
       ⛔ ONLY WHILE THE SEQUENCE BOX IS TICKED (Ryan: a delayed follow-up 1 on its own is an email
       that can slip through the cracks). The worker refuses the combination too. */
    function startDays() {
      if (!allowed) return 0;
      var d = def();
      var box = $('seq-arm');
      var sel = $('seq-start');
      if (!d || !d.startDelay || !box || !box.checked || box.disabled || !sel) return 0;
      var n = parseInt(sel.value, 10);
      return (n >= 1 && n <= 3) ? n : 0;
    }
    /* The instant the delayed follow-up 1 is written for, or 0 when it goes now. */
    function startMs() {
      var n = startDays();
      return n ? _businessDaysAheadMs(n) : 0;
    }

    /* ⚠️ `opts.overlay` IS READ ON EVERY LOOKUP, NOT CAPTURED AT INSTALL. Both callers today
       install after their overlay exists, so capturing would work — and would break silently the
       first time somebody installed earlier, which is exactly the kind of ordering bug a shared
       helper should not be able to have. */
    function $(sfx) { return opts.overlay.querySelector('#' + P + sfx); }
    function def() { return _SEQ_FLOWS[opts.getTplId() || ''] || null; }

    function labelFor(id) {
      var t = (window.RQDTemplates && (window.RQDTemplates.list || []).filter(function (x) { return x.id === id; })[0]) || null;
      return (t && t.label) || id;
    }

    /* ============================================
       CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — THE BELL GOES QUIET WHILE THE SEQUENCE RUNS,
       AND THE CONTROL SAYS SO
       ============================================
       Ryan: *"there's also the box below it checked for 'Send the remaining 2 automatically'…
       should the 'in 3 days' [grey] out… only allowing admin to change it if they uncheck the
       send the remaining 2 automatically box?"*

       Yes for the CONTROL — while the sequence is arming, the interval is not a decision the rep
       is making, and a live dropdown invites one that changes nothing they can see.

       ⛔ BUT IT KEEPS ITS VALUE, AND THAT IS THE HALF THAT MATTERS. The obvious reading of the
       ask is to blank it to N/A. That would silence the SAFETY NET: the bell this dropdown arms
       is suppressed while the sequence sends and rings only if the sequence STALLS — a
       disconnected mailbox, a reassigned deal — which is the one moment a human has to be told.
       Blanking it would make a stalled sequence silent, which is the failure the note further
       down was written to prevent. So the control greys, the value stands, and the label says
       which of the two it is. Ryan picked this shape on 2026-08-30, having been shown both.

       ⚠️ A DISABLED <select> STILL REPORTS ITS `.value`, which is all the send path reads — so
       the backstop really is armed on send, and this is not a display trick over a field that
       has quietly stopped being sent.

       ⚠️ N/A IS LEFT ALONE. Appending 'backstop only' to 'N/A — no follow-up' would name a
       backstop that does not exist; the sentence under the row already warns about exactly that
       case, and it is the branch that still needs the rep to act.

       📌 UNTICKING RESTORES, and restores from `data-base-label` rather than by rebuilding the
       options — a rebuild would drop the rep's selection, which is the thing they are unticking
       the box in order to keep. */
    function lockFollowUp(locked) {
      var sel = $('followup-days');
      if (!sel) return;                                  // suppressFollowUp composers have no row
      Array.prototype.forEach.call(sel.options, function (o) {
        if (o.dataset.baseLabel == null) o.dataset.baseLabel = o.textContent;
        o.textContent = (locked && o.selected && o.value !== 'na')
          ? o.dataset.baseLabel + ' — backstop only'
          : o.dataset.baseLabel;
      });
      sel.disabled = !!locked;
      var wrap = sel.closest ? sel.closest('.rqd-email-modal__followup-opt') : null;
      if (wrap) {
        wrap.style.opacity = locked ? '.55' : '.9';
        wrap.title = locked
          ? 'The automated sequence owns the chasing while it runs. This bell is the backstop — untick the box below to set your own reminder.'
          : '';
      }
    }

    /* ⛔ ARMING FOLLOWS THE BODY, NOT THE CLICK. The control is live only while the template
       that starts a sequence is actually in the editor — a rep who inserted follow-up 1, ticked
       the box, then replaced it with a different template must not still be arming a quote
       ladder. Runs on every insert and on every re-render, so there is no state to get out of
       step with what is on screen. */
    function sync() {
      if (!allowed) return;
      var row = $('seq-row');
      var box = $('seq-arm');
      var lbl = $('seq-label');
      var why = $('seq-why');
      var prev = $('seq-preview');
      var pane = $('seq-pane');
      if (!row || !box || !lbl) return;

      var d = def();
      /* ⛔ THE PREVIEW IS DROPPED WHEN THE TEMPLATE CHANGES, AND ONLY THEN. A built preview is
         stale the moment a different template lands in the body, and dropping it is cheaper and
         more honest than working out whether it still applies.
         ⚠️ BUT THIS FUNCTION RUNS ON EVERY TICK OF THE CHECKBOX AND EVERY MOVE OF THE
         Follow-up DROPDOWN (both change the sentence it writes), and neither of those touches
         the copy. Clearing unconditionally would wipe a preview the rep had just opened — and
         worse, sendWith() would then silently rebuild it mid-send. Compare the id. */
      var _defId = d ? d.flow : '';
      if (_defId !== _builtFor) {
        _rungs = null;
        _builtFor = _defId;
        if (pane) { pane.hidden = true; pane.innerHTML = ''; }
        /* ⛔ THE DEFAULT IS SET HERE AND NOWHERE ELSE — on the template CHANGING, not on every
           call. This function also runs on every tick of the checkbox and every move of the
           Follow-up dropdown, so setting `checked` unconditionally would put the box straight
           back the instant the rep unticked it: an opt-out you cannot opt out of. */
        box.checked = !!(d && d.autoArm);
        /* CUSTOM_DELAYEDSTART_BY_CLAUDE — and the start delay's default, on the same rule: set when
           the template CHANGES, never on a later sync, or picking "Now" would snap back to +2. */
        var _selD = $('seq-start');
        if (_selD) _selD.value = (d && d.startDelay) ? String(START_DEFAULT) : '0';
      }

      /* CUSTOM_DELAYEDSTART_BY_CLAUDE — the start row follows the flow; the dropdown follows the box.
         Unticking the box greys it AND puts it back to "Now", so a re-tick never silently resumes a
         delay the rep had stopped looking at. */
      var _srow = $('seq-start-row');
      var _ssel = $('seq-start');
      if (_srow) _srow.style.display = (d && d.startDelay) ? 'flex' : 'none';
      if (_ssel) {
        _ssel.disabled = !(d && d.startDelay && box.checked);
        if (!box.checked) _ssel.value = '0';
      }

      if (!d) {
        box.checked = false;
        box.disabled = true;
        lockFollowUp(false);   /* nothing is arming, so the bell is the rep's own again */
        row.style.opacity = '.55';
        lbl.textContent = '⚡ Automated sequence';
        if (prev) prev.hidden = true;
        /* A disabled control usually earns a reason — it is the answer to the question that made
           the rep look at it, the same rule the template picker's greyed rows follow.

           ⚠️ BUT ONLY WHEN THERE IS A QUESTION. Ryan had this line removed on 2026-08-20: with an
           EMPTY body the row is the resting state of every compose window, so a standing
           instruction to go and insert a template was permanent furniture under a control nobody
           had asked about yet. The greyed "⚡ Automated sequence" says enough on its own.
           ⛔ The other branch stays. A rep who HAS inserted a template and finds the box still
           greyed has a real question, and that sentence is its answer. */
        if (why) {
          why.textContent = opts.getTplId()
            ? 'That template does not start a sequence. Insert Follow-up 1 or Sample order 1 to arm the rest.'
            : '';
        }
        _syncStart();
        return;
      }

      box.disabled = false;
      row.style.opacity = '1';
      lockFollowUp(box.checked);
      /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — renamed from "⚡ Send the remaining N
         automatically (...)", for both flows: the box is the SEQUENCE, and with a delayed start
         follow-up 1 is part of what it schedules, so "the remaining" stopped being true. */
      lbl.textContent = '⚡ Follow-up sequence (' + d.cadence + ')';
      if (prev) prev.hidden = false;
      if (why) {
        /* ⚠️ AN OPT-OUT HAS TO SAY SO. A box that is already ticked when the rep arrives reads as
           a box they ticked, so the sentence has to name what happens if they do nothing. */
        var base = (d.autoArm ? 'On by default — untick to send this one on its own. ' : '')
          + 'Each one replies into this thread from your mailbox. It stops on its own '
          + 'if they reply, you speak to them, or the deal closes.';
        /* ⛔ SAY WHAT HAPPENS TO THE FOLLOW-UP DROPDOWN ABOVE (Ryan 2026-08-20). Ticking this box
           does NOT clear that selector, and the two do not clash — but while the sequence is
           running the bell it arms is SUPPRESSED (the To Do cell counts down to the automated
           email instead), so a rep who picked "in 3 days" and is told nothing would reasonably
           expect a reminder on day 3 and never get one.

           ⚠️ THE SELECTION IS DELIBERATELY LEFT ALONE RATHER THAN FORCED TO N/A, because the bell
           it arms is the SAFETY NET: if the sequence stalls (Outlook disconnected, the deal
           reassigned) the suppression lifts and that bell is what puts the deal back in front of
           a human. Forcing it to N/A would make a stalled sequence silent, which is the failure
           this whole build is trying not to have. A shorter interval here is a TIGHTER net, not
           a conflict. */
        var fuSel = $('followup-days');
        var fuVal = fuSel ? fuSel.value : 'na';
        if (box.checked) {
          why.textContent = base + (fuVal === 'na'
            ? ' ⚠ Your Follow-up above is set to N/A, so nothing will chase you if the sequence '
              + 'stalls — pick an interval if you want a backstop.'
            /* ⚠️ SHORTER THAN IT WAS, BECAUSE THE DROPDOWN NOW CARRIES THE OTHER HALF. It used
               to spell out 'Your 🔔 Follow-up in N days becomes a BACKSTOP' because nothing on
               screen said so; the greyed row reads 'backstop only' now, and repeating the number
               here is the same fact in two places that can disagree. */
            : ' The bell only rings if the sequence stalls.');
        } else {
          why.textContent = base;
        }
        /* CUSTOM_DELAYEDSTART_BY_CLAUDE — say WHEN, because "Send" no longer means now. */
        var _sd = startDays();
        if (_sd) {
          why.textContent = 'Follow-up 1 sends ' + _mtWeekdayShort(_businessDaysAheadMs(_sd))
            + ' at about this time, and the rest follow ' + d.cadence + ' after it. ' + why.textContent;
        }
      }
      _syncStart();
    }

    /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — the two things a start delay changes outside
       this row: the Send button's words, and the email itself.
         · "Schedule for Wed" instead of "Send", so a rep never believes an email left when it did
           not. ⚠️ Not while the button is busy ("Sending…"), which owns its own label.
         · the BODY is written for the day it arrives. The composer owns the editor, so it is told
           through `opts.onStartChange` - and only when the delay actually changed, so a sync that
           runs on every keystroke-adjacent event never re-renders the body. */
    /* ⚠️ The button is only touched when the delay CHANGES, and "back to now" restores whatever it
       said before - a composer whose button reads something other than "Send" keeps its words. */
    var _restSend = null;
    function _syncStart() {
      var n = startDays();
      if (n === _lastStart) return;
      _lastStart = n;
      var sb = $('send');
      if (sb && !sb.disabled) {
        if (_restSend == null) _restSend = sb.textContent;
        sb.textContent = n ? 'Schedule for ' + _mtWeekdayShort(_businessDaysAheadMs(n)) : _restSend;
      }
      if (typeof opts.onStartChange === 'function') {
        try { opts.onStartChange(n, n ? _businessDaysAheadMs(n) : 0); } catch (_) { /* never break sync */ }
      }
    }
    /* The Send button's resting words right now - for the failure paths that reset it. */
    function sendLabel() {
      var n = startDays();
      return n ? 'Schedule for ' + _mtWeekdayShort(_businessDaysAheadMs(n)) : (_restSend || 'Send');
    }

    /* Build the later rungs. ⛔ `automated: true` is what drops the sentences that would go
       stale — see email-tpl-followups.js `_read` for which ones and why.
       ⚠️ Sequential, not Promise.all: each build reads the same deal chain and the kernel
       memoizes it per ref, so a parallel fan-out would fire N identical fetches. */
    function buildRungs() {
      var d = def();
      if (!d || !window.RQDTemplates) return Promise.resolve([]);
      var base = opts.getTplCtx();
      base.automated = true;
      var out = [];
      return d.rungs.reduce(function (chain, id) {
        return chain.then(function () {
          return window.RQDTemplates.build(id, base).then(function (built) {
            out.push({
              id: id,
              label: labelFor(id),
              subject: built.subject || '',
              bodyHtml: _paragraphsToEmailHtml(built.paragraphs),
            });
          });
        });
      }, Promise.resolve()).then(function () { return out; });
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — READ THE WHOLE SEQUENCE BEFORE ARMING IT.
       ⚠️ This is only honest because the copy is FROZEN: what the preview shows is the bytes
       that will send, three weeks later, not an approximation of them. A ladder that rebuilt
       its copy at send time could not offer this at all.
       ⚠️ Returns true when it handled the event, so a composer's click handler can bail out the
       way it does for its own buttons. */
    function handleClick(ev) {
      if (!ev || !ev.target || ev.target.id !== P + 'seq-preview') return false;
      var pane = $('seq-pane');
      if (!pane) return true;
      if (!pane.hidden) { pane.hidden = true; return true; }      // a second press closes it
      pane.hidden = false;
      pane.textContent = 'Building…';
      buildRungs().then(function (rungs) {
        _rungs = rungs;
        if (!rungs.length) { pane.textContent = 'Nothing to preview.'; return; }
        pane.innerHTML = rungs.map(function (r) {
          return '<div class="rqd-email-modal__seq-rung">'
            + '<div class="rqd-email-modal__seq-rung-head">' + _e(r.label) + '</div>'
            + (r.subject ? '<div class="rqd-email-modal__seq-rung-subj">' + _e(r.subject) + '</div>' : '')
            /* The body is our own generated markup, and it is the thing being reviewed, so it
               renders. ⚠️ Scoped by the class, not injected into the editor — a preview that
               shared the editor's styles would show Quill's zeroed margins and hide exactly the
               spacing defect PM³³ was about. */
            + '<div class="rqd-email-modal__seq-rung-body">' + r.bodyHtml + '</div>'
            + '</div>';
        }).join('');
      }).catch(function (e) {
        pane.textContent = 'Could not build the sequence (' + ((e && e.message) || e) + ').';
      });
      return true;
    }

    /* What rides out with the send, or null. ⛔ GATED ON `tplSent`, WHICH IS THE SAME TEST THE
       SEND LEDGER USES. A rep who selected all, deleted, and wrote their own email has not sent
       follow-up 1 — so arming three follow-ups that reply to it would be chasing a conversation
       that never started. The worker refuses too (it derives the flow from `templateId`), and
       this is the half that lets the composer say so. */
    function payload(tplSent) {
      var box = $('seq-arm');
      if (!tplSent || !box || !box.checked || !def() || !_rungs || !_rungs.length) return null;
      return { flow: def().flow, rungs: _rungs };
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — ⛔ THE RUNGS ARE BUILT BEFORE THE SEND,
       NOT DURING IT. `doSend` is synchronous up to the fetch, and the builds are async (each
       reads the deal chain). Building inside it would mean either an await that reshapes the
       whole function or a payload posted without them — and the second one sends rung 1 to a
       customer and arms nothing, which is the worst outcome available here.

       ⚠️ AND A FAILED BUILD STOPS THE SEND rather than quietly downgrading it. A rep who ticked
       the box is choosing a four-email sequence; sending them one email and saying nothing is
       not a smaller version of that, it is a different thing.

       ⚠️ THE SEND BUTTON'S RESTING LABEL IS READ, NOT ASSUMED. The two composers do not label it
       identically, and hard-coding 'Send' here would silently rename the other one's button the
       first time a build failed. */
    function sendWith(doSend, externalConfirmed) {
      var box = $('seq-arm');
      if (!box || !box.checked || !def()) return doSend(externalConfirmed);
      if (_rungs && _rungs.length) return doSend(externalConfirmed);

      var sendBtn = $('send');
      var restLabel = sendBtn ? sendBtn.textContent : 'Send';
      if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = 'Preparing…'; }
      return buildRungs().then(function (rungs) {
        _rungs = rungs;
        if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = restLabel; }
        if (!rungs.length) throw new Error('none of the follow-up emails could be built');
        return doSend(externalConfirmed);
      }).catch(function (e) {
        if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = restLabel; }
        var st = $('status');
        if (st) {
          st.textContent = 'Nothing was sent — the automated sequence could not be built ('
            + ((e && e.message) || e) + '). Untick it to send just this email.';
          st.classList.add('rqd-email-modal__compose-status--err');
        }
      });
    }

    return {
      allowed: allowed, def: def, sync: sync, buildRungs: buildRungs,
      handleClick: handleClick, payload: payload, sendWith: sendWith,
      /* CUSTOM_DELAYEDSTART_BY_CLAUDE */
      startDays: startDays, startMs: startMs, sendLabel: sendLabel,
    };
  }

  function _installChipFields(opts) {
    var overlay = opts.overlay;
    var kinds = opts.kinds || ['to', 'cc', 'bcc'];
    var esc = opts.esc;
    var getChips = opts.getChips;
    var setChips = opts.setChips;
    var onChange = opts.onChange || function() {};

    function renderChips(kind) {
      var field = overlay.querySelector('[data-kind="' + kind + '"]');
      if (!field) return;
      var input = field.querySelector('.rqd-email-modal__chip-input');
      var arr = getChips(kind);
      var chipsHtml = arr.map(function(addr, i) {
        var isValid = _CHIP_EMAIL_RX.test(addr);
        var cls = 'rqd-email-modal__chip' + (isValid ? '' : ' rqd-email-modal__chip--invalid');
        var title = isValid ? addr : 'Invalid: ' + addr;
        return '<span class="' + cls + '" role="listitem" data-idx="' + i + '" title="' + esc(title) + '">'
          + esc(addr)
          + '<button type="button" class="rqd-email-modal__chip-rm" data-idx="' + i + '" data-kind="' + kind + '" aria-label="Remove ' + esc(addr) + '">×</button>'
          + '</span>';
      }).join('');
      while (field.firstChild) field.removeChild(field.firstChild);
      field.insertAdjacentHTML('beforeend', chipsHtml);
      field.appendChild(input);
    }
    function addFromText(kind, raw) {
      if (!raw) return 0;
      var tokens = String(raw).split(/[,;\s]+/).map(function(t) { return t.trim(); }).filter(Boolean);
      if (tokens.length === 0) return 0;
      var arr = getChips(kind).slice();
      var added = 0;
      tokens.forEach(function(t) {
        if (_CHIP_EMAIL_RX.test(t)) { arr.push(t); added++; }
      });
      if (added > 0) {
        setChips(kind, arr);
        renderChips(kind);
      }
      return added;
    }
    function commitInputAsChip(kind) {
      var field = overlay.querySelector('[data-kind="' + kind + '"]');
      var input = field && field.querySelector('.rqd-email-modal__chip-input');
      if (!input) return false;
      var val = String(input.value || '').trim();
      if (!val) return false;
      var tokens = val.split(/[,;\s]+/).map(function(t) { return t.trim(); }).filter(Boolean);
      var valid = tokens.filter(function(t) { return _CHIP_EMAIL_RX.test(t); });
      var invalid = tokens.filter(function(t) { return !_CHIP_EMAIL_RX.test(t); });
      if (valid.length === 0) return false;
      setChips(kind, getChips(kind).slice().concat(valid));
      input.value = invalid.join(', ');
      renderChips(kind);
      return true;
    }
    function removeChip(kind, idx) {
      var arr = getChips(kind).slice();
      if (idx < 0 || idx >= arr.length) return;
      arr.splice(idx, 1);
      setChips(kind, arr);
      renderChips(kind);
    }
    function wireChipField(kind) {
      var field = overlay.querySelector('[data-kind="' + kind + '"]');
      if (!field) return;
      var input = field.querySelector('.rqd-email-modal__chip-input');
      if (!input) return;
      field.addEventListener('click', function(ev) {
        if (ev.target === field) { try { input.focus(); } catch (_) {} }
      });
      field.addEventListener('click', function(ev) {
        var rm = ev.target.closest('.rqd-email-modal__chip-rm');
        if (rm) {
          ev.stopPropagation();
          var idx = parseInt(rm.dataset.idx, 10);
          if (!isNaN(idx)) {
            removeChip(kind, idx);
            try { input.focus(); } catch (_) {}
            onChange();
          }
        }
      });
      input.addEventListener('keydown', function(ev) {
        if (ev.key === 'Enter' || ev.key === ',' || ev.key === ';') {
          ev.preventDefault();
          if (commitInputAsChip(kind)) onChange();
          return;
        }
        if (ev.key === 'Tab') {
          if (input.value && input.value.trim()) {
            if (commitInputAsChip(kind)) onChange();
          }
          return;
        }
        if (ev.key === 'Backspace' && input.value === '') {
          var arr = getChips(kind);
          if (arr.length > 0) {
            ev.preventDefault();
            removeChip(kind, arr.length - 1);
            onChange();
          }
        }
      });
      input.addEventListener('paste', function(ev) {
        var pasted = (ev.clipboardData || window.clipboardData).getData('text');
        if (pasted && /[,;\s]/.test(pasted)) {
          ev.preventDefault();
          if (addFromText(kind, pasted) > 0) onChange();
        }
      });
      input.addEventListener('blur', function() {
        if (input.value && input.value.trim()) {
          if (commitInputAsChip(kind)) onChange();
        }
      });
      input.addEventListener('input', onChange);
    }

    // Wire all kinds + render initial state
    kinds.forEach(function(k) { renderChips(k); wireChipField(k); });

    return {
      renderAll: function() { kinds.forEach(renderChips); },
      commitAll: function() { kinds.forEach(commitInputAsChip); },
      addFromText: addFromText,
      removeChip: removeChip,
    };
  }

  window.RQD.openEmailModal = function(entry, q, deps) {
    deps = deps || {};
    var esc = deps.esc;
    var formatNoteDate = deps.formatNoteDate;
    var fetchQuotes = deps.fetchQuotes;

      if (!entry || !entry.emailMeta) return;
      var existing = document.getElementById('rqd-email-modal');
      if (existing) existing.remove();

      var em = entry.emailMeta;

      /* Ryan 2026-05-21: opening the modal IS the "read" event. If
         this is an unread inbound email, fire /outlook-mark-read to
         (a) PATCH Graph so it reads as read in Outlook too, and
         (b) flip emailMeta.isRead on the stored KV record.
         Also: optimistically remove the 📧 envelope from the row's
         To Do cell so the UI reflects the read state immediately
         without waiting for a full re-render or the next cron sync. */
      if (em.direction === 'inbound' && em.isRead === false && em.messageId) {
        em.isRead = true; // optimistic local flip on in-memory allQuotes entry
        try {
          /* Flip the email card's --unread class off so the bottom-fade
             bar shrinks from "full-card-height" to "14px settled" and
             the status tint fades down — visual confirmation that this
             email was just read. Find every email card on this deal
             with the matching messageId (cards exist both in the
             current deal-feed render + potentially in earlier-revision
             cascades), drop the class on each. */
          var _cards = document.querySelectorAll('[data-msg="' + em.messageId.replace(/"/g, '\\"') + '"]');
          _cards.forEach(function(card) {
            card.classList.remove('rqd-email-entry--unread');
          });
          var _row = document.querySelector('tr[data-ref="' + (q.reference || '').replace(/"/g, '\\"') + '"]');
          if (_row) {
            // If this was the LAST unread inbound on the deal, drop the envelope.
            var stillUnread = (q.noteEntries || []).some(function(n) {
              return n && n.type === 'email' && n.emailMeta
                && n.emailMeta.direction === 'inbound'
                && n.emailMeta.isRead === false;
            });
            if (!stillUnread) {
              var _envEl = _row.querySelector('.rqd-todo-envelope');
              if (_envEl) _envEl.remove();
              var _textEl = _row.querySelector('.rqd-progress-cell__text--has-envelope');
              if (_textEl) _textEl.classList.remove('rqd-progress-cell__text--has-envelope');
              /* Ryan 2026-05-21: countdowns were inline-styled
                 display:none by the renderer because the envelope was
                 there. Now that the envelope is gone, clear the inline
                 style so the countdown reappears immediately (without
                 waiting for the next 2-min auto-refresh re-render). */
              _row.querySelectorAll('.rqd-followup-countdown, .rqd-hold-countdown').forEach(function(el) {
                if (el.style && el.style.display === 'none') el.style.display = '';
              });
            } else {
              // Decrement the badge count
              var _countEl = _row.querySelector('.rqd-todo-envelope__count');
              var _newCount = (q.noteEntries || []).filter(function(n) {
                return n && n.type === 'email' && n.emailMeta
                  && n.emailMeta.direction === 'inbound'
                  && n.emailMeta.isRead === false;
              }).length;
              if (_countEl) {
                if (_newCount <= 1) _countEl.remove();
                else _countEl.textContent = _newCount;
              }
            }
          }
        } catch (_) { /* DOM patch is cosmetic */ }
        try {
          if (window.RepPortal && window.RepPortal.API) {
            window.RepPortal.API.post('/outlook-mark-read', {
              ref: q.reference,
              messageId: em.messageId,
            }).catch(function() { /* silent — local flip already applied */ });
          }
        } catch (_) { /* */ }
      }

      // Find sibling emails in the same conversationId on this deal.
      // Sort most-recent-first to mirror the DEAL FEED order (newest at
      // top). Auto-scroll-to-clicked still works regardless of direction.
      var threadEntries = (em.conversationId && Array.isArray(q.noteEntries))
        ? q.noteEntries.filter(function(n) {
            return n && n.emailMeta && n.emailMeta.conversationId === em.conversationId;
          }).slice().sort(function(a, b) { return new Date(b.ts) - new Date(a.ts); })
        : [entry];
      var hasThread = threadEntries.length > 1;

      /* Ryan 2026-05-22: rich HTML body render path.
         When emailMeta.bodyHtml is present (Outlook delivered an HTML
         email — most branded customer emails are HTML), render it
         as sanitized HTML in the read modal so logos / tables /
         formatting / signatures show up properly. Falls back to the
         existing plain-text prepBodyHtml render when bodyHtml is
         null (plain-text emails or older entries from before the
         worker started capturing HTML).

         Strategy:
         1. Synchronous render emits the plain-text version as a
            placeholder (so the modal shows something immediately
            with no waiting on DOMPurify CDN load).
         2. Marker class + data attribute on the placeholder div let
            _hydrateRichBodies() find it after mount + async-swap the
            inner content to sanitized HTML.
         3. Remote images blocked by default — DOMPurify hook strips
            `src` from http(s) <img> tags, preserves as data-blocked-src
            for a future "Load images" toggle.
      */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — cid: inline-image
         resolution. The worker's /outlook-inline-images returns a message's
         inline attachments as base64; we convert to blob: URLs and swap
         them into the stripped cid: <img>s so emails render like Outlook.
         blob: URLs also give the browser's native right-click → open in
         new tab / save image for free. Cached per message so reopening the
         modal (or thread view rendering the same message) doesn't refetch. */
      var _inlineImgCache = {};
      function _fetchInlineImages(entryRef, msgId) {
        var key = entryRef + ':' + msgId;
        if (_inlineImgCache[key]) return _inlineImgCache[key];
        if (!(window.RepPortal && window.RepPortal.API)) return Promise.resolve([]);
        var p = window.RepPortal.API.get('/outlook-inline-images?ref=' + encodeURIComponent(entryRef) + '&messageId=' + encodeURIComponent(msgId))
          .then(function(d) { return (d && d.images) || []; })
          .catch(function() { delete _inlineImgCache[key]; return []; });
        _inlineImgCache[key] = p;
        return p;
      }
      function _resolveCidImages(holder, matchedEntry, msgId) {
        var cidImgs = Array.prototype.filter.call(
          holder.querySelectorAll('img[data-blocked-src]'),
          function(img) { return /^cid:/i.test(img.getAttribute('data-blocked-src') || ''); }
        );
        if (!cidImgs.length) return;
        // Hide while resolving — an empty src renders as a broken-image icon.
        cidImgs.forEach(function(img) { img.style.display = 'none'; });
        var entryRef = (matchedEntry && matchedEntry.originRef) || q.reference;
        _fetchInlineImages(entryRef, msgId).then(function(images) {
          if (!images.length) return;
          var byCid = {};
          images.forEach(function(im) {
            if (!im || !im.contentId || !im.dataBase64) return;
            try {
              var bin = atob(im.dataBase64);
              var bytes = new Uint8Array(bin.length);
              for (var bi = 0; bi < bin.length; bi++) bytes[bi] = bin.charCodeAt(bi);
              byCid[String(im.contentId).replace(/^<|>$/g, '').toLowerCase()] =
                URL.createObjectURL(new Blob([bytes], { type: im.contentType || 'image/png' }));
            } catch (e) { /* malformed base64 — skip this image */ }
          });
          cidImgs.forEach(function(img) {
            var cid = String(img.getAttribute('data-blocked-src') || '')
              .replace(/^cid:/i, '').replace(/^<|>$/g, '').toLowerCase();
            if (byCid[cid]) {
              img.setAttribute('src', byCid[cid]);
              img.removeAttribute('data-blocked-src');
              img.style.display = '';
            }
          });
        });
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — trim quoted history
         from rich HTML bodies in THREAD view. Every reply's HTML embeds the
         full chain before it (Outlook composition), so a 6-email thread
         rendered 6+5+4+3+2+1 messages — the thread "repeated from N-1 down"
         (invisible pre-backfill when most threads had one captured email).
         Each thread message should stand alone; the rest of the chain IS the
         other thread entries. Markers tried in order: Outlook web's
         #appendonsend divider, Gmail's .gmail_quote, then the classic
         "From: … Sent: … To:/Subject:" header block. The marker and
         everything after it (walking up nested wrappers) is removed. */
      /* (Reworked same day) — the trim now runs on the RAW email HTML
         BEFORE sanitization: DOMPurify strips id/class attributes, which
         killed the reliable markers (#divRplyFwdMsg, .gmail_quote, …) and
         left only a brittle text heuristic — customer replies kept their
         embedded quoted history and the thread showed every email twice.
         DOMParser.parseFromString is inert (nothing executes), so marker
         detection on raw HTML is safe; the trimmed HTML then goes through
         the normal sanitize pass. */
      function _removeMarkerAndRest(marker, root) {
        var parent = marker.parentNode;
        if (!parent) return;
        while (marker.nextSibling) parent.removeChild(marker.nextSibling);
        parent.removeChild(marker);
        // Walk UP removing only following-siblings of each ancestor (never
        // the ancestor itself — it holds content preceding the quote).
        var node = parent;
        while (node && node !== root) {
          var p2 = node.parentNode;
          if (!p2) break;
          while (node.nextSibling) p2.removeChild(node.nextSibling);
          node = p2;
        }
      }
      function _trimQuotedHistoryRaw(rawHtml) {
        /* (Reworked again same day, RW-1188-A) — a reply often carries
           MULTIPLE quote markers: the Outlook-desktop divider
           (border-top:#E1E1E1 + "From:") where the chain actually starts,
           AND a #divRplyFwdMsg from an older OWA hop buried DEEPER inside
           the chain. Trimming at the first marker found BY TYPE kept one
           quoted email in the box ("his reply + my email combined").
           Collect every candidate and cut at the EARLIEST in document
           order. */
        try {
          var doc = new DOMParser().parseFromString(String(rawHtml || ''), 'text/html');
          var body = doc.body;
          if (!body) return rawHtml;
          var markers = [];
          // 1. Client-specific containers/dividers (ids + classes intact here).
          body.querySelectorAll(
            '#divRplyFwdMsg, #appendonsend, .gmail_quote, .yahoo_quoted, blockquote[type="cite"], #x_divRplyFwdMsg, #x_appendonsend'
          ).forEach(function (m) { markers.push(m); });
          // 2. Outlook DESKTOP reply divider: a div styled with border-top
          //    whose text begins with the From:-header (the chain's true
          //    start in Outlook-composed replies — RW-1188-A class).
          body.querySelectorAll('div[style*="border-top"]').forEach(function (d) {
            var dt = (d.textContent || '').trim();
            if (/^From:\s/.test(dt)) markers.push(d);
          });
          // 3. <hr> divider followed by a From:-header block (OWA).
          var hrs = body.querySelectorAll('hr');
          for (var hi = 0; hi < hrs.length; hi++) {
            var after = '';
            var n = hrs[hi];
            var steps = 0;
            while (n && steps < 6 && after.length < 200) {
              n = n.nextElementSibling || (n.parentNode !== body ? n.parentNode.nextElementSibling : null);
              if (n) after += ' ' + (n.textContent || '');
              steps++;
            }
            if (/\bFrom:\s/.test(after) && /\b(Sent|Date):\s/.test(after)) markers.push(hrs[hi]);
          }
          // 4. Single element carrying the whole From/Sent/To header block.
          var candidates = body.querySelectorAll('div, p');
          for (var ci = 0; ci < candidates.length; ci++) {
            var t = (candidates[ci].textContent || '').trim();
            if (/^From:\s/.test(t) && /\b(Sent|Date):\s/.test(t) && /\b(To|Subject):\s/.test(t)
                && t.length < 600) { markers.push(candidates[ci]); break; }
          }
          // 5. "On <date>, <person> wrote:" intro line (Gmail/Apple/Yahoo).
          var cands2 = body.querySelectorAll('div, p, span');
          for (var c2 = 0; c2 < cands2.length; c2++) {
            var t2 = (cands2[c2].textContent || '').trim();
            if (/^On\s.+\bwrote:\s*$/.test(t2) && t2.length < 300) { markers.push(cands2[c2]); break; }
          }
          if (!markers.length) return rawHtml;
          // Earliest marker in document order wins (4 = DOCUMENT_POSITION_FOLLOWING:
          // candidate follows current best → keep best; 2 = PRECEDING → candidate
          // sits earlier → take it. A marker containing another reads as earlier,
          // which is correct — cut at the outermost boundary.)
          var earliest = markers[0];
          for (var mi = 1; mi < markers.length; mi++) {
            if (earliest.compareDocumentPosition(markers[mi]) & 2) earliest = markers[mi];
          }
          _removeMarkerAndRest(earliest, body);
          return body.innerHTML;
        } catch (e) {
          return rawHtml; // any parse hiccup → render untrimmed rather than blank
        }
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      var DOMPURIFY_CDN = 'https://cdn.jsdelivr.net/npm/dompurify@3.1.7/dist/purify.min.js';
      function _loadDOMPurify() {
        if (window.DOMPurify) return Promise.resolve();
        return new Promise(function(resolve, reject) {
          if (window.DOMPurify) return resolve();
          var s = document.createElement('script');
          s.src = DOMPURIFY_CDN;
          s.onload = function() { resolve(); };
          s.onerror = function() { reject(new Error('Failed to load DOMPurify from CDN')); };
          document.head.appendChild(s);
        });
      }
      function _sanitizeEmailHtml(html) {
        if (!window.DOMPurify) return null;
        /* Strip remote-image src by default — privacy + tracking pixel
           prevention, matches Gmail/Outlook web behavior. We preserve
           the original src in data-blocked-src so a future "Load
           images" toggle can restore it. cid: refs (inline attach-
           ments) can't render without fetching from Graph; null those
           too for v1. */
        if (!_sanitizeEmailHtml._hookInstalled) {
          window.DOMPurify.addHook('uponSanitizeAttribute', function(node, data) {
            if (data.attrName === 'src' && node.tagName === 'IMG') {
              var v = String(data.attrValue || '');
              /* Remote http(s) images now load by DEFAULT (Ryan 2026-06-09) — they're
                 almost always our own signature/logo images, and it's how the rep
                 would see the email in Outlook anyway. Only cid: inline-attachment
                 refs stay blocked: they can't render without a Graph fetch, so a kept
                 cid src would just show a broken-image icon. */
              if (/^cid:/i.test(v)) {
                node.setAttribute('data-blocked-src', v);
                data.attrValue = '';
                data.keepAttr = true;
              }
            }
            /* Strip on* event handlers + javascript: URLs — DOMPurify's
               default config already does this but belt-and-suspenders. */
            if (/^on/i.test(data.attrName)) data.keepAttr = false;
          });
          _sanitizeEmailHtml._hookInstalled = true;
        }
        return window.DOMPurify.sanitize(html, {
          ALLOWED_TAGS: ['a', 'b', 'i', 'u', 'em', 'strong', 'p', 'div', 'span', 'br',
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
            'table', 'thead', 'tbody', 'tr', 'td', 'th', 'caption',
            'img', 'blockquote', 'pre', 'code', 'hr', 'sub', 'sup',
            'font', 'small'],
          ALLOWED_ATTR: ['href', 'src', 'data-blocked-src', 'alt', 'title',
            'style', 'width', 'height', 'colspan', 'rowspan',
            'align', 'valign', 'border', 'cellpadding', 'cellspacing',
            'bgcolor', 'color', 'face', 'size'],
          ALLOW_DATA_ATTR: false,
          KEEP_CONTENT: true,
          ADD_ATTR: ['target', 'rel'],
        });
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-31) — `entryId` so a PROVISIONAL entry can
         hydrate. When a rep sends from the DD the composer writes a provisional feed entry
         immediately, carrying the real composed `bodyHtml` — but `messageId` is deliberately null
         (lib/outbound-email-entry.js: "NULL, NOT A PLACEHOLDER", because a fake Graph handle would
         satisfy a truthiness check and then 404). Hydration matched entries by messageId ALONE, so
         for the ~5 minutes until the Outlook sync upgrades the entry, a rich body sat in the entry,
         shipped to the browser, and rendered as the flattened one-line feed `text`.

         Ryan, on his own reply: *"this email that just sent shows everything wrapped onto 2 lines
         compacted together with no image"* — and he quoted `"→ Re: Blue Sky Quote RW-1333-A — My
         pleasure! Kind regards..."`, which is the feed `text` field verbatim, not a body render at
         all. The email itself was perfect in Outlook; only the preview was wrong, and only briefly. */
      function _buildBodyHtml(emE, fallbackText, entryId) {
        // Always render the plain-text fallback synchronously so the
        // modal shows content immediately. The hydration pass swaps in
        // sanitized HTML for entries that have it.
        var plain = prepBodyHtml(emE.bodyFull || fallbackText);
        if (emE.bodyHtml || (emE.bodyOnDemand && emE.messageId)) {
          // bodyOnDemand (payload slimming 2026-06-11): the dashboard ships
          // only the preview; the hydration pass fetches the stored body
          // from /outlook-email-body and swaps it in.
          return '<div class="rqd-email-modal__body-rich" data-rich-msg-id="' + esc(emE.messageId || '')
            + '" data-rich-entry-id="' + esc(entryId || '') + '">' + plain + '</div>';
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — plain-text/legacy
           entries (synced before HTML capture shipped 2026-05-22, or
           genuinely plain emails) get the SAME white paper card as rich
           ones — a thread was mixing white "Outlook" cards with dark
           portal-themed cards depending on each email's sync vintage
           (RW-1058-C). No data-rich-msg-id → hydration skips it. */
        return '<div class="rqd-email-modal__body-rich rqd-email-modal__body-rich--plain">' + plain + '</div>';
      }
      function _hydrateRichBodies() {
        var placeholders = overlay.querySelectorAll('.rqd-email-modal__body-rich');
        if (!placeholders.length) return;
        _loadDOMPurify().then(function() {
          placeholders.forEach(function(holder) {
            var msgId = holder.getAttribute('data-rich-msg-id');
            var entId = holder.getAttribute('data-rich-entry-id');
            /* Either handle identifies the entry. messageId is the durable one and stays the
               primary match; entry id covers the provisional window, where messageId is null by
               design and the entry id is the only thing that names it. The entry id also SURVIVES
               the upgrade (outbound-email-entry.js keeps it precisely so an open card is not
               orphaned), so the two never disagree about which entry they mean. */
            if (!msgId && !entId) return;
            var matchedEntry = threadEntries.find(function(t) {
              if (!t || !t.emailMeta) return false;
              if (msgId && t.emailMeta.messageId === msgId) return true;
              return !!entId && t.id === entId;
            });
            if (!matchedEntry) return;
            var _emM = matchedEntry.emailMeta;
            /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — on-demand body
               fetch (payload slimming): the dashboard response no longer
               carries bodyHtml/bodyFull. Fetch the stored body once and
               merge it onto the entry (entries are references into
               allQuotes, so this doubles as the session cache), then
               render. Entries that still carry a body skip the fetch. */
            /* ⛔ `!msgId` joins the short-circuit: /outlook-email-body is keyed by Graph message
               id, so a provisional has nothing to fetch WITH — and nothing to fetch, since the
               composer put the body straight on the entry. Without this the fetch would fire with
               an empty messageId and 400. */
            var _bodyReady = (_emM.bodyHtml || _emM.bodyFull || !_emM.bodyOnDemand || !msgId)
              ? Promise.resolve()
              : (function() {
                  var bRef = matchedEntry.originRef || q.reference;
                  if (!(window.RepPortal && window.RepPortal.API)) return Promise.resolve();
                  return window.RepPortal.API.get('/outlook-email-body?ref=' + encodeURIComponent(bRef) + '&messageId=' + encodeURIComponent(msgId))
                    .then(function(b) {
                      if (b && b.ok) {
                        _emM.bodyHtml = b.bodyHtml || null;
                        _emM.bodyFull = b.bodyFull || '';
                        if (b.bodyContentType) _emM.bodyContentType = b.bodyContentType;
                        _emM.bodyOnDemand = false;
                      }
                    })
                    .catch(function() { /* preview stays — better than blank */ });
                })();
            _bodyReady.then(function() { _renderRichBody(holder, matchedEntry, msgId); });
          });
        }).catch(function(err) {
          console.warn('[email-modal] DOMPurify load failed; plain-text fallback rendering used.', err);
        });
      }
      function _renderRichBody(holder, matchedEntry, msgId) {
            if (!matchedEntry.emailMeta.bodyHtml) {
              // Plain-text body (or fetch came back text-only): swap in the
              // full plain text on the same white card. No sanitize needed —
              // prepBodyHtml escapes everything.
              if (matchedEntry.emailMeta.bodyFull) {
                holder.innerHTML = prepBodyHtml(matchedEntry.emailMeta.bodyFull);
                holder.classList.add('rqd-email-modal__body-rich--loaded');
              }
              return;
            }
            // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — THREAD view:
            // trim quoted history from the RAW HTML before sanitizing (the
            // sanitizer strips the id/class markers detection relies on).
            var _rawForRender = matchedEntry.emailMeta.bodyHtml;
            if (holder.closest('.rqd-email-modal__body--thread')) {
              _rawForRender = _trimQuotedHistoryRaw(_rawForRender);
            }
            var safe = _sanitizeEmailHtml(_rawForRender);
            if (safe == null) return;
            holder.innerHTML = safe;
            holder.classList.add('rqd-email-modal__body-rich--loaded');
            // Mark all anchors target=_blank + rel for safe link opening
            holder.querySelectorAll('a[href]').forEach(function(a) {
              a.setAttribute('target', '_blank');
              a.setAttribute('rel', 'noopener noreferrer');
            });
            // http(s) images already loaded above; only cid: refs remain blocked and
            // there's no client-side "Load" action for those — so the toggle no longer
            // appears. Guard kept (filtering out cid) in case a loadable image is ever
            // blocked again in the future.
            var blocked = Array.prototype.filter.call(
              holder.querySelectorAll('img[data-blocked-src]'),
              function(img){ return !/^cid:/i.test(img.getAttribute('data-blocked-src') || ''); }
            );
            if (blocked.length && !holder.querySelector('.rqd-email-modal__load-images')) {
              var btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'rqd-email-modal__load-images';
              btn.textContent = '⊕ Load ' + blocked.length + ' blocked image' + (blocked.length === 1 ? '' : 's');
              btn.addEventListener('click', function() {
                blocked.forEach(function(img) {
                  var src = img.getAttribute('data-blocked-src');
                  if (src && !/^cid:/i.test(src)) {
                    img.setAttribute('src', src);
                    img.removeAttribute('data-blocked-src');
                  }
                });
                btn.remove();
              });
              holder.insertBefore(btn, holder.firstChild);
            }
            // (Quote-history trim moved to the RAW HTML pre-sanitize — see
            // _trimQuotedHistoryRaw above. Single-email view keeps the
            // quote: that's the email as sent.)
            // Fetch + swap cid: inline images for this message (renders like
            // Outlook). Runs on the trimmed body so we don't fetch images
            // that only existed in the removed quoted block.
            _resolveCidImages(holder, matchedEntry, msgId);
      }

      function prepBodyHtml(rawBody) {
        var text = String(rawBody || '')
          .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
          .replace(/\[(?:cid|signature|image|picture)[_:][^\]]+\]/gi, '')
          .replace(/<https?:\/\/[^>]*(?:urldefense|proofpoint)[^>]*>/gi, '')
          .replace(/<mailto:[^>]+>/gi, '')
          .replace(/([A-Za-z0-9\)\].,!?:;"'])(https?:\/\/)/g, '$1 $2')
          // Outlook's HTML→text conversion outputs `<li>` as "*"; convert
          // to proper bullet glyph so the list reads as a list.
          .replace(/^([ \t]*)\*[ \t]+/gm, '$1•  ')
          .replace(/^([ \t]*)-[ \t]+(?=[A-Za-z])/gm, '$1•  ')
          /* Ryan 2026-06-11: the "— quoted history hidden —" marker dropped —
             in thread view the history IS the other cards, so the marker was
             noise (and legacy stored bodies carry it baked in; stripped too). */
          .replace(/\n+(From:\s.+\n(Sent|Date):\s.+\n(To|Subject):\s[\s\S]*)$/i, '')
          .replace(/\n+(On\s.+\bwrote:\s*\n[\s\S]*)$/i, '')
          .replace(/—\s*quoted history hidden\s*—/g, '')
          .replace(/[ \t]+\n/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        var links = [];
        function saveLink(t, u) {
          links.push({ text: t.trim(), url: u });
          return '__LNK' + (links.length - 1) + 'END__';
        }
        var withMarkers = text
          .replace(/([^\s<\n][^<\n]*?)<(https?:\/\/[^>]+)>/g, function(m, t, u) { return saveLink(t, u); })
          .replace(/<(https?:\/\/[^>]+)>/g, function(m, u) { return saveLink(u, u); });
        var html = esc(withMarkers)
          .replace(/(https?:\/\/[^\s<>"']+)/g, '<a href="$1" target="_blank" rel="noopener" class="rqd-email-modal__link">$1</a>')
          .replace(/__LNK(\d+)END__/g, function(m, idxStr) {
            var lk = links[parseInt(idxStr, 10)];
            if (!lk) return '';
            return '<a href="' + esc(lk.url) + '" target="_blank" rel="noopener" class="rqd-email-modal__link">' + esc(lk.text) + '</a>';
          });
        /* Ryan 2026-05-21: bullet lines wrap with hanging indent so
           the wrapped text aligns under the first character after the
           bullet instead of falling back to column 0. Wrap each line
           starting with `•` in a div with `text-indent: -1.5em;
           padding-left: 1.5em`. Other lines stay as `<br>`-separated
           inline text. */
        var _lines = html.split('\n');
        var _out = '';
        for (var i = 0; i < _lines.length; i++) {
          var _ln = _lines[i];
          if (/^([ \t]*)•[ \t]+/.test(_ln)) {
            _out += '<div class="rqd-email-bullet-line">' + _ln + '</div>';
          } else {
            _out += _ln;
            if (i < _lines.length - 1) _out += '<br>';
          }
        }
        return _out;
      }

      function renderSingleEmail(e) {
        var emE = e.emailMeta;
        var isIn = emE.direction === 'inbound';
        var dIcon = isIn
            ? '<svg class="rqd-email-arrow rqd-email-arrow--inbound" width="18" height="18" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3 C 8 3, 8 8, 8 13 M5 10 L 8 13 L 11 10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>'
            : '<svg class="rqd-email-arrow rqd-email-arrow--outbound" width="18" height="18" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8 C 3 4, 8 4, 12 4 M9 1 L 12 4 L 9 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';
        var dLabel = isIn ? 'Received' : 'Sent';
        var fromHtml = emE.fromName
          ? esc(emE.fromName) + ' &lt;' + esc(emE.from || '') + '&gt;'
          : esc(emE.from || '');
        var toArrE = Array.isArray(emE.to) ? emE.to : [];
        var toNamesArrE = Array.isArray(emE.toNames) ? emE.toNames : [];
        var toHtml = toArrE.map(function(addr, i) {
          return toNamesArrE[i]
            ? esc(toNamesArrE[i]) + ' &lt;' + esc(addr) + '&gt;'
            : esc(addr);
        }).join(', ');
        var ccArrE = Array.isArray(emE.cc) ? emE.cc : [];
        var ccHtml = ccArrE.map(esc).join(', ');
        var bodyHtmlE = _buildBodyHtml(emE, e.text, e.id);
        return ''
          + '<div class="rqd-email-modal__head">'
          +   '<div class="rqd-email-modal__dir">' + dIcon + ' ' + dLabel + ' &middot; ' + esc(q.reference || '') + '</div>'
          +   '<h3 class="rqd-email-modal__subject">' + esc(emE.subject || '(no subject)') + '</h3>'
          +   '<div class="rqd-email-modal__meta">'
          +     '<div><span class="rqd-email-modal__label">From:</span> ' + fromHtml + '</div>'
          +     '<div><span class="rqd-email-modal__label">To:</span> ' + toHtml + '</div>'
          +     (ccHtml ? '<div><span class="rqd-email-modal__label">cc:</span> ' + ccHtml + '</div>' : '')
          +     '<div><span class="rqd-email-modal__label">Date:</span> ' + esc(formatNoteDate(e.ts)) + '</div>'
          +     (emE.mailFilter ? '<div>' + _mailFilterTag(emE) + '</div>' : '') // CUSTOM_MAILFILTERTAG_BY_CLAUDE
          +     (emE.hasAttachments
                  ? (function() {
                      /* Ryan 2026-05-21: clickable download links for
                         attachments. If we have full metadata
                         (attachments[] with ids), render each as a
                         link to /outlook-attachment. Otherwise fall
                         back to the name-list (older entries pre-
                         attachment-id capture) or generic placeholder.
                         The worker streams the raw bytes back with
                         proper Content-Disposition so the browser
                         downloads instead of navigating. */
                      /* Filter inline / signature attachments at render
                         time so existing email entries (which were
                         stored before the worker started filtering
                         isInline:true) don't surface image001.png /
                         ~WRDxxx.jpg / oledata.mso as if they were real
                         attachments. Pattern match catches the common
                         Outlook signature + Word doc icons. */
                      function _isInlineAttName(n) {
                        if (!n) return true;
                        if (/^image\d+\.(png|jpe?g|gif|bmp)$/i.test(n)) return true;
                        if (/^~WRD/i.test(n)) return true;
                        if (/^oledata\.mso$/i.test(n)) return true;
                        /* Filename heuristic — "signature" anywhere in
                           the name (Outlook web's behavior: cid-
                           referenced signature attachments are hidden
                           from the user even though they're file-typed
                           in MIME). */
                        if (/signature/i.test(n)) return true;
                        return false;
                      }
                      var atts = Array.isArray(emE.attachments)
                        ? emE.attachments.filter(function(a) { return a && a.name && !_isInlineAttName(a.name); })
                        : [];
                      if (atts.length > 0) {
                        // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — download must
                        // target the record the entry LIVES on (originRef for entries
                        // cascaded from another revision), else the ownership gate 404s.
                        var _attRef = entry.originRef || q.reference;
                        return '<div class="rqd-email-modal__attach">'
                          + '<span class="rqd-email-modal__attach-icon" aria-hidden="true">📎</span>'
                          + '<div class="rqd-email-modal__attach-chips">'
                          + atts.map(function(a) {
                              return '<button type="button" class="rqd-email-modal__attach-link" '
                                + 'data-ref="' + esc(_attRef) + '" '
                                + 'data-msg="' + esc(emE.messageId || '') + '" '
                                + 'data-aid="' + esc(a.id) + '" '
                                + 'data-name="' + esc(a.name) + '" '
                                + 'title="Download ' + esc(a.name) + '">⬇ ' + esc(a.name) + '</button>';
                            }).join('')
                          + '</div>'
                          + '</div>';
                      }
                      // Fallback: older entries with names but no ids
                      if (Array.isArray(emE.attachmentNames) && emE.attachmentNames.length > 0) {
                        return '<div class="rqd-email-modal__attach"><span class="rqd-email-modal__attach-icon" aria-hidden="true">📎</span> ' + emE.attachmentNames.map(esc).join(' ') + ' — open in Outlook to download</div>';
                      }
                      return '<div class="rqd-email-modal__attach"><span class="rqd-email-modal__attach-icon" aria-hidden="true">📎</span> Attachment(s) — open in Outlook to download</div>';
                    })()
                  : '')
          +   '</div>'
          + '</div>'
          + '<div class="rqd-email-modal__body">' + bodyHtmlE + '</div>';
      }

      function renderThreadView() {
        var rootSubject = (em.subject || '(no subject)').replace(/^(?:Re|Fwd?|FW):\s*/i, '').trim();
        var headHtml = ''
          + '<div class="rqd-email-modal__head">'
          +   '<div class="rqd-email-modal__dir">' + esc(q.reference || '') + ' &middot; thread of ' + threadEntries.length + '</div>'
          +   '<h3 class="rqd-email-modal__subject">' + esc(rootSubject) + '</h3>'
          + '</div>';
        var msgsHtml = '<div class="rqd-email-modal__body rqd-email-modal__body--thread">';
        threadEntries.forEach(function(te) {
          var teEm = te.emailMeta;
          var isCurrent = te.id === entry.id;
          var isIn = teEm.direction === 'inbound';
          var dIcon = isIn
            ? '<svg class="rqd-email-arrow rqd-email-arrow--inbound" width="18" height="18" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3 C 8 3, 8 8, 8 13 M5 10 L 8 13 L 11 10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>'
            : '<svg class="rqd-email-arrow rqd-email-arrow--outbound" width="18" height="18" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8 C 3 4, 8 4, 12 4 M9 1 L 12 4 L 9 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';
          var fromLine = teEm.fromName
            ? esc(teEm.fromName) + ' &lt;' + esc(teEm.from || '') + '&gt;'
            : esc(teEm.from || '');
          var toFirst = Array.isArray(teEm.to) && teEm.to[0]
            ? (Array.isArray(teEm.toNames) && teEm.toNames[0]
                ? esc(teEm.toNames[0]) + ' &lt;' + esc(teEm.to[0]) + '&gt;'
                : esc(teEm.to[0]))
            : '';
          var teBodyHtml = _buildBodyHtml(teEm, te.text, te.id);
          /* Ryan 2026-05-21: render the attachment row in thread view
             too (was only in single-email view). Same clickable-chip
             logic so reps can download attachments without leaving
             the thread modal. */
          var teAttachHtml = '';
          if (teEm.hasAttachments) {
            /* Same inline filter as the single-email view above. */
            function _isInlineAttNameTh(n) {
              if (!n) return true;
              if (/^image\d+\.(png|jpe?g|gif|bmp)$/i.test(n)) return true;
              if (/^~WRD/i.test(n)) return true;
              if (/^oledata\.mso$/i.test(n)) return true;
              if (/signature/i.test(n)) return true;
              return false;
            }
            var teAtts = Array.isArray(teEm.attachments)
              ? teEm.attachments.filter(function(a) { return a && a.name && !_isInlineAttNameTh(a.name); })
              : [];
            if (teAtts.length > 0) {
              // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — originRef for
              // cross-revision cascaded entries (see single-view note).
              var _teAttRef = te.originRef || q.reference;
              teAttachHtml = '<div class="rqd-email-modal__attach">'
                + '<span class="rqd-email-modal__attach-icon" aria-hidden="true">📎</span>'
                + '<div class="rqd-email-modal__attach-chips">'
                + teAtts.map(function(a) {
                    return '<button type="button" class="rqd-email-modal__attach-link" '
                      + 'data-ref="' + esc(_teAttRef) + '" '
                      + 'data-msg="' + esc(teEm.messageId || '') + '" '
                      + 'data-aid="' + esc(a.id) + '" '
                      + 'data-name="' + esc(a.name) + '" '
                      + 'title="Download ' + esc(a.name) + '">⬇ ' + esc(a.name) + '</button>';
                  }).join('')
                + '</div>'
                + '</div>';
            } else if (Array.isArray(teEm.attachmentNames) && teEm.attachmentNames.length > 0) {
              teAttachHtml = '<div class="rqd-email-modal__attach"><span class="rqd-email-modal__attach-icon" aria-hidden="true">📎</span> ' + teEm.attachmentNames.map(esc).join(' ') + ' — open in Outlook to download</div>';
            } else {
              teAttachHtml = '<div class="rqd-email-modal__attach"><span class="rqd-email-modal__attach-icon" aria-hidden="true">📎</span> Attachment(s) — open in Outlook to download</div>';
            }
          }
          msgsHtml += '<div class="rqd-email-modal__thread-msg' + (isCurrent ? ' rqd-email-modal__thread-msg--current' : '') + '" data-entry-id="' + esc(te.id || '') + '">';
          msgsHtml +=   '<div class="rqd-email-modal__thread-msg-head">';
          msgsHtml +=     '<span class="rqd-email-modal__thread-msg-dir">' + dIcon + '</span>';
          msgsHtml +=     '<span class="rqd-email-modal__thread-msg-from"><strong>' + fromLine + '</strong>' + (toFirst ? ' &rarr; ' + toFirst : '') + '</span>';
          msgsHtml +=     '<span class="rqd-email-modal__thread-msg-date">' + esc(formatNoteDate(te.ts)) + '</span>';
          msgsHtml +=   '</div>';
          msgsHtml +=   '<div class="rqd-email-modal__thread-msg-subject">' + esc(teEm.subject || '') + '</div>';
          if (teEm.mailFilter) msgsHtml += '<div>' + _mailFilterTag(teEm) + '</div>'; // CUSTOM_MAILFILTERTAG_BY_CLAUDE
          msgsHtml +=   teAttachHtml;
          msgsHtml +=   '<div class="rqd-email-modal__thread-msg-body">' + teBodyHtml + '</div>';
          msgsHtml += '</div>';
        });
        msgsHtml += '</div>';
        return headHtml + msgsHtml;
      }

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-21) — Phase 2 Reply
         buttons. Reply-all is enabled when the email has multiple
         recipients (>=2 To, or >=1 Cc) — single-recipient reply-all
         would be identical to reply, so we disable it. The "active"
         email being replied to is `entry` in single view OR the
         current message in thread view (entry is the most recent
         clicked email — same in both modes). */
      var _emActive = entry && entry.emailMeta ? entry.emailMeta : null;
      var _toCount = _emActive && Array.isArray(_emActive.to) ? _emActive.to.length : 0;
      var _ccCount = _emActive && Array.isArray(_emActive.cc) ? _emActive.cc.length : 0;
      var _canReplyAll = !!_emActive && (_toCount >= 2 || _ccCount >= 1);
      var _canReply = !!_emActive && !!q.reference && !!_emActive.messageId;
      function buildModalHtml() {
        return '<div class="rqd-daterange-modal rqd-email-modal" role="dialog" aria-label="Email">'
          + (hasThread ? renderThreadView() : renderSingleEmail(entry))
          + '<div class="rqd-daterange-modal__footer rqd-email-modal__footer">'
          + (_canReply
              ? '<button type="button" class="rqd-email-modal__reply-btn" id="rqd-email-modal-reply" title="Reply" data-reply-all="false">'
                + '<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style="vertical-align:middle;margin-right:6px;"><path d="M9 4 L 4 8 L 9 12 M4 8 L 13 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>'
                + 'Reply</button>'
              : '')
          + (_canReply && _canReplyAll
              ? '<button type="button" class="rqd-email-modal__reply-btn" id="rqd-email-modal-reply-all" title="Reply to all recipients" data-reply-all="true">'
                + '<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style="vertical-align:middle;margin-right:6px;"><path d="M9 4 L 4 8 L 9 12 M4 8 L 13 8 M6 1 L 1 5 L 6 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>'
                + 'Reply All</button>'
              : '')
          +   '<button type="button" class="rqd-pill" id="rqd-email-modal-close">Close</button>'
          + '</div>'
          + '</div>';
      }

      var overlay = document.createElement('div');
      overlay.id = 'rqd-email-modal';
      overlay.className = 'rqd-daterange-backdrop rqd-email-modal-backdrop';
      overlay.innerHTML = buildModalHtml();
      var portalWrap = document.querySelector('.rep-quote-portal-wrap') || document.body;
      portalWrap.appendChild(overlay);
      // Async-hydrate any HTML-bearing email bodies (plain-text shown
      // until DOMPurify loads + sanitizes the rich version).
      _hydrateRichBodies();

      function close() {
        overlay.remove();
        document.removeEventListener('keydown', onEsc);
        /* Ryan 2026-05-21: after Esc-close, focus returns to the
           originally-clicked email card and the browser paints a
           focus ring. Blur whatever's focused so the dashboard
           doesn't show that artifact. */
        if (document.activeElement && document.activeElement.blur) {
          try { document.activeElement.blur(); } catch (_) {}
        }
      }
      /* Ryan 2026-05-21 (rev): Esc-to-close re-enabled. Click-outside
         still disabled so the rep doesn't accidentally lose their
         spot when clicking the dim backdrop while skimming a long
         thread. Only Esc + the Close button dismiss the modal. */
      function onEsc(ev) { if (ev.key === 'Escape') close(); }
      document.addEventListener('keydown', onEsc);

      // Auto-scroll the clicked email into view (only meaningful in
      // thread mode where there are multiple cards stacked).
      if (hasThread) {
        var current = overlay.querySelector('.rqd-email-modal__thread-msg--current');
        if (current && current.scrollIntoView) {
          requestAnimationFrame(function() {
            current.scrollIntoView({ block: 'start', behavior: 'auto' });
          });
        }
      }

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-21) — Phase 2 reply
         subsystem. Loads Quill on demand, swaps the modal into compose
         mode, handles send + attachment + external-recipient confirm. */
      var _composeState = null;   // { quill, attachments: [{name,contentType,sizeBytes,base64}], replyAll, draftKey, autoSaveTimer }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 3b7daa183669, Ryan 2026-09-14) — the shared automated
         sequence control for THIS composer, or null before Reply has been pressed.
         ⛔ IT LIVES OUT HERE, BESIDE `_composeState`, AND NOT INSIDE `_enterCompose`. The reply
         composer is split the same way `_composeState` is: `_enterCompose` builds the pane, but
         `_doSend`, `_renderAttachList` and the overlay's click handler are all siblings of it in
         this scope. A `var` inside `_enterCompose` parses fine and then throws a ReferenceError
         the first time an attachment is added. Every read is `if (_replySeq)` guarded for the
         same reason `_composeState` is: none of those siblings can assume Reply was pressed. */
      var _replySeq = null;
      var MAX_ATTACH_BYTES = _MAX_ATTACH_BYTES_M;  // 10 MB a file (CUSTOM_BIGATTACH_BY_CLAUDE)
      var MAX_ATTACH_COUNT = 5;
      // _loadQuill is now module-scope (Phase B.5b — shared with openComposeModal).

      function _formatBytes(n) {
        if (n < 1024) return n + ' B';
        if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB';
        return (n / (1024 * 1024)).toFixed(1) + ' MB';
      }
      function _fileToBase64(file) {
        return new Promise(function(resolve, reject) {
          var r = new FileReader();
          r.onload = function() {
            var s = String(r.result || '');
            var i = s.indexOf(',');
            resolve(i >= 0 ? s.slice(i + 1) : s);
          };
          r.onerror = reject;
          r.readAsDataURL(file);
        });
      }

      /* CUSTOM_HOUSEDOCS_BY_CLAUDE (Ryan 2026-09-13) — ONE definition of "put a file on this
         email", now that the SNIPPET path can attach as well as the template path. It enforces the
         five-file cap AND repaints the chips; a second copy of it is how a file ends up attached
         with no pill on screen, which the rep would only discover after pressing Send. */
      function _replyAttachOne(att) {
        if ((_composeState.attachments || []).length >= MAX_ATTACH_COUNT) return false;
        _composeState.attachments = (_composeState.attachments || []).concat([att]);
        _renderAttachList();
        return true;
      }
      function _replyFx() {
        return { attach: _replyAttachOne, attachments: function () { return _composeState.attachments || []; } };
      }

      function _renderAttachList() {
        var host = overlay.querySelector('.rqd-email-modal__compose-attach-list');
        if (!host) return;
        if (_composeState) _composeState.attachments = _sortChainQuotes(_composeState.attachments || []);   // CUSTOM_CHAINSORT_BY_CLAUDE
        host.innerHTML = (_composeState.attachments || []).map(function(a, i) {
          return '<span class="rqd-email-modal__compose-chip" data-idx="' + i + '">'
            + '<span class="rqd-email-modal__compose-chip-name">📎 ' + esc(a.name) + '</span>'
            + '<span class="rqd-email-modal__compose-chip-size">' + _formatBytes(a.sizeBytes) + '</span>'
            + '<button type="button" class="rqd-email-modal__compose-chip-rm" data-idx="' + i + '" aria-label="Remove">×</button>'
            + '</span>';
        }).join('');
        // Ryan 2026-08-18 — keep an untouched template's quote/quotes in step with the list.
        if (_composeState && _composeState._tplBinding) {
          _retemplateIfPristine(_composeState._tplBinding, _composeState.quill, _replyTplCtx);
          /* That re-render may have rebuilt the body under a preview the rep already opened, so
             the control re-reads it too — the same pairing openComposeModal has. */
          if (_replySeq) _replySeq.sync();
        }
      }
      /* The template context for the reply composer: the deal, plus the live To/Cc so
         the greeting can still find a name on a deal the dashboard never loaded. */
      function _replyTplCtx() {
        var c = {
          ref: q.reference,
          attachments: (_composeState && _composeState.attachments) || [],
          toEmails: (_composeState && _composeState.chipState) ? _composeState.chipState.getTo() : [],
        };
        /* CUSTOM_DELAYEDSTART_BY_CLAUDE — written for the day a scheduled follow-up 1 arrives. */
        var start = (_replySeq && _replySeq.startMs) ? _replySeq.startMs() : 0;
        if (start) { c.nowMs = start; c.delayedStart = true; }
        return c;
      }
      /* Snippet feedback lands on the same status card templates use, so a rep has one place
         to look. Both kinds of message go here: "no first name on this deal" (a warning about
         copy that DID go in) and "attach the quote first" (a refusal, where nothing did). */
      function _replySnipStatus(msg, isErr) {
        var s = overlay.querySelector('#rqd-reply-status');
        if (!s) return;
        s.textContent = msg || '';
        s.classList.toggle('rqd-email-modal__compose-status--err', !!isErr);
        s.classList.toggle('rqd-email-modal__compose-status--tpl', !!msg && !isErr);
      }

      function _scheduleDraftSave() {
        if (!_composeState) return;
        if (_composeState.autoSaveTimer) clearTimeout(_composeState.autoSaveTimer);
        _composeState.autoSaveTimer = setTimeout(function() {
          try {
            if (!_composeState || !_composeState.quill) return;
            var html = _editorHtml(_composeState.quill);
            // Only a body with words is a draft (CUSTOM_DDMOBILE_BY_CLAUDE: emptied = removed)
            if (_bodyHasText(html)) {
              localStorage.setItem(_composeState.draftKey, html);
            } else {
              localStorage.removeItem(_composeState.draftKey);
            }
          } catch (_) { /* localStorage may be unavailable; non-fatal */ }
        }, 1500);
      }

      function _enterCompose(replyAll) {
        if (!_canReply || !_emActive) return;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 3b7daa183669, Ryan 2026-09-14) — may this composer
           offer an automated sequence? The same two questions `openComposeModal` asks, minus the
           ones that cannot arise here.
           ⛔ `session.admin === true`, NEVER `role` — role reads 'rep' for every real admin.
           ✅ AND THE ABBY EXCLUSION IS FREE ON THIS PATH, which is why there is no test for it.
           openComposeModal has to rule out `sendOverride` because it serves PO, freight-balance
           and Abby flows that leave the orders@ mailbox; this composer only ever posts a
           /outlook-reply, and that route sends from the DEAL OWNER'S OWN mailbox by construction
           (see the note above getValidAccessToken in the worker). There is no Abby send to
           exclude. ⚠️ If that ever stops being true, this gate is the line that has to know. */
        var _replySeqAllowed = (function () {
          try {
            if (!q || !q.reference || !_emActive.messageId) return false;
            var _s = (window.RepPortal && window.RepPortal.Auth.getSession()) || {};
            return _s.admin === true;
          } catch (_) { return false; }
        })();
        var headWrap = overlay.querySelector('.rqd-email-modal__head');
        var bodyWrap = overlay.querySelector('.rqd-email-modal__body');
        var footWrap = overlay.querySelector('.rqd-daterange-modal__footer');
        if (!bodyWrap || !footWrap) return;

        /* Ryan 2026-05-22: To / Cc / Bcc / Subject are now editable chip
           fields (was read-only display before). Pre-fill chips from the
           original email's context — rep can edit before send. Worker
           PATCHes the draft created by Graph createReply with these
           overrides, preserving thread integrity. */
        var EMAIL_RX_R = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        var origTo = Array.isArray(_emActive.to) ? _emActive.to : [];
        var origCc = Array.isArray(_emActive.cc) ? _emActive.cc : [];
        var toChipsR = [];
        var ccChipsR = [];
        var bccChipsR = [];   // Always empty initially — Bcc is private
        if (_emActive.direction === 'inbound') {
          if (_emActive.from) toChipsR.push(_emActive.from);
          if (replyAll) {
            // Pre-fill Cc with original To + Cc (excluding the from address
            // since they're already in our To). Dedupe.
            var seen = {};
            seen[_emActive.from || ''] = 1;
            origTo.concat(origCc).forEach(function(addr) {
              if (addr && !seen[addr]) { ccChipsR.push(addr); seen[addr] = 1; }
            });
          }
        } else {
          // Outbound — replying to own sent email. To = original recipients.
          toChipsR = origTo.slice();
          if (replyAll) ccChipsR = origCc.slice();
        }
        var subj = _emActive.subject || '';
        if (!/^re:/i.test(subj)) subj = 'Re: ' + subj;

        // Base ref (no revision letter) for the "+ ref" insert chip.
        // Matcher is chain-aware so the base ref alone threads across
        // all revisions.
        var _replyBaseRef = String(q.reference || '').replace(/-[A-Z]$/i, '');
        var _replyRefChipHtml = _replyBaseRef
          ? '<button type="button" class="rqd-email-modal__ref-chip" id="rqd-reply-ref-chip" '
              + 'data-ref="' + esc(_replyBaseRef) + '" '
              + 'title="Insert ' + esc(_replyBaseRef) + ' at the cursor — helps thread future replies back to this deal">'
              + '+ ' + esc(_replyBaseRef) + '</button>'
          : '';

        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Area 1 (Outlook-style): a "Load previous
           messages" button sits BELOW the reply. Clicking reveals the prior chain inline, in the
           composer's own scroll, so the rep reads it by scrolling down — no nested scroll box. The
           thread DOM is already rendered + hydrated in read mode; we relocate it into the host
           (below, after the compose pane mounts) and keep it hidden until the button is clicked. */
        var _priorCount = (typeof threadEntries !== 'undefined' && threadEntries && threadEntries.length)
          ? threadEntries.length : 1;
        // The "Load previous messages" button rides on the attach row (right side, same line as the
        // "Max 5 files…" hint); the chain itself loads in the hidden wrap at the bottom of the form.
        var _loadPrevBtnHtml = '<button type="button" id="rqd-reply-thread-toggle" data-count="' + _priorCount + '" aria-expanded="false" '
          + 'style="margin-left:auto;display:inline-flex;align-items:center;gap:6px;padding:5px 10px;background:var(--rq-input-bg,#f7f9fc);border:1px solid var(--rq-border,#2a3a4d);border-radius:8px;cursor:pointer;font-size:12px;font-weight:600;color:var(--rq-text,#000F1C);">'
          + '<span id="rqd-reply-thread-caret" style="display:inline-block;transition:transform .15s ease;">&#9656;</span>'
          + '<span id="rqd-reply-thread-label">Load previous messages (' + _priorCount + ')</span>'
          + '</button>';
        /* The chain loads inline at the BOTTOM of the composer (this wrap, hidden until the
           Load-previous button is clicked); content flows in the composer's own scroll so the rep
           scrolls down to read it (Outlook-style). */
        var _threadPanelHtml = ''
          + '<div class="rqd-email-modal__compose-prevthread" id="rqd-reply-thread-wrap" style="display:none;margin-top:14px;padding-top:12px;border-top:1px solid var(--rq-border,#2a3a4d);">'
          +   '<div id="rqd-reply-thread-host"></div>'
          + '</div>';

        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Area 3: attach button now rides ON the
           Quill toolbar (injected after mount, below), matching the New-Email composer. Same SVG
           paperclip + id so the existing #rqd-reply-attach-btn click handler still fires. */
        var _replyAttachBtnHtml = '<button type="button" id="rqd-reply-attach-btn" class="rqd-email-modal__tb-attach" title="Attach a file">'
          + '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
          + '<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>'
          + '</svg></button>';

        var composeHtml = ''
          + '<div class="rqd-email-modal__compose">'
          +   '<div class="rqd-email-modal__compose-meta">'
          +     '<div class="rqd-email-modal__compose-meta-row">'
          +       '<span class="rqd-email-modal__label">To:</span> '
          +       '<div class="rqd-email-modal__chip-field" data-kind="to" id="rqd-reply-to-field" role="list" aria-label="To recipients">'
          +         '<input type="text" class="rqd-email-modal__chip-input" id="rqd-reply-to" placeholder="recipient@example.com" autocomplete="off" spellcheck="false">'
          +       '</div>'
          +     '</div>'
          +     '<div class="rqd-email-modal__compose-meta-row">'
          +       '<span class="rqd-email-modal__label">Cc:</span> '
          +       '<div class="rqd-email-modal__chip-field" data-kind="cc" id="rqd-reply-cc-field" role="list" aria-label="Cc recipients">'
          +         '<input type="text" class="rqd-email-modal__chip-input" id="rqd-reply-cc" placeholder="(optional)" autocomplete="off" spellcheck="false">'
          +       '</div>'
          +     '</div>'
          +     '<div class="rqd-email-modal__compose-meta-row">'
          +       '<span class="rqd-email-modal__label">Bcc:</span> '
          +       '<div class="rqd-email-modal__chip-field" data-kind="bcc" id="rqd-reply-bcc-field" role="list" aria-label="Bcc recipients">'
          +         '<input type="text" class="rqd-email-modal__chip-input" id="rqd-reply-bcc" placeholder="(optional, hidden from other recipients)" autocomplete="off" spellcheck="false">'
          +       '</div>'
          +     '</div>'
          +     '<div class="rqd-email-modal__compose-meta-row">'
          +       '<span class="rqd-email-modal__label">Subject:</span> '
          +       '<input type="text" class="rqd-email-modal__compose-input" id="rqd-reply-subject" value="' + esc(subj) + '">'
          +       _replyRefChipHtml
          +     '</div>'
          +   '</div>'
          +   '<div class="rqd-email-modal__compose-merged">'
          +     '<div class="rqd-email-modal__compose-editor-wrap"><div id="rqd-reply-editor"></div></div>'
          +     '<div class="rqd-email-modal__compose-sig-wrap" id="rqd-reply-sig-wrap">'
          +       '<div class="rqd-email-modal__compose-sig-preview" id="rqd-reply-sig-preview">Loading signature…</div>'
          +     '</div>'
          +   '</div>'
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — signature preview hidden by default
             (more room to type); this checkbox toggles the VISUAL preview only — the signature
             is always in the sent email. Mirrors the New-Email composer toggle. */
          +   '<label class="rqd-email-modal__sig-toggle" title="Preview your signature here. It is always included in the sent email either way." style="display:flex;align-items:center;gap:6px;margin-top:8px;font-size:12px;opacity:.85;cursor:pointer;user-select:none;"><input type="checkbox" id="rqd-reply-sig-toggle"> Show signature</label>'
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Area 3: the Attach BUTTON now lives on
             the Quill toolbar (injected after mount). This row keeps only the hidden input, the
             size hint, and the attachment pills — same layout as the New-Email composer. */
          +   '<div class="rqd-email-modal__compose-attach">'
          +     '<input type="file" multiple id="rqd-reply-file-input" style="display:none;">'
          +     '<span class="rqd-email-modal__compose-attach-hint">Max 5 files, 10 MB each, 15 MB total</span>'
          +     _loadPrevBtnHtml
          +     '<div class="rqd-email-modal__compose-attach-list"></div>'
          +   '</div>'
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-28) — Follow-up cadence opt-in.
             Pre-checked; arms/advances the deal's follow-up to its next rung on
             send (server-authoritative). Day count shown reflects the deal's
             current cadence position (7/7/7/10/10/10). */
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-28) — Follow-up is MANDATORY
             on send (no opt-out). The dropdown only changes the interval; default
             is the cadence's current rung (7→7→7→10→10→10). */
          +   '<label class="rqd-email-modal__followup-opt" style="display:flex;align-items:center;gap:6px;margin-top:10px;font-size:13px;opacity:.9;">'
          +     '🔔 Follow-up set: '
          +     '<select id="rqd-reply-followup-days" style="font-size:13px;padding:2px 6px;border-radius:6px;background:var(--rq-input-bg,#ffffff);color:var(--rq-text,#000F1C);border:1px solid var(--rq-border,#2a3a4d);">'
          +       (function(){var d=_cadenceDefaultDays(q);return [2,3,5,7,10,14,21,30,45].map(function(x){return '<option value="'+x+'"'+(x===d?' selected':'')+'>in '+x+' days</option>';}).join('');})()
          +     '</select>'
          +     ' after you send'
          +   '</label>'
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 3b7daa183669, Ryan 2026-09-14) — the automated
             sequence row, the same one openComposeModal renders, in the same slot under the
             Follow-up bell and for the same reason: that row says "remind ME in N days", this one
             says "or let the system write the next three". Ryan found it on one composer and not
             the other, and replying to the customer's actual email is the path reps take.
             ⚠️ THIS COMPOSER'S BELL HAS NO 'N/A' OPTION (follow-up is mandatory on a reply), so
             the "nothing will chase you" warning in the shared control simply never fires here.
             That is correct, not a gap: there is always a backstop on this path. */
          +   _seqControlHtml('rqd-reply-', _replySeqAllowed)
          +   '<div class="rqd-email-modal__compose-status" id="rqd-reply-status"></div>'
          +   _threadPanelHtml
          + '</div>';

        // Hide read-mode head; the prior message body(ies) are RELOCATED into the collapsible
        // "Previous messages" panel below (Area 1) rather than hidden.
        if (headWrap) headWrap.style.display = 'none';
        var collapseBtns = overlay.querySelectorAll('.rqd-email-modal__thread-btn');
        collapseBtns.forEach(function(b) { b.style.display = 'none'; });

        var composePane = document.createElement('div');
        composePane.className = 'rqd-email-modal__compose-host';
        composePane.innerHTML = composeHtml;
        (overlay.querySelector('.rqd-email-modal') || overlay).insertBefore(composePane, footWrap);

        /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 3b7daa183669, Ryan 2026-09-14) — wire the shared
           sequence control now that its row is in the document. Everything it knows about this
           composer is the id prefix; see `_installSeqControl` at module scope.
           ⚠️ `_composeState` DOES NOT EXIST YET on the first pass — Quill mounts async below —
           so the template-id getter has to tolerate that rather than assume a binding. It is read
           live on every sync, so the first real answer arrives with the first template pick. */
        _replySeq = _installSeqControl({
          overlay: overlay,
          prefix: 'rqd-reply-',
          allowed: _replySeqAllowed,
          getTplId: function () {
            return (_composeState && _composeState._tplBinding && _composeState._tplBinding.id) || '';
          },
          getTplCtx: function () { return _replyTplCtx(); },
          /* CUSTOM_DELAYEDSTART_BY_CLAUDE — re-write the body for the day it will arrive. */
          onStartChange: function (n, whenMs) {
            if (!_composeState) return;
            _onStartChange(n, whenMs, _composeState._tplBinding, _composeState.quill, _replyTplCtx, _replySnipStatus);
          },
        });
        /* The resting state, once: a composer reopened on a restored draft can already HAVE a
           template in the body, and the row would otherwise sit greyed with the wrong reason
           under it. */
        _replySeq.sync();
        /* ⛔ AND IT RE-READS ON EITHER INPUT, because the sentence under it describes BOTH — the
           same binding openComposeModal has. */
        (function _wireReplySeqInputs() {
          var b = overlay.querySelector('#rqd-reply-seq-arm');
          var f = overlay.querySelector('#rqd-reply-followup-days');
          if (b) b.addEventListener('change', function () { _replySeq.sync(); });
          if (f) f.addEventListener('change', function () { _replySeq.sync(); });
          /* CUSTOM_DELAYEDSTART_BY_CLAUDE — the start dropdown re-reads the same way. */
          var s = overlay.querySelector('#rqd-reply-seq-start');
          if (s) s.addEventListener('change', function () { _replySeq.sync(); });
        })();

        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Area 1: move the already-rendered +
           hydrated prior email(s) into the collapsible panel so the rep can scroll the chain
           while composing a reply. In thread mode this is .rqd-email-modal__body--thread; in
           single mode the .rqd-email-modal__body — and since the thread body carries BOTH
           classes, querySelector('.rqd-email-modal__body') grabs the right element either way.
           Panel stays collapsed until the rep expands it. */
        var _priorBodyEl = overlay.querySelector('.rqd-email-modal__body');
        var _threadHostEl = composePane.querySelector('#rqd-reply-thread-host');
        if (_priorBodyEl && _threadHostEl) {
          _priorBodyEl.style.display = '';
          /* The relocated element carries .rqd-email-modal__body, which sets max-height:60vh +
             overflow-y:auto — a SECOND scroll container nested inside our panel host, so a long
             chain wouldn't scroll cleanly (the inner box swallows the wheel). Neutralize it so
             #rqd-reply-thread-host (max-height:320 + overflow:auto) is the single scroll box. */
          _priorBodyEl.style.maxHeight = 'none';
          _priorBodyEl.style.overflowY = 'visible';
          _threadHostEl.appendChild(_priorBodyEl);
        } else {
          var _lpBtn = composePane.querySelector('#rqd-reply-thread-toggle');
          if (_lpBtn) _lpBtn.style.display = 'none';   // nothing prior → no Load button
        }

        // Swap footer buttons: hide Reply/Reply-all/Close, add Cancel + Send
        footWrap.innerHTML = ''
          + '<button type="button" class="rqd-pill rqd-email-modal__compose-cancel" id="rqd-reply-cancel">Cancel</button>'
          + '<button type="button" class="rqd-pill" id="rqd-reply-test" title="Send this email to your own inbox to preview formatting — does not touch the deal or the customer.">Send Test</button>'
          + '<button type="button" class="rqd-email-modal__compose-send" id="rqd-reply-send">Send</button>';

        var draftKey = 'rqd-reply-draft:' + (q.reference || '') + ':' + (_emActive.messageId || '');

        /* Phase B.5a: chip subsystem delegated to module-scope
           _installChipFields (shared with openComposeModal). State
           lives here (toChipsR / ccChipsR / bccChipsR); the shared
           helper wires all DOM behavior. */
        var _replyChipApi = _installChipFields({
          overlay: overlay,
          kinds: ['to', 'cc', 'bcc'],
          esc: esc,
          getChips: function(kind) {
            if (kind === 'cc') return ccChipsR;
            if (kind === 'bcc') return bccChipsR;
            return toChipsR;
          },
          setChips: function(kind, arr) {
            if (kind === 'cc') ccChipsR = arr;
            else if (kind === 'bcc') bccChipsR = arr;
            else toChipsR = arr;
          },
          onChange: _scheduleDraftSave,
        });

        // Subject input change → draft save
        var _subjEl = overlay.querySelector('#rqd-reply-subject');
        if (_subjEl) _subjEl.addEventListener('input', _scheduleDraftSave);

        // Signature preview — Phase B.5b shared helper.
        _loadSignaturePreviewInto(
          overlay.querySelector('#rqd-reply-sig-preview'),
          overlay.querySelector('#rqd-reply-sig-wrap')
        );

        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — "Show signature" toggle (visual only;
           signature always sent). Class-gated so CSS !important wins over the loader's inline. */
        (function () {
          var _sigCb = overlay.querySelector('#rqd-reply-sig-toggle');
          var _sigWrapR = overlay.querySelector('#rqd-reply-sig-wrap');
          if (_sigCb && _sigWrapR) _sigCb.addEventListener('change', function () { _sigWrapR.classList.toggle('rqd-sig-shown', _sigCb.checked); });
        })();

        // Expose chip arrays + commit helper on _composeState so _doSend
        // can read them at send time. commitAll comes from the shared
        // chip API installed above (Phase B.5a).
        var _replyChipState = {
          getTo: function() { return toChipsR.slice(); },
          getCc: function() { return ccChipsR.slice(); },
          getBcc: function() { return bccChipsR.slice(); },
          commitAll: _replyChipApi.commitAll,
        };

        _loadQuill().then(function() {
          _registerQuillSizes();   // Phase B.5b — shared module helper
          var quill = new Quill('#rqd-reply-editor', {
            theme: 'snow',
            modules: {
              toolbar: {
                container: [
                  ['bold', 'italic', 'underline'],
                  [{ color: [] }, { background: [] }],
                  [{ size: ['9pt', '10pt', '11pt', '12pt', '14pt', '16pt', '18pt', '24pt', '36pt'] }],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link'],
                  ['clean'],
                ],
                handlers: {
                  // Route the link button through the un-clippable native prompt.
                  link: function () { _promptQuillLink(this.quill); },
                },
              },
              clipboard: { matchVisual: false },
              /* Ryan 2026-05-22: Ctrl+Alt+H toggles yellow highlight on
                 selected text — same shortcut Outlook uses. shortKey
                 maps to Ctrl on Win / Cmd on Mac for portability. */
              keyboard: {
                bindings: {
                  toggleHighlight: {
                    key: 'H',
                    altKey: true,
                    shortKey: true,
                    handler: function(range) {
                      if (!range || range.length === 0) return true;
                      var current = this.quill.getFormat(range).background;
                      this.quill.format('background', current ? false : '#ffff00');
                      return false;
                    },
                  },
                  // Ctrl/Cmd+K → insert link via native prompt (see _promptQuillLink).
                  insertLink: {
                    key: 'K',
                    shortKey: true,
                    handler: function(range) { _promptQuillLink(this.quill); return false; },
                  },
                },
              },
            },
            placeholder: 'Type your reply…',
          });
          _applyPlainPaste(quill); // paste = plain text + links only
          _floatQuillTooltip(quill); // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-10-27) - the link bubble overlaps the window
          _installProductMentions(quill, function () {   // "@" -> product picker, inserts a linked title
            return {
              ref: q.reference,
              toEmails: (_composeState && _composeState.chipState) ? _composeState.chipState.getTo() : [],
            };
          });
          _installDashAutocorrect(quill);  // "--" -> em dash, Word/Outlook style
          _installSpellAutocorrect(quill); // sentence capitals + one-obvious-fix spelling (admins)
          /* "#" -> snippet picker, inserts one or two sentences at the caret. ctx read LIVE
             (same reason as the "@" picker): a snippet that names the attached proposals has
             to see the attachment list as it stands when the rep picks, not when the modal
             opened. */
          _installSnippetPicker(quill, _replyTplCtx, _replySnipStatus, function (m) { _replySnipStatus(m, true); }, _replyFx);

          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Area 3: drop the Attach button onto
             the Quill toolbar (same line as bold/italic), matching the New-Email composer so the
             control is consistently on TOP instead of in a row below the editor. Same id so the
             existing #rqd-reply-attach-btn click handler fires. */
          var _rTb = overlay.querySelector('.rqd-email-modal__compose-editor-wrap .ql-toolbar')
            || overlay.querySelector('.rqd-email-modal__compose .ql-toolbar')
            || overlay.querySelector('.ql-toolbar');
          if (_rTb && !_rTb.querySelector('#rqd-reply-attach-btn')) {
            /* Ryan 2026-08-18 — 📝 templates ride alongside the paperclip here too, so the
               shared composer layer keeps behaving the same way in both modals. Ryan
               2026-08-19 — and ⚡ snippets after it: whole-email, then sentence. */
            _rTb.insertAdjacentHTML('beforeend', _replyAttachBtnHtml + _templateBtnHtml('rqd-reply-template-btn') + _snippetBtnHtml('rqd-reply-snippet-btn'));
          }
          /* CUSTOM_AICOMPOSER_BY_CLAUDE — "Draft it" on the reply pane: answers the email this pane opened on. */
          _installDraftIt(quill, _rTb, {
            getRef: function () { return q.reference; },
            mode: 'reply',
            getMessageId: function () { return (_emActive && _emActive.messageId) || ''; },
            /* CUSTOM_COMPOSERPHASE6_BY_CLAUDE — the same approved-wording hooks as the new-email window. Subject and
               threading are not wired on a reply, the 📝 menu's own rule here. */
            wording: function () { return _draftItWording(_replyTplCtx()); },
            useWording: function (kind, id) {
              if (!_composeState || !_composeState.quill) return Promise.reject(new Error('the editor is still loading'));
              if (kind === 'snippet') {
                try { _composeState.quill.focus(); } catch (_) {}
                return _applySnippet(_composeState.quill, id, _replyTplCtx(), _replySnipStatus, function (m) { _replySnipStatus(m, true); }, _replyFx());
              }
              if (!_composeState._tplBinding) _composeState._tplBinding = _newTemplateBinding();
              return _applyTemplate(_composeState._tplBinding, _composeState.quill, id, _replyTplCtx(), function (w) { _replySnipStatus(w, false); }, { attach: _replyAttachOne })
                .then(function (out) { if (_replySeq) _replySeq.sync(); return out; });
            },
          });

          // Restore draft if present
          try {
            var draft = localStorage.getItem(draftKey);
            if (draft) {
              quill.root.innerHTML = _stripQuillCursor(draft);   // a pre-09-24 draft may still carry the marker
              var status = overlay.querySelector('#rqd-reply-status');
              if (status) status.textContent = 'Draft restored';
            }
          } catch (_) {}

          _composeState = {
            quill: quill,
            attachments: [],
            replyAll: !!replyAll,
            draftKey: draftKey,
            autoSaveTimer: null,
            chipState: _replyChipState,
          };

          quill.on('text-change', _scheduleDraftSave);

          // Focus the editor
          setTimeout(function() { if (_mayAutoFocus(quill.root)) quill.focus(); }, 50);   // CUSTOM_DDMOBILE_BY_CLAUDE

          // Ctrl/Cmd+Enter to send
          quill.root.addEventListener('keydown', function(ev) {
            if ((ev.ctrlKey || ev.metaKey) && ev.key === 'Enter') {
              ev.preventDefault();
              /* ⛔ THE SAME DOOR AS THE BUTTON. A keyboard send that called `_doSend` directly
                 would post the email with `templateId` and NO rungs — the customer gets
                 follow-up 1 and nothing follows it, and the rep sees a tick. Every send path in
                 this composer goes through the sequence, which is a pass-through when the box is
                 not ticked. */
              if (_replySeq) _replySeq.sendWith(_doSend, false); else _doSend(false);
            }
          });
        }).catch(function(err) {
          var status = overlay.querySelector('#rqd-reply-status');
          if (status) {
            status.textContent = 'Editor failed to load: ' + (err && err.message ? err.message : 'unknown');
            status.classList.add('rqd-email-modal__compose-status--err');
          }
        });
      }

      function _exitCompose() {
        if (_composeState && _composeState.autoSaveTimer) clearTimeout(_composeState.autoSaveTimer);
        _composeState = null;
        // Re-render the modal entirely — parse buildModalHtml into a temp
        // div, then transplant its inner markup so the outer overlay +
        // its event listeners stay intact.
        var temp = document.createElement('div');
        temp.innerHTML = buildModalHtml();
        var newInner = temp.firstChild;
        var oldInner = overlay.querySelector('.rqd-email-modal');
        if (newInner && oldInner) {
          oldInner.innerHTML = newInner.innerHTML;
        }
      }

      function _doSend(externalConfirmed) {
        if (!_composeState || !_composeState.quill) return;
        var sendBtn = overlay.querySelector('#rqd-reply-send');
        var cancelBtn = overlay.querySelector('#rqd-reply-cancel');
        var status = overlay.querySelector('#rqd-reply-status');
        var html = _editorHtml(_composeState.quill);
        var textLen = (_composeState.quill.getText() || '').trim().length;

        // Auto-commit any half-typed text in chip fields as a final chip
        // before sending. Then read the chip arrays + subject.
        if (_composeState.chipState) _composeState.chipState.commitAll();
        var to = _composeState.chipState ? _composeState.chipState.getTo() : [];
        var cc = _composeState.chipState ? _composeState.chipState.getCc() : [];
        var bcc = _composeState.chipState ? _composeState.chipState.getBcc() : [];
        var subjEl = overlay.querySelector('#rqd-reply-subject');
        var subject = subjEl ? String(subjEl.value || '').trim() : '';

        if (to.length === 0) {
          if (status) {
            status.textContent = 'At least one To recipient required.';
            status.classList.add('rqd-email-modal__compose-status--err');
          }
          return;
        }
        if (!subject) {
          if (status) {
            status.textContent = 'Subject cannot be empty.';
            status.classList.add('rqd-email-modal__compose-status--err');
          }
          return;
        }
        if (textLen === 0) {
          if (status) {
            status.textContent = 'Reply body cannot be empty.';
            status.classList.add('rqd-email-modal__compose-status--err');
          }
          return;
        }
        /* CUSTOM_COMPOSERCHECK_BY_CLAUDE — what the check before Send reads (see the new-email window's doSend). */
        var _rq = _composeState.quill;
        var _pc = q.reference ? {
          ref: q.reference, mode: 'reply', messageId: (_emActive && _emActive.messageId) || '',
          to: to, cc: cc, subject: subject, html: html, text: _rq.getText() || '',
          attachments: (_composeState.attachments || []).map(function (a) { return a && a.name; }).filter(Boolean),
        } : null;
        var _pcDone = !!(_pc && _checkPassed(_rq, _pc));
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-29) — Forgot-attachment guard:
           body/subject mentions an attachment but none is attached. */
        var _attWarn = (_composeState.attachments || []).length === 0
            && /\battach(ed|ing|ment|ments)?\b/i.test(((_composeState.quill.getText() || '') + ' ' + subject));
        if (_attWarn && !_pcDone
            && !window.confirm('Your email mentions an attachment, but no file is attached.\n\nClick Cancel to add one, or OK to send anyway.')) {
          return;
        }
        /* Ryan 2026-05-29: recipient + attachment-size guards before send. */
        if (Array.prototype.some.call(overlay.querySelectorAll('.rqd-email-modal__chip-input'), function (i) { return i && i.value && i.value.trim(); })) {
          if (status) { status.textContent = "Finish or remove the recipient you're still typing."; status.classList.add('rqd-email-modal__compose-status--err'); }
          return;
        }
        var _badR = to.concat(cc, bcc).filter(function (a) { return !_CHIP_EMAIL_RX.test(a); });
        if (_badR.length) {
          if (status) { status.textContent = 'Invalid email address: ' + _badR[0]; status.classList.add('rqd-email-modal__compose-status--err'); }
          return;
        }
        if ((_composeState.attachments || []).reduce(function (s, a) { return s + (a && a.base64 ? a.base64.length : 0); }, 0) > _MAX_ATTACH_TOTAL_B64) {
          if (status) { status.textContent = 'Attachments too large combined (15 MB max). Remove some or share a link.'; status.classList.add('rqd-email-modal__compose-status--err'); }
          return;
        }
        /* CUSTOM_COMPOSERCHECK_BY_CLAUDE (Ryan 2026-10-18) — the check before Send (Fix / Send anyway). */
        if (_pc && !_pcDone) {
          _pc.attachWarned = _attWarn;
          _pc.sendBtn = sendBtn;
          _pc.host = sendBtn ? sendBtn.parentNode : null;
          _pc.send = function () { _doSend(externalConfirmed); };
          if (!_checkBeforeSend(_rq, _pc)) return;
        }
        if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = 'Sending…'; }
        if (!_composeState.idemKey) {
          _composeState.idemKey = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : (Date.now() + '-' + Math.random().toString(36).slice(2));
        }
        if (cancelBtn) cancelBtn.disabled = true;
        if (status) {
          status.textContent = '';
          status.classList.remove('rqd-email-modal__compose-status--err');
          status.classList.remove('rqd-email-modal__compose-status--tpl');
        }

        var payload = {
          ref: q.reference,
          messageId: _emActive.messageId,
          body: html,
          subject: subject,
          to: to,
          cc: cc,
          bcc: bcc,
          replyAll: !!_composeState.replyAll,
          idempotencyKey: _composeState.idemKey,
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-28) — External recipients
             are the norm for BSF, so always pre-confirm: the external-recipient
             "send anyway?" prompt is pure friction here. (Server guard stays as
             a backstop for any other caller.) */
          externalConfirmed: true,
          armFollowUp: true,
          followUpDays: parseInt((document.getElementById('rqd-reply-followup-days') || {}).value, 10) || undefined,
          attachments: (_composeState.attachments || []).map(function(a) {
            return { name: a.name, contentType: a.contentType, base64: a.base64 };
          }),
        };
        /* CUSTOM_REPLYREF_BY_CLAUDE (Ryan 2026-10-28, RW-1549-A) — the "+ RW-1549" chip is the one subject change a
           reply carries: the worker adds the deal ref to the thread's subject (replySubjectWithRef). Asked for only when
           the box now holds the ref and the email being answered did not. */
        var _refBaseR = String(q.reference || '').replace(/-[A-Z]$/i, '').toUpperCase();
        if (_refBaseR && subject.toUpperCase().indexOf(_refBaseR) !== -1
            && String((_emActive && _emActive.subject) || '').toUpperCase().indexOf(_refBaseR) === -1) {
          payload.replySubjectRef = _refBaseR;
        }
        /* Which template produced this body, for the once-per-customer ledger. Only when
           what we inserted is still recognisably in there - see _templateSurvived. */
        var _tplSent = _templateSurvived(_composeState._tplBinding, _composeState.quill)
          ? _composeState._tplBinding.id : '';
        if (_tplSent) payload.templateId = _tplSent;
        /* CUSTOM_AICOMPOSER_BY_CLAUDE — this email started as a Draft it draft: the worker keeps the pair. */
        var _replyAi = _aiDraftFor(_composeState.quill);
        if (_replyAi) payload.aiDraft = _replyAi;
        /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE — the floor plan on the attached quote, for the ledger.
           Not gated on the template: a rep's own email around the attached quote still sent it. */
        var _replyLayout = _quoteLayoutPayload(_composeState.attachments, q.reference);
        if (_replyLayout) payload.quoteLayout = _replyLayout;
        var _replyQuoteRefs = _attachedQuoteRefs(_composeState.attachments); // CUSTOM_QUOTESENTCLOCK_BY_CLAUDE
        if (_replyQuoteRefs.length) payload.quoteRefs = _replyQuoteRefs;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 3b7daa183669, Ryan 2026-09-14) — the rest of the
           sequence, pre-rendered. Gated on `_tplSent` inside the control, which is the same test
           the send ledger uses. */
        var _replySeqPayload = _replySeq ? _replySeq.payload(_tplSent) : null;
        if (_replySeqPayload) payload.sequence = _replySeqPayload;
        /* CUSTOM_DELAYEDSTART_BY_CLAUDE — only alongside the sequence it starts. */
        if (_replySeqPayload && _replySeq.startDays() > 0) payload.startDelayDays = _replySeq.startDays();
        var workerBase = 'https://rfq-worker.ryan-29b.workers.dev';
        (window.RepPortal && window.RepPortal.Auth ? window.RepPortal.Auth.getAccessToken() : Promise.resolve(''))
          .then(function(token) {
            return fetch(workerBase + '/outlook-reply', {
              method: 'POST',
              headers: {
                'Authorization': 'Bearer ' + (token || ''),
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload),
            });
          })
          .then(function(r) {
            if (r.status === 412) {
              return r.json().then(function(j) {
                var addrs = (j.externalRecipients || []).join('\n  • ');
                var msg = 'Reply-all includes ' + (j.externalRecipients || []).length + ' external recipient(s):\n\n  • ' + addrs + '\n\nSend anyway?';
                if (window.confirm(msg)) {
                  return _doSend(true);   // retry with externalConfirmed
                }
                throw new Error('Cancelled');
              });
            }
            if (!r.ok) {
              return r.json().then(function(j) {
                throw new Error((j && j.error) || ('HTTP ' + r.status));
              }, function() {
                throw new Error('HTTP ' + r.status);
              });
            }
            return r.json();
          })
          .then(function(resp) {
            if (!resp) return;   // user-cancelled confirm path
            /* Success: clear draft, close modal, repaint the row.
               ⛔ The old note here said "the next 5-min cron will pull the actual sent message
               in" — that WAS the behaviour and it is what Ryan hit. The worker now writes the
               feed entry and advances the cadence before answering (2026-08-19), so the reply
               is on the deal the moment this runs; the cron only upgrades it with its Graph
               ids later. */
            try { localStorage.removeItem(_composeState.draftKey); } catch (_) {}
            _composeState.idemKey = null; // sent — next send gets a fresh key
            /* Close the row in THIS page too. The worker's ledger write is eventually
               consistent, and a rep can reopen the composer well inside that window. */
            if (_tplSent && window.RQDTemplates && window.RQDTemplates.markSent) {
              try { window.RQDTemplates.markSent(q.reference, _tplSent); } catch (_) {}
            }
            try { if (window.RPDash && window.RPDash.onEmailSent) window.RPDash.onEmailSent(q.reference, resp); } catch (_) {}
            if (status) {
              /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 3b7daa183669, Ryan 2026-09-14) — SAY WHAT
                 HAPPENED TO THE SEQUENCE, not just to this email. A rep who ticked the box and
                 read a bare "Sent." has no way to tell an armed ladder from a refused one, and
                 the refusals are real (audience gate, a ladder already live on this deal).
                 Same sentence openComposeModal prints. */
              var _rSeqOut = resp && resp.sequence;
              var _rSeqNote = '';
              if (_rSeqOut && _rSeqOut.armed) {
                _rSeqNote = ' ' + _rSeqOut.count + ' more email' + (_rSeqOut.count === 1 ? '' : 's')
                  + ' armed, the first on ' + _seqFmtDay(_rSeqOut.sendAfter) + '.';
              } else if (_rSeqOut && _rSeqOut.why) {
                _rSeqNote = ' ⚠ The email sent, but the sequence was NOT armed: ' + _rSeqOut.why;
              }
              /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — ⛔ A SCHEDULED EMAIL MUST NOT SAY
                 "Sent". This composer never held anything before; a scheduled follow-up 1 is the
                 first thing it can queue, so it gets the main composer's rule. */
              status.textContent = resp.held
                ? '📅 Scheduled — Follow-up 1 sends ' + _seqFmtWhen(resp.sendAfter)
                  + '. The rest of the follow-up sequence starts from that send. Nothing has gone to the customer yet.'
                : '✓ Sent.' + _rSeqNote;
              status.classList.add('rqd-email-modal__compose-status--ok');
            }
            setTimeout(function() {
              close();
              if (typeof fetchQuotes === 'function') {
                try { fetchQuotes({ quiet: true }); } catch (_) {}
              }
            }, (resp && resp.held) ? 4000 : 1200);
          })
          .catch(function(err) {
            /* CUSTOM_DELAYEDSTART_BY_CLAUDE — back to "Schedule for Wed" when a delay is set. */
            if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = (_replySeq && _replySeq.sendLabel) ? _replySeq.sendLabel() : 'Send'; }
            if (cancelBtn) cancelBtn.disabled = false;
            if (status && err && err.message !== 'Cancelled') {
              status.textContent = 'Send failed: ' + err.message;
              /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (2026-08-29) — DROP THE SUCCESS CLASS FIRST.
                 These only ADDED --err, so a failure that followed a successful Send Test kept
                 the green --ok already on the element and the error rendered GREEN. Ryan hit
                 exactly that sequence: test, then Send. The test path has always cleared its
                 opposite (see ERR/OK at the top); the failure paths never did. */
              status.classList.remove('rqd-email-modal__compose-status--ok');
              status.classList.add('rqd-email-modal__compose-status--err');
            } else if (status && err && err.message === 'Cancelled') {
              status.textContent = '';
            }
          });
      }

      // File-input change → ingest selected files as base64 attachments
      overlay.addEventListener('change', function(ev) {
        if (ev.target && ev.target.id === 'rqd-reply-file-input' && _composeState) {
          var files = Array.from(ev.target.files || []);
          var status = overlay.querySelector('#rqd-reply-status');
          var oversized = files.filter(function(f) { return f.size > MAX_ATTACH_BYTES; });
          if (oversized.length) {
            if (status) {
              status.textContent = 'Skipped (over 10 MB): ' + oversized.map(function(f) { return f.name; }).join(', ');
              /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (2026-08-29) — DROP THE SUCCESS CLASS FIRST.
                 These only ADDED --err, so a failure that followed a successful Send Test kept
                 the green --ok already on the element and the error rendered GREEN. Ryan hit
                 exactly that sequence: test, then Send. The test path has always cleared its
                 opposite (see ERR/OK at the top); the failure paths never did. */
              status.classList.remove('rqd-email-modal__compose-status--ok');
              status.classList.add('rqd-email-modal__compose-status--err');
            }
            files = files.filter(function(f) { return f.size <= MAX_ATTACH_BYTES; });
          }
          var room = MAX_ATTACH_COUNT - (_composeState.attachments || []).length;
          if (files.length > room) {
            if (status && !oversized.length) {
              status.textContent = 'Only first ' + room + ' file(s) added — max ' + MAX_ATTACH_COUNT + ' attachments.';
              /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (2026-08-29) — DROP THE SUCCESS CLASS FIRST.
                 These only ADDED --err, so a failure that followed a successful Send Test kept
                 the green --ok already on the element and the error rendered GREEN. Ryan hit
                 exactly that sequence: test, then Send. The test path has always cleared its
                 opposite (see ERR/OK at the top); the failure paths never did. */
              status.classList.remove('rqd-email-modal__compose-status--ok');
              status.classList.add('rqd-email-modal__compose-status--err');
            }
            files = files.slice(0, room);
          }
          Promise.all(files.map(function(f) {
            return _fileToBase64(f).then(function(b64) {
              return {
                name: f.name,
                contentType: f.type || 'application/octet-stream',
                sizeBytes: f.size,
                base64: b64,
              };
            });
          })).then(function(arr) {
            _composeState.attachments = (_composeState.attachments || []).concat(arr);
            _renderAttachList();
            // Reset input so the same file can be re-added if removed
            ev.target.value = '';
          });
        }
      });

      overlay.addEventListener('click', function(ev) {
        // Reply / Reply-all → enter compose mode
        if (ev.target.id === 'rqd-email-modal-reply' || ev.target.closest('#rqd-email-modal-reply')) {
          _enterCompose(false);
          return;
        }
        if (ev.target.id === 'rqd-email-modal-reply-all' || ev.target.closest('#rqd-email-modal-reply-all')) {
          _enterCompose(true);
          return;
        }
        // Cancel compose → exit back to read mode
        if (ev.target.id === 'rqd-reply-cancel') {
          // Ryan 2026-05-29: attachments live in memory only — warn before they're lost.
          if (_composeState && (_composeState.attachments || []).length > 0
              && !window.confirm('Discard this reply? Your attached file(s) will be removed (the typed text is saved as a draft).')) {
            return;
          }
          // Keep draft in localStorage; rep can reopen and finish later
          _exitCompose();
          return;
        }
        // Send compose
        if (ev.target.id === 'rqd-reply-send') {
          /* ⛔ THROUGH THE SEQUENCE, NOT STRAIGHT TO _doSend. The later rungs are built here,
             BEFORE the send, and a failed build stops it — see `sendWith`. An untouched box is a
             pass-through, so every non-sequence reply behaves exactly as it did. */
          if (_replySeq) _replySeq.sendWith(_doSend, false); else _doSend(false);
          return;
        }
        if (ev.target.id === 'rqd-reply-test') {
          _sendTestEmail({
            ref: q.reference,
            quill: _composeState && _composeState.quill,
            replyToMessageId: (_emActive && _emActive.messageId) || '', // CUSTOM_TESTTHREADALWAYS_BY_CLAUDE - the reply pane always replies on this message
            subjectEl: overlay.querySelector('#rqd-reply-subject'),
            attachments: (_composeState && _composeState.attachments) || [],
            testBtn: overlay.querySelector('#rqd-reply-test'),
            statusEl: overlay.querySelector('#rqd-reply-status'),
            commitChips: function () { if (_composeState && _composeState.chipState) _composeState.chipState.commitAll(); },
          });
          return;
        }
        // Attach file button (now on the Quill toolbar) → open the attach menu (device files OR
        // an existing deal file). closest() because the click can land on the inner <svg>/<path>.
        var _replyAttachBtn = ev.target.closest('#rqd-reply-attach-btn');
        if (_replyAttachBtn) {
          _openDealAttachMenu({
            anchorBtn: _replyAttachBtn,
            ref: q.reference,
            /* Ryan 2026-08-18 — the live To list, so "previously sent to this customer"
               can reach files sent on the customer's OTHER deals too. */
            toEmails: (_composeState && _composeState.chipState) ? _composeState.chipState.getTo() : [],
            /* CUSTOM_HOUSEDOCS_BY_CLAUDE — read LIVE, so the "✓ already attached" marker on a
               house-document row reflects what is on the email at the moment the menu paints. */
            attachments: function () { return (_composeState && _composeState.attachments) || []; },
            onDevice: function () { var fi = overlay.querySelector('#rqd-reply-file-input'); if (fi) fi.click(); },
            onAdd: function (att) {
              if (!_composeState) return false;
              var st = overlay.querySelector('#rqd-reply-status');
              if ((_composeState.attachments || []).length >= MAX_ATTACH_COUNT) {
                if (st) { st.textContent = 'Max ' + MAX_ATTACH_COUNT + ' attachments.'; st.classList.add('rqd-email-modal__compose-status--err'); }
                return false;
              }
              _composeState.attachments = (_composeState.attachments || []).concat([att]);
              _renderAttachList();
              return true;
            },
            onError: function (msg) { var st = overlay.querySelector('#rqd-reply-status'); if (st) { st.textContent = msg; st.classList.add('rqd-email-modal__compose-status--err'); } },
          });
          return;
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19) — ⚡ snippets. The keyboard path is
           "#" in the body; this is the same corpus for a rep who has not learned it. */
        var _replySnipBtn = ev.target.closest('#rqd-reply-snippet-btn');
        if (_replySnipBtn) {
          if (!_composeState || !_composeState.quill) return;
          _openSnippetMenu({
            anchorBtn: _replySnipBtn,
            ctx: _replyTplCtx(),
            onPick: function (id) {
              _replySnipStatus('');
              /* Focus first: the menu is a click, so the caret may be nowhere. getSelection(true)
                 inside the insert then resolves to the end of the document rather than throwing. */
              try { _composeState.quill.focus(); } catch (_) {}
              return _applySnippet(_composeState.quill, id, _replyTplCtx(), _replySnipStatus,
                function (m) { _replySnipStatus(m, true); }, _replyFx());
            },
          });
          return;
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — 📝 email templates. */
        /* Read the whole sequence before arming it - handled by the shared control, which
           answers true when the click was its Preview button. */
        if (_replySeq && _replySeq.handleClick(ev)) return;
        var _replyTplBtn = ev.target.closest('#rqd-reply-template-btn');
        if (_replyTplBtn) {
          var _rTplStatus = function (msg, isErr) {
            var s = overlay.querySelector('#rqd-reply-status');
            if (!s) return;
            s.textContent = msg;
            s.classList.toggle('rqd-email-modal__compose-status--err', !!isErr);
            s.classList.toggle('rqd-email-modal__compose-status--tpl', !!msg);
          };
          if (!_composeState || !_composeState.quill) return;
          if (!_composeState._tplBinding) _composeState._tplBinding = _newTemplateBinding();
          _openTemplateMenu({
            anchorBtn: _replyTplBtn,
            ctx: _replyTplCtx,
            isDirty: function () { return !!_composeState.quill.getText().trim(); },
            onPick: function (id) {
              _rTplStatus('');
              return _applyTemplate(_composeState._tplBinding, _composeState.quill, id, _replyTplCtx(),
                function (w) { _rTplStatus(w, false); }, {
                  /* Re-attach works here too - a rep who reaches the ladder by replying to
                     the customer still expects follow-up 1 to carry the quote. Subject and
                     threading are deliberately NOT wired: this send is already inside a
                     thread the rep chose, and a template must not redirect it. */
                  attach: _replyAttachOne,   // CUSTOM_HOUSEDOCS_BY_CLAUDE — the one definition
                });
            },
            onError: function (msg) { _rTplStatus(msg, true); },
            /* The sequence control follows the BODY: a different template just landed, so what
               (if anything) can be armed has changed with it. */
            onPicked: function () { if (_replySeq) _replySeq.sync(); },
          });
          return;
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Area 1: toggle the collapsible
           "Previous messages" panel open/closed and rotate the caret. */
        var _threadToggle = ev.target.closest('#rqd-reply-thread-toggle');
        if (_threadToggle) {
          var _twrapEl = overlay.querySelector('#rqd-reply-thread-wrap');
          var _tcaret = overlay.querySelector('#rqd-reply-thread-caret');
          var _tlabel = overlay.querySelector('#rqd-reply-thread-label');
          if (_twrapEl) {
            var _open = (_twrapEl.style.display === 'none' || !_twrapEl.style.display);
            _twrapEl.style.display = _open ? 'block' : 'none';
            _threadToggle.setAttribute('aria-expanded', _open ? 'true' : 'false');
            if (_tcaret) _tcaret.style.transform = _open ? 'rotate(90deg)' : '';
            if (_tlabel) {
              var _cnt = _threadToggle.getAttribute('data-count') || '';
              _tlabel.textContent = (_open ? 'Hide' : 'Load') + ' previous messages' + (_cnt ? ' (' + _cnt + ')' : '');
            }
            // Reveal-and-scroll: bring the newly-loaded chain into view (Outlook-style scroll-down).
            if (_open && _twrapEl.scrollIntoView) {
              requestAnimationFrame(function () { try { _twrapEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_) {} });
            }
          }
          return;
        }
        /* "+ RW-1166" ref-insert chip — same as compose-modal handler.
           Inserts the base ref at cursor position inside the subject
           input (or appends if no cursor). Helps thread future replies
           on the same conversation. */
        var refChipR = ev.target.closest('#rqd-reply-ref-chip');
        if (refChipR) {
          var refTxtR = refChipR.dataset.ref || '';
          var subjInputR = overlay.querySelector('#rqd-reply-subject');
          if (subjInputR && refTxtR) {
            var sR = subjInputR.selectionStart;
            var eR = subjInputR.selectionEnd;
            if (sR == null) { sR = subjInputR.value.length; eR = sR; }
            var beforeR = subjInputR.value.slice(0, sR);
            // Prefix " - " so the ref reads cleanly at the end of a subject line;
            // skip the separator when the subject (up to the cursor) is blank.
            var insertR = (beforeR.replace(/\s+$/, '').length ? ' - ' : '') + refTxtR;
            subjInputR.value = beforeR + insertR + subjInputR.value.slice(eR);
            try { subjInputR.focus(); subjInputR.setSelectionRange(sR + insertR.length, sR + insertR.length); } catch (_) {}
            _scheduleDraftSave();
          }
          return;
        }
        // Remove an attachment chip
        var rm = ev.target.closest('.rqd-email-modal__compose-chip-rm');
        if (rm && _composeState) {
          var idx = parseInt(rm.dataset.idx, 10);
          if (!isNaN(idx) && _composeState.attachments[idx]) {
            _composeState.attachments.splice(idx, 1);
            _renderAttachList();
          }
          return;
        }

        // Only the Close button dismisses (no backdrop-click, no Esc-only).
        if (ev.target.id === 'rqd-email-modal-close') { close(); return; }

        /* Ryan 2026-05-21: attachment download chip click. Fetch with
           the rep's JWT, build a blob URL, trigger download. Can't
           use a plain `<a href>` because Cloudflare Workers'
           validateRepCode reads from the Authorization header which
           a target=_blank navigation wouldn't send. */
        var attBtn = ev.target.closest('.rqd-email-modal__attach-link');
        if (attBtn) {
          var ref = attBtn.dataset.ref;
          var msgId = attBtn.dataset.msg;
          var attId = attBtn.dataset.aid;
          var fname = attBtn.dataset.name || 'attachment';
          attBtn.disabled = true;
          var origLabel = attBtn.textContent;
          attBtn.textContent = 'Downloading…';
          var workerBase = 'https://rfq-worker.ryan-29b.workers.dev';
          var url = workerBase + '/outlook-attachment'
            + '?ref=' + encodeURIComponent(ref)
            + '&messageId=' + encodeURIComponent(msgId)
            + '&attachmentId=' + encodeURIComponent(attId);
          /* Use Auth.getAccessToken() which auto-refreshes the JWT if
             it's within 2 min of expiry. Previous code read
             `session.token` — wrong field (actual field is
             `accessToken`) → sent "Bearer undefined" → worker 401. */
          (window.RepPortal && window.RepPortal.Auth
            ? window.RepPortal.Auth.getAccessToken()
            : Promise.resolve(''))
            .then(function(token) {
              return fetch(url, { headers: { 'Authorization': 'Bearer ' + (token || '') } });
            })
            .then(function(r) {
              if (!r.ok) throw new Error('HTTP ' + r.status);
              return r.blob();
            })
            .then(function(blob) {
              var objectUrl = URL.createObjectURL(blob);
              var a = document.createElement('a');
              a.href = objectUrl;
              a.download = fname;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              setTimeout(function() { URL.revokeObjectURL(objectUrl); }, 1000);
              attBtn.disabled = false;
              attBtn.textContent = origLabel;
            })
            .catch(function(err) {
              attBtn.disabled = false;
              attBtn.textContent = origLabel;
              alert('Download failed: ' + (err && err.message ? err.message : 'unknown'));
            });
          return;
        }
      });
  };

  /* ============================================
     RQD.openComposeModal(ctx) — Fresh email composer (NOT a reply)
     ============================================
     Opens a standalone compose modal for sending a brand-new email
     via Outlook. Powers the "📨 New Email" button on the activity
     card. No original message context — rep types To / Cc / Subject
     manually. Body editor is the same Quill setup as the reply path
     (lazy-loaded on first use; the link/script tags from the reply
     path are reused).

     ctx = {
       ref:         deal ref (e.g. "RW-1162-A") — used for subject
                    prefix + audit log. Optional.
       prefillTo:   default To recipient (the deal's customer email).
                    Editable.
       prefillCc:   default Cc list (array, usually empty). Editable.
       fetchQuotes: dashboard refresh helper (called quiet after send).
     }
     ============================================ */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-29) — default follow-up interval =
     the deal's next cadence rung. Leads front-load (3→5→7→14→14→30); quotes use
     7→7→7→10→10→10. Lead counter is q.consecutiveUnanswered; quote counter is
     q.followUp.consecutiveUnanswered. Unknown/fresh compose → lead first rung. */
  function _cadenceDefaultDays(dq) {
    dq = dq || {};
    /* CFA prospects (cfaCode): cold ride the 7-rung prospect ladder [3,7,7,14,14,30,45];
       once they've replied (prospectRepliedAt) the suggested default is a flat 7. Mirrors
       the worker's PROSPECT_LADDER / ENGAGED_PROSPECT_DAYS. */
    if (dq.cfaCode) {
      if (dq.prospectRepliedAt) return 7;
      var pl = [3, 7, 7, 14, 14, 30, 45, 45];
      var pc = (dq.consecutiveUnanswered) || (dq.followUp && dq.followUp.consecutiveUnanswered) || 0;
      return pl[Math.min(pc, pl.length - 1)];
    }
    var qs = !!(dq.lifecycleStatus === 'quoted' || dq.lifecycleStatus === 'sold' || dq.lifecycleStatus === 'lost' || dq.chainQuoted || dq.pdfDownloaded || dq.chainPdfDownloaded);
    var lad = qs ? [7, 7, 7, 10, 10, 10] : [3, 5, 7, 14, 14, 30];
    var c = qs ? ((dq.followUp && dq.followUp.consecutiveUnanswered) || 0)
               : ((dq.consecutiveUnanswered) || (dq.followUp && dq.followUp.consecutiveUnanswered) || 0);
    return lad[Math.min(c, 5)];
  }
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS c811b43f9288, Ryan 2026-09-28) — THE THREADS, NOT THE EMAILS.
     /deal-email-thread now also returns the customer's emails from their OTHER deals (RW-1461-A's
     customer was mid-conversation on RW-1426-A), so the list can hold several conversations.
     Ryan's shape: reply defaults to the NEWEST thread, with a picker listing each as
     "subject — REF (date)". One helper so the QW chooser and the DD ✉ menu cannot disagree about
     what a thread is or what it is called.

     A thread is a conversationId (a message with none is its own thread). `emails` arrives
     newest-first, so each group's first message is its reply target and the groups come out
     ordered by their newest message. Each group carries its own `emails`, which is what the
     composer previews — the conversation being replied to, not every email the customer has. */
  window.RQD = window.RQD || {};
  window.RQD.groupEmailThreads = function (emails) {
    var list = Array.isArray(emails) ? emails : [];
    var byKey = {};
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var m = list[i];
      if (!m) continue;
      var k = m.conversationId || ('msg:' + (m.messageId || i));
      if (!byKey[k]) { byKey[k] = { key: k, top: m, emails: [] }; out.push(byKey[k]); }
      byKey[k].emails.push(m);
    }
    for (var j = 0; j < out.length; j++) out[j].label = window.RQD.emailThreadLabel(out[j]);
    return out;
  };
  window.RQD.emailThreadLabel = function (thread) {
    var top = (thread && thread.top) || {};
    var ref = String(top.originRef || '');
    var subj = String(top.subject || '').replace(/^\s*((re|fw|fwd)\s*:\s*)+/i, '').trim();
    /* Our own quote subjects end in the ref ("... - RW-1439-C"); the label already says it once. */
    if (ref && subj.slice(-ref.length) === ref) subj = subj.slice(0, -ref.length).replace(/[\s\-–—|:]+$/, '');
    subj = subj || '(no subject)';
    if (subj.length > 60) subj = subj.slice(0, 57).replace(/\s+\S*$/, '') + '…';
    var d = top.ts ? new Date(top.ts) : null;
    var when = (d && !isNaN(d.getTime()))
      ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '';
    return subj + ' — ' + ref + (when ? ' (' + when + ')' : '');
  };
  window.RQD.openComposeModal = function(ctx) {
    ctx = ctx || {};
    var ref = String(ctx.ref || '').trim();
    var prefillTo = String(ctx.prefillTo || '').trim();
    var prefillCcArr = Array.isArray(ctx.prefillCc) ? ctx.prefillCc.slice() : [];
    /* CUSTOM_SELFBCC_BY_CLAUDE (Ryan 2026-08-13, correcting a 2026-06-15 note) — opt-in self-BCC.
       Resend-transport emails never land in Ryan's Outlook Sent folder, so the caller passes
       bccSelfWhenRep: 'ryan@…' and the Bcc field is pre-filled with it as a visible, removable
       chip. Outlook-sent emails don't pass this — they already appear in Sent.

       ⚠️ The note here used to say the chip was "kept in sync with the From dropdown: present on
       REP, removed on ABBY. The worker still force-adds it on send as the guarantee, so a copy is
       kept regardless." Both halves were wrong in a way that lost mail: the worker force-adds
       nothing (po-routes.js:2437), so removing the chip removed the copy. The sync is gone — see
       _syncSelfBcc below. The Bcc field means exactly what it says.

       ⛔ NO CALLER PASSES THIS ANY MORE (Ryan 2026-08-27). The two that did — the PO composer and
       the freight/dims composer — were the hardcoded ryan@ rule, retired once the Vendor Guide's
       PO Profile → Bcc could carry the same thing per vendor. The option is kept rather than
       deleted because it is a general composer capability and the next "always copy me on X" would
       otherwise be rebuilt from scratch; it is simply dormant. ⚠️ Do not re-wire it to a vendor
       email without a reason — two things appending to one Bcc field is what made the original
       note above wrong. */
    var _bccSelfWhenRep = String(ctx.bccSelfWhenRep || '').trim();
    var fetchQuotes = ctx.fetchQuotes;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-13) — hide the follow-up cadence
       opt-in for composers where it doesn't apply (e.g. the online-order
       delay/backorder notice — online orders have no follow-up cadence). */
    var suppressFollowUp = !!ctx.suppressFollowUp;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — may this composer offer an automated
       sequence at all? Admin, on a real deal, writing to a customer from the REP's own mailbox.
       ⛔ `session.admin === true`, NEVER `role` — role reads 'rep' for every real admin, which is
       the trap PM³⁵ recorded for the From row two rows above this one.
       ⛔ AND NEVER BEHIND AN ABBY SEND. These rungs go from the rep's mailbox over their
       signature; arming one behind an email the customer received from Abby would change voice
       mid-thread. `sendOverride` flows (PO, freight-balance) are excluded for the same reason —
       they are not the rep writing to their customer. */
    var _seqAllowed = (function () {
      try {
        if (!ref || suppressFollowUp || ctx.sendOverride) return false;
        if (typeof ctx.richBody === 'string' && ctx.richBody) return false;
        var _s = (window.RepPortal && window.RepPortal.Auth.getSession()) || {};
        return _s.admin === true;
      } catch (_) { return false; }
    })();
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — "❗ High importance" checkbox in the footer.
       Shown on EVERY email now (same pop-up everywhere); a caller can opt out with
       showHighImportance:false. Its state rides out in the send payload + sendOverride args
       (real send + Send Test) so each transport can stamp the importance headers. */
    var _showHighImportance = ctx.showHighImportance !== false;
    /* Importance toggle markup — injected into the formatting toolbar (Quill toolbar for
       Quill emails, rich-body toolbar for rich-body emails) so it sits on the same line as
       bold/italic on EVERY email. Read via _highImportanceChecked() by id. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — the word "Important" is gone and the ❗ does
       the whole job. Ryan: *"you can change the '! Important' checkbox text to just say '!' so we
       have more room."* The toolbar has to fit bold/italic/link/template/snippet/attach plus two
       toggles on a narrow modal, and a red ❗ next to a ticked box is not ambiguous.
       ⚠️ THE TITLE CARRIES THE MEANING NOW, so it is written as a full sentence rather than the
       fragment it was — hover is the only place the word "important" still appears. */
    var _importanceToggleHtml = _showHighImportance
      ? '<label class="rqd-email-modal__tb-importance" title="Send as high importance — Outlook shows this email with a red ❗ in the customer’s inbox."><input type="checkbox" id="rqd-compose-high-importance"> ❗</label>'
      : '';
    /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-13) — "Send within business hours", the checkbox
       the shipping-notification composer has always had, now available to any composer that opts
       in with showBusinessHours:true. Ticked by default, like the shipping one: the point is that
       mail does not land at 2am unless someone deliberately says otherwise.

       Window = 6am–6pm MT, SEVEN DAYS (email/notification.js). It was Mon–Fri until today; Ryan
       widened it while adding this, so a Friday-evening PO now goes Saturday morning rather than
       waiting for Monday. Opt-IN rather than default-on because most composers here send internal
       or reply mail where deferring would be wrong. */
    var _showBusinessHours = ctx.showBusinessHours === true;
    /* ⚠️ THE WORDS ARE GONE FROM THIS ONE TOO (Ryan 2026-08-20) — "⏱ Business hours" was the
       widest thing on the toolbar. The clock alone, with the meaning in the tooltip, buys the
       room the ❗ relabel was asked for.
       ⛔ UNLIKE ❗, THE MEANING IS NOT SELF-EVIDENT FROM THE ICON, so the tooltip has to say both
       halves: what ticking it does AND what unticking it does. A clock that only explained the
       ticked state would leave the rep guessing at the one that mails somebody at 2am. */
    var _bizHoursToggleHtml = _showBusinessHours
      ? '<label class="rqd-email-modal__tb-bizhours" title="Business hours only — hold this email until the next 6am–6pm MT window (7 days a week). Untick to send it right now, whatever the time."><input type="checkbox" id="rqd-compose-bizhours"' + (ctx.businessHoursDefault === false ? '' : ' checked') + '> ⏱</label>'
      : '';
    /* Attach-file button — also lives on the formatting toolbar (every email). The hidden file
       input + attachment pills stay in the attach row below; this is just the trigger. */
    var _attachBtnHtml = '<button type="button" id="rqd-compose-attach-btn" class="rqd-email-modal__tb-attach" title="Attach a file">'
      + '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      + '<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>'
      + '</svg></button>';
    var _enterFrom = String(ctx.enterFrom || '');
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — default the follow-up dropdown to
       "N/A — no follow-up" for email types that don't use the rep cadence. Any flow with
       its own send endpoint (sendOverride: PO, Abby freight-balance, …) never arms a
       follow-up, so those default to N/A. Customer/new emails (default /outlook-send
       transport) keep the cadence default. Callers can also force it with followUpDefaultNa. */
    var _followUpDefaultNa = !!ctx.sendOverride || !!ctx.followUpDefaultNa;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — Phase 2: rich-body mode. For emails that
       are a full server-rendered HTML document with a table woven through the narrative
       (Mark-as-Fulfilled / offline Notify), Quill would mangle the table — so the body is an
       editable contenteditable holding the whole rendered email instead of the Quill editor.
       The universal WINDOW (To/Cc/Bcc, subject, Send/Test, footer, slide) is identical;
       only the body editor differs. */
    var _richBodyHtml = (typeof ctx.richBody === 'string' && ctx.richBody) ? ctx.richBody : '';
    var _richBodyEl = null;
    // Tracks whether the rep ACTUALLY edited the rich-body canvas. A contentEditable
    // re-serializes its HTML on load (so canvas.innerHTML !== the string we set), which
    // would otherwise make us send the browser-mangled markup — with extra <p> padding —
    // instead of letting the worker render its clean canonical email. So when untouched we
    // return the pristine HTML verbatim; only a real edit sends the canvas markup.
    var _richBodyDirty = false;

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — "From:" identity selector. When the caller
       passes ctx.emailType, the compose pop-up shows a From dropdown (you vs Abby/orders@). The
       per-type choice is remembered server-side per rep (rep-settings emailFromPrefs) and drives
       the send mailbox + signature. _fromMailbox holds the live selection ('REP'|'ABBY'). */
    var _emailType = (typeof ctx.emailType === 'string' && ctx.emailType) ? ctx.emailType.slice(0, 60) : '';
    /* From: identity options. The pop-up is the SAME everywhere; the dropdown just lists the
       senders THIS email can use. ctx.fromOptions = ['REP','ABBY'] (switchable), ['ABBY'] or
       ['REP'] (single → rendered locked/disabled). Back-compat: a caller that passes emailType
       but no fromOptions gets both (the original customer/PO/freight behavior). No emailType +
       no fromOptions → no From row (legacy callers untouched). */
    var _fromOptions = Array.isArray(ctx.fromOptions)
      ? ctx.fromOptions.map(function (o) { return String(o).toUpperCase(); }).filter(function (o) { return o === 'REP' || o === 'ABBY'; })
      : (_emailType ? ['REP', 'ABBY'] : null);
    if (_fromOptions && !_fromOptions.length) _fromOptions = null;
    var _defFrom = String(ctx.defaultFrom || '').toUpperCase();
    /* CUSTOM_VENDORFROM_BY_CLAUDE (Ryan 2026-08-13) — forceFrom: a sender chosen by a RULE about
       this email rather than by the rep's habit. Ryan: *"make it so that the default from when
       emails like PO's go out to Legend is always Abby."*

       Two things separate it from defaultFrom, and both matter:
         1. It BEATS the saved per-rep default loaded from /rep-settings. defaultFrom only seeds
            the dropdown and the saved preference overwrites it a moment later, which would make a
            vendor rule silently lose to whatever the rep last picked on some other PO.
         2. Changing the dropdown does NOT persist a new per-type default. Overriding Abby on one
            Legend PO is a one-off; writing it back would change the default for every OTHER
            vendor's POs too, which is precisely the coupling the rule is meant to remove.

       Still switchable, deliberately — Ryan asked for the DEFAULT, and a hard lock would leave no
       way out on the one PO that needs to come from a person. */
    var _forceFrom = String(ctx.forceFrom || '').toUpperCase();
    if (_forceFrom !== 'REP' && _forceFrom !== 'ABBY') _forceFrom = '';
    var _initialFrom = (_forceFrom && _fromOptions && _fromOptions.indexOf(_forceFrom) >= 0) ? _forceFrom : _defFrom;
    var _fromMailbox = _fromOptions
      ? ((_fromOptions.indexOf(_initialFrom) >= 0) ? _initialFrom : _fromOptions[0])
      : 'REP';
    /* The signature HTML matching the current From selection (rep's or Abby's, from
       /rep-settings). Exposed to sendOverride as args.fromSignatureHtml so client-composed
       flows (freight) bake in the SAME signature the dropdown previews — preview == sent. */
    var _selectedFromSig = '';
    /* Pre-built From row HTML (inserted into the compose meta below). Built here because the
       option list is dynamic. Single option → disabled (locked) select. */
    var _fromRowHtml = '';
    if (_fromOptions) {
      var _fromOpts = _fromOptions.map(function (o) {
        return '<option value="' + o + '"' + (o === _fromMailbox ? ' selected' : '') + '>'
          + (o === 'ABBY' ? 'Abby Rivers &lt;orders@blueskyfitsupply.com&gt;' : 'Me')
          + '</option>';
      }).join('');
      _fromRowHtml = '<div class="rqd-email-modal__compose-meta-row" id="rqd-compose-from-row">'
        + '<span class="rqd-email-modal__label">From:</span> '
        + '<select class="rqd-email-modal__compose-input" id="rqd-compose-from" style="flex:1;min-width:0;max-width:360px;cursor:' + (_fromOptions.length === 1 ? 'default' : 'pointer') + ';"' + (_fromOptions.length === 1 ? ' disabled' : '') + '>'
        + _fromOpts
        + '</select>'
        + '</div>';
    }

    var existing = document.getElementById('rqd-email-modal');
    if (existing) existing.remove();

    /* Local helpers (lifted from RQD.openEmailModal — minor duplication
       on purpose, kept verbatim so the compose-modal codepath is
       fully self-contained + readable in isolation). Refactor to a
       shared helper module in a later cleanup if the duplication
       starts mattering. */
    function _esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    function _formatBytes(n) {
      if (n < 1024) return n + ' B';
      if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB';
      return (n / (1024 * 1024)).toFixed(1) + ' MB';
    }
    function _fileToBase64(file) {
      return new Promise(function(resolve, reject) {
        var r = new FileReader();
        r.onload = function() {
          var s = String(r.result || '');
          var i = s.indexOf(',');
          resolve(i >= 0 ? s.slice(i + 1) : s);
        };
        r.onerror = reject;
        r.readAsDataURL(file);
      });
    }

    var MAX_ATTACH_BYTES = _MAX_ATTACH_BYTES_M;  // 10 MB a file (CUSTOM_BIGATTACH_BY_CLAUDE)
    var MAX_ATTACH_COUNT = 5;
    // _loadQuill is module-scope (Phase B.5b — shared with the reply path).

    // Subject prefill: EMPTY (Ryan 2026-05-22). Auto-prefilling the
    // deal ref reads as transactional/CRM-spam on cold outreach and
    // hurts open rates. The customer-email matcher already threads
    // sent emails back to the deal (since the deal's customerEmail
    // matches the To recipient), so ref-in-subject is no longer
    // required for matching — it's just a precision enhancer.
    // Reps who want the ref for clarity can click the "+ ref" chip
    // next to the subject input (see modal markup below).
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-30) — allow callers (the QW
       Send-Quote flow) to override the empty default with a prefilled subject
       (e.g. "Your Blue Sky Fitness Supply Quote — {ref}"). DD callers pass
       nothing → stays empty as before. */
    var subjectPrefill = String(ctx.prefillSubject || '');
    // Base ref (no revision letter) for the insert chip. Matcher is
    // chain-aware so the base ref alone threads across all revisions.
    var refBaseForChip = (ref || '').replace(/-[A-Z]$/i, '');
    var _composeRefChipHtml = refBaseForChip
      ? '<button type="button" class="rqd-email-modal__ref-chip" id="rqd-compose-ref-chip" '
          + 'data-ref="' + _esc(refBaseForChip) + '" '
          + 'title="Insert ' + _esc(refBaseForChip) + ' at the cursor — helps thread future replies back to this deal">'
          + '+ ' + _esc(refBaseForChip) + '</button>'
      : '';

    /* Ryan 2026-05-22: Outlook-style recipient chips for To + Cc.
       The chip container is a flex wrap with the typing input embedded
       at the end. State (chip arrays) lives in toChips / ccChips below.
       Enter/Tab/comma/semicolon/blur converts typed text → chip.
       Backspace on empty input removes the last chip. */
    var EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — a deal can have SEVERAL contacts, and
       `prefillTo` arrives as the comma-joined list the customer record stores.

       ⛔ IT USED TO BE WRAPPED WHOLE: `prefillTo ? [prefillTo] : []` made ONE chip out of
       "a@x.com, b@y.com", which rendered as a single red (invalid) pill. Ryan hit it on
       RW-1350-A: two chips on the quote, one broken pill in the composer. The standalone
       installChipField in custom-rep-portal.js has always split on /[,;\s]+/; this seeding path
       never did — two chip implementations, one of which splits.

       ⛔ FIRST IS To, THE REST ARE Cc — Ryan's rule: *"the first/default one added will be the
       'primary' contact or the one we To on emails, and the 2nd+ one will be the secondary
       contact which gets a CC on emails."* Putting them all in To would send a group email
       instead; the primary is who the thread is addressed to.

       An explicit `prefillCc` still wins for its own entries: they are unioned, deduped
       case-insensitively, and anything already in To never repeats in Cc. */
    var _splitAddrs = function (v) {
      return String(v == null ? '' : v).split(/[,;\s]+/)
        .map(function (t) { return String(t || '').trim(); })
        .filter(Boolean);
    };
    var _prefillToList = _splitAddrs(prefillTo);
    var toChips = _prefillToList.length ? [_prefillToList[0]] : [];
    var ccChips = (function () {
      var out = [], seen = {};
      toChips.forEach(function (a) { seen[String(a).toLowerCase()] = 1; });
      _prefillToList.slice(1)
        .concat((Array.isArray(prefillCcArr) ? prefillCcArr : []).filter(Boolean))
        .forEach(function (a) {
          var k = String(a || '').trim().toLowerCase();
          if (!k || seen[k]) return;
          seen[k] = 1;
          out.push(String(a).trim());
        });
      return out;
    })();
    /* CUSTOM_DEALPEOPLE_BY_CLAUDE (Ryan 2026-10-27) - A NEW EMAIL TO THE CUSTOMER STARTS WITH EVERYONE ON THE DEAL: the people
       a rep marked To in To, the CC people in Cc, each pill removable. Only when the email is already addressed to somebody
       on the deal, so a supplier or freight email opened from the same deal is left exactly as its caller filled it.
       The deal's people come from the caller (`ctx.dealCustomer`, the QW) or the Deal Dashboard row. */
    (function () {
      var CT = window.RepPortal && window.RepPortal.Contacts;
      var dq = ctx.dealCustomer || null;
      if (!dq && ref) {
        var _dqRow = _dealQByRef(ref);
        if (_dqRow) dq = { name: _dqRow.customer, email: _dqRow.customerEmail, contacts: _dqRow.customerContacts };
      }
      if (!CT || !CT.dealAudience || !dq || !toChips.length) return;
      var aud = CT.dealAudience(dq);
      var onDeal = {};
      aud.to.concat(aud.cc).forEach(function (p) { onDeal[p.email] = 1; });
      if (!onDeal[String(toChips[0]).trim().toLowerCase()]) return;
      var seen = {}, nextTo = [], nextCc = [];
      var push = function (list, a) { var k = String(a || '').trim().toLowerCase(); if (!k || seen[k]) return; seen[k] = 1; list.push(String(a).trim()); };
      aud.to.forEach(function (p) { push(nextTo, p.email); });
      toChips.forEach(function (a) { push(nextTo, a); });
      aud.cc.forEach(function (p) { push(nextCc, p.email); });
      ccChips.forEach(function (a) { push(nextCc, a); });
      toChips = nextTo;
      ccChips = nextCc;
    })();
    var bccChips = Array.isArray(ctx.prefillBcc) ? ctx.prefillBcc.filter(Boolean).slice() : [];   // may be pre-filled by vendor profile BCC

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — name the customer in the head.
       "RW-1330-B · NEW EMAIL" says which deal but not who, and a rep with several composers
       open reads the person faster than the ref. The reply path already does this (it
       rewrites the dir to "· Reply to <whom>" below), so this closes the gap on the
       new-email path. q.customer is the contact; customerCompany is the org — prefer the
       person, fall back to the org, then to the address being typed to, so the slot is
       filled whenever anything is known. Rendered text-transform:none: the rest of the line
       is uppercase chrome, a person's name is not. */
    var _headQ = ref ? _dealQByRef(ref) : null;
    var _headWho = String((ctx.customerName || '')
      || (_headQ && (_headQ.customer || _headQ.customerCompany))
      || prefillTo || '').trim();
    var _headWhoHtml = _headWho
      ? '<span style="text-transform:none;color:var(--rq-text);font-weight:600;">' + _esc(_headWho) + '</span> &middot; '
      : '';
    var modalHtml = ''
      + '<div class="rqd-daterange-modal rqd-email-modal rqd-email-modal--compose" role="dialog" aria-label="New email">'
      +   '<div class="rqd-email-modal__head" style="display:flex;align-items:center;justify-content:space-between;gap:8px;">'
      +     '<div class="rqd-email-modal__dir">' + (ref ? _esc(ref) + ' &middot; ' : '') + _headWhoHtml + 'New Email</div>'
      /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE (Ryan 2026-10-10) — minimize and close. Both keep the draft. */
      +     '<div class="rqd-email-modal__winbtns" style="display:flex;gap:2px;flex:0 0 auto;">'
      +       '<button type="button" id="rqd-compose-minimize" title="Minimize (your draft is kept)" aria-label="Minimize" style="width:28px;height:28px;border:0;border-radius:6px;background:transparent;color:var(--rq-text-secondary);font:600 18px/1 Montserrat,sans-serif;cursor:pointer;">&minus;</button>'
      +       '<button type="button" id="rqd-compose-x" title="Close (your draft is kept)" aria-label="Close" style="width:28px;height:28px;border:0;border-radius:6px;background:transparent;color:var(--rq-text-secondary);font:600 18px/1 Montserrat,sans-serif;cursor:pointer;">&times;</button>'
      +     '</div>'
      +   '</div>'
      +   '<div class="rqd-email-modal__compose">'
      +     '<div class="rqd-email-modal__compose-meta">'
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — From: identity selector. Same pop-up for
         every email; the dropdown lists the senders THIS email can use (single = locked). Built
         into _fromRowHtml above; wired by _installFromSelector after open. */
      +       _fromRowHtml
      +       '<div class="rqd-email-modal__compose-meta-row">'
      +         '<span class="rqd-email-modal__label">To:</span> '
      +         '<div class="rqd-email-modal__chip-field" id="rqd-compose-to-field" data-kind="to" role="list" aria-label="To recipients">'
      +           '<input type="text" class="rqd-email-modal__chip-input" id="rqd-compose-to" placeholder="recipient@example.com" autocomplete="off" spellcheck="false">'
      +         '</div>'
      +       '</div>'
      +       '<div class="rqd-email-modal__compose-meta-row">'
      +         '<span class="rqd-email-modal__label">Cc:</span> '
      +         '<div class="rqd-email-modal__chip-field" id="rqd-compose-cc-field" data-kind="cc" role="list" aria-label="Cc recipients">'
      +           '<input type="text" class="rqd-email-modal__chip-input" id="rqd-compose-cc" placeholder="(optional)" autocomplete="off" spellcheck="false">'
      +         '</div>'
      +       '</div>'
      +       '<div class="rqd-email-modal__compose-meta-row">'
      +         '<span class="rqd-email-modal__label">Bcc:</span> '
      +         '<div class="rqd-email-modal__chip-field" id="rqd-compose-bcc-field" data-kind="bcc" role="list" aria-label="Bcc recipients">'
      +           '<input type="text" class="rqd-email-modal__chip-input" id="rqd-compose-bcc" placeholder="(optional, hidden from other recipients)" autocomplete="off" spellcheck="false">'
      +         '</div>'
      +       '</div>'
      +       '<div class="rqd-email-modal__compose-meta-row">'
      +         '<span class="rqd-email-modal__label">Subject:</span> '
      +         '<input type="text" class="rqd-email-modal__compose-input" id="rqd-compose-subject" value="' + _esc(subjectPrefill) + '">'
      +         _composeRefChipHtml
      +       '</div>'
      +     '</div>'
      /* Merged compose area: editor + sig preview share a single
         bordered container so the signature reads as part of the
         compose surface (visually "embedded") even though it's
         functionally a separate non-interactive block. */
      +     '<div class="rqd-email-modal__compose-merged">'
      +       '<div class="rqd-email-modal__compose-editor-wrap"><div id="rqd-compose-editor"></div></div>'
      +       '<div class="rqd-email-modal__compose-sig-wrap" id="rqd-compose-sig-wrap" style="display:none;">'
      +         '<div class="rqd-email-modal__compose-sig-preview" id="rqd-compose-sig-preview">Loading signature…</div>'
      +       '</div>'
      +     '</div>'
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — the signature preview is hidden by
         default (more room to see + type the body without the big logo eating space); this
         checkbox toggles the VISUAL preview only. The signature is ALWAYS included in the
         sent email (fromSignatureHtml) regardless of this toggle. Unchecked on every open. */
      +     '<label class="rqd-email-modal__sig-toggle" title="Preview your signature here. It is always included in the sent email either way." style="display:flex;align-items:center;gap:6px;margin-top:8px;font-size:12px;opacity:.85;cursor:pointer;user-select:none;"><input type="checkbox" id="rqd-compose-sig-toggle"> Show signature</label>'
      /* Attach BUTTON lives on the formatting toolbar now (added by _installRichBody / the Quill
         injection). This row keeps just the hidden file input + hint + the attachment pills. */
      +     '<div class="rqd-email-modal__compose-attach">'
      +       '<input type="file" multiple id="rqd-compose-file-input" style="display:none;">'
      +       '<span class="rqd-email-modal__compose-attach-hint">Max 5 files, 10 MB each, 15 MB total</span>'
      +       '<div class="rqd-email-modal__compose-attach-list" id="rqd-compose-attach-list"></div>'
      +     '</div>'
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-28) — Follow-up cadence opt-in.
         Pre-checked; arms/advances the deal's follow-up cadence on send (server
         computes the next rung from the deal's counter). Only takes effect when
         this compose is tied to a deal ref. */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — Follow-up dropdown now carries an
         "N/A — no follow-up" option (first). Customer/new emails default to the cadence
         rung; sendOverride flows (PO, freight-balance) default to N/A since they don't arm
         a follow-up. Selecting N/A on a normal send skips arming (see the send payload). */
      +     (suppressFollowUp ? '' : (
            '<label class="rqd-email-modal__followup-opt" style="display:flex;align-items:center;gap:6px;margin-top:10px;font-size:13px;opacity:.9;">'
      +       '🔔 Follow-up: '
      +       '<select id="rqd-compose-followup-days" style="font-size:13px;padding:2px 6px;border-radius:6px;background:var(--rq-input-bg,#ffffff);color:var(--rq-text,#000F1C);border:1px solid var(--rq-border,#2a3a4d);">'
      +         (function(){var _cq=(typeof allQuotes!=='undefined'&&allQuotes)?allQuotes.find(function(x){return x.reference===ref;}):null;var d=_cadenceDefaultDays(_cq);var na='<option value="na"'+(_followUpDefaultNa?' selected':'')+'>N/A &mdash; no follow-up</option>';var days=[2,3,5,7,10,14,21,30,45].map(function(x){return '<option value="'+x+'"'+((!_followUpDefaultNa&&x===d)?' selected':'')+'>in '+x+' days</option>';}).join('');return na+days;})()
      +       '</select>'
      +     '</label>'))
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — AUTOMATED FOLLOW-UP SEQUENCES.
         Ryan: *"I want to be able to select something which triggers automatic follow-ups... it
         sends the email from the rep (admin in this case), then waits 7 days and sends the second
         follow-up, then another 7 and sends the 3rd, then 10 days and sends the 4th."*

         ⛔ IT SITS UNDER THE FOLLOW-UP DROPDOWN BECAUSE IT ANSWERS THE SAME QUESTION. That row
         says "remind ME in N days"; this one says "or let the system write the next three". A rep
         deciding what happens after this email should find both in one place.

         ⛔ ADMIN ONLY FOR NOW, off `session.admin` and never `role` — which reads 'rep' for every
         real admin (PM³⁵). ⚠️ This is a UI gate; the server has its own, on the same
         `deal-automation-audience` setting the samples chain uses, so hiding the control is not
         what makes it safe.

         ⚠️ RENDERED DISABLED AND WITHOUT A CADENCE. Both come from `_seqSyncControl`, which runs
         whenever the inserted template changes — the cadence belongs to the SEQUENCE, so the
         control cannot name one until it knows which template is in the body. */
      +     _seqControlHtml('rqd-compose-', _seqAllowed)
      /* CUSTOM_DISCDRIP_BY_CLAUDE (Ryan 2026-09-06) — A CALLER'S OWN TICKBOX, IN THE SAME SLOT AS THE
         SEQUENCE ROW. The discontinued notice sends from Abby on its own route, so the rep-mailbox
         sequence control above cannot serve it (`_seqAllowed` is false under sendOverride, rightly).
         What it needs is one box the worker reads: `ctx.extraToggle = { label, why, checked, title }`
         renders here and its state rides the send args as `args.extraToggle`. Generic on purpose —
         the next Abby email with a yes/no attached should not have to add a fourth control here.
         ⚠️ Rendered only when the caller asks; every existing composer is untouched. */
      +     (ctx.extraToggle && ctx.extraToggle.label ? (
            '<label class="rqd-email-modal__seq-opt" id="rqd-compose-extra-row" style="display:flex;align-items:center;gap:6px;margin-top:8px;font-size:13px;"'
      +       (ctx.extraToggle.title ? ' title="' + _esc(ctx.extraToggle.title) + '"' : '') + '>'
      +       '<input type="checkbox" id="rqd-compose-extra-toggle"' + (ctx.extraToggle.checked === false ? '' : ' checked') + '>'
      +       '<span>' + _esc(ctx.extraToggle.label) + '</span>'
      +     '</label>'
      +     (ctx.extraToggle.why ? '<div class="rqd-email-modal__seq-why">' + _esc(ctx.extraToggle.why) + '</div>' : '')) : '')
      +     '<div class="rqd-email-modal__compose-status" id="rqd-compose-status"></div>'
      +   '</div>'
      +   '<div class="rqd-daterange-modal__footer rqd-email-modal__footer">'
      +     '<button type="button" class="rqd-pill" id="rqd-compose-cancel">Cancel</button>'
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-29) — one optional destructive action, next
         to Cancel. Added for "Delete Draft" on a held email opened as the draft it is; kept
         generic (label + handler) rather than named after that one caller, because a footer slot
         hard-coded to one feature is the thing the next feature has to work around. Renders only
         when a caller asks for it, so every existing composer is untouched. */
      +     (ctx.extraAction && ctx.extraAction.label
            ? '<button type="button" class="rqd-pill rqd-pill--danger" id="rqd-compose-extra">'
              + _esc(ctx.extraAction.label) + '</button>'
            : '')
      /* High-importance toggle moved OUT of the footer and INTO the formatting toolbar
         (Quill toolbar + rich-body toolbar) — same line as bold/italic on every email. */
      +     '<button type="button" class="rqd-pill" id="rqd-compose-test" title="Send this email to your own inbox to preview formatting — does not touch the deal or the customer.">Send Test</button>'
      +     '<button type="button" class="rqd-email-modal__compose-send" id="rqd-compose-send">Send</button>'
      +   '</div>'
      + '</div>';

    var overlay = document.createElement('div');
    overlay.id = 'rqd-email-modal';
    overlay.className = 'rqd-daterange-backdrop rqd-email-modal-backdrop';
    overlay.innerHTML = modalHtml;
    var portalWrap = document.querySelector('.rep-quote-portal-wrap') || document.body;
    portalWrap.appendChild(overlay);
    /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE — reopened from a tab brought back after a refresh: grow out of where the tab was. */
    if (ctx._growFrom && !_reduceMotion()) {
      var _gb = overlay.querySelector('.rqd-email-modal--compose');
      var _gf = (_gb && _gb.animate) ? _flyFromRect(_gb, ctx._growFrom) : null;
      if (_gf) {
        overlay.animate([{ backgroundColor: 'rgba(0,0,0,0)' }, { backgroundColor: getComputedStyle(overlay).backgroundColor }], { duration: 340, easing: 'ease-out' });
        _gb.animate([{ transform: _gf, opacity: 0.35 }, { transform: 'none', opacity: 1 }], { duration: 340, easing: 'cubic-bezier(.2,.8,.3,1)' });
      }
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — "Show signature" toggle: reveal/hide
       the signature preview (visual only; the signature is always sent). Unchecked on open. */
    (function _wireSigToggle() {
      var cb = overlay.querySelector('#rqd-compose-sig-toggle');
      var wrap = overlay.querySelector('#rqd-compose-sig-wrap');
      if (!cb || !wrap) return;
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-17) — hide the toggle entirely on a richBody
         email. Those carry their signature INSIDE ctx.richBody (the canvas IS the email), so this
         wrap has nothing to reveal and ticking the box appears to do nothing. Ryan hit exactly
         that: "I clicked the 'Show signature' checkbox in the composer, and no signature appeared."
         The signature is already on screen, further down the canvas. Removing the control is
         honest; leaving a checkbox that cannot do anything is not. */
      if (typeof ctx.richBody === 'string' && ctx.richBody) {
        var lbl = cb.closest ? cb.closest('.rqd-email-modal__sig-toggle') : null;
        if (lbl) lbl.style.display = 'none';
        return;
      }
      // Class-gated so the CSS !important rule beats any inline display the async
      // signature-setup applies (it force-shows the wrap in a few branches).
      cb.addEventListener('change', function () { wrap.classList.toggle('rqd-sig-shown', cb.checked); });
    })();

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — Phase 0a: rendered-preview blocks.
       Caller passes ctx.previewBlocks (array of HTML strings or {html}) — e.g. an item or
       shipment table — rendered READ-ONLY inside the white compose canvas, between the
       editor and the signature. This lets structured emails (PO / freight / fulfillment)
       preview fully (words + tables + signature) without putting tables into Quill (which
       mangles them). The table HTML still rides into the sent email via the caller's
       sendOverride. No previewBlocks → canvas unchanged. */
    (function _installPreviewBlocks() {
      var blocks = Array.isArray(ctx.previewBlocks) ? ctx.previewBlocks.filter(Boolean) : [];
      if (!blocks.length) return;
      var merged = overlay.querySelector('.rqd-email-modal__compose-merged');
      if (!merged) return;
      var sigWrap = overlay.querySelector('#rqd-compose-sig-wrap');
      var host = document.createElement('div');
      host.className = 'rqd-email-modal__compose-preview-blocks';
      host.innerHTML = blocks.map(function (b) {
        var h = (typeof b === 'string') ? b : ((b && b.html) || '');
        return '<div class="rqd-email-modal__compose-preview-block">' + h + '</div>';
      }).join('');
      if (sigWrap) merged.insertBefore(host, sigWrap); else merged.appendChild(host);
    })();

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — Phase 0b: optional preStep. Renders a
       custom first panel INSIDE this same universal modal shell (identical chrome/header/
       footer/dark-mode) and slides into the compose step on Continue; the compose Cancel
       becomes "← Back" and returns here. This is how an email type adds its unique inputs
       (PO instructions, freight items, fulfillment tracking) without a separate silo'd
       window. ctx.preStep = { title, html, continueLabel?, onMount(panelEl), onContinue(panelEl)
       -> return false to block }. No preStep → modal opens straight on compose (unchanged). */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) - ctx.noticeHtml: a muted strip pinned to
       the compose panel's bottom edge, just above the footer. For the things a caller needs to say
       ABOUT the email rather than IN it - a body preview that failed to load being the first one.
       No noticeHtml -> nothing rendered, modal unchanged. */
    (function _installNotice() {
      var notice = (ctx.noticeHtml || '').toString();
      if (!notice) return;
      var composePanel = overlay.querySelector('.rqd-email-modal--compose');
      var footer = composePanel ? composePanel.querySelector('.rqd-email-modal__footer') : null;
      if (!footer || !footer.parentNode) return;
      var el = document.createElement('div');
      el.className = 'rqd-email-modal__notice';
      el.setAttribute('role', 'status');
      el.innerHTML = notice;
      footer.parentNode.insertBefore(el, footer);
    })();

    (function _installPreStep() {
      var ps = ctx.preStep;
      if (!ps || typeof ps !== 'object') return;
      var composePanel = overlay.querySelector('.rqd-email-modal--compose');
      if (!composePanel) return;
      var pre = document.createElement('div');
      pre.className = 'rqd-daterange-modal rqd-email-modal rqd-email-modal--prestep';
      pre.setAttribute('role', 'dialog');
      pre.innerHTML = ''
        + '<div class="rqd-email-modal__head">'
        +   '<div class="rqd-email-modal__dir">' + (ref ? _esc(ref) + ' &middot; ' : '') + 'Step 1 of 2</div>'
        +   '<h3 class="rqd-email-modal__subject">' + _esc(ps.title || 'Details') + '</h3>'
        + '</div>'
        + '<div class="rqd-email-modal__prestep-body" id="rqd-prestep-body">' + (ps.html || '') + '</div>'
        + '<div class="rqd-daterange-modal__footer rqd-email-modal__footer">'
        +   '<button type="button" class="rqd-pill" id="rqd-prestep-cancel">Cancel</button>'
        +   '<button type="button" class="rqd-email-modal__compose-send" id="rqd-prestep-continue">' + _esc(ps.continueLabel || 'Continue to email →') + '</button>'
        + '</div>';
      overlay.insertBefore(pre, composePanel);   // preStep is the first panel
      composePanel.style.display = 'none';        // compose hidden until Continue
      var composeCancel = composePanel.querySelector('#rqd-compose-cancel');
      if (composeCancel) composeCancel.textContent = '← Back';

      function _slideIn(panel, dir) {
        panel.classList.remove('rqd-email-modal--enter-right', 'rqd-email-modal--enter-left');
        void panel.offsetWidth; // reflow so the animation restarts
        panel.classList.add(dir === 'left' ? 'rqd-email-modal--enter-left' : 'rqd-email-modal--enter-right');
      }
      function showCompose() { pre.style.display = 'none'; composePanel.style.display = ''; _slideIn(composePanel, 'right'); }
      function showPre() { composePanel.style.display = 'none'; pre.style.display = ''; _slideIn(pre, 'left'); }

      if (typeof ps.onMount === 'function') { try { ps.onMount(pre); } catch (_) {} }
      pre.querySelector('#rqd-prestep-continue').addEventListener('click', function () {
        if (typeof ps.onContinue === 'function') {
          var ok = true; try { ok = ps.onContinue(pre); } catch (_) { ok = true; }
          if (ok === false) return;
        }
        showCompose();
      });
      pre.querySelector('#rqd-prestep-cancel').addEventListener('click', function () { close(); });
      // Compose "← Back" returns to the preStep (capture + stop so the normal
      // cancel/close + onBack handler on the overlay doesn't also fire).
      if (composeCancel) {
        composeCancel.addEventListener('click', function (e) { e.stopPropagation(); showPre(); }, true);
      }
    })();

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — Phase 2: rich-body mode. Some emails
       (Mark-as-Fulfilled / shipping notifications) are composed server-side into a single
       narrative where the shipment table is woven MID-prose — not a "prose + trailing table"
       shape Quill can model. ctx.richBody = the full rendered email HTML (greeting → table →
       tracking → signature, all of it). We replace the Quill editor + preview-blocks + sig
       preview with ONE editable canvas holding that exact HTML, so the rep edits the email
       WYSIWYG in the same universal window chrome. Bold/italic via the browser's native
       Ctrl+B/Ctrl+I on the contentEditable. doSend/_sendTestEmail read the body through
       _bodyHtml()/_bodyText() so the rest of the send path is unchanged. */
    (function _installRichBody() {
      if (!_richBodyHtml) return;
      var merged = overlay.querySelector('.rqd-email-modal__compose-merged');
      if (!merged) return;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — rich-body gets the SAME formatting
         controls as the Quill emails (bold/italic/underline/lists/link/clear). The canvas is a
         contentEditable, so these are document.execCommand buttons acting on the selection —
         feature parity with the Quill toolbar even though it's not a Quill instance. */
      /* CUSTOM_LINESWAP_BY_CLAUDE (Ryan 2026-09-15) — QUILL'S OWN ICONS, INLINED.
         Ryan: *"the bar with all the buttons on it is a different type/style/UI to what we use
         on most composers."* It was: this bar is NOT a Quill instance (see the note above —
         rich-body is a contentEditable because Quill strips the <table> this email is built
         around), and it wore text and emoji glyphs — B, I, U, a bullet, "1.", a link emoji —
         beside Quill's uniform SVG set everywhere else in the portal.

         ⛔ INLINED, NOT IMPORTED. `Quill.import('ui/icons')` would give the same strings and
         reintroduce the exact dependency rich-body exists to avoid: Quill is lazy-loaded from a
         CDN here, so the toolbar would render icons or text depending on whether something
         else had already pulled it in. These are copied verbatim from quill@2.0.2, the version
         QUILL_CDN_JS pins, so the two bars are the same artwork and not an impression of it.
         ⚠ If that pin moves, re-copy them rather than redrawing by hand. */
      merged.innerHTML =
        '<div class="rqd-email-modal__richbody-toolbar" id="rqd-richbody-toolbar">'
        +   '<button type="button" data-cmd="bold" title="Bold">' + '<svg viewbox="0 0 18 18"><path class="ql-stroke" d="M5,4H9.5A2.5,2.5,0,0,1,12,6.5v0A2.5,2.5,0,0,1,9.5,9H5A0,0,0,0,1,5,9V4A0,0,0,0,1,5,4Z"/><path class="ql-stroke" d="M5,9h5.5A2.5,2.5,0,0,1,13,11.5v0A2.5,2.5,0,0,1,10.5,14H5a0,0,0,0,1,0,0V9A0,0,0,0,1,5,9Z"/></svg>' + '</button>'
        +   '<button type="button" data-cmd="italic" title="Italic">' + '<svg viewbox="0 0 18 18"><line class="ql-stroke" x1="7" x2="13" y1="4" y2="4"/><line class="ql-stroke" x1="5" x2="11" y1="14" y2="14"/><line class="ql-stroke" x1="8" x2="10" y1="14" y2="4"/></svg>' + '</button>'
        +   '<button type="button" data-cmd="underline" title="Underline">' + '<svg viewbox="0 0 18 18"><path class="ql-stroke" d="M5,3V9a4.012,4.012,0,0,0,4,4H9a4.012,4.012,0,0,0,4-4V3"/><rect class="ql-fill" height="1" rx="0.5" ry="0.5" width="12" x="3" y="15"/></svg>' + '</button>'
        +   '<input type="color" class="rqd-rb-tbinput" data-cmd="foreColor" title="Text color" value="#002060">'
        +   '<input type="color" class="rqd-rb-tbinput" data-cmd="hiliteColor" title="Highlight color" value="#ffff00">'
        +   '<select class="rqd-rb-tbsize" data-cmd="fontSize" title="Font size">'
        +     '<option value="">Size</option><option value="2">Small</option><option value="3">Normal</option><option value="4">Medium</option><option value="5">Large</option><option value="6">X-Large</option><option value="7">Huge</option>'
        +   '</select>'
        +   '<button type="button" data-cmd="insertUnorderedList" title="Bulleted list">' + '<svg viewbox="0 0 18 18"><line class="ql-stroke" x1="6" x2="15" y1="4" y2="4"/><line class="ql-stroke" x1="6" x2="15" y1="9" y2="9"/><line class="ql-stroke" x1="6" x2="15" y1="14" y2="14"/><line class="ql-stroke" x1="3" x2="3" y1="4" y2="4"/><line class="ql-stroke" x1="3" x2="3" y1="9" y2="9"/><line class="ql-stroke" x1="3" x2="3" y1="14" y2="14"/></svg>' + '</button>'
        +   '<button type="button" data-cmd="insertOrderedList" title="Numbered list">' + '<svg viewbox="0 0 18 18"><line class="ql-stroke" x1="7" x2="15" y1="4" y2="4"/><line class="ql-stroke" x1="7" x2="15" y1="9" y2="9"/><line class="ql-stroke" x1="7" x2="15" y1="14" y2="14"/><line class="ql-stroke ql-thin" x1="2.5" x2="4.5" y1="5.5" y2="5.5"/><path class="ql-fill" d="M3.5,6A0.5,0.5,0,0,1,3,5.5V3.085l-0.276.138A0.5,0.5,0,0,1,2.053,3c-0.124-.247-0.023-0.324.224-0.447l1-.5A0.5,0.5,0,0,1,4,2.5v3A0.5,0.5,0,0,1,3.5,6Z"/><path class="ql-stroke ql-thin" d="M4.5,10.5h-2c0-.234,1.85-1.076,1.85-2.234A0.959,0.959,0,0,0,2.5,8.156"/><path class="ql-stroke ql-thin" d="M2.5,14.846a0.959,0.959,0,0,0,1.85-.109A0.7,0.7,0,0,0,3.75,14a0.688,0.688,0,0,0,.6-0.736,0.959,0.959,0,0,0-1.85-.109"/></svg>' + '</button>'
        +   '<button type="button" data-cmd="createLink" title="Insert link">' + '<svg viewbox="0 0 18 18"><line class="ql-stroke" x1="7" x2="11" y1="7" y2="11"/><path class="ql-even ql-stroke" d="M8.9,4.577a3.476,3.476,0,0,1,.36,4.679A3.476,3.476,0,0,1,4.577,8.9C3.185,7.5,2.035,6.4,4.217,4.217S7.5,3.185,8.9,4.577Z"/><path class="ql-even ql-stroke" d="M13.423,9.1a3.476,3.476,0,0,0-4.679-.36,3.476,3.476,0,0,0,.36,4.679c1.392,1.392,2.5,2.542,4.679.36S14.815,10.5,13.423,9.1Z"/></svg>' + '</button>'
        +   '<button type="button" data-cmd="removeFormat" title="Clear formatting">' + '<svg class="" viewbox="0 0 18 18"><line class="ql-stroke" x1="5" x2="13" y1="3" y2="3"/><line class="ql-stroke" x1="6" x2="9.35" y1="12" y2="3"/><line class="ql-stroke" x1="11" x2="15" y1="11" y2="15"/><line class="ql-stroke" x1="15" x2="11" y1="11" y2="15"/><rect class="ql-fill" height="1" rx="0.5" ry="0.5" width="7" x="2" y="14"/></svg>' + '</button>'
        +   _attachBtnHtml
        +   _importanceToggleHtml
        +   _bizHoursToggleHtml
        + '</div>'
        + '<div id="rqd-compose-richbody" class="rqd-email-modal__compose-richbody" contenteditable="true" spellcheck="true"></div>';
      _richBodyEl = merged.querySelector('#rqd-compose-richbody');
      var _rbToolbar = merged.querySelector('#rqd-richbody-toolbar');
      if (_rbToolbar) {
        /* contentEditable + execCommand. Native controls (color/size) steal focus and lose the
           selection, so we save the canvas range on interaction and restore it before applying.
           styleWithCSS=true for color/size → inline styles (email-safe); false for bold/italic/
           underline → <b>/<i>/<u> (most compatible). */
        var _rbRange = null;
        function _saveRbRange() { try { var s = window.getSelection(); if (s && s.rangeCount && _richBodyEl && _richBodyEl.contains(s.anchorNode)) _rbRange = s.getRangeAt(0).cloneRange(); } catch (_) {} }
        function _restoreRbRange() { try { if (_rbRange) { var s = window.getSelection(); s.removeAllRanges(); s.addRange(_rbRange); } } catch (_) {} }
        function _rbExec(cmd, val, useCSS) {
          if (_richBodyEl) { try { _richBodyEl.focus(); } catch (_) {} }
          _restoreRbRange();
          try { document.execCommand('styleWithCSS', false, !!useCSS); document.execCommand(cmd, false, (val === undefined ? null : val)); } catch (_) {}
          _richBodyDirty = true;
        }
        if (_richBodyEl) { _richBodyEl.addEventListener('keyup', _saveRbRange); _richBodyEl.addEventListener('mouseup', _saveRbRange); }
        _rbToolbar.addEventListener('mousedown', function (ev) { _saveRbRange(); if (ev.target.closest('button')) ev.preventDefault(); });
        _rbToolbar.addEventListener('click', function (ev) {
          var btn = ev.target && ev.target.closest ? ev.target.closest('button[data-cmd]') : null;
          if (!btn) return;
          ev.preventDefault();
          var cmd = btn.getAttribute('data-cmd');
          if (cmd === 'createLink') { var url = window.prompt('Link URL:', 'https://'); if (url) _rbExec('createLink', url, false); }
          else _rbExec(cmd, null, false);
        });
        _rbToolbar.addEventListener('input', function (ev) {
          var ci = ev.target && ev.target.closest ? ev.target.closest('input[type=color][data-cmd]') : null;
          if (ci) _rbExec(ci.getAttribute('data-cmd'), ci.value, true);
        });
        _rbToolbar.addEventListener('change', function (ev) {
          var ss = ev.target && ev.target.closest ? ev.target.closest('select[data-cmd]') : null;
          if (ss && ss.value) { _rbExec(ss.getAttribute('data-cmd'), ss.value, true); ss.value = ''; }
        });
      }
      if (_richBodyEl) {
        _richBodyEl.innerHTML = _richBodyHtml;
        // Only a genuine edit flips dirty → then we send the canvas markup; otherwise
        // the pristine HTML goes out and the worker renders its clean canonical email.
        _richBodyEl.addEventListener('input', function () { _richBodyDirty = true; });
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — make links clickable in the preview.
           A contentEditable swallows link clicks (places the cursor instead of navigating), so
           the rep can't verify a tracking link before sending. Intercept clicks on any <a> and
           open it in a new tab. Doesn't fire 'input', so it never marks the body edited. */
        _richBodyEl.addEventListener('click', function (ev) {
          var a = ev.target && ev.target.closest ? ev.target.closest('a[href]') : null;
          if (a && a.getAttribute('href')) {
            ev.preventDefault();
            window.open(a.href, '_blank', 'noopener');
          }
        });
      }
      // Attach row (file input + pills) stays visible like every other email — the attach
      // BUTTON is on the toolbar above. (Ryan 2026-06-15: was hidden; now consistent.)
    })();

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — slide-in entrance (PO hand-off). */
    if (_enterFrom === 'right') {
      var _panelEl = overlay.querySelector('.rqd-email-modal');
      if (_panelEl) _panelEl.classList.add('rqd-email-modal--enter-right');
    }
    function _highImportanceChecked() {
      var el = overlay.querySelector('#rqd-compose-high-importance');
      return !!(el && el.checked);
    }
    /* CUSTOM_SENDWINDOW_BY_CLAUDE — false when the composer doesn't show the control at all, so a
       composer that never opted in can never accidentally defer a send. */
    function _businessHoursChecked() {
      var el = overlay.querySelector('#rqd-compose-bizhours');
      return !!(el && el.checked);
    }
    /* CUSTOM_DISCDRIP_BY_CLAUDE (Ryan 2026-09-06) — the caller's own tickbox: null when the composer
       has none, so a caller can tell "unticked" from "never offered". */
    function _extraToggleChecked() {
      var el = overlay.querySelector('#rqd-compose-extra-toggle');
      return el ? !!el.checked : null;
    }
    /* Follow-up dropdown → integer days, or null when "N/A — no follow-up" is selected
       (or the dropdown is absent / suppressed). */
    function _followUpSelectionDays() {
      var el = document.getElementById('rqd-compose-followup-days');
      if (!el) return null;
      var n = parseInt(el.value, 10);
      return isNaN(n) ? null : n;
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-30) — seed attachments from
       ctx.preAttachments (e.g. the freshly-generated quote PDF from the QW
       Send-Quote flow). Clone the array + each entry so caller state can't be
       mutated. Respect the same 5-file / size guards as manual attaches; rep
       can remove any pre-attached file before sending. DD callers pass none →
       starts empty as before. */
    var attachments = (Array.isArray(ctx.preAttachments) ? ctx.preAttachments : [])
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — a displayOnly entry has NO base64: it's a
         visual pill for a file the WORKER attaches server-side (the Abby shipping invoice), so the
         rep sees it's coming without us shipping the bytes from the client. */
      .filter(function(a) { return a && (a.base64 || a.displayOnly); })
      .slice(0, MAX_ATTACH_COUNT)
      .map(function(a) {
        return {
          name: String(a.name || 'attachment'),
          contentType: String(a.contentType || 'application/octet-stream'),
          base64: a.base64,
          sizeBytes: (typeof a.sizeBytes === 'number') ? a.sizeBytes : 0,
          locked: !!a.locked,        // PO PDF / invoice: shown but not removable (sent server-side)
          displayOnly: !!a.displayOnly, // no base64 — pill only, never sent from the client
          previewUrl: a.previewUrl || '', // openable even without bytes (see _openAttachmentPreview)
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — CARRY quoteMeta THROUGH THE CLONE.

             ⛔ THIS WHITELIST IS A SECOND, INDEPENDENT STRIP. The QW's Send-Quote flow now stamps
             the ordering page / total / payment link onto the attachment it hands over, but this
             clone rebuilds each entry field by field — so the metadata was produced and then
             discarded one line later, and the ordering-instructions snippet still answered
             "attach the quote first" to a rep looking at the attached quote.

             The DD's attach menu never hit this: it adds through onAdd, which concats the WHOLE
             object (~L6161). Two ways in, one of them lossy, and only the lossy one is the
             Workstation.

             ⛔ CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-09-01) - IT HAPPENED AGAIN, TO THE VERY
             NEXT FIELD. This used to be a field-by-field whitelist, and the note here said *"any
             future field an attachment carries has to be added HERE too; a whitelist silently
             drops what it does not know about, which is exactly how this one got missed."*
             `layoutPages` was added to `quoteMeta` and not to the list, so the QW's Send Quote
             produced a PDF WITH the gym layout in it and an email that would not mention it,
             while the DD's ✉ pill said it correctly - the same split, one release later.

             So the whitelist is gone. Copying every own key cannot fall behind a new field, and
             it keeps the only property the whitelist was actually there for: this is a COPY, so
             the caller's object graph stays untouched, exactly as the comment above promises.
             ⚠️ quoteMeta is flat by construction (strings and numbers off the X-BSF-* response
             headers), so a shallow copy is a full one. */
          quoteMeta: (a.quoteMeta && typeof a.quoteMeta === 'object')
            ? Object.keys(a.quoteMeta).reduce(function (o, k) { o[k] = a.quoteMeta[k]; return o; }, {})
            : null,
        };
      });
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-30) — reply-in-thread + onSent hooks.
       If replyToMessageId is set, doSend posts /outlook-reply (keeps conversationId)
       instead of /outlook-send. onSent fires once per successful send. */
    var _replyToMessageId = ctx.replyToMessageId ? String(ctx.replyToMessageId) : '';
    /* Set by a template that wants its send threaded onto an earlier message. Distinct from
       _replyToMessageId, which is the CALLER opening the modal in reply mode and drives the
       modal's chrome; this one only reaches the send. */
    var _tplReplyTo = null;
    var _onSentCb = (typeof ctx.onSent === 'function') ? ctx.onSent : null;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-30) — "Back" mode. When the caller
       (QW Send-Quote, opened via the reply/new chooser) passes onBack, the Cancel
       button becomes "Back" and returns to that chooser instead of cancelling all
       the way out to the QW — so the rep can switch forks with the already-
       generated PDF still ready. No onBack → plain Cancel, unchanged. */
    var _onBackCb = (typeof ctx.onBack === 'function') ? ctx.onBack : null;
    if (_onBackCb) {
      var _cancelBtnLbl = overlay.querySelector('#rqd-compose-cancel');
      if (_cancelBtnLbl) _cancelBtnLbl.textContent = 'Back';
    }
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-30) — reply-mode thread context.
       When the QW Send-Quote "Reply on existing thread" path opens this modal it
       passes replyThread (the /deal-email-thread emails array) + replyContext
       (subject / other party). We render a scrollable preview of the prior
       emails above the editor so the rep can see WHICH thread they're on and
       read the history. DD callers pass neither → no panel, behavior unchanged. */
    var _replyThread = Array.isArray(ctx.replyThread) ? ctx.replyThread.slice() : [];
    /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE (Ryan 2026-10-10) — the Workstation no longer asks "reply or new?" before this
       opens: the composer's own boxes are that choice, and the thread picker lives here too. `threadChoices` =
       [{label, messageId, subject, to, cc, withWhom, emails}]; `newModeSubject` replaces the thread's subject when
       the rep ticks "new email" (and goes back when they untick it), unless they typed their own. */
    var _threadChoices = Array.isArray(ctx.threadChoices) ? ctx.threadChoices : [];
    var _threadIdx = Math.max(0, Math.min(_threadChoices.length - 1, parseInt(ctx.threadIndex, 10) || 0));
    var _newModeSubject = ctx.newModeSubject ? String(ctx.newModeSubject) : '';
    /* CUSTOM_REPLYSUBJECT_BY_CLAUDE (AIOPS 0d681a4b5eb7, Ryan 2026-10-11) — the subject a picked template
       asked for (used when the rep switches to a new email) and whether the rep has typed in the box.
       Once they type, nothing rewrites the subject again: not a mode switch, not an attachment. */
    var _tplSubject = '';
    var _subjTouched = false;
    /* CUSTOM_REPLYREF_BY_CLAUDE (Ryan 2026-10-28, RW-1549-A) - the base ref the "+ RW-1549" chip put on a REPLY's
       subject, or ''. The box stays read-only on a reply; this is the one addition the worker carries (it appends the
       ref to the thread's subject, people only). Ryan: *"make it so I can click the '+RW-1549' button at least"*. */
    var _replyRefOn = '';
    /* The reference at the end of a NEW email's subject follows the quote PDFs attached (Ryan):
       one version attached -> that version (RW-1494-B); several -> the base (RW-1494); none -> the
       deal's own ref. Only a subject that already ends in this deal's ref is touched, and only until
       the rep types in the box. A reply's subject is the thread's and is never touched. */
    function _attachedQuoteRefs() {
      var base = String(ref || '').toUpperCase().replace(/-[A-Z]$/, '');
      var seen = {}, out = [];
      (attachments || []).forEach(function (a) {
        var r = '';
        if (a && a.quoteMeta && a.quoteMeta.ref) r = String(a.quoteMeta.ref);
        else if (a && /\.pdf$/i.test(String(a.name || ''))) {
          var m = /([A-Z]{2,5}-\d{3,6}-[A-Z])\b/i.exec(String(a.name || ''));
          if (m) r = m[1];
        }
        r = r.toUpperCase();
        if (!r || r.replace(/-[A-Z]$/, '') !== base || seen[r]) return;
        seen[r] = 1; out.push(r);
      });
      return out;
    }
    function _syncSubjectRef() {
      if (_subjTouched || _sendMode === 'reply' || !ref) return;
      var el = overlay && overlay.querySelector('#rqd-compose-subject');
      if (!el) return;
      var base = String(ref).toUpperCase().replace(/-[A-Z]$/, '');
      var refs = _attachedQuoteRefs();
      var want = refs.length === 1 ? refs[0] : (refs.length > 1 ? base : String(ref).toUpperCase());
      var rx = new RegExp('(' + base.replace(/[-]/g, '\\-') + ')(-[A-Z])?(\\s*)$', 'i');
      var cur = String(el.value || '');
      if (!rx.test(cur)) return;
      var next = cur.replace(rx, want + '$3');
      if (next !== cur) el.value = next;
    }
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — set when the mailbox could not be read.
       ⛔ "No thread" and "we could not look" must never render alike: the first says start a new
       email, the second says we do not know. Ryan picked this fail mode explicitly — say so and
       let the rep decide, rather than defaulting silently either way. */
    var _replyUnavailable = ctx.replyUnavailable ? String(ctx.replyUnavailable) : '';
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — is there a message Abby may actually
       reply to? ⛔ NOT "did the thread come back non-empty": a thread can be full of messages she
       is not on, or of tests, none of which are legal parents. The worker answers this with the
       same predicate that picks the target (/abby/thread → canReply), so the box the rep sees
       greyed out and the decision the send makes can never disagree.
       ⚠️ Defaults TRUE when the caller says nothing, so every pre-existing reply-mode caller (the
       QW Send-Quote fork) keeps working exactly as it did. */
    var _replyAllowed = (ctx.canReply === undefined) ? true : !!ctx.canReply;

    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — one thread card's body, safe to inject.
       ⛔ THIS IS THIRD-PARTY HTML. A customer wrote it, and it is going into a page holding an
       authenticated portal session, so scripts, styles, iframes, objects, event handlers and
       javascript: URLs come out before anything is inserted. Remote images are left alone (they
       are how a real signature looks) but everything executable is not negotiable.
       ⚠️ Plain-text bodies are escaped and newline-converted, never fed to the HTML path — running
       plain text through a renderer eats every line break and the email arrives as one paragraph. */
    function _threadBodyHtml(m) {
      var html = (m && m.bodyHtml) ? String(m.bodyHtml) : '';
      if (!html) {
        var txt = (m && (m.bodyText || m.snippet)) ? String(m.bodyText || m.snippet) : '';
        if (!txt) return '<em class="rqd-email-modal__thread-card-empty">(no body captured)</em>';
        return _esc(txt).replace(/\n/g, '<br>');
      }
      /* Cut the quoted chain. Outlook and Gmail both mark where the previous message begins; the
         markers below are the ones the DD viewer already relies on. First match wins, so a card
         keeps only what its sender actually typed. */
      var markers = [
        /<div[^>]+id=["']?(?:appendonsend|divRplyFwdMsg)["']?/i,
        /<div[^>]+class=["'][^"']*gmail_quote/i,
        /<blockquote[^>]+type=["']cite["']/i,
        /-----\s*Original Message\s*-----/i,
        /<hr[^>]*id=["']?stopSpelling/i,
      ];
      for (var mi = 0; mi < markers.length; mi++) {
        var hit = html.search(markers[mi]);
        if (hit > 0) { html = html.slice(0, hit); break; }
      }
      var doc;
      try { doc = new DOMParser().parseFromString(html, 'text/html'); }
      catch (_) { return _esc(String((m && m.snippet) || '')); }
      doc.querySelectorAll('script,style,iframe,object,embed,link,meta,base,form,input,button').forEach(function (n) { n.remove(); });
      doc.querySelectorAll('*').forEach(function (n) {
        Array.prototype.slice.call(n.attributes || []).forEach(function (a) {
          var nm = String(a.name || '').toLowerCase();
          var val = String(a.value || '');
          if (nm.indexOf('on') === 0) n.removeAttribute(a.name);
          else if ((nm === 'href' || nm === 'src' || nm === 'xlink:href') && /^\s*javascript:/i.test(val)) n.removeAttribute(a.name);
        });
        // Links open away from the portal, never in this tab.
        if (n.tagName === 'A') { n.setAttribute('target', '_blank'); n.setAttribute('rel', 'noopener noreferrer'); }
      });
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — THE BROKEN SIGNATURE IMAGES.

         Ryan: *"email signature images don't show up — they just appear as a grey outline with a
         broken image/file icon in the top left corner. we don't have this problem on the other
         email viewer I told you to model this one off of."*

         ⛔ A `cid:` SRC CANNOT RENDER IN A BROWSER, EVER. Outlook embeds signature logos as INLINE
         ATTACHMENTS and points at them with `src="cid:image001.png@01D…"` — a reference into the
         MIME message, not a URL. Nothing fetches it, so the browser draws its broken-image glyph.
         The bytes live in Graph and have to be fetched and swapped in (see _resolveThreadCidImages).

         ⚠️ THE POLICY IS COPIED FROM THE DD VIEWER, NOT INVENTED. There, remote http(s) images
         LOAD by default — Ryan 2026-06-09, because they are almost always our own logos and it is
         how the email looks in Outlook anyway — and only `cid:` is neutralised. Blocking remote
         images here too would have "fixed" the broken icon by making the rest of the signature
         disappear, which is not what he asked for.

         Blanking the src rather than leaving it is what stops the glyph appearing at all; the
         original rides in data-blocked-src, which is the handle the resolver matches on. */
      doc.querySelectorAll('img[src]').forEach(function (img) {
        var v = String(img.getAttribute('src') || '');
        if (!/^\s*cid:/i.test(v)) return;
        img.setAttribute('data-blocked-src', v.trim());
        img.setAttribute('src', '');
        img.style.display = 'none';        // until the bytes land, show nothing rather than a gap
      });
      return doc.body ? doc.body.innerHTML : '';
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — fetch the inline attachments and swap them in.

       One request per MESSAGE (Graph lists a message's attachments with their bytes inline), fired
       only for cards that actually carry a cid: reference, and memoised per message so re-opening
       the accordion does not re-download a signature.

       ⛔ IT MUST BE THE EMAIL'S OWN REF, NOT THE COMPOSER'S. `/outlook-inline-images` gates by
       finding the messageId inside `workstation_meta:{ref}` / `lead:{ref}` for the exact ref given,
       and 404s otherwise. The thread spans a whole revision chain plus the lead, so an email filed
       against RW-1296-B asked about under RW-1296-A resolves nothing. `originRef` is shipped
       per-email by /deal-email-thread for precisely this.

       ⚠️ Failure is silent and leaves the image hidden, never broken: a rep with no Outlook
       connection, a message aged out of the mailbox, or an image over Graph's cap all land here,
       and a missing logo reads far better than a broken-file icon in a customer's signature. */
    var _threadCidCache = {};
    function _resolveThreadCidImages(scope) {
      if (!scope || !(window.RepPortal && window.RepPortal.API)) return;
      var cards = scope.querySelectorAll('[data-cid-msg]');
      Array.prototype.forEach.call(cards, function (card) {
        var imgs = card.querySelectorAll('img[data-blocked-src]');
        if (!imgs.length) return;
        var msgId = card.getAttribute('data-cid-msg') || '';
        var oRef = card.getAttribute('data-cid-ref') || '';
        if (!msgId || !oRef) return;
        var key = oRef + ':' + msgId;
        if (!_threadCidCache[key]) {
          _threadCidCache[key] = window.RepPortal.API
            .get('/outlook-inline-images?ref=' + encodeURIComponent(oRef) + '&messageId=' + encodeURIComponent(msgId))
            .then(function (d) { return (d && d.images) || []; })
            .catch(function () { delete _threadCidCache[key]; return []; });
        }
        _threadCidCache[key].then(function (images) {
          if (!images.length) return;
          var byCid = {};
          images.forEach(function (im) {
            if (!im || !im.contentId || !im.dataBase64) return;
            try {
              var bin = atob(im.dataBase64);
              var bytes = new Uint8Array(bin.length);
              for (var bi = 0; bi < bin.length; bi++) bytes[bi] = bin.charCodeAt(bi);
              /* Content-Id arrives wrapped in angle brackets on some senders and bare on others;
                 the src is always bare. Normalise both ends or half of them never match. */
              byCid[String(im.contentId).replace(/^<|>$/g, '').toLowerCase()] =
                URL.createObjectURL(new Blob([bytes], { type: im.contentType || 'image/png' }));
            } catch (e) { /* malformed base64 — that one image stays hidden */ }
          });
          Array.prototype.forEach.call(imgs, function (img) {
            var cid = String(img.getAttribute('data-blocked-src') || '')
              .replace(/^\s*cid:/i, '').replace(/^<|>$/g, '').toLowerCase();
            if (!byCid[cid]) return;
            img.setAttribute('src', byCid[cid]);
            img.removeAttribute('data-blocked-src');
            img.style.display = '';
          });
        });
      });
    }
    var _replyContext = (ctx.replyContext && typeof ctx.replyContext === 'object') ? ctx.replyContext : null;
    var _isReplyMode = !!_replyToMessageId;
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — 'reply' | 'new'. Threading is the DEFAULT
       whenever a thread exists, and the rep may override it. Ryan: *"we can make it default to
       replying on the thread when there is one — that way there's no forced extra click or choice
       — then the rep can override and pick the other option if they want. that is cleaner."*
       ⚠️ A composer opened WITHOUT a reply target is 'new' and has no control to change that:
       there is nothing to thread onto, so offering the choice would be offering a lie. */
    var _sendMode = (_isReplyMode && _replyAllowed) ? 'reply' : 'new';

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-30) — render reply context.
       Mark the modal as reply-mode (scoping CSS), relabel the head so the rep
       sees the thread they're answering, and inject a scrollable preview of the
       prior thread emails as the first child of the compose area. All guarded
       on _isReplyMode → no-op for the normal new-email + DD compose flows. */
    if (_isReplyMode) {
      var _modalRoot = overlay.querySelector('.rqd-email-modal');
      if (_modalRoot) _modalRoot.classList.add('rqd-email-modal--reply-ctx');

      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — the thread could not be read. The
         accordion below only builds when there ARE messages, so without this the rep would get
         reply-mode chrome, no thread, and no way to opt out — the worst of both. Carries its own
         copy of the override so the choice survives the failure. */
      if (!_replyThread.length && _replyUnavailable) {
        var _cAreaU = overlay.querySelector('.rqd-email-modal__compose');
        if (_cAreaU) {
          var _warn = document.createElement('div');
          _warn.className = 'rqd-email-modal__reply-unavailable';
          _warn.innerHTML = '<div><strong>⚠ Could not load the email thread.</strong> '
            + _esc(_replyUnavailable)
            + ' This will still <strong>reply</strong> on the last message we know about, which is almost certainly right. '
            + 'Tick the box to start a new email instead.</div>'
            + '<label class="rqd-email-modal__reply-mode" for="rqd-compose-send-new-u">'
            +   '<input type="checkbox" id="rqd-compose-send-new-u">'
            +   '<span>Send as a <strong>new email</strong> instead</span>'
            + '</label>';
          _cAreaU.insertBefore(_warn, _cAreaU.firstChild);
          var _uBox = _warn.querySelector('#rqd-compose-send-new-u');
          if (_uBox) _uBox.addEventListener('change', function () { _sendMode = _uBox.checked ? 'new' : 'reply'; });
        }
      }

      // Relabel the head: "Reply" + which thread / who.
      var _dirEl = overlay.querySelector('.rqd-email-modal__dir');
      var _subjEl0 = overlay.querySelector('.rqd-email-modal__subject');
      var _ctxSubject = (_replyContext && _replyContext.subject) ? _replyContext.subject : '';
      var _ctxWhom = (_replyContext && _replyContext.withWhom) ? _replyContext.withWhom : '';
      if (_dirEl) {
        _dirEl.innerHTML = (ref ? _esc(ref) + ' &middot; ' : '') + 'Reply'
          + (_ctxWhom ? ' to ' + _esc(_ctxWhom) : '');
      }
      if (_subjEl0) {
        _subjEl0.textContent = _ctxSubject
          ? _ctxSubject.replace(/^(?:Re|Fwd?|FW):\s*/i, '').trim()
          : 'Reply';
      }

      /* Split the compose UI into a 2-section exclusive accordion:
         "Replying on this thread" (open by default — the thread preview) and
         "Email details" (collapsed — the To/Cc/Subject/body/attachments). Only
         one open at a time so whichever is open shows ALL its content without
         the cramped, can't-see-anything short panel. (Ryan 2026-05-30) */
      if (_replyThread.length) {
        var _composeArea = overlay.querySelector('.rqd-email-modal__compose');
        if (_composeArea) {
          function _threadRowsHtml() { return _replyThread.map(function(m) {
            var isIn = m && m.direction === 'inbound';
            var who = (m && (m.fromName || m.from)) || '';
            var when = '';
            if (m && m.ts) {
              try { when = new Date(m.ts).toLocaleString(); } catch (_) { when = String(m.ts); }
            }
            var dirArrow = isIn ? '↘' : '↗';
            var dirLabel = isIn ? 'Received' : 'Sent';
            var subj = (m && m.subject) || '(no subject)';
            var snip = (m && m.snippet) ? m.snippet : '';
            return '<div class="rqd-email-modal__reply-thread-msg' + (isIn ? ' rqd-email-modal__reply-thread-msg--in' : ' rqd-email-modal__reply-thread-msg--out') + '">'
              + '<div class="rqd-email-modal__reply-thread-meta">'
              +   '<span class="rqd-email-modal__reply-thread-dir" title="' + dirLabel + '">' + dirArrow + ' ' + dirLabel + '</span>'
              +   '<span class="rqd-email-modal__reply-thread-who">' + _esc(who) + '</span>'
              +   (when ? '<span class="rqd-email-modal__reply-thread-date">' + _esc(when) + '</span>' : '')
              + '</div>'
              + '<div class="rqd-email-modal__reply-thread-subj">' + _esc(subj) + '</div>'
              + (snip ? '<div class="rqd-email-modal__reply-thread-snip">' + _esc(snip) + '</div>' : '')
              + '</div>';
          }).join(''); }

          // Snapshot the existing compose fields BEFORE we add the accordion —
          // these get re-homed inside the collapsible "Email details" section.
          // Their IDs are preserved by the move, so all the later
          // overlay.querySelector('#rqd-compose-*') wiring still resolves.
          var _detailNodes = Array.prototype.slice.call(_composeArea.children);

          var _acc = document.createElement('div');
          _acc.className = 'rqd-acc';
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — with no message Abby may answer,
             the thread section starts COLLAPSED and Email details starts open: there is nothing to
             read and no choice to make, so leading with a thread the rep cannot use would put the
             least useful thing at the top. With a live thread, unchanged — thread first, because
             what you are replying to is the thing you want to see before you type. */
          _acc.innerHTML = ''
            + '<div class="rqd-acc__section' + (_replyAllowed ? ' rqd-acc__section--open' : '') + '" data-acc="thread">'
            +   '<button type="button" class="rqd-acc__header">'
            +     '<span class="rqd-acc__icon" aria-hidden="true">' + (_replyAllowed ? '−' : '+') + '</span>'
            +     '<span class="rqd-acc__title">Replying on this thread</span>'
            +     '<span class="rqd-acc__count">' + _replyThread.length + ' message' + (_replyThread.length === 1 ? '' : 's') + '</span>'
            +   '</button>'
            +   '<div class="rqd-acc__body">'
            /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — the override, sitting ON the
               thread it overrides. Unchecked (reply) is the default and the common case, so the
               normal path costs nobody a click; ticking it is the deliberate act. Placed above
               the message list rather than in the toolbar because the decision and the evidence
               for it belong in the same eyeline. */
            /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — TWO boxes, one decision. They are
               mutually exclusive and exactly one is always ticked, so this is a radio wearing
               checkbox clothes: Ryan asked for a second box rather than a radio group, and two
               boxes read better here because each states its own full sentence instead of sharing
               a label with a heading.
               ⚠️ The reply box is DISABLED when Abby has no message she may lawfully answer — no
               thread, or a thread of tests and rep-only messages she is not on. In that state the
               new-email box is ticked and the section starts collapsed, because there is nothing
               to look at and no decision to make. */
            + (_threadChoices.length >= 1
                ? '<label class="rqd-email-modal__thread-pick" style="display:flex;align-items:center;gap:8px;margin:0 0 8px;font-size:12px;color:var(--rq-text-secondary,#9aa7b8);">'
                  + '<span style="flex:0 0 auto;">Thread</span>'
                  + '<select id="rqd-compose-thread-pick" style="flex:1 1 auto;min-width:0;padding:6px 8px;border:1px solid var(--rq-border,#1e2a42);border-radius:6px;background:var(--rq-bg-input,#0b1220);color:var(--rq-text,#e6edf6);font:500 12px Montserrat,sans-serif;">'
                  +   _threadChoices.map(function (c, i) { return '<option value="' + i + '"' + (i === _threadIdx ? ' selected' : '') + '>' + _esc(c.label || ('Thread ' + (i + 1))) + '</option>'; }).join('')
                  +   '<option value="new">New email thread</option>'
                  + '</select></label>'
                : '')
            +     '<label class="rqd-email-modal__reply-mode" for="rqd-compose-send-new">'
            +       '<input type="checkbox" id="rqd-compose-send-new"' + (_replyAllowed ? '' : ' checked') + '>'
            +       '<span>Send as a <strong>new email</strong> instead of replying on this thread</span>'
            +     '</label>'
            +     '<label class="rqd-email-modal__reply-mode' + (_replyAllowed ? '' : ' rqd-email-modal__reply-mode--disabled') + '" for="rqd-compose-reply-thread">'
            +       '<input type="checkbox" id="rqd-compose-reply-thread"'
            +         (_replyAllowed ? ' checked' : ' disabled') + '>'
            /* ⛔ "Reply", NOT "Reply all", and the wording is load-bearing. Ryan asked for a box
               saying "Reply all to the previous thread"; abbyReplyCore uses Graph's `createReply`
               and PATCHes the recipients to exactly the To/Cc shown in this composer. It is not a
               reply-all and must not claim to be — a label promising one would have the admin
               believe everyone on the thread is being written to.
               ⚠️ And it should stay that way. The REP path (email/rep-thread-send.js) does use
               createReplyAll, and has to subtract our own mailboxes afterwards precisely because
               it keeps everyone the thread ever had — which is how a vendor spliced into a
               conversation ends up Cc'd on a customer email. That was fixed on 2026-08-20; Abby
               should not re-acquire the same exposure. The To/Cc row above is the list, and it is
               editable, which is the honest version of the same control. */
            +       '<span>Reply on the previous thread <em class="rqd-email-modal__reply-mode-note">(goes to the To/Cc above)</em>'
            +         (_replyAllowed ? '' : ' <em>&mdash; no earlier email on this thread that Abby is on</em>')
            +       '</span>'
            +     '</label>'
            /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — ⛔ THE MESSAGE LIST IS GONE FROM
               HERE. Ryan: *"when the 2nd box is checked, don't show the other emails below it...
               only show them when you then go and click on the 'Email details' tab."* This section
               is now purely the decision; the emails themselves render in full under Email details,
               which is the one place they exist. Showing a snippet list here AND full cards there
               was the same content twice, in two fidelities, a scroll apart. The header count still
               says a thread exists without putting it on screen. */
            +   '</div>'
            + '</div>'
            + '<div class="rqd-acc__section' + (_replyAllowed ? '' : ' rqd-acc__section--open') + '" data-acc="details">'
            +   '<button type="button" class="rqd-acc__header">'
            +     '<span class="rqd-acc__icon" aria-hidden="true">' + (_replyAllowed ? '+' : '−') + '</span>'
            +     '<span class="rqd-acc__title">Email details</span>'
            +   '</button>'
            +   '<div class="rqd-acc__body" id="rqd-acc-details-body"></div>'
            + '</div>';

          _composeArea.appendChild(_acc);
          var _detailsBody = _acc.querySelector('#rqd-acc-details-body');
          _detailNodes.forEach(function(n) { _detailsBody.appendChild(n); });

          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — the previous emails, rendered in
             full, UNDER the compose fields inside Email details. Ryan: *"you can scroll down and
             see the previous emails written out similar style/UX/UI to how you can in the DD when
             you click on an email, it pops up in white, then you can scroll below it to see
             previous emails in the same thread below it."*

             ⚠️ Bodies come from Abby's MAILBOX (/abby/thread), not the deal feed — the feed is
             empty on exactly these deals. Rendered into a sandboxed container with scripts and
             remote-loading tags stripped: this is third-party HTML a customer authored, pasted
             into a page that holds an authenticated portal session.

             ⛔ The quoted history is stripped from each card. Outlook composes every reply
             containing the entire chain before it, so an untrimmed 6-email thread renders
             6+5+4+3+2+1 messages — the DD viewer hit exactly that and its fix is the same idea.
             Here the rest of the chain IS the other cards, so each one only needs its own text. */
          /* ⚠️ TWO FIDELITIES, AND THE SOURCE DECIDES WHICH — bodies present → white cards; none
             anywhere → the compact meta rows.

             BOTH feeds now carry bodies. The DD's /abby/thread always did. /deal-email-thread was
             metadata-only until 2026-08-26, which is why this branch exists at all: rendering it as
             cards would have printed "(no body captured)" once per message. Ryan, on the QW fork:
             *"I also want it to show me the actual email, instead of just a header/card with the
             from/subjectline/date/time… like it looks in the DD when you click on an email in the
             Deal Feed."* So the endpoint now ships the HTML it had in KV all along.

             ⛔ THE META BRANCH IS NOT DEAD CODE. /deal-email-thread caps bodies at the newest 20
             messages, so a long-running deal's older mail still arrives with a snippet only — and a
             record written before the sync stored bodies has neither. This is the floor those fall
             to, and it is the resting state a rep sees if the body cap is ever lowered. */
          function _renderThreadHistory() {
          var _oldHist = _detailsBody.querySelector('.rqd-email-modal__thread-history');
          if (_oldHist) _oldHist.remove();
          var _rows = _threadRowsHtml();
          var _anyBodies = _replyThread.some(function (m) { return m && (m.bodyHtml || m.bodyText || m.snippet); });
          if (_replyThread.length && !_anyBodies) {
            var _metaWrap = document.createElement('div');
            _metaWrap.className = 'rqd-email-modal__thread-history';
            _metaWrap.innerHTML = '<div class="rqd-email-modal__thread-history-head">Previous emails on this thread ('
              + _replyThread.length + ')</div>'
              + '<div class="rqd-email-modal__reply-thread-list">' + _rows + '</div>';
            _detailsBody.appendChild(_metaWrap);
          } else if (_replyThread.length) {
            var _histWrap = document.createElement('div');
            _histWrap.className = 'rqd-email-modal__thread-history';
            _histWrap.innerHTML = '<div class="rqd-email-modal__thread-history-head">Previous emails on this thread ('
              + _replyThread.length + ')</div>'
              + _replyThread.map(function (m) {
                var isIn = m && m.direction === 'inbound';
                var who = (m && (m.fromName || m.from)) || '';
                var when = '';
                if (m && m.ts) { try { when = new Date(m.ts).toLocaleString(); } catch (_) { when = String(m.ts); } }
                /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — the FROM and TO, not just the from.
                   Ryan asked for these cards to read the way the Deal Feed's email viewer does:
                   *"the header with the from and to, the date/time, the subject line."* Without the
                   recipients a rep scrolling the thread cannot tell their own reply to the customer
                   from their own reply to somebody else — every one of them says "Ryan Walrod".
                   Cc rides the same line and only appears when there is one.
                   ⚠️ Names, then addresses: `toNames` is positional against `to`, so a recipient
                   Outlook had no display name for falls back to its address rather than shifting
                   the whole list by one. */
                var _pretty = function (addrs, names) {
                  var A = Array.isArray(addrs) ? addrs : [], N = Array.isArray(names) ? names : [];
                  return A.map(function (a, i) { return String(N[i] || a || '').trim(); })
                          .filter(Boolean).join(', ');
                };
                var _to = _pretty(m && m.to, m && m.toNames);
                var _cc = _pretty(m && m.cc, m && m.ccNames);
                /* data-cid-* is the handle _resolveThreadCidImages needs: which message to ask
                   Graph about, and under WHICH ref (the email's own filing record — a sibling
                   revision on a chained deal, not necessarily the composer's ref). */
                return '<article class="rqd-email-modal__thread-card' + (isIn ? ' rqd-email-modal__thread-card--in' : '') + '"'
                  + ' data-cid-msg="' + _esc((m && m.messageId) || '') + '"'
                  + ' data-cid-ref="' + _esc((m && (m.originRef || m.ref)) || ref || '') + '">'
                  + '<header class="rqd-email-modal__thread-card-head">'
                  +   '<span class="rqd-email-modal__thread-card-who">' + (isIn ? '↘ ' : '↗ ') + _esc(who) + '</span>'
                  +   (when ? '<span class="rqd-email-modal__thread-card-when">' + _esc(when) + '</span>' : '')
                  + '</header>'
                  + (_to ? '<div class="rqd-email-modal__thread-card-to"><span>To</span> ' + _esc(_to) + '</div>' : '')
                  + (_cc ? '<div class="rqd-email-modal__thread-card-to"><span>Cc</span> ' + _esc(_cc) + '</div>' : '')
                  + '<div class="rqd-email-modal__thread-card-subj">' + _esc((m && m.subject) || '(no subject)') + '</div>'
                  + '<div class="rqd-email-modal__thread-card-body">' + _threadBodyHtml(m) + '</div>'
                  + '</article>';
              }).join('');
            _detailsBody.appendChild(_histWrap);
            /* AFTER the mount, never before: the resolver walks the live DOM to find which images
               are still waiting on bytes, and an unmounted fragment has nothing to walk. */
            _resolveThreadCidImages(_histWrap);
          }
          }
          _renderThreadHistory();
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — with previous emails underneath it,
             the compose editor releases its 240px cap and the modal scrolls as ONE. Flagged on the
             root rather than inferred in CSS because "is there a thread below" is a fact this code
             knows and a stylesheet cannot. Set for both fidelities: the QW meta list is shorter but
             it is still content under the editor, and two scrollbars are two scrollbars. */
          if (_replyThread.length && _modalRoot) _modalRoot.classList.add('rqd-email-modal--thread-open');

          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — flip the mode and SAY SO. The header
             and the greyed thread are the whole feedback: a checkbox that silently changed where a
             customer email lands would be the same class of invisible state the alert() this
             replaces was compensating for. */
          var _newBox = _acc.querySelector('#rqd-compose-send-new');
          var _replyBox = _acc.querySelector('#rqd-compose-reply-thread');
          var _threadSec = _acc.querySelector('[data-acc="thread"]');
          var _threadTitle = _threadSec ? _threadSec.querySelector('.rqd-acc__title') : null;

          /* One decision, two boxes: whichever the rep touched wins and the other follows. Written
             as a single applier rather than two handlers so the pair can never both be ticked or
             both be clear — a state in which the send would have to guess. */
          function _applyMode(mode) {
            _sendMode = (mode === 'new' || !_replyAllowed) ? 'new' : 'reply';
            if (_newBox) _newBox.checked = (_sendMode === 'new');
            if (_replyBox && !_replyBox.disabled) _replyBox.checked = (_sendMode === 'reply');
            if (_threadTitle) {
              _threadTitle.textContent = (_sendMode === 'new')
                ? 'Starting a NEW email (not replying on this thread)'
                : 'Replying on this thread';
            }
            /* Grey the previous emails when this is no longer a reply. They live under Email
               details now, so the flag goes on the modal root — the section class this used to
               toggle pointed at a message list that is no longer in that section, which would
               have been a rule quietly matching nothing. */
            if (_modalRoot) _modalRoot.classList.toggle('rqd-email-modal--mode-new', _sendMode === 'new');
            /* The subject box is inert on a reply (Graph's createReply owns "RE: ..."), and live on
               a new email. Un-disable it so the rep can actually set one, rather than typing into a
               field that is ignored — the exact complaint that put an alert() here. */
            var _subjIn = overlay.querySelector('#rqd-compose-subject');
            if (_subjIn) _subjIn.disabled = false;
            /* CUSTOM_REPLYSUBJECT_BY_CLAUDE (AIOPS 0d681a4b5eb7, Ryan 2026-10-11) — ⛔ A REPLY KEEPS ITS
               THREAD'S SUBJECT. The box is read-only on a reply and shows the thread's subject; the
               worker keeps Graph's "RE: <thread>" whatever the box says (replyDraftPatch). A new email
               takes the template's subject when one was picked, else the caller's new-email subject. */
            if (_subjIn) {
              _subjIn.readOnly = (_sendMode === 'reply');
              _subjIn.title = (_sendMode === 'reply')
                ? 'A reply keeps the thread\'s subject. Tick "new email" to send under a different one.'
                : '';
              var _thS = String((_replyContext && _replyContext.subject) || '');
              if (_sendMode === 'reply') {
                if (_thS) _subjIn.value = _thS + ((_replyRefOn && _thS.toUpperCase().indexOf(_replyRefOn) === -1) ? ' - ' + _replyRefOn : '');   // CUSTOM_REPLYREF_BY_CLAUDE
              } else if (!_subjTouched) {
                var _cur0 = String(_subjIn.value || '').trim();
                if (!_cur0 || _cur0 === _thS || _cur0 === _newModeSubject) {
                  var _next = _tplSubject || _newModeSubject || '';
                  if (_next) { _subjIn.value = _next; _syncSubjectRef(); }
                }
              }
            }
            /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE — the drop-down and the boxes are one choice: "new" shows New email thread. */
            if (_pickSel) _pickSel.value = (_sendMode === 'new') ? 'new' : String(_threadIdx);
          }
          var _pickSel = _acc.querySelector('#rqd-compose-thread-pick');
          if (_pickSel) _pickSel.addEventListener('change', function () {
            if (_pickSel.value === 'new') { _applyMode('new'); return; }
            var c = _threadChoices[parseInt(_pickSel.value, 10) || 0];
            if (!c) return;
            _threadIdx = parseInt(_pickSel.value, 10) || 0;
            var _prevSubj = String((_replyContext && _replyContext.subject) || '');
            _replyToMessageId = String(c.messageId || '');
            _replyThread = Array.isArray(c.emails) ? c.emails.slice() : [];
            _replyContext = { subject: c.subject || '', withWhom: c.withWhom || '', count: _replyThread.length };
            /* The audience is the picked thread's (reply-all minus our mailboxes, built by the worker). */
            if (Array.isArray(c.to) && c.to.length) toChips = c.to.slice();
            ccChips = Array.isArray(c.cc) ? c.cc.slice() : [];
            if (_composeChipApi) _composeChipApi.renderAll();
            var _sIn = overlay.querySelector('#rqd-compose-subject');
            if (_sIn && _sendMode === 'reply' && (!_sIn.value || _sIn.value === _prevSubj)) _sIn.value = c.subject || '';
            var _cnt = _threadSec && _threadSec.querySelector('.rqd-acc__count');
            if (_cnt) _cnt.textContent = _replyThread.length + ' message' + (_replyThread.length === 1 ? '' : 's');
            if (_dirEl) _dirEl.innerHTML = (ref ? _esc(ref) + ' &middot; ' : '') + 'Reply' + (c.withWhom ? ' to ' + _esc(c.withWhom) : '');
            if (_subjEl0) _subjEl0.textContent = c.subject ? String(c.subject).replace(/^(?:Re|Fwd?|FW):\s*/i, '').trim() : 'Reply';
            _renderThreadHistory();
            _applyMode('reply');
          });
          if (_newBox) _newBox.addEventListener('change', function () { _applyMode(_newBox.checked ? 'new' : 'reply'); });
          if (_replyBox) _replyBox.addEventListener('change', function () { _applyMode(_replyBox.checked ? 'reply' : 'new'); });
          _applyMode(_sendMode);

          /* Open/close a section. Mirrors the QW card mechanism: max-height
             drives the slide (set to scrollHeight on open, released to none
             after the transition so the editor/thread can grow + scroll);
             closed sections drop to max-height 0 with zero vertical padding so
             only the header shows. +/− icon flips to match. */
          function _accSet(sec, open) {
            var body = sec.querySelector('.rqd-acc__body');
            var icon = sec.querySelector('.rqd-acc__icon');
            if (!body) return;
            if (open) {
              sec.classList.add('rqd-acc__section--open');
              if (icon) icon.textContent = '−';
              body.style.maxHeight = body.scrollHeight + 'px';
              var _rel = function (e) {
                if (e.target !== body || e.propertyName !== 'max-height') return;
                body.removeEventListener('transitionend', _rel);
                if (sec.classList.contains('rqd-acc__section--open')) body.style.maxHeight = 'none';
              };
              body.addEventListener('transitionend', _rel);
            } else {
              // From 'none'/auto → a concrete px so the collapse can animate.
              if (!body.style.maxHeight || body.style.maxHeight === 'none') {
                body.style.maxHeight = body.scrollHeight + 'px';
                body.offsetHeight; // force reflow
              }
              sec.classList.remove('rqd-acc__section--open');
              if (icon) icon.textContent = '+';
              body.style.maxHeight = '0px';
            }
          }
          // Exclusive-open: opening one closes the other. Re-clicking the open
          // header keeps it open, so exactly one section is always visible.
          var _accSections = _acc.querySelectorAll('.rqd-acc__section');
          function _accOpen(target) {
            _accSections.forEach(function(s) { _accSet(s, s === target); });
            _gateSendButtons();
          }
          /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE (Ryan 2026-10-10) — Send and Send Test are greyed until Email details is open,
             so nothing goes out before the rep has seen the email. A click on a greyed one opens Email details instead.
             A class, not `disabled`: the send handler owns `disabled` while a send is in flight. */
          var _detailsSec = _acc.querySelector('[data-acc="details"]');
          function _gateSendButtons() {
            var shut = !!_detailsSec && !_detailsSec.classList.contains('rqd-acc__section--open');
            ['#rqd-compose-send', '#rqd-compose-test'].forEach(function (sel) {
              var b = overlay.querySelector(sel);
              if (!b) return;
              b.classList.toggle('rqd-compose-gated', shut);
              b.style.opacity = shut ? '0.45' : '';
              b.style.cursor = shut ? 'not-allowed' : '';
              if (shut) { if (!b.dataset.gateTitle) b.dataset.gateTitle = b.title || ''; b.title = 'Open Email details to see the email first'; }
              else if (b.dataset.gateTitle !== undefined) { b.title = b.dataset.gateTitle; delete b.dataset.gateTitle; }
            });
            /* Cancel reads Back while Email details is open: Back returns to the thread, Cancel there closes the
               composer (keeping the draft). A caller's own Back (onBack) is left alone. */
            var cb = overlay.querySelector('#rqd-compose-cancel');
            if (cb && !_onBackCb) {
              cb.classList.toggle('rqd-compose-back', !shut);
              cb.textContent = shut ? 'Cancel' : 'Back';
            }
          }
          overlay.addEventListener('click', function (e) {
            var bk = e.target.closest && e.target.closest('.rqd-compose-back');
            if (bk) {
              e.preventDefault(); e.stopImmediatePropagation();
              var _thSec = _acc.querySelector('[data-acc="thread"]');
              if (_thSec) _accOpen(_thSec);
              return;
            }
            var g = e.target.closest && e.target.closest('.rqd-compose-gated');
            if (!g) return;
            e.preventDefault(); e.stopImmediatePropagation();
            if (_detailsSec) _accOpen(_detailsSec);
          }, true);
          _accSections.forEach(function(s) {
            s.querySelector('.rqd-acc__header').addEventListener('click', function() { _accOpen(s); });
          });
          // Initial state (no animation): thread open at natural height,
          // details fully collapsed.
          var _threadBody = _acc.querySelector('[data-acc="thread"] .rqd-acc__body');
          if (_threadBody) _threadBody.style.maxHeight = 'none';
          var _detailsBody2 = _acc.querySelector('[data-acc="details"] .rqd-acc__body');
          if (_detailsBody2) _detailsBody2.style.maxHeight = '0px';
          _gateSendButtons();
        }
      }
    }
    var _composeIdemKey = null; // Ryan 2026-05-29: send idempotency key (per modal session)
    var autoSaveTimer = null;
    /* ⚠️ THE KEY IS VERSIONED, AND v2 IS A ONE-TIME AMNESTY (Ryan 2026-08-20). Until the fix in
       `_saveDraftNow` below, every prefilled compose — PO, freight, dims, invoice, the ECORE
       sample request — wrote its server-built body and recipients into this deal-wide key. Those
       drafts are still sitting in reps' browsers, and the first free-form compose on each of
       those deals would restore one. Bumping the key retires every poisoned draft at once
       instead of waiting for each rep to hit it and clear it by hand.
       ⚠️ The cost, stated plainly: a genuinely-typed, unsent free-form draft is also forgotten
       once. Drafts are autosaves of unsent work, not records, and the alternative is a rep
       opening an email to their customer and finding a purchase order in it. */
    var draftKey = 'rqd-compose-draft:v2:' + (ref || '_no_ref');
    /* Sweep the pre-v2 keys so the retired drafts do not sit in localStorage for ever. Cheap
       (a handful of keys), best-effort, and it runs before any restore below. */
    try {
      var _stale = [];
      for (var _i = 0; _i < localStorage.length; _i++) {
        var _k = localStorage.key(_i);
        if (_k && _k.indexOf('rqd-compose-draft:') === 0 && _k.indexOf('rqd-compose-draft:v2:') !== 0) _stale.push(_k);
      }
      _stale.forEach(function (k) { localStorage.removeItem(k); });
    } catch (_) { /* a full or disabled localStorage is not worth failing the composer over */ }
    var quill = null;
    /* Ryan 2026-08-18 — email templates. The binding remembers the exact text we
       inserted so the copy can follow the attachment list until the rep edits it. */
    var _tplBinding = _newTemplateBinding();
    function _tplCtx() {
      var c = { ref: ref, attachments: attachments, toEmails: (toChips || []).concat(ccChips || []) };
      /* ⛔ WRITE THE EMAIL FOR WHEN IT WILL ARRIVE. If the ⏱ clock is ticked and we are outside
         the window, every template renders against the SCHEDULED send time — so "earlier today"
         becomes "yesterday" on screen, before the rep sends it, rather than in the customer's
         inbox at 6am. See _nextSendWindowMs. */
      var held = _heldSendMs();
      if (held) c.nowMs = held;
      /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — a follow-up 1 scheduled days out is
         written for THAT day, and it outranks the ⏱ hold (the worker ignores the clock when a start
         delay is set). `delayedStart` tells the template to name the call by its weekday. */
      var start = (_seq && _seq.startMs) ? _seq.startMs() : 0;
      if (start) { c.nowMs = start; c.delayedStart = true; }
      return c;
    }

    /* The scheduled send time, or 0 when this email is going out now. */
    function _heldSendMs() {
      var box = overlay.querySelector('#rqd-compose-bizhours');
      if (!box || !box.checked) return 0;
      var when = _nextSendWindowMs();
      /* Inside the window `_nextSendWindowMs` answers "now", and re-rendering against now is a
         no-op — so a zero here means "nothing is being held" and every caller can test it. */
      return (when > Date.now() + 60000) ? when : 0;
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (AIOPS 3b7daa183669, Ryan 2026-09-14) — the automated-sequence
       control, which used to be ~240 lines right here and is now the module-scope
       `_installSeqControl` shared with the Deal Feed's Reply composer. Everything it knows about
       this composer is the id prefix; everything this composer knows about it is these four
       calls. See the note above `_SEQ_FLOWS` for why it moved. */
    var _seq = _installSeqControl({
      overlay: overlay,
      prefix: 'rqd-compose-',
      allowed: _seqAllowed,
      getTplId: function () { return (_tplBinding && _tplBinding.id) || ''; },
      getTplCtx: function () { return _tplCtx(); },
      /* CUSTOM_DELAYEDSTART_BY_CLAUDE — re-write the body for the day it will arrive. */
      onStartChange: function (n, whenMs) { _onStartChange(n, whenMs, _tplBinding, quill, _tplCtx, _snipStatus); },
    });
    /* Kept as a named function rather than inlined at the call sites: it is called from six
       places in this composer (every template insert, every re-render, the checkbox, the
       Follow-up dropdown, the draft restore) and the name is what says why. */
    function _seqSyncControl() { return _seq.sync(); }
    /* Snippet feedback shares the template status card, so a rep has one place to look. Both
       kinds of message land here: a warning about copy that DID go in ("no first name on this
       deal"), and a refusal where nothing did ("attach the quote first"). */
    function _snipStatus(msg, isErr) {
      var s = overlay.querySelector('#rqd-compose-status');
      if (!s) return;
      s.textContent = msg || '';
      s.classList.toggle('rqd-email-modal__compose-status--err', !!isErr);
      s.classList.toggle('rqd-email-modal__compose-status--tpl', !!msg && !isErr);
    }

    /* Phase B.5a: chip subsystem delegated to module-scope
       _installChipFields (shared with the reply modal's _enterCompose).
       State lives here (toChips / ccChips / bccChips); shared helper
       wires DOM behavior. addChipsFromText exposed for draft restore. */
    var _composeChipApi = null;   // populated below after scheduleDraftSave is defined
    function addChipsFromText(kind, raw) {
      return _composeChipApi ? _composeChipApi.addFromText(kind, raw) : 0;
    }

    function close() {
      if (autoSaveTimer) clearTimeout(autoSaveTimer);
      overlay.remove();
      if (_minTab) { try { _minTab.remove(); } catch (_) {} _minTab = null; }
      if (_liveMin && _liveMin.ref === ref) _liveMin = null;
      if (ctx.persistMinimized) _minClear(ref);   // CUSTOM_COMPOSEDRAFT_BY_CLAUDE — closed = put away (the draft stays)
      setTimeout(_refreshDraftMarkers, 0);
      document.removeEventListener('keydown', onEsc);
      if (document.activeElement && document.activeElement.blur) {
        try { document.activeElement.blur(); } catch (_) {}
      }
    }
    function onEsc(ev) { if (ev.key === 'Escape' && overlay.style.display !== 'none') close(); }
    /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE (Ryan 2026-10-10) — minimize: the composer hides (the page scrolls and clicks
       as normal) and a small tab sits bottom-left; clicking it brings the email back exactly as it was. */
    var _minTab = null;
    /* No draft-file write until the saved draft's files have been read back, or the first paint (the fresh quote
       PDF alone) would overwrite them. */
    var _draftFilesReady = false;
    function _keepDraft() {
      _saveDraftNow();
      /* A server-prefilled email (PO, freight, invoice...) never writes the deal's draft: see _saveDraftNow. */
      if (!ctx.prefillBody && !_richBodyHtml && _draftFilesReady) _idbDraftPut(draftKey, attachments);
    }
    /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE (Ryan 2026-10-10) — the tab sits just right of the portal's left menu rail (it
       covered the Settings button at the bottom of it); the email shrinks into the tab on minimize and grows back out
       of it on restore. No animation for a visitor who asked for reduced motion. */
    var _minAnimating = false;
    _ensureWinBtnCss();
    function _composeBox() { return overlay.querySelector('.rqd-email-modal--compose'); }
    function _minimize() {
      if (_minAnimating) return;
      _keepDraft();
      if (_minTab) _minTab.remove();
      var subj = String((overlay.querySelector('#rqd-compose-subject') || {}).value || '').trim();
      _minTab = _buildMinTab(subj, ref, _restoreFromTab, function () { _keepDraft(); close(); _composeToast('Draft email saved'); });
      _minTab.className = 'rqd-compose-mintab';
      /* Remembered across a refresh only for a customer email whose draft is saved (never a PO, freight or invoice). */
      if (ref) {
        _liveMin = { ref: ref, restore: _restoreFromTab };
        if (ctx.persistMinimized && !ctx.prefillBody && !_richBodyHtml) {
          _minWrite(ref, { ref: ref, subject: subj, at: Date.now(), ctx: _snapshotCtx(ctx) });
          if (_restoredTab && _restoredTab.ref === ref) _dropRestoredTab();
          setTimeout(_refreshDraftMarkers, 0);
        }
      }
      var box = _composeBox();
      var fly = (box && box.animate && !_reduceMotion()) ? _flyFromRect(box, _rectOf(_minTab)) : null;
      if (!fly) { overlay.style.display = 'none'; return; }
      _minAnimating = true;
      _minTab.style.opacity = '0';
      var dur = 340;
      var a = box.animate([{ transform: 'none', opacity: 1 }, { transform: fly, opacity: 0.35 }],
        { duration: dur, easing: 'cubic-bezier(.55,0,.75,.2)', fill: 'forwards' });
      overlay.animate([{ backgroundColor: getComputedStyle(overlay).backgroundColor }, { backgroundColor: 'rgba(0,0,0,0)' }],
        { duration: dur, easing: 'ease-in', fill: 'forwards' });
      a.onfinish = function () {
        overlay.style.display = 'none';
        try { overlay.getAnimations().forEach(function (x) { x.cancel(); }); box.getAnimations().forEach(function (x) { x.cancel(); }); } catch (_) {}
        _minAnimating = false;
        if (!_minTab) return;
        _minTab.style.opacity = '';
        _popIn(_minTab);
      };
    }
    function _restoreFromTab() {
      if (_minAnimating) return;
      var tab = _minTab;
      _minTab = null;
      if (_liveMin && _liveMin.ref === ref) _liveMin = null;
      if (ctx.persistMinimized) _minClear(ref);
      overlay.style.display = '';
      var box = _composeBox();
      var fly = (tab && box && box.animate && !_reduceMotion()) ? _flyFromRect(box, _rectOf(tab)) : null;
      if (tab) tab.remove();
      if (!fly) return;
      _minAnimating = true;
      var dur = 340;
      overlay.animate([{ backgroundColor: 'rgba(0,0,0,0)' }, { backgroundColor: getComputedStyle(overlay).backgroundColor }],
        { duration: dur, easing: 'ease-out' });
      var a = box.animate([{ transform: fly, opacity: 0.35 }, { transform: 'none', opacity: 1 }],
        { duration: dur, easing: 'cubic-bezier(.2,.8,.3,1)' });
      a.onfinish = a.oncancel = function () { _minAnimating = false; };
    }
    document.addEventListener('keydown', onEsc);

    /* CUSTOM_HOUSEDOCS_BY_CLAUDE (Ryan 2026-09-13) — see the reply composer's twin: one definition
       of "put a file on this email", shared by the template path and the snippet path. */
    function composeAttachOne(att) {
      if (attachments.length >= MAX_ATTACH_COUNT) return false;
      attachments = attachments.concat([att]);
      renderAttachList();
      return true;
    }
    function composeFx() {
      return { attach: composeAttachOne, attachments: function () { return attachments || []; } };
    }
    /* CUSTOM_COMPOSERPHASE6_BY_CLAUDE (Ryan 2026-10-18) — what a 📝 template insert may touch here (subject, thread,
       files). One definition for the menu and for Draft it's "Use template", so the two cannot drift. */
    function _composeTemplateFx() {
      return {
        setSubject: function (v) {
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-09-06) — ⛔ A REPLY KEEPS ITS THREAD'S
             NAME. Two templates now return a subject (new-inquiry, first-quote); on a reply
             the box already holds the thread subject and Outlook keeps it server-side
             regardless, so overwriting here would only show the rep a subject that is not
             the one that leaves. Refused rather than merged. */
          /* CUSTOM_REPLYSUBJECT_BY_CLAUDE (AIOPS 0d681a4b5eb7, Ryan 2026-10-11) — the CURRENT
             mode decides, not how the window opened: a reply switched to "New email" takes the
             template's subject, and switching back puts the thread's subject back (_applyMode).
             The subject is remembered either way so that switch can use it. */
          _tplSubject = String(v || '');
          _subjTouched = false;
          if (_sendMode === 'reply') return;
          var el = overlay.querySelector('#rqd-compose-subject');
          if (el) { el.value = v; _syncSubjectRef(); }
        },
        setReplyTo: function (rt) { _tplReplyTo = rt; },
        /* Same rule the attach menu enforces, inline because the composer keeps it
           in a closure rather than a named function: refuse past the cap and say so. */
        attach: composeAttachOne,   // CUSTOM_HOUSEDOCS_BY_CLAUDE — the one definition
      };
    }

    function renderAttachList() {
      var host = overlay.querySelector('#rqd-compose-attach-list');
      if (!host) return;
      attachments = _sortChainQuotes(attachments || []);   // CUSTOM_CHAINSORT_BY_CLAUDE
      _syncSubjectRef();   // CUSTOM_REPLYSUBJECT_BY_CLAUDE — the ref follows the attached quotes
      if (!ctx.prefillBody && !_richBodyHtml && _draftFilesReady) _idbDraftPut(draftKey, attachments);   // CUSTOM_COMPOSEDRAFT_BY_CLAUDE
      host.innerHTML = attachments.map(function(a, i) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — a locked attachment (the PO
           PDF, attached server-side by poNumber) shows no remove button so the rep
           can't desync "removed chip" from "PDF still sent". */
        var rmBtn = (a.locked || a.displayOnly)
          ? '<span class="rqd-email-modal__compose-chip-lock" title="' + (a.displayOnly ? 'Attached automatically when you send' : 'This PDF is sent with this email') + '">🔒</span>'
          : '<button type="button" class="rqd-email-modal__compose-chip-rm" data-idx="' + i + '" aria-label="Remove">×</button>';
        /* displayOnly pills have no base64 and so no size badge — but one that names a
           `previewUrl` is still openable, and reads as clickable so the rep knows it. */
        var _canPreview = !!(a.base64 || a.previewUrl);
        var nameCls = _canPreview
          ? 'rqd-email-modal__compose-chip-name rqd-email-modal__compose-chip-name--preview'
          : 'rqd-email-modal__compose-chip-name';
        var nameTitle = a.displayOnly
          ? (a.previewUrl ? 'Attached automatically when you send — click to open' : 'Attached automatically when you send')
          : 'Click to preview in a new tab';
        return '<span class="rqd-email-modal__compose-chip" data-idx="' + i + '">'
          + '<span class="' + nameCls + '" title="' + nameTitle + '">📎 ' + _esc(a.name) + '</span>'
          + (a.displayOnly ? '' : '<span class="rqd-email-modal__compose-chip-size">' + _formatBytes(a.sizeBytes) + '</span>')
          + rmBtn
          + '</span>';
      }).join('');
      /* Ryan 2026-08-18 — "as soon as I attach more than 1, change it to quotes".
         Every attachment add/remove lands here, so this is the one place that has to
         know. No-op unless a template is in the body and still untouched. */
      _retemplateIfPristine(_tplBinding, quill, _tplCtx);
      /* That re-render may have rebuilt the body under a preview the rep already opened, so the
         control re-reads it too. */
      _seqSyncControl();
    }

    /* Open an attachment (e.g. the freshly-generated quote PDF) in a new tab so
       the rep can eyeball the render before sending. Uses the in-memory base64
       — no server round-trip. Allowed by pop-up blockers since it fires from a
       click gesture. */
    function _openAttachmentPreview(att) {
      if (!att) return;
      /* CUSTOM_BALLAST_BY_CLAUDE (Ryan 2026-09-01) — A DISPLAY-ONLY PILL CAN STILL BE OPENED, if it
         names a URL. The blob route below needs base64, which a server-side attachment deliberately
         does not carry (that is the whole point of `displayOnly` — the bytes never come near this
         page). The Vendor Design Sheet has a public, unguessable `/wd-pdf-file/` URL that serves
         `inline`, so the same click can just open it. Still inside the gesture, so no blocker. */
      if (!att.base64 && att.previewUrl) {
        try { window.open(att.previewUrl, '_blank', 'noopener'); } catch (_) {}
        return;
      }
      if (!att.base64) return;
      try {
        var bin = atob(att.base64);
        var len = bin.length;
        var arr = new Uint8Array(len);
        for (var i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
        var blob = new Blob([arr], { type: att.contentType || 'application/octet-stream' });
        var url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Release the object URL once the new tab has had time to load it.
        setTimeout(function () { try { URL.revokeObjectURL(url); } catch (_) {} }, 60000);
      } catch (_) {}
    }

    /* Synchronous draft persist — shared by the debounced autosave and the
       Cancel/Back paths (so a quick exit can't beat the 1500ms timer and lose
       typed work). Returns true if a draft was actually stored. */
    function _saveDraftNow() {
      /* ⛔ A SERVER-PREFILLED COMPOSE NEVER WRITES THE SHARED DRAFT (Ryan 2026-08-20).
         `rqd-compose-draft:{ref}` is keyed on the DEAL, not on the kind of email, and the
         2026-06-17 fix above only stopped prefilled composers from READING it. They kept
         WRITING it — so sending the ECORE sample request and then opening Activity → ✉ Email
         on the same deal restored the work order: `samples@ecoreintl.com` in the To chips and
         the whole supplier body in the editor, on a button that is only ever the rep writing
         to the customer.

         ⚠️ NOTHING IS LOST BY REFUSING. A prefilled compose can never restore this draft
         (that is the rule above), so every byte it wrote was already unreadable to the flow
         that wrote it — it existed only to leak sideways into a different email. And a real
         free-form draft the rep typed earlier now SURVIVES a trip through a PO or a sample
         request, where before it was silently overwritten by a body they never typed. */
      if (ctx.prefillBody || _richBodyHtml) return false;
      try {
        var html = _editorHtml(quill);
        // Chip state is the source of truth for recipients; preserve
        // any half-typed input text too so we don't lose work mid-
        // typing on accidental cancel.
        var toRaw = (overlay.querySelector('#rqd-compose-to') || {}).value || '';
        var ccRaw = (overlay.querySelector('#rqd-compose-cc') || {}).value || '';
        var subj = (overlay.querySelector('#rqd-compose-subject') || {}).value || '';
        var draft = {
          toChips: toChips.slice(),
          ccChips: ccChips.slice(),
          toRaw: toRaw,
          ccRaw: ccRaw,
          subject: subj,
          body: html,
        };
        // Only a body with words is a draft (CUSTOM_DDMOBILE_BY_CLAUDE): recipients or a subject on an empty body are not.
        if (_bodyHasText(html)) {
          localStorage.setItem(draftKey, JSON.stringify(draft));
          return true;
        }
        localStorage.removeItem(draftKey);
        return false;
      } catch (_) { return false; }
    }
    function scheduleDraftSave() {
      if (autoSaveTimer) clearTimeout(autoSaveTimer);
      autoSaveTimer = setTimeout(_saveDraftNow, 1500);
    }
    /* Transient amber toast (top-center, auto-dismiss, no buttons). Self-
       contained so it works in both the QW + DD contexts regardless of which
       page-level toast helper happens to be loaded. */
    function _composeToast(msg) {
      try {
        var t = document.createElement('div');
        t.setAttribute('role', 'status');
        t.textContent = msg;
        t.style.cssText = [
          'position:fixed', 'top:18px', 'left:50%', 'transform:translateX(-50%)',
          'z-index:100002', 'background:#F59E0B', 'color:#1a1205',
          'font-family:Montserrat,sans-serif', 'font-size:13px', 'font-weight:700',
          'padding:10px 18px', 'border-radius:8px', 'box-shadow:0 6px 20px rgba(0,0,0,.28)',
          'opacity:0', 'transition:opacity .18s ease', 'pointer-events:none',
        ].join(';');
        document.body.appendChild(t);
        requestAnimationFrame(function () { t.style.opacity = '1'; });
        setTimeout(function () {
          t.style.opacity = '0';
          setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 200);
        }, 2400);
      } catch (_) {}
    }

    // Initial chip render + wiring via the shared subsystem (Phase B.5a).
    // Must happen AFTER scheduleDraftSave is declared above (uses it as onChange).
    _composeChipApi = _installChipFields({
      overlay: overlay,
      kinds: ['to', 'cc', 'bcc'],
      esc: _esc,
      getChips: function(kind) {
        if (kind === 'cc') return ccChips;
        if (kind === 'bcc') return bccChips;
        return toChips;
      },
      setChips: function(kind, arr) {
        if (kind === 'cc') ccChips = arr;
        else if (kind === 'bcc') bccChips = arr;
        else toChips = arr;
      },
      onChange: scheduleDraftSave,
    });

    /* CUSTOM_SELFBCC_BY_CLAUDE (Ryan 2026-08-13) — self-BCC chip. The Bcc field is the SINGLE
       source of truth: pre-fill ryan@ as a real, removable chip, and then LEAVE IT ALONE.

       ⚠️ It used to be tied to the From dropdown — added on REP, stripped on ABBY. Ryan:
       *"when I switch to Abby, the default BCC which is normally included gets removed… don't
       want that."* He is right, and the reason it mattered is worse than cosmetic: the BCC exists
       because these are Resend-transport emails that never land in his Outlook Sent folder, and
       that is true no matter whose name is on the From line. Switching to Abby removed his only
       copy of the email — and the worker does NOT add it back (see below), so the copy was simply
       gone.

       Now: added ONCE, on open. Changing From never touches the Bcc list, so removing the chip
       with its × sticks — previously, deleting it and then toggling From silently re-added it.

       ⚠️ THE OLD COMMENT HERE CLAIMED "the worker still force-adds it on send as the guarantee,
       so a copy is kept regardless". That is FALSE and was worth checking rather than trusting:
       po-routes.js:2437 sends exactly the Bcc list the composer submits, falling back to the
       VENDOR's configured bcc when that list is empty. So an emptied Bcc meant no copy for Ryan —
       and on a vendor with a configured bcc, a hidden copy to someone else instead. */
    var _selfBccApplied = false;
    function _syncSelfBcc() {
      if (!_bccSelfWhenRep || _selfBccApplied) return;
      _selfBccApplied = true;
      var self = _bccSelfWhenRep.toLowerCase();
      if (bccChips.some(function (b) { return String(b).trim().toLowerCase() === self; })) return;
      bccChips = bccChips.concat(_bccSelfWhenRep);
      if (_composeChipApi) _composeChipApi.renderAll();
    }
    _syncSelfBcc();

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — team inline autocomplete on
       To / Cc / Bcc. When the rep starts typing an onboarded teammate's
       @blueskyfitsupply.com address, ghost in the rest; accept it by clicking the ghost
       OR pressing → (at caret end) / Tab / Enter — it becomes a chip. Candidate pool =
       the logged-in rep's own email (session) + every onboarded employee from
       /rep-list-basic. Each ghost lives in its field's meta-row (not the chip-field) so it
       survives renderChips, which wipes the field's children. */
    (function _installTeamAutocomplete() {
      var myEmail = '';
      try { var raw = sessionStorage.getItem('rep_portal_session'); myEmail = raw ? String((JSON.parse(raw) || {}).email || '').trim() : ''; } catch (_) {}

      // Shared candidate pool (original casing for the accepted chip). Self first so
      // self-complete is instant; teammates merge in when /rep-list-basic resolves.
      var candidates = [];
      var _seen = {};
      function _addCandidate(e) {
        e = String(e || '').trim();
        if (!e || !EMAIL_RX.test(e)) return;
        var k = e.toLowerCase();
        if (_seen[k]) return;
        _seen[k] = 1;
        candidates.push(e);
      }
      _addCandidate(myEmail);

      function _installForKind(kind, chipsGetter) {
        var input = overlay.querySelector('#rqd-compose-' + kind);
        var field = overlay.querySelector('#rqd-compose-' + kind + '-field');
        if (!input || !field) return null;
        var row = field.closest('.rqd-email-modal__compose-meta-row') || field.parentNode;
        try { if (getComputedStyle(row).position === 'static') row.style.position = 'relative'; } catch (_) {}
        var ghost = document.createElement('div');
        ghost.className = 'rqd-email-modal__bcc-ghost';
        ghost.innerHTML = '<span class="rqd-email-modal__bcc-ghost-typed"></span>'
          + '<span class="rqd-email-modal__bcc-ghost-rest" title="Click, or press → / Tab / Enter to add"></span>';
        ghost.style.display = 'none';
        row.appendChild(ghost);
        var typedSpan = ghost.querySelector('.rqd-email-modal__bcc-ghost-typed');
        var restSpan = ghost.querySelector('.rqd-email-modal__bcc-ghost-rest');

        function _alreadyChipped(addrLower) {
          return chipsGetter().some(function (a) { return String(a).toLowerCase() === addrLower; });
        }
        // First onboarded address whose start matches what's typed (and isn't already a chip).
        function _suggestion() {
          var v = String(input.value || '');
          if (!v) return null;
          var vl = v.toLowerCase();
          for (var i = 0; i < candidates.length; i++) {
            var cl = candidates[i].toLowerCase();
            if (cl.indexOf(vl) === 0 && vl.length < cl.length && !_alreadyChipped(cl)) return candidates[i];
          }
          return null;
        }
        function _position() {
          var cs = getComputedStyle(input);
          var ir = input.getBoundingClientRect();
          var rr = row.getBoundingClientRect();
          ghost.style.left = (ir.left - rr.left) + 'px';
          ghost.style.top = (ir.top - rr.top) + 'px';
          ghost.style.height = ir.height + 'px';
          ghost.style.lineHeight = ir.height + 'px';
          ghost.style.paddingLeft = cs.paddingLeft;
          ghost.style.paddingRight = cs.paddingRight;
          ghost.style.fontFamily = cs.fontFamily;
          ghost.style.fontSize = cs.fontSize;
          ghost.style.fontWeight = cs.fontWeight;
          ghost.style.letterSpacing = cs.letterSpacing;
        }
        function _hide() { ghost.style.display = 'none'; }
        function _show() {
          var full = _suggestion();
          if (!full) { _hide(); return; }
          var v = String(input.value || '');
          typedSpan.textContent = v;            // transparent spacer — keeps the rest aligned
          restSpan.textContent = full.slice(v.length);
          _position();
          ghost.style.display = '';
        }
        function _accept() {
          var full = _suggestion();
          if (!full) return false;
          input.value = '';
          addChipsFromText(kind, full);
          scheduleDraftSave();
          _hide();
          try { input.focus(); } catch (_) {}
          return true;
        }

        input.addEventListener('input', _show);
        input.addEventListener('focus', _show);
        input.addEventListener('blur', function () { setTimeout(_hide, 120); });
        /* Keydown on the FIELD in the capture phase, so we intercept Enter / Tab / →
           BEFORE the chip field's own commit handler (registered on the input). At the
           target element a same-element capture listener wouldn't win — it runs in
           registration order — so we listen on the ancestor and stopPropagation. */
        field.addEventListener('keydown', function (ev) {
          if (ev.target !== input || !_suggestion()) return;
          if (ev.key === 'ArrowRight') {
            // Accept only at caret end so → still navigates within typed text otherwise.
            var atEnd = input.selectionStart === input.value.length && input.selectionStart === input.selectionEnd;
            if (atEnd) { ev.preventDefault(); ev.stopPropagation(); _accept(); }
          } else if (ev.key === 'Enter' || ev.key === 'Tab') {
            ev.preventDefault(); ev.stopPropagation(); _accept();
          }
        }, true);
        // mousedown (not click) so it fires before the input's blur tears the ghost down.
        restSpan.addEventListener('mousedown', function (ev) { ev.preventDefault(); _accept(); });

        return { refresh: function () { if (document.activeElement === input) _show(); } };
      }

      var ctrls = [
        _installForKind('to', function () { return toChips; }),
        _installForKind('cc', function () { return ccChips; }),
        _installForKind('bcc', function () { return bccChips; }),
      ];
      _loadRepEmails().then(function (list) {
        list.forEach(_addCandidate);
        // If a field is mid-type when the roster lands, refresh its ghost.
        ctrls.forEach(function (c) { if (c) c.refresh(); });
      });
    })();

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-30) — render any pre-attached
       files (ctx.preAttachments) as chips immediately so the rep sees the quote
       PDF is attached on open. No-op if none were seeded. */
    if (attachments.length) renderAttachList();

    // Signature preview — Phase B.5b shared helper. A caller (the freight-balance
    // adapter) can override with ctx.signatureNote, since that email uses Abby's
    // signature (appended server-side), not the rep's.
    if (ctx.signatureHtml) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — Phase 0c: render the caller's EXACT
         signature HTML in the canvas (e.g. the supplier-facing rep signature for freight),
         so the preview matches what actually sends — instead of the default /rep-settings
         (customer) signature or a text note. */
      var _sigPrevH = overlay.querySelector('#rqd-compose-sig-preview');
      var _sigWrapH = overlay.querySelector('#rqd-compose-sig-wrap');
      if (_sigPrevH) _sigPrevH.innerHTML = ctx.signatureHtml;
      if (_sigWrapH) _sigWrapH.style.display = '';
      // No meta-label — the signature renders as part of the email (canvas = the email).
      var _sigNoteH = overlay.querySelector('.rqd-email-modal__compose-sig-note');
      if (_sigNoteH) _sigNoteH.style.display = 'none';
    } else if (ctx.signatureNote) {
      var _sigPrev = overlay.querySelector('#rqd-compose-sig-preview');
      var _sigWrap = overlay.querySelector('#rqd-compose-sig-wrap');
      if (_sigPrev) _sigPrev.innerHTML = '<em style="color:var(--rq-text-secondary,#6b7280);font-size:12px;">' + _esc(ctx.signatureNote) + '</em>';
      if (_sigWrap) _sigWrap.style.display = '';
    } else if (!_fromOptions || _fromOptions.length <= 1) {
      /* A locked sender previews ITS signature (Ryan 2026-09-06) — see the loader's note. */
      _loadSignaturePreviewInto(
        overlay.querySelector('#rqd-compose-sig-preview'),
        overlay.querySelector('#rqd-compose-sig-wrap'),
        _fromMailbox
      );
    }
    /* else: a multi-option From selector is present → _installFromSelector owns the signature
       preview, swapping it between the rep's and Abby's signature per selection. */

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — From: identity selector wiring. Fetches
       /rep-settings once for the rep signature + Abby signature + saved per-type default, fills
       the dropdown (rep label from session), applies the matching signature to the preview, and
       on change persists the new default for this email type + swaps the signature. */
    (function _installFromSelector() {
      if (!_fromOptions) return;
      var sel = overlay.querySelector('#rqd-compose-from');
      if (!sel) return;

      // Non-Abby ("REP") option label. Caller can pin it (e.g. vendor PO/freight always
      // send from "Ryan Walker <ryan@…>"); otherwise it's the logged-in rep ("Me <email>").
      var repOpt = sel.querySelector('option[value="REP"]');
      if (repOpt) {
        if (ctx.fromRepLabel) {
          repOpt.textContent = String(ctx.fromRepLabel);
        } else {
          try {
            var s = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
            var nm = String(s.name || '').trim(), em = String(s.email || '').trim();
            repOpt.textContent = (nm ? nm : 'Me') + (em ? ' <' + em + '>' : '');
          } catch (_) {}
        }
      }

      // Single option → locked sender; nothing to wire (the signature preview is handled by
      // the default load / ctx.signatureNote above). Same window, just no sender choice.
      if (_fromOptions.length <= 1) return;
      if (!(window.RepPortal && window.RepPortal.API)) return;
      var sigPrev = overlay.querySelector('#rqd-compose-sig-preview');
      var sigWrap = overlay.querySelector('#rqd-compose-sig-wrap');
      var sigNote = overlay.querySelector('.rqd-email-modal__compose-sig-note');
      var _repSig = '', _abbySig = '';

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — optional ctx.onFromChange(mailbox). Callers
         that don't pass one are completely unaffected. Wrapped because it reaches into caller code
         mid-render: a throw here would take the signature preview and the Bcc sync down with it. */
      function _notifyFromChange() {
        if (typeof ctx.onFromChange !== 'function') return;
        try { ctx.onFromChange(_fromMailbox); } catch (_) { /* caller's problem, not the modal's */ }
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

      function _applySig() {
        var html = _fromMailbox === 'ABBY' ? _abbySig : _repSig;
        _selectedFromSig = html || '';   // exposed to sendOverride (freight composes with it)
        if (sigPrev) sigPrev.innerHTML = html || '';
        if (sigWrap) sigWrap.style.display = html ? '' : 'none';
        if (sigNote) sigNote.style.display = 'none';
      }

      window.RepPortal.API.get('/rep-settings').then(function (d) {
        d = d || {};
        _repSig = d.signatureHtml || '';
        _abbySig = d.abbySignatureHtml || '';
        // Default the dropdown to the saved per-type choice (falls back to the ctx default).
        var saved = (d.emailFromPrefs && d.emailFromPrefs[_emailType]) || '';
        // A forced sender is a rule about THIS email; the rep's saved habit must not override it.
        if (!_forceFrom && (saved === 'ABBY' || saved === 'REP')) _fromMailbox = saved;
        sel.value = _fromMailbox;
        _applySig();
        _syncSelfBcc();   // no-op after the first call - the Bcc no longer tracks the From identity
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — announce the SETTLED sender, not just
           later changes. This is the moment the saved per-type default can move the dropdown out
           from under whatever the caller assumed when it opened the modal, so a caller that has to
           match the sender (the PO flow re-renders its PDF for {SENDER_EMAIL}) has no other way to
           learn the real value. Fires here and in the change handler below; the callback is
           expected to no-op when the mailbox has not actually moved. */
        _notifyFromChange();
      }).catch(function () { _applySig(); _notifyFromChange(); });

      sel.addEventListener('change', function () {
        _fromMailbox = sel.value === 'ABBY' ? 'ABBY' : 'REP';
        _applySig();
        _notifyFromChange();   // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26)
        _syncSelfBcc();   // no-op after the first call - switching From must not touch the Bcc list
        /* Persist this email type's default for next time (best-effort, fire-and-forget) — but
           NOT when the sender was forced by a rule. Overriding Abby on one Legend PO must not
           rewrite the default for every other vendor's POs. */
        if (!_forceFrom) {
          try { window.RepPortal.API.post('/rep-settings/save', { emailFromPref: { type: _emailType, value: _fromMailbox } }); } catch (_) {}
        }
      });
    })();

    if (_richBodyHtml) {
      // Rich-body mode — no Quill. Just focus the editable canvas and wire
      // Ctrl/Cmd+Enter to send, mirroring the Quill path's keyboard shortcut.
      setTimeout(function() { try { if (_richBodyEl && _mayAutoFocus(_richBodyEl)) _richBodyEl.focus(); } catch (_) {} }, 50);   // CUSTOM_DDMOBILE_BY_CLAUDE
      if (_richBodyEl) {
        _richBodyEl.addEventListener('keydown', function(ev) {
          if ((ev.ctrlKey || ev.metaKey) && ev.key === 'Enter') { ev.preventDefault(); _sendWithSequence(false); }
        });
      }
    } else
    _loadQuill().then(function() {
      _registerQuillSizes();   // Phase B.5b — shared module helper
      quill = new Quill('#rqd-compose-editor', {
        theme: 'snow',
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline'],
              [{ color: [] }, { background: [] }],
              [{ size: ['9pt', '10pt', '11pt', '12pt', '14pt', '16pt', '18pt', '24pt', '36pt'] }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link'],
              ['clean'],
            ],
            handlers: {
              // Route the link button through the un-clippable native prompt.
              link: function () { _promptQuillLink(this.quill); },
            },
          },
          clipboard: { matchVisual: false },
          /* Ryan 2026-05-22: Ctrl+Alt+H toggles yellow highlight on
             selected text — matches Outlook. shortKey = Ctrl/Cmd. */
          keyboard: {
            bindings: {
              toggleHighlight: {
                key: 'H',
                altKey: true,
                shortKey: true,
                handler: function(range) {
                  if (!range || range.length === 0) return true;
                  var current = this.quill.getFormat(range).background;
                  this.quill.format('background', current ? false : '#ffff00');
                  return false;
                },
              },
              // Ctrl/Cmd+K → insert link via native prompt (see _promptQuillLink).
              insertLink: {
                key: 'K',
                shortKey: true,
                handler: function(range) { _promptQuillLink(this.quill); return false; },
              },
            },
          },
        },
        placeholder: 'Type your email…',
      });
      _applyPlainPaste(quill); // paste = plain text + links only
      _floatQuillTooltip(quill); // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-10-27) - the link bubble overlaps the window
      _installProductMentions(quill, function () {   // "@" -> product picker, inserts a linked title
        return { ref: ctx.ref, toEmails: (toChips || []).concat(ccChips || []) };
      });
      _installDashAutocorrect(quill);  // "--" -> em dash, Word/Outlook style
      _installSpellAutocorrect(quill); // sentence capitals + one-obvious-fix spelling (admins)
      /* "#" -> snippet picker. _tplCtx is read live, so a snippet that describes the attached
         proposals sees the attachment list as it stands at the moment of the pick. */
      _installSnippetPicker(quill, _tplCtx, _snipStatus, function (m) { _snipStatus(m, true); }, composeFx);

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — drop the "❗ Important" toggle INTO the
         Quill toolbar (same line as bold/italic), matching the rich-body toolbar. */
      var _qtb = overlay.querySelector('.ql-toolbar');
      /* Ryan 2026-08-18 — the 📝 template button sits right next to the paperclip.
         Ryan 2026-08-19 — and ⚡ snippets after it: whole-email, then sentence. */
      if (_qtb && !_qtb.querySelector('#rqd-compose-attach-btn')) {
        _qtb.insertAdjacentHTML('beforeend', _attachBtnHtml + _templateBtnHtml('rqd-compose-template-btn')
          + _snippetBtnHtml('rqd-compose-snippet-btn')
          + _importanceToggleHtml + _bizHoursToggleHtml);
      }
      /* CUSTOM_AICOMPOSER_BY_CLAUDE — "Draft it" on the plain new email only (QW Send Quote, the DD Email pill).
         A PO / freight / invoice composer (sendOverride), a rich body or a server-prefilled body keeps its
         approved wording and gets no button. Reply vs new is read at click time: the rep can switch. */
      if (ref && !ctx.sendOverride && !ctx.prefillBody && !_richBodyHtml) {
        _installDraftIt(quill, _qtb, {
          getRef: function () { return ref; },
          mode: function () {
            var _to = (_sendMode === 'new') ? '' : (_replyToMessageId || (_tplReplyTo && _tplReplyTo.messageId) || '');
            return _to ? 'reply' : 'new';
          },
          getMessageId: function () {
            return (_sendMode === 'new') ? '' : (_replyToMessageId || (_tplReplyTo && _tplReplyTo.messageId) || '');
          },
          /* CUSTOM_COMPOSERPHASE6_BY_CLAUDE — approved wording the draft may point to, and the button that drops it in
             through the menus' own insert (a snippet at the caret, a template replacing the body). */
          wording: function () { return _draftItWording(_tplCtx()); },
          useWording: function (kind, id) {
            if (!quill) return Promise.reject(new Error('the editor is still loading'));
            if (kind === 'snippet') {
              try { quill.focus(); } catch (_) {}
              return _applySnippet(quill, id, _tplCtx(), _snipStatus, function (m) { _snipStatus(m, true); }, composeFx());
            }
            return _applyTemplate(_tplBinding, quill, id, _tplCtx(), function (w) { _snipStatus(w, false); }, _composeTemplateFx())
              .then(function (out) { _seqSyncControl(); return out; });
          },
        });
      }

      // Restore draft if present — supports new (chip-based) AND legacy
      // (raw-string) draft shapes for forward + backward compat.
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-17) — only restore a saved draft for a
         FREE-FORM compose. A caller-prefilled compose (PO / freight / dims / freight-balance,
         server-built ctx.prefillBody) is authoritative. The per-ref draft key
         (rqd-compose-draft:{ref}) is shared across ALL email types for an order, so restoring
         here leaked a prior type's draft into the new one — a stale dims-request draft
         concatenated onto the PO email body ("Thank you!" + "Hi Team…" = "Thank you!i Team…",
         SHOP-1769). Prefilled types skip restore so the fresh server body always wins. */
      /* CUSTOM_COMPOSEDRAFT_BY_CLAUDE (Ryan 2026-10-10) — the draft's files come back too, after the text. A file
         already on the email (the quote PDF the Workstation attaches fresh) is not added twice: same quote ref, or
         same name, and the fresh one wins. */
      if (!ctx.prefillBody && !_richBodyHtml) _idbDraftGet(draftKey).then(function (rec) {
        _draftFilesReady = true;
        if (!rec || !Array.isArray(rec.attachments) || !rec.attachments.length || !overlay.isConnected) return;
        var have = {};
        (attachments || []).forEach(function (a) { have['n:' + String(a.name || '').toLowerCase()] = 1; var r = _attQuoteRef(a); if (r) have['r:' + r] = 1; });
        var add = rec.attachments.filter(function (a) {
          var r = _attQuoteRef(a);
          return a && a.base64 && !have['n:' + String(a.name || '').toLowerCase()] && !(r && have['r:' + r]);
        });
        if (!add.length) return;
        attachments = (attachments || []).concat(add).slice(0, MAX_ATTACH_COUNT);
        renderAttachList();
        _composeToast('Your draft email and its attachments were restored');
      });
      if (!ctx.prefillBody) try {
        var stored = localStorage.getItem(draftKey);
        if (stored) {
          var draft = JSON.parse(stored);
          if (draft.body) quill.root.innerHTML = _stripQuillCursor(draft.body);   // a pre-09-24 draft may still carry the marker
          if (Array.isArray(draft.toChips)) { toChips = draft.toChips.slice(); if (_composeChipApi) _composeChipApi.renderAll(); }
          else if (draft.to) {
            // Legacy string draft — split into chips
            addChipsFromText('to', draft.to);
          }
          if (Array.isArray(draft.ccChips)) { ccChips = draft.ccChips.slice(); if (_composeChipApi) _composeChipApi.renderAll(); }
          else if (draft.cc) {
            addChipsFromText('cc', draft.cc);
          }
          if (draft.toRaw) overlay.querySelector('#rqd-compose-to').value = draft.toRaw;
          if (draft.ccRaw) overlay.querySelector('#rqd-compose-cc').value = draft.ccRaw;
          /* Ryan 2026-05-22: skip restoring subject if it looks like
             the old auto-prefill pattern (bare ref with optional
             trailing separator like "RW-1166 — " or "RW-1166-A — ").
             Those drafts were saved before we dropped the prefill;
             restoring them now would put the ref back in the subject
             that we just stopped pre-filling on purpose. */
          var _OLD_PREFILL_RX = /^[A-Z]+-\d+(-[A-Z])?\s*[—–-]?\s*$/i;
          /* CUSTOM_REPLYSUBJECT_BY_CLAUDE (AIOPS 0d681a4b5eb7, Ryan 2026-10-11) — ⛔ NEVER ON A REPLY.
             The draft key is per DEAL, not per thread, so a draft saved by a new-email window on this
             deal ("Quote for Gabrielle Correia - ... - RW-1494-A") was restored into the reply on
             Gabrielle's answer and renamed the thread (RW-1494-A). A reply's subject is the thread's. */
          if (draft.subject && !_OLD_PREFILL_RX.test(draft.subject) && _sendMode !== 'reply') {
            overlay.querySelector('#rqd-compose-subject').value = draft.subject;
            _subjTouched = true;
          }
          var status = overlay.querySelector('#rqd-compose-status');
          if (status) status.textContent = 'Draft restored';
        }
      } catch (_) {}

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-09, Phase 3) — caller-supplied body
         (e.g. the freight-balance prose draft). Loaded AFTER draft-restore so the
         fresh server draft wins on each open, matching the old freight-balance modal.
         CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-17) — apply via clipboard.dangerouslyPasteHTML,
         NOT quill.root.innerHTML. A body wrapped in a styled block (the freight-balance
         email's outer <div style="...max-width:640px">) is normalized away by Quill 2.x
         when set as raw innerHTML — an unknown top-level blot gets stripped along with its
         children, leaving a blank editor (regressed the freight "Send Email" pop-up).
         dangerouslyPasteHTML routes through Quill's parser (div -> paragraphs) AND updates
         the document model, so draft-save + send read the real content. innerHTML stays as
         a defensive fallback. */
      if (ctx.prefillBody) {
        // setText('') first to clear the model + DOM cleanly. A prior innerHTML write or a
        // stray node would otherwise desync Quill's length so the paste APPENDS instead of
        // replacing (the concatenation seen on SHOP-1769). Belt-and-suspenders alongside the
        // draft-restore skip above.
        try {
          quill.setText('');
          _allowRichPrefill = true;   // keep the prefill's bold/color past the plain-paste matcher
          quill.clipboard.dangerouslyPasteHTML(ctx.prefillBody);
        }
        catch (_) { try { quill.root.innerHTML = ctx.prefillBody; } catch (_e) {} }
        finally { _allowRichPrefill = false; }
      }

      quill.on('text-change', scheduleDraftSave);
      var subjEl = overlay.querySelector('#rqd-compose-subject');
      if (subjEl) subjEl.addEventListener('input', scheduleDraftSave);
      /* CUSTOM_REPLYSUBJECT_BY_CLAUDE — typing in the box makes the subject the rep's own. */
      if (subjEl) subjEl.addEventListener('input', function () { _subjTouched = true; });
      _syncSubjectRef();

      // Focus the body if To already has chips; otherwise focus To input.
      setTimeout(function() {
        var toInput = overlay.querySelector('#rqd-compose-to');
        if (toInput && toChips.length === 0) { try { if (_mayAutoFocus(toInput)) toInput.focus(); } catch (_) {} }   // CUSTOM_DDMOBILE_BY_CLAUDE
        else { try { if (_mayAutoFocus(quill.root)) quill.focus(); } catch (_) {} }
      }, 50);

      quill.root.addEventListener('keydown', function(ev) {
        if ((ev.ctrlKey || ev.metaKey) && ev.key === 'Enter') {
          ev.preventDefault();
          _sendWithSequence(false);
        }
      });

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — the automated-sequence control's resting
         state. It has to run once here rather than only on a template pick: a composer reopened
         on a restored draft can already HAVE a template in the body, and the control would sit
         greyed with the wrong reason under it. */
      _seqSyncControl();
      /* ⛔ AND IT RE-READS ON EITHER INPUT, because the sentence under it describes BOTH. Ticking
         the box changes what the Follow-up dropdown means, and changing the dropdown changes the
         sentence that explains it — a one-way binding would leave "in 3 days becomes a backstop"
         on screen after the rep moved it to 10. */
      (function _wireSeqInputs() {
        var b = overlay.querySelector('#rqd-compose-seq-arm');
        var f = overlay.querySelector('#rqd-compose-followup-days');
        if (b) b.addEventListener('change', function () { _seqSyncControl(); });
        if (f) f.addEventListener('change', function () { _seqSyncControl(); });
        /* CUSTOM_DELAYEDSTART_BY_CLAUDE — the start dropdown re-reads the same way. */
        var s = overlay.querySelector('#rqd-compose-seq-start');
        if (s) s.addEventListener('change', function () { _seqSyncControl(); });

        /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-20) — ⏱ CHANGES WHAT THE EMAIL SAYS, not just
           when it leaves. Ticking it means the email arrives tomorrow morning, so every time
           phrase in it ("just now", "earlier today") has to be written for THEN. */
        var clock = overlay.querySelector('#rqd-compose-bizhours');
        if (clock) {
          clock.addEventListener('change', function () {
            var held = _heldSendMs();
            /* ⛔ ONLY IF THE BODY IS STILL OURS. `_retemplateIfPristine` refuses the moment the rep
               has typed a character, which is the right rule — their words are not ours to
               rewrite. But that leaves a real hole, so it is SAID rather than left silent. */
            var pristine = !!(_tplBinding && _tplBinding.id && quill
              && _tplNorm(quill.getText()) === _tplBinding.text);
            _retemplateIfPristine(_tplBinding, quill, _tplCtx);
            /* ⚠️ `_snipStatus`, NOT `_tplStatus`. The latter is a LOCAL declared inside the
               toolbar's click handler and is invisible from here — calling it would throw a
               ReferenceError and this checkbox would silently do half its job, which is the
               exact shape of the 2026-08-19 dead-snippet-button bug. Both write the same
               element with the same classes. */
            if (!held) { _snipStatus(''); return; }
            if (_tplBinding && _tplBinding.id && !pristine) {
              /* ⚠️ A WARNING, NOT A REFUSAL. The rep may have edited a sentence that has nothing
                 to do with timing, and blocking the hold over that would be worse than telling
                 them to look. Naming the actual send time is what makes it checkable. */
              _snipStatus('⏱ This sends ' + _heldWhenLabel(held)
                + '. You have edited the body, so anything in it that says "today" or "just now" '
                + 'was written for right now — check it still reads correctly for then.', false);
            } else if (_tplBinding && _tplBinding.id) {
              _snipStatus('⏱ Sends ' + _heldWhenLabel(held)
                + '. The wording has been updated to read correctly then.', false);
            }
          });
        }
      })();
    }).catch(function(err) {
      var status = overlay.querySelector('#rqd-compose-status');
      if (status) {
        status.textContent = 'Editor failed to load: ' + (err && err.message ? err.message : 'unknown');
        status.classList.add('rqd-email-modal__compose-status--err');
      }
    });

    function _splitAddrs(raw) {
      return String(raw || '')
        .split(/[,;]+/)
        .map(function(s) { return s.trim(); })
        .filter(Boolean);
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — body accessors. In rich-body mode
       the body lives in the contentEditable canvas; otherwise it's the Quill editor.
       Everything downstream (doSend, Send Test, attachment guard) reads through these. */
    function _bodyHtml() { return _richBodyEl ? (_richBodyDirty ? _richBodyEl.innerHTML : _richBodyHtml) : _editorHtml(quill); }
    function _bodyText() { return _richBodyEl ? (_richBodyEl.innerText || _richBodyEl.textContent || '') : (quill ? (quill.getText() || '') : ''); }

    /* The sequence builds its own rungs before the send and refuses to send without them — see
       `sendWith` in `_installSeqControl`. This composer only has to hand it the send. */
    function _sendWithSequence(externalConfirmed) { return _seq.sendWith(doSend, externalConfirmed); }

    function doSend(externalConfirmed) {
      if (!quill && !_richBodyEl) return;
      var sendBtn = overlay.querySelector('#rqd-compose-send');
      var cancelBtn = overlay.querySelector('#rqd-compose-cancel');
      var status = overlay.querySelector('#rqd-compose-status');
      // Auto-commit any half-typed text in the chip inputs as a final
      // chip before send — so the rep doesn't have to remember to press
      // Enter or Tab. Mirrors Outlook's behavior on Send.
      if (_composeChipApi) _composeChipApi.commitAll();
      // Chip arrays are the source of truth for recipients.
      var to = toChips.slice();
      var cc = ccChips.slice();
      var bcc = bccChips.slice();
      var subject = String((overlay.querySelector('#rqd-compose-subject') || {}).value || '').trim();
      var html = _bodyHtml();
      var textLen = _bodyText().trim().length;

      if (to.length === 0) {
        if (status) {
          status.textContent = 'At least one To recipient required.';
          status.classList.add('rqd-email-modal__compose-status--err');
        }
        return;
      }
      if (!subject) {
        if (status) {
          status.textContent = 'Subject cannot be empty.';
          status.classList.add('rqd-email-modal__compose-status--err');
        }
        return;
      }
      if (textLen === 0) {
        if (status) {
          status.textContent = 'Body cannot be empty.';
          status.classList.add('rqd-email-modal__compose-status--err');
        }
        return;
      }
      /* CUSTOM_COMPOSERCHECK_BY_CLAUDE — what the check before Send reads. Built here so the attachment guard below is
         not asked a second time when the check hands the send back for this same email. Plain composers only. */
      var _pcOn = !!(ref && quill && !_richBodyEl && !ctx.sendOverride && !ctx.prefillBody);
      var _pc = _pcOn ? {
        ref: ref,
        mode: ((_sendMode === 'new') ? '' : (_replyToMessageId || (_tplReplyTo && _tplReplyTo.messageId) || '')) ? 'reply' : 'new',
        messageId: (_sendMode === 'new') ? '' : (_replyToMessageId || (_tplReplyTo && _tplReplyTo.messageId) || ''),
        to: to, cc: cc, subject: subject, html: html, text: _bodyText(),
        attachments: (attachments || []).map(function (a) { return a && a.name; }).filter(Boolean),
      } : null;
      var _pcDone = !!(_pc && _checkPassed(quill, _pc));
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-29) — Forgot-attachment guard:
         body/subject mentions an attachment but no file is attached. Confirm
         before sending so the rep can add it (Cancel) or send anyway (OK). */
      var _attWarn = !ctx.suppressAttachmentGuard
          && (attachments || []).length === 0
          && /\battach(ed|ing|ment|ments)?\b/i.test((_bodyText() + ' ' + subject));
      if (_attWarn && !_pcDone
          && !window.confirm('Your email mentions an attachment, but no file is attached.\n\nClick Cancel to add one, or OK to send anyway.')) {
        return;
      }
      /* Ryan 2026-05-29: recipient + attachment-size guards before send. */
      if (Array.prototype.some.call(overlay.querySelectorAll('.rqd-email-modal__chip-input'), function (i) { return i && i.value && i.value.trim(); })) {
        if (status) { status.textContent = "Finish or remove the recipient you're still typing."; status.classList.add('rqd-email-modal__compose-status--err'); }
        return;
      }
      var _badR = to.concat(cc, bcc).filter(function (a) { return !_CHIP_EMAIL_RX.test(a); });
      if (_badR.length) {
        if (status) { status.textContent = 'Invalid email address: ' + _badR[0]; status.classList.add('rqd-email-modal__compose-status--err'); }
        return;
      }
      if (attachments.reduce(function (s, a) { return s + (a && a.base64 && !a.locked ? a.base64.length : 0); }, 0) > _MAX_ATTACH_TOTAL_B64) {
        if (status) { status.textContent = 'Attachments too large combined (15 MB max). Remove some or share a link.'; status.classList.add('rqd-email-modal__compose-status--err'); }
        return;
      }
      /* CUSTOM_COMPOSERCHECK_BY_CLAUDE (Ryan 2026-10-18) — the check before Send. Clean = it hands the send straight back;
         found something = a card above Send (Fix / Send anyway); slow or broken = it sends as before. */
      if (_pc && !_pcDone) {
        _pc.attachWarned = _attWarn;
        _pc.sendBtn = sendBtn;
        _pc.host = sendBtn ? sendBtn.parentNode : null;
        _pc.send = function () { doSend(externalConfirmed); };
        if (!_checkBeforeSend(quill, _pc)) return;
      }

      if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = 'Sending…'; }
      if (!_composeIdemKey) {
        _composeIdemKey = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : (Date.now() + '-' + Math.random().toString(36).slice(2));
      }
      if (cancelBtn) cancelBtn.disabled = true;
      if (status) {
        status.textContent = '';
        status.classList.remove('rqd-email-modal__compose-status--err');
        /* ⛔ The warning card and the send status share one element. Leaving the card on
           would frame "Sending..." and then "Sent" in a red-outlined alert box. */
        status.classList.remove('rqd-email-modal__compose-status--tpl');
      }

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-09, Phase 3) — pluggable send. A
         caller (the freight-balance adapter) routes the send to its own endpoint
         while reusing this entire compose UI. Bypasses the /outlook-send payload +
         external-confirm dance; success/error UX mirrors the normal path. */
      if (typeof ctx.sendOverride === 'function') {
        Promise.resolve(ctx.sendOverride({
          to: to, cc: cc, bcc: bcc, subject: subject, bodyHtml: html,
          attachments: attachments, isTest: false,
          highImportance: _highImportanceChecked(),
          // CUSTOM_SENDWINDOW_BY_CLAUDE — hold until the next 6am-6pm MT window.
          businessHoursOnly: _businessHoursChecked(),
          fromMailbox: _fromMailbox,
          fromSignatureHtml: _selectedFromSig,
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — 'reply' | 'new'. A sendOverride
             caller posts to its OWN endpoint, so the composer cannot enforce the choice for it;
             it can only report it. /order-adjust/email honours this, and would otherwise
             re-derive a reply target from the ledger and thread anyway. */
          sendMode: _sendMode,
        }))
          .then(function(_res) {
            try { localStorage.removeItem(draftKey); } catch (_) {}
            _idbDraftDel(draftKey);   // CUSTOM_COMPOSEDRAFT_BY_CLAUDE
            _composeIdemKey = null;
            if (_onSentCb) { try { _onSentCb({}); } catch (_) {} }
            /* CUSTOM_SENTITEMS_BY_CLAUDE (Ryan 2026-08-28) — a sendOverride can now qualify its own
               success. The send paths try the sender's real Outlook mailbox first and fall back to
               Resend when it is not connected; the email still goes either way, but on the fallback
               it will NOT appear in that mailbox's Sent Items — which is exactly the invisible state
               this work exists to remove, so it has to be said out loud rather than discovered.
               A note also holds the window open longer, since "✓ Sent." for 1.2s is not long enough
               to read a caveat. */
            var _note = (_res && _res.composerNote) ? String(_res.composerNote) : '';
            if (status) {
              status.textContent = _note ? ('✓ ' + _note) : '✓ Sent.';
              status.classList.add('rqd-email-modal__compose-status--ok');
            }
            setTimeout(function() {
              close();
              if (typeof fetchQuotes === 'function') { try { fetchQuotes({ quiet: true }); } catch (_) {} }
            }, _note ? 5000 : 1200);
          })
          .catch(function(err) {
            if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = 'Send'; }
            if (cancelBtn) cancelBtn.disabled = false;
            if (status) {
              status.textContent = 'Send failed: ' + (err && err.message ? err.message : 'unknown');
              /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (2026-08-29) — DROP THE SUCCESS CLASS FIRST.
                 These only ADDED --err, so a failure that followed a successful Send Test kept
                 the green --ok already on the element and the error rendered GREEN. Ryan hit
                 exactly that sequence: test, then Send. The test path has always cleared its
                 opposite (see ERR/OK at the top); the failure paths never did. */
              status.classList.remove('rqd-email-modal__compose-status--ok');
              status.classList.add('rqd-email-modal__compose-status--err');
            }
          });
        return;
      }

      var payload = {
        ref: ref || undefined,
        idempotencyKey: _composeIdemKey,
        to: to,
        cc: cc,
        bcc: bcc,
        subject: subject,
        body: html,
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — From identity: 'ABBY' sends from
           orders@ (Abby's signature), default 'REP' from the rep's Outlook. */
        fromMailbox: _fromMailbox,
        // High-importance flag → worker stamps Importance/X-Priority headers.
        highImportance: _highImportanceChecked(),
          // CUSTOM_SENDWINDOW_BY_CLAUDE — hold until the next 6am-6pm MT window.
          businessHoursOnly: _businessHoursChecked(),
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-28) — External recipients are
           the norm for BSF; always pre-confirm so the external-recipient prompt
           never fires. (Server guard stays as a backstop.) */
        externalConfirmed: true,
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — "N/A — no follow-up" selected
           => don't arm a follow-up; any day value arms the cadence at that interval. */
        armFollowUp: _followUpSelectionDays() != null,
        followUpDays: _followUpSelectionDays() != null ? _followUpSelectionDays() : undefined,
        attachments: attachments.filter(function(a) { return a.base64 && !a.displayOnly; }).map(function(a) {
          return { name: a.name, contentType: a.contentType, base64: a.base64 };
        }),
      };
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-30) — reply-in-thread mode:
         if a replyToMessageId was passed, post /outlook-reply with messageId so
         the send threads onto the customer's existing conversation. Everything
         else in the payload is compatible with /outlook-reply. */
      /* Which template produced this body, for the once-per-customer ledger. Only when
         what we inserted is still recognisably in there - see _templateSurvived. */
      var _tplSent = _templateSurvived(_tplBinding, quill) ? _tplBinding.id : '';
      if (_tplSent) payload.templateId = _tplSent;
      /* CUSTOM_AICOMPOSER_BY_CLAUDE — this email started as a Draft it draft: the worker keeps the pair. */
      var _sendAi = _aiDraftFor(quill);
      if (_sendAi) payload.aiDraft = _sendAi;
      /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE — the floor plan on the attached quote, for the ledger.
         Not gated on the template: a rep's own email around the attached quote still sent it. */
      var _sendLayout = _quoteLayoutPayload(attachments, ref);
      if (_sendLayout) payload.quoteLayout = _sendLayout;
      var _sendQuoteRefs = _attachedQuoteRefs(attachments); // CUSTOM_QUOTESENTCLOCK_BY_CLAUDE
      if (_sendQuoteRefs.length) payload.quoteRefs = _sendQuoteRefs;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — the rest of the sequence, pre-rendered.
         ⛔ GATED ON `_tplSent`, WHICH IS THE SAME TEST THE SEND LEDGER USES. A rep who selected
         all, deleted, and wrote their own email has not sent follow-up 1 — so arming three
         follow-ups that reply to it would be chasing a conversation that never started. The
         worker refuses too (it derives the flow from `templateId`), and this is the half that
         lets the composer say so. */
      var _seqPayload = _seq.payload(_tplSent);
      if (_seqPayload) payload.sequence = _seqPayload;
      /* CUSTOM_DELAYEDSTART_BY_CLAUDE — only alongside the sequence it starts; the worker refuses a
         delay without one, and this is the half that never asks for it. */
      if (_seqPayload && _seq.startDays() > 0) payload.startDelayDays = _seq.startDays();
      /* A template can ask for this send to land INSIDE an existing thread. Ryan: "each
         email replies to our previous email ... they can scroll through and see email 4,
         then 3, then 2, then 1, then the quote sent email" - one conversation in the
         customer's inbox rather than five subject lines that look like a mail-merge.
         An explicit reply-mode open still wins: that is the rep pointing at a message. */
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-21) — the override wins over both sources.
         'new' means a new conversation, so neither the caller's reply target nor a template's
         threading request may quietly reinstate one. */
      var _threadOnto = (_sendMode === 'new')
        ? ''
        : (_replyToMessageId || (_tplReplyTo && _tplReplyTo.messageId) || '');
      var _sendPath = '/outlook-send';
      if (_threadOnto) {
        payload.messageId = _threadOnto;
        _sendPath = '/outlook-reply';
        if (_replyRefOn) payload.replySubjectRef = _replyRefOn;   // CUSTOM_REPLYREF_BY_CLAUDE - the "+ RW-1549" chip
        /* /outlook-reply requires a ref; a compose-mode send may have left it undefined. */
        if (!payload.ref) payload.ref = (_tplReplyTo && _tplReplyTo.ref) || ref || undefined;
      }
      var workerBase = 'https://rfq-worker.ryan-29b.workers.dev';
      (window.RepPortal && window.RepPortal.Auth ? window.RepPortal.Auth.getAccessToken() : Promise.resolve(''))
        .then(function(token) {
          return fetch(workerBase + _sendPath, {
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + (token || ''),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });
        })
        .then(function(r) {
          if (r.status === 412) {
            return r.json().then(function(j) {
              var addrs = (j.externalRecipients || []).join('\n  • ');
              var msg = 'Email includes ' + (j.externalRecipients || []).length + ' external recipient(s):\n\n  • ' + addrs + '\n\nSend anyway?';
              if (window.confirm(msg)) {
                return doSend(true);
              }
              throw new Error('Cancelled');
            });
          }
          if (!r.ok) {
            return r.json().then(function(j) {
              throw new Error((j && j.error) || ('HTTP ' + r.status));
            }, function() {
              throw new Error('HTTP ' + r.status);
            });
          }
          return r.json();
        })
        .then(function(resp) {
          if (!resp) return;
          try { localStorage.removeItem(draftKey); } catch (_) {}
          _idbDraftDel(draftKey);   // CUSTOM_COMPOSEDRAFT_BY_CLAUDE
          _composeIdemKey = null; // sent — next send gets a fresh key
          /* Close the row in THIS page too — the worker's ledger write is eventually
             consistent and the rep can reopen the composer inside that window. */
          if (_tplSent && ref && window.RQDTemplates && window.RQDTemplates.markSent) {
            try { window.RQDTemplates.markSent(ref, _tplSent); } catch (_) {}
          }
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-30) — fire the caller's
             onSent hook once per successful send (QW uses it to mark the quote
             sent + flip the status badge). Guarded so a throwing callback can't
             break the success UX. */
          if (_onSentCb) { try { _onSentCb(resp); } catch (_) {} }
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19) — the board reacts NOW: the worker
             wrote the feed entry and advanced the cadence before answering, and this repaints
             the row off what it reported. See RPDash.onEmailSent. */
          try { if (window.RPDash && window.RPDash.onEmailSent) window.RPDash.onEmailSent((_tplReplyTo && _tplReplyTo.ref) || ref || ctx.ref, resp); } catch (_) {}
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — say what happened to the SEQUENCE,
             not just to the email. ⛔ A REFUSED ARM MUST BE LOUD. The email went out either way,
             so a silent refusal leaves the rep believing three more are coming when none are —
             and they would not find out for a week. The refusals are real and reachable: a
             ladder already live on the deal, a stop somebody set earlier, the admin-only gate. */
          var _seqOut = resp && resp.sequence;
          var _seqNote = '';
          var _seqFailed = false;
          if (_seqOut && _seqOut.armed) {
            _seqNote = ' ' + _seqOut.count + ' more email' + (_seqOut.count === 1 ? '' : 's')
              + ' armed, the first on ' + _seqFmtDay(_seqOut.sendAfter) + '.';
          } else if (_seqOut && _seqOut.why) {
            _seqNote = ' ⚠ The email sent, but the sequence was NOT armed: ' + _seqOut.why;
            _seqFailed = true;
          }
          /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-20) — ⛔ A HELD EMAIL MUST NOT SAY "Sent".
             The worker answers `held: true` when the ⏱ clock was ticked outside the 6am-6pm MT
             window: nothing has reached the customer, and a rep who reads "Sent" and closes the
             tab has been told the opposite of what happened. The whole point of building the
             queue rather than leaving the tickbox decorative was that the hold is REAL — so it
             has to be visible at the one moment the rep is looking. */
          if (resp.held) {
            if (status) {
              /* CUSTOM_DELAYEDSTART_BY_CLAUDE — a scheduled follow-up 1 says what it is, and that the
                 sequence starts from it (nothing is armed yet, so there is no `_seqNote`). */
              status.textContent = resp.delayedStart
                ? '📅 Scheduled — Follow-up 1 sends ' + _seqFmtWhen(resp.sendAfter)
                  + '. The rest of the follow-up sequence starts from that send. Nothing has gone to the customer yet.'
                : '⏱ Held — sends ' + _seqFmtWhen(resp.sendAfter)
                  + '. Nothing has gone to the customer yet.' + _seqNote;
              status.classList.add('rqd-email-modal__compose-status--ok');
            }
            /* ⚠️ LONGER THAN A SEND, because it is a sentence that changes what the rep does next
               — 1.2s is right for confirming the expected thing and far too fast for correcting
               an expectation. */
            setTimeout(function () {
              close();
              if (typeof fetchQuotes === 'function') { try { fetchQuotes({ quiet: true }); } catch (_) {} }
            }, 4000);
            return;
          }
          if (status) {
            /* Was "will appear in the feed within ~5 min" — an honest description of the old
               behaviour, and now simply false. */
            status.textContent = '✓ Sent.' + _seqNote;
            status.classList.add(_seqFailed
              ? 'rqd-email-modal__compose-status--err'
              : 'rqd-email-modal__compose-status--ok');
          }
          setTimeout(function() {
            close();
            if (typeof fetchQuotes === 'function') {
              try { fetchQuotes({ quiet: true }); } catch (_) {}
            }
            /* ⚠️ A refusal holds the modal open long enough to be read. 1.2s is right for "Sent."
               and is not enough for a sentence explaining that the thing they asked for did not
               happen. */
          }, _seqFailed ? 6000 : 1200);
        })
        .catch(function(err) {
          /* CUSTOM_DELAYEDSTART_BY_CLAUDE — back to "Schedule for Wed" when a delay is set. */
          if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = (_seq && _seq.sendLabel) ? _seq.sendLabel() : 'Send'; }
          if (cancelBtn) cancelBtn.disabled = false;
          if (status && err && err.message !== 'Cancelled') {
            status.textContent = 'Send failed: ' + err.message;
            status.classList.add('rqd-email-modal__compose-status--err');
          } else if (status && err && err.message === 'Cancelled') {
            status.textContent = '';
          }
        });
    }

    overlay.addEventListener('change', function(ev) {
      if (ev.target && ev.target.id === 'rqd-compose-file-input') {
        var files = Array.from(ev.target.files || []);
        var status = overlay.querySelector('#rqd-compose-status');
        var oversized = files.filter(function(f) { return f.size > MAX_ATTACH_BYTES; });
        if (oversized.length) {
          if (status) {
            status.textContent = 'Skipped (over 10 MB): ' + oversized.map(function(f) { return f.name; }).join(', ');
            status.classList.add('rqd-email-modal__compose-status--err');
          }
          files = files.filter(function(f) { return f.size <= MAX_ATTACH_BYTES; });
        }
        var room = MAX_ATTACH_COUNT - attachments.length;
        if (files.length > room) {
          if (status && !oversized.length) {
            status.textContent = 'Only first ' + room + ' file(s) added — max ' + MAX_ATTACH_COUNT + ' attachments.';
            status.classList.add('rqd-email-modal__compose-status--err');
          }
          files = files.slice(0, room);
        }
        Promise.all(files.map(function(f) {
          return _fileToBase64(f).then(function(b64) {
            return {
              name: f.name,
              contentType: f.type || 'application/octet-stream',
              sizeBytes: f.size,
              base64: b64,
            };
          });
        })).then(function(arr) {
          attachments = attachments.concat(arr);
          renderAttachList();
          ev.target.value = '';
        });
      }
    });

    overlay.addEventListener('click', function(ev) {
      /* Read the whole sequence before arming it — handled by the shared control, which answers
         true when the click was its Preview button. */
      if (_seq.handleClick(ev)) return;
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-29) — the optional destructive footer action.
         The handler owns its own confirmation and its own close: this modal knows nothing about
         what is being deleted, and a shared footer that tried to guess would either double-confirm
         or skip one. Resolving `close` through the ctx lets the caller shut the composer only
         after its own work succeeded. */
      if (ctx.extraAction && ev.target.id === 'rqd-compose-extra') {
        Promise.resolve(ctx.extraAction.onClick({ close: close }))
          .catch(function (e) { _composeToast('Failed: ' + ((e && e.message) || e)); });
        return;
      }
      if (ev.target.id === 'rqd-compose-minimize') { _minimize(); return; }
      if (ev.target.id === 'rqd-compose-x') {
        var _hadWork = !!(quill && (quill.getText() || '').trim().length > 0);
        _keepDraft();
        close();
        if (_hadWork) _composeToast('Draft email saved');
        return;
      }
      if (ev.target.id === 'rqd-compose-cancel') {
        if (!ctx.prefillBody && !_richBodyHtml && _draftFilesReady) _idbDraftPut(draftKey, attachments);   // CUSTOM_COMPOSEDRAFT_BY_CLAUDE — the files stay with the draft
        // Persist any typed work synchronously (beats the 1500ms autosave on a
        // quick exit). No discard confirm dialog — just leave.
        var _typedBody = !!(quill && (quill.getText() || '').trim().length > 0);
        _saveDraftNow();
        close();
        // "Back" mode: return to the Send-this-quote chooser (PDF stays ready
        // in the caller). If the rep had typed a message, a quiet amber toast
        // confirms it was kept as a draft — no pop-up, no buttons.
        if (_onBackCb) {
          if (_typedBody) _composeToast('Draft email saved');
          try { _onBackCb(); } catch (_) {}
        }
        return;
      }
      if (ev.target.id === 'rqd-compose-send') { _sendWithSequence(false); return; }
      if (ev.target.id === 'rqd-compose-test') {
        _sendTestEmail({
          ref: ref,
          /* CUSTOM_TESTTHREADALWAYS_BY_CLAUDE - the same thread pick the live send makes (_threadOnto below). */
          replyToMessageId: (_sendMode === 'new') ? '' : (_replyToMessageId || (_tplReplyTo && _tplReplyTo.messageId) || ''),
          /* Rich-body mode has no Quill — pass a tiny shim exposing the same
             getText()/root.innerHTML surface _sendTestEmail reads from. */
          quill: quill || { getText: function() { return _bodyText(); }, root: { get innerHTML() { return _bodyHtml(); } } },
          subjectEl: overlay.querySelector('#rqd-compose-subject'),
          attachments: attachments,
          testBtn: overlay.querySelector('#rqd-compose-test'),
          statusEl: overlay.querySelector('#rqd-compose-status'),
          commitChips: function () { if (_composeChipApi) _composeChipApi.commitAll(); },
          sendOverride: ctx.sendOverride,
          highImportance: _highImportanceChecked(),
          // CUSTOM_SENDWINDOW_BY_CLAUDE — hold until the next 6am-6pm MT window.
          businessHoursOnly: _businessHoursChecked(),
          extraToggle: _extraToggleChecked(), // CUSTOM_DISCDRIP_BY_CLAUDE — the caller's own tickbox
          fromMailbox: _fromMailbox,
          fromSignatureHtml: _selectedFromSig,
        });
        return;
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-22) — match via closest() so a
         click landing on the button's inner <svg>/<path> (ev.target is the icon,
         not the button) still opens the file picker. The id=== check only fired
         when the click hit the button's padding, so clicking the icon did nothing
         — reading as a dead "second click." */
      var _composeAttachBtn = ev.target.closest && ev.target.closest('#rqd-compose-attach-btn');
      if (_composeAttachBtn) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Area 2: attach menu (device files OR an
           existing deal file: quote PDF / floor plan). Deal files appear only when ctx.ref
           resolves to a dashboard quote; otherwise the menu just offers "From files…". */
        _openDealAttachMenu({
          anchorBtn: _composeAttachBtn,
          ref: ctx.ref,
          /* Ryan 2026-08-18 — the live To list, so a brand-new email to a customer can
             re-attach what was sent on any of their deals, even with no ctx.ref. */
          toEmails: (toChips || []).concat(ccChips || []),
          attachments: function () { return attachments || []; },   // CUSTOM_HOUSEDOCS_BY_CLAUDE
          onDevice: function () { var fi = overlay.querySelector('#rqd-compose-file-input'); if (fi) fi.click(); },
          onAdd: function (att) {
            var status = overlay.querySelector('#rqd-compose-status');
            if (attachments.length >= MAX_ATTACH_COUNT) {
              if (status) { status.textContent = 'Max ' + MAX_ATTACH_COUNT + ' attachments.'; status.classList.add('rqd-email-modal__compose-status--err'); }
              return false;
            }
            attachments = attachments.concat([att]);
            renderAttachList();
            return true;
          },
          onError: function (msg) { var status = overlay.querySelector('#rqd-compose-status'); if (status) { status.textContent = msg; status.classList.add('rqd-email-modal__compose-status--err'); } },
        });
        return;
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19) — ⚡ snippets. The keyboard path is "#"
         in the body; this is the same corpus for a rep who has not learned it. */
      var _composeSnipBtn = ev.target.closest && ev.target.closest('#rqd-compose-snippet-btn');
      if (_composeSnipBtn) {
        _openSnippetMenu({
          anchorBtn: _composeSnipBtn,
          ctx: _tplCtx(),
          onPick: function (id) {
            if (!quill) return Promise.reject(new Error('the editor is still loading'));
            _snipStatus('');
            /* Focus first: the menu is a click, so the caret may be nowhere. getSelection(true)
               inside the insert then resolves to the end of the document rather than throwing. */
            try { quill.focus(); } catch (_) {}
            return _applySnippet(quill, id, _tplCtx(), _snipStatus, function (m) { _snipStatus(m, true); }, composeFx());
          },
        });
        return;
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-18) — 📝 email templates. */
      var _composeTplBtn = ev.target.closest && ev.target.closest('#rqd-compose-template-btn');
      if (_composeTplBtn) {
        var _tplStatus = function (msg, isErr) {
          var s = overlay.querySelector('#rqd-compose-status');
          if (!s) return;
          s.textContent = msg;
          s.classList.toggle('rqd-email-modal__compose-status--err', !!isErr);
          /* The card appears only when the template actually has something to flag, and
             an empty message takes it away again - an empty navy box would read as a
             broken panel rather than as "nothing to worry about". */
          s.classList.toggle('rqd-email-modal__compose-status--tpl', !!msg);
        };
        _openTemplateMenu({
          anchorBtn: _composeTplBtn,
          ctx: _tplCtx,
          isDirty: function () { return !!(quill && quill.getText().trim()); },
          onPick: function (id) {
            if (!quill) return Promise.reject(new Error('the editor is still loading'));
            _tplStatus('');
            return _applyTemplate(_tplBinding, quill, id, _tplCtx(), function (w) { _tplStatus(w, false); }, _composeTemplateFx());
          },
          onError: function (msg) { _tplStatus(msg, true); },
          /* The sequence control follows the BODY: a different template just landed, so what
             (if anything) can be armed has changed with it. */
          onPicked: function () { _seqSyncControl(); },
        });
        return;
      }
      /* Ryan 2026-05-22: "+ RW-1166" ref-insert chip — clicking
         inserts the base ref at the cursor position inside the
         subject input (or appends to end if no cursor). Lets reps
         drop the ref into the subject when they want better thread
         matching, without forcing it for cold outreach. */
      var refChip = ev.target.closest('#rqd-compose-ref-chip');
      if (refChip) {
        var refTxt = refChip.dataset.ref || '';
        var subjInput = overlay.querySelector('#rqd-compose-subject');
        /* CUSTOM_REPLYREF_BY_CLAUDE - on a reply the chip toggles the ref on the end of the thread's subject. */
        if (_sendMode === 'reply' && subjInput && refTxt) {
          var _thR = String((_replyContext && _replyContext.subject) || subjInput.value || '');
          var _refU = refTxt.toUpperCase();
          if (_replyRefOn) _replyRefOn = '';
          else if (_thR.toUpperCase().indexOf(_refU) === -1) _replyRefOn = _refU;
          else { _composeToast("This thread's subject already has " + refTxt + '.'); return; }
          subjInput.value = _thR + (_replyRefOn ? ' - ' + _replyRefOn : '');
          scheduleDraftSave();
          return;
        }
        if (subjInput && refTxt) {
          var start = subjInput.selectionStart;
          var end = subjInput.selectionEnd;
          if (start == null) { start = subjInput.value.length; end = start; }
          var before = subjInput.value.slice(0, start);
          var after = subjInput.value.slice(end);
          // Prefix " - " so the ref reads cleanly at the end of a subject line;
          // skip the separator when the subject (up to the cursor) is blank.
          var insertTxt = (before.replace(/\s+$/, '').length ? ' - ' : '') + refTxt;
          subjInput.value = before + insertTxt + after;
          _subjTouched = true;   // CUSTOM_REPLYSUBJECT_BY_CLAUDE — the rep put it there
          try { subjInput.focus(); subjInput.setSelectionRange(start + insertTxt.length, start + insertTxt.length); } catch (_) {}
          scheduleDraftSave();
        }
        return;
      }
      // Click the attachment name → preview it in a new tab (the × button is a
      // separate element, so removing still works independently).
      var chipName = ev.target.closest('.rqd-email-modal__compose-chip-name');
      if (chipName) {
        var pchip = chipName.closest('.rqd-email-modal__compose-chip');
        var pidx = pchip ? parseInt(pchip.dataset.idx, 10) : -1;
        if (!isNaN(pidx) && attachments[pidx]) _openAttachmentPreview(attachments[pidx]);
        return;
      }
      var rm = ev.target.closest('.rqd-email-modal__compose-chip-rm');
      if (rm) {
        var idx = parseInt(rm.dataset.idx, 10);
        if (!isNaN(idx) && attachments[idx]) {
          attachments.splice(idx, 1);
          renderAttachList();
        }
        return;
      }
    });
  };


  /* ============================================
     CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-29) — OPEN A HELD EMAIL AS THE DRAFT IT IS
     ============================================
     Ryan: *"skip the first pop-up.. just open the 2nd ('Edit') pop-up first.. this is essentially
     a draft non-sent email, so it makes most sense to bring that up right away."*

     The first version opened a read-only preview with Edit / Cancel / Close, and he was right that
     it was a step in the way — a held email has not been sent, so there is nothing to "view" that
     the editor does not already show, including the attachments. Deleting that shell also deleted
     the two bugs it had (a body wrapped in a class that did not exist, and a heading that needed
     the portal wrap for its colours).

     THE THREE ACTIONS ARE THE COMPOSER'S OWN, which is the point of opening it directly:
       · Cancel       — close, leave the held email exactly as it is
       · Delete Draft — remove it entirely (confirmed, and the confirm sits ABOVE this modal)
       · Send         — RE-QUEUE it, recomputing the send time from the ⏱ checkbox

     ⛔ SEND RESCHEDULES, AND THAT IS THE WHOLE REASON THE ⏱ BOX IS SHOWN HERE. Ryan: *"If I simply
     re-click send on that, it should re-trigger it with the new time based on the 'during business
     hours' checkbox thing."* So this posts the normal /outlook-send — the same path a fresh email
     takes — which re-queues at the NEXT opening (ticked) or sends immediately (unticked). It does
     NOT call /held-email-update, which deliberately preserves the original send time; that
     endpoint stays for a future "edit without rescheduling" and is not what Send means.

     ⛔ SEND FIRST, THEN DELETE THE OLD JOB. The reverse order loses the email outright if the send
     fails. Briefly two jobs exist, which is the safe direction: a duplicate is visible and
     fixable, a vanished draft is neither.
     ⚠️ The supersede delete passes `replaced: true` so the worker does NOT free the template
     ledger — the template genuinely has just been sent again, and clearing it here would re-open a
     row that should stay closed. Only a real Delete Draft frees it. */
  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-29) — separate the rep's message from the
     signature, so a held draft can be edited in QUILL like every other composer.

     ⛔ WHY THIS EXISTS AT ALL: the two editors have different toolbars. `richBody` is a
     contentEditable canvas with a reduced bar; Quill has the full one. Ryan: *"the buttons at the
     top of the email composer are genuinely different on this composer than if I was in the DD and
     just clicked the email button... Want consistency"* and *"want to use the other more
     comprehensive button bar wherever possible on these composer pop-ups."*

     The reason the draft was on richBody is real and has not gone away: a held job stores the
     FINISHED email, signature already baked in, and Quill's path re-adds the signature on send —
     so prefilling the stored body straight into Quill would send the customer two signatures.

     `buildEmailHtml` (worker: email/signature.js) composes a known shape:
         <div style="font-family:Calibri…">      ← wrapper
           <div style="margin:0;">BODY</div>     ← what the rep typed
           <p …><br></p>                          ← one blank line
           SIGNATURE
         </div>
     so the rep's own message is the wrapper's first child div. Parsed with the DOM rather than a
     regex, because the body contains arbitrary nested HTML that a regex would mis-balance.

     ⚠️ CONSERVATIVE BY DESIGN. It returns ok:false unless it finds exactly that shape, and the
     caller then keeps richBody. A wrong strip is a truncated customer email; the cost of failing
     closed is only an inconsistent toolbar on an odd draft. Verified against the real stored body
     for RW-1327-A before being relied on. */
  function _heldSplitSignature(html) {
    try {
      var host = document.createElement('div');
      host.innerHTML = String(html || '');
      var wrap = host.firstElementChild;
      if (!wrap || wrap.tagName !== 'DIV' || host.children.length !== 1) return { ok: false };
      var first = wrap.firstElementChild;
      if (!first || first.tagName !== 'DIV') return { ok: false };
      if (!/margin:\s*0/.test(first.getAttribute('style') || '')) return { ok: false };
      /* A wrapper with only the body div and nothing after it is not the composed shape — it has
         no signature to strip, and treating it as one would be guessing. */
      if (wrap.children.length < 2) return { ok: false };
      return { ok: true, body: first.innerHTML };
    } catch (_) { return { ok: false }; }
  }

  window.RQD.openHeldEmailModal = function (key, entry, ref, deps) {
    if (!key) return;
    var fetchQuotes = (deps && deps.fetchQuotes) || null;
    var API = (window.RepPortal && window.RepPortal.API) || null;
    if (!API || !window.RQD.openComposeModal) return;

    /* `full=1` — the bytes are needed, or re-opening would silently drop the quote PDF off a
       queued email and Send would put a linkless quote in front of the customer. */
    API.get('/held-email?key=' + encodeURIComponent(key) + '&full=1').then(function (f) {
      if (!f || f.success !== true) throw new Error((f && f.error) || 'Could not open it');

      /* Split once, up front: both the editor choice and the test-send fallback read it. */
      var _heldSplit = _heldSplitSignature(f.bodyHtml || '');

      function supersede() {
        /* Best-effort: the new email is already queued by this point. A failure here leaves a
           duplicate, which the feed shows and a human can delete — far better than the send
           having been blocked to protect a bookkeeping step. */
        return API.post('/held-email-cancel', { key: key, replaced: true }).catch(function () {});
      }

      /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — ⛔ A SCHEDULED FOLLOW-UP 1 IS SAVED IN PLACE,
         NOT RE-SENT. The re-send below posts a fresh /outlook-send: no thread, no template, no
         sequence, and a send time from the ⏱ box - which on this email would silently turn "Follow-up
         1 on Wednesday, then +7, +7, +10" into a one-off email tomorrow morning in a new thread. So
         Send here means "save my changes": /held-email-update keeps the key, the day, the thread and
         the sequence, and re-signs the body server-side (the editor holds it without its signature). */
      var _isScheduledStart = Number(f.startDelayDays) > 0;
      function _saveScheduledInPlace(args) {
        var upd = {
          key: key,
          subject: args.subject || f.subject || '',
          to: args.to || [], cc: args.cc || [], bcc: args.bcc || [],
          attachments: (args.attachments || []).map(function (a) {
            return { name: a.name, contentType: a.contentType, base64: a.base64 };
          }),
        };
        /* Quill held the rep's words only, so the worker re-signs them; the rich-body fallback edited
           the finished email (signature included), which is stored as it stands. */
        if (_heldSplit.ok) upd.quillBody = args.bodyHtml || _heldSplit.body; else upd.bodyHtml = args.bodyHtml || f.bodyHtml || '';
        return API.post('/held-email-update', upd).then(function (r) {
          if (!r || r.success !== true) throw new Error((r && r.error) || 'Could not save it');
          if (fetchQuotes) fetchQuotes();
          return { composerNote: 'Saved. Follow-up 1 still sends ' + (f.sendAfterLabel || 'as scheduled')
            + ', and the follow-up sequence still starts from it.' };
        });
      }

      window.RQD.openComposeModal({
        ref: f.ref || ref || '',
        prefillTo: (f.to || []).join(', '),
        prefillCc: (f.cc || []),
        prefillSubject: f.subject || '',
        /* Quill when the signature can be split off cleanly — that is the comprehensive toolbar
           every other composer has, and consistency was the ask. richBody only as the fallback,
           where splitting is not certain and a doubled signature would be the alternative. */
        ...(_heldSplit.ok ? { prefillBody: _heldSplit.body } : { richBody: f.bodyHtml || '' }),
        preAttachments: (f.attachments || []).map(function (a) {
          return { name: a.name, contentType: a.contentType, base64: a.base64, sizeBytes: a.sizeBytes };
        }),
        fromOptions: ['REP'],
        /* ⛔ THE ⏱ BOX MUST BE HERE AND MUST START TICKED. Ryan: *"just make sure the business
           hours checkbox is on the pop-up.. b/c right now it aint."* It is what Send reads to
           decide between re-queueing and sending now, so hiding it would leave the only control
           over the send time invisible. Ticked because this email IS currently held — the box
           should describe the state the rep is looking at, not a default. */
        /* ⚠️ Not on a scheduled follow-up 1: its day is already set and Send only saves the words, so
           a clock box here would be a control that does nothing. */
        showBusinessHours: !_isScheduledStart,
        businessHoursDefault: true,
        suppressFollowUp: true,
        extraAction: {
          label: 'Delete Draft',
          onClick: function (api) {
            var msg = 'This deletes the draft. It will not send, and it cannot be recovered.';
            var ask = (window.RQD && typeof window.RQD.confirm === 'function')
              ? window.RQD.confirm(msg, { title: 'Delete this draft?', confirmLabel: 'Delete it', cancelLabel: 'Keep it', danger: true })
              : Promise.resolve(window.confirm(msg));
            return Promise.resolve(ask).then(function (ok) {
              if (!ok) return null;
              return API.post('/held-email-cancel', { key: key }).then(function () {
                api.close();
                if (fetchQuotes) fetchQuotes();
              });
            });
          },
        },
        sendOverride: function (args) {
          /* A test goes to the rep's own inbox and must not touch the held job at all. The
             composer passes EMPTY recipients here (the worker redirects a test), which is why this
             cannot fall through to the send path. */
          if (args.isTest) {
            /* ⚠️ The fallback must be the SIGNATURE-STRIPPED body, not the stored one. /outlook-send
               composes body + signature, so falling back to the finished email would send a test
               carrying two signatures — and a test exists precisely to show the real formatting. */
            var _tb = String(args.bodyHtml || '');
            if (!_tb.replace(/<[^>]*>/g, '').trim()) {
              _tb = _heldSplit.ok ? _heldSplit.body : (f.bodyHtml || '');
            }
            return API.post('/outlook-send', {
              ref: f.ref || ref || '', test: true, to: [], cc: [], bcc: [],
              subject: args.subject || f.subject || '', body: _tb, fromMailbox: 'REP',
              attachments: (args.attachments || []).map(function (a) {
                return { name: a.name, contentType: a.contentType, base64: a.base64 };
              }),
            });
          }
          if (_isScheduledStart) return _saveScheduledInPlace(args);
          /* ⛔ PRE-CONFIRMED, LIKE EVERY OTHER COMPOSER IN THIS FILE. /outlook-send answers 412 on
             an external recipient until the client confirms, and a sendOverride bypasses the
             machinery that handles it — so this send failed with "Email includes external
             recipients. Confirm to send." (Ryan hit it on the first real re-send.)

             ⚠️ MY FIRST FIX WAS TO PROMPT, AND THAT WAS WRONG. Both of this file's own send paths
             (:3573 and :6234) deliberately pass externalConfirmed up front, and say why: emailing
             external customers IS the job here, so the prompt is *"pure friction"* and the server
             guard stays as the backstop for other callers. Adding a prompt only to the held draft
             would have made it the one send in the Deal Dashboard that nags — the opposite of the
             consistency Ryan had just asked for two messages earlier. Matching the house rule.

             Raw fetch rather than API.post so a non-2xx body still yields its real error text
             instead of being collapsed by the wrapper. */
          var _payload = {
            ref: f.ref || ref || '',
            to: args.to || [], cc: args.cc || [], bcc: args.bcc || [],
            subject: args.subject || '',
            body: args.bodyHtml || '',
            fromMailbox: 'REP',
            highImportance: args.highImportance === true,
            /* The checkbox decides: ticked re-queues at the next opening, unticked sends now. */
            businessHoursOnly: args.businessHoursOnly === true,
            attachments: (args.attachments || []).map(function (a) {
              return { name: a.name, contentType: a.contentType, base64: a.base64 };
            }),
          };
          function _postSend() {
            return _getRepToken().then(function (tok) {
              return fetch(_WORKER_BASE + '/outlook-send', {
                method: 'POST',
                headers: { Authorization: 'Bearer ' + (tok || ''), 'Content-Type': 'application/json' },
                body: JSON.stringify(Object.assign({}, _payload, { externalConfirmed: true })),
              });
            }).then(function (res) {
              if (!res.ok) {
                return res.json().then(
                  function (j) { throw new Error((j && j.error) || ('HTTP ' + res.status)); },
                  function () { throw new Error('HTTP ' + res.status); }
                );
              }
              return res.json();
            });
          }
          return _postSend().then(function (r) {
            if (r && r.success === false) throw new Error(r.error || 'Could not send it');
            return supersede().then(function () {
              if (fetchQuotes) fetchQuotes();
              return {
                composerNote: (r && r.held)
                  ? 'Rescheduled — it now sends ' + (r.sendAfterLabel || 'at the next opening') + '.'
                  : 'Sent.',
              };
            });
          });
        },
        fetchQuotes: fetchQuotes,
      });
    }).catch(function (err) {
      var m = (err && err.message) || 'Could not open it';
      /* ⛔ A "GONE" ANSWER IS PROOF THE FEED IS STALE — SO REPAINT IT. The row said pending and the
         server says the job no longer exists; both cannot be true, and the server is the one that
         knows. The board serves its list from a 6h SWR cache, so after a re-send elsewhere the
         superseded row can sit there claiming to be live indefinitely. Refetching here (the deps'
         fetchQuotes invalidates first) turns the one moment the contradiction is visible into the
         moment it fixes itself, instead of leaving the rep to wonder which of the two to believe.
         Ryan hit exactly that: "deal feed entry still says it's going out on Monday. and clicking
         it still produces that message about it already being sent or cancelled." */
      if (fetchQuotes) { try { fetchQuotes({ quiet: true }); } catch (_) {} }
      if (window.RQD && typeof window.RQD.alert === 'function') window.RQD.alert(m);
      else alert(m);
    });
  };

})();
